// SessionCanvasView: a trace's workspace, its traces on one canvas, time
// running across it on ONE clock, long pauses closed to a break.
//
// It is a board of cards whose lanes sit on that clock: the anchor (the trace
// it was opened on) and the traces laid on beside it, carried, sized, folded
// and ordered by hand. What is about a card hangs under it at its moment: the
// review's findings, the reader's kept readings, the chat's notes and claims,
// the agents the anchor started. A card per machine holds the work the chat's
// investigation found there outside the board's traces. By time, the cards
// take a row each in the order they began, until the reader moves one;
// otherwise they pack where they fit. The Lanes reading's session graph
// (`sessionGraph`) is the same board, by time to start, with the sessions
// related to the anchor on it too: the strongest joined to it on a cell at
// both ends open on their own, the rest placeholders at their time (stubs),
// empty and dashed until one is loaded there. A lens zooms a stretch of the clock for every card at once,
// and a cursor on it dims what had not happened yet on every card.
//
// The one board is drawn wherever the trace's workspace is: over the page
// (TraceCanvas), in place of the trace's lanes (the Lanes reading's session
// graph), or for a chat's investigation; the trace page's preview is its
// anchor's card alone.
//
// A wire is one shared string, dropped from the cell that carries it in the
// anchor to the point (or, opened, the cell) that carries it there, and
// labelled with how long the hand-off took; the investigation's claims are
// numbered badges over the cells they rest on.
import { createContext, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { rangeText } from "./SessionTimeScrubber.jsx";
import { CANVAS_SANS as SANS, alphaMix, clipText, drawScene, overMix, readTheme, roundRect } from "./LanesCanvasPlane.jsx";
import { formatCompact, getDotTilePattern } from "../lib/renderers.js";
import { buildLaneScene, hitTestScene, layoutRows } from "../lib/laneScene.js";
import { isConversationDomain, lanesAtTurn, opLine, sameCallLanes } from "../lib/traceLanes.js";
import { candidateDirectionLabel, locateNeedleStrings } from "../lib/traceNeedleLens.js";
import { query } from "../lib/dataClient.js";
import { buildTraceGraphOverlay, toolUseLanes } from "../lib/traceGraphOverlay.js";
import { codeModeReadable, unwrapCodeModeTurns } from "../lib/traceCodeModeCells.js";
import { readShellWork } from "../lib/traceShellWork.js";
import { recordTurnBase } from "../lib/traceTurnFocus.js";
import { applySessionPage, sessionPageRequest } from "../lib/sessionTabState.js";
import { warmGrammar } from "../lib/syntax/parserHost.js";
import { useDimensionEntry } from "../lib/dimensionDefs.js";
import { useThemeVersion } from "../lib/theme.js";
import { useSessionTitles } from "../lib/sessionTitles.js";
import { deriveTraceTooling, toolingInputs } from "../lib/traceTooling.js";
import { modelLabel } from "../lib/agentIdentity.js";
import { addToChat } from "../lib/chatContextStore.js";
import { postChatJob } from "../lib/chatJobs.js";
import { useBoardWalk } from "../lib/traceWalkthrough.js";
import { clearRemoval, messageAskOpen, publishBoardClaims, publishWorkspaceCanvas, publishWorkspaceNotesPointed, recordRemoval, requestWorkspaceFocus, settleWorkspaceMessage, useChatAnswer, useClaimEvidenceAsk, useLastRemoval, useWorkspaceFocus, useWorkspaceMessageAsk, useWorkspaceNoteAsk, useWorkspaceOpenAsk } from "../lib/workspaceStore.js";
import { evidenceList, inListedOrder, inTimeOrder } from "../lib/answerEvidence.js";
import { onBoardDragOver, onBoardDrop } from "../lib/boardDrops.js";
import { buildLanesThreads, spawnWindowFromTurns, threadMissText } from "../lib/traceThreadsWiring.js";
import { buildFindingsReport, fetchTraceReviewFindings, findingsCovering, readFindingsCache, readTraceReviewFindings } from "../lib/traceReviewFindings.js";
import { liteVerdictLabel } from "../lib/triageLite.js";
import { useFindingVerdicts } from "../lib/useFindingVerdicts.js";
import { SEVERITY_COLOR, behaviourTone, defaultReviewModel } from "../lib/traceReview.js";
import { getChatCreds } from "../lib/chatCreds.js";
import { createHandoffLight, litOn } from "../lib/handoffLight.js";
import { buildReadLanes, draftReadingCalls, readReadingCache, readingCacheKey, readingClaimsHash, sessionHasReading, subscribeReadingSessions } from "../lib/traceReading.js";
import { SeverityChip } from "./lenses/triageParts.jsx";
import { VerdictChip } from "./lenses/LiteSession.jsx";
import { StepChevron, stepText } from "./GraphTimeline.jsx";
import { timelineView } from "../lib/graphTimeline.js";
import {
  CLOCK_U, MIN_WINDOW_U, bucketAt, bucketLanes, buildClock, cardRows, dropRoute, fitFocusToHeight, fitRowsToHeight, gapText, groupOfLane, helpersOf, latencyText, readingTurns, spanText, stampedShare, turnTimes, windowOver, wireRoute,
} from "../lib/sessionWorkbench.js";
import { COMPACT_H } from "../lib/traceLaneLayout.js";
import { WALKTHROUGH_BY } from "../lib/traceWalkthrough.js";

const HUES = ["#8fb376", "#7fa3c9", "#c9a45f", "#a98fc9", "#c98383", "#7bbfae", "#bdb56e", "#8b9dc9"];
const STRONG = 2.5;
// The related sessions the session graph shows, strongest first; the add
// sheet's Related list holds every one.
const MAX_RELATED_ROWS = 40;
// In the session graph, the strongest related sessions read to find the ones
// joined to the anchor on a cell at both ends, and how many of those open on
// their own.
const EAGER_READS = 20;
const AUTO_JOIN = 3;
const WIRES_PER_SESSION = 3;
const MINUTE = 60_000;
const HOUR = 3600_000;
const DAY = 24 * HOUR;
const FOCAL = "focal";
const DOT = " \u00b7 ";
// What the chat found, in the words people use: a process it saw run is a
// command.
const FIND_WORDS = { process: ["command", "commands"], message: ["message", "messages"], prompt: ["prompt", "prompts"] };
const findWords = (kind, n) => `${n} ${(FIND_WORDS[kind] || [kind, `${kind}s`])[n === 1 ? 0 : 1]}`;
// A list of what was found, counted by kind: "3 commands, 1 message".
const foundWords = (events) => {
  const counts = new Map();
  for (const e of events || []) counts.set(e.kind, (counts.get(e.kind) || 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => findWords(k, n)).join(", ");
};
const ARROW = " \u2192 ";

// The canvas: the strip across the top (the whole shared clock, every trace's
// span on it, and the window the cards show), and under it the cards. No
// machine rail: which machine a session ran on rides on its head, so every
// pixel of width is time.
const MACH_W = 0;
const RULER_BOARD = 74;
// A lens opens its stretch to this share of the plane.
const LENS_SHARE = 0.55;
// A card: its head (name, then where and when) over its rows on the clock, the
// rows' names on a rail just before its first moment (BOARD_RAIL).
const CARD_HEAD = 40;
// Attachments (the board): the chat's notes and claims, the review's
// findings, the reader's kept readings, the anchor's helpers. Nothing hangs
// under a card until it is opened: the chat's notes stand over their cells as
// pins, what the review found marks its cells, and a card's tag on its head
// lists what is about it (TAG_KINDS). Opened, one stands under its card at
// its moment, tied to its cells, until it is closed (a kept reading comes
// opened). The band is as deep as the opened cards stacked in it, the same
// at every zoom, so the board's places never change with the zoom.
// What a trace card's tag on its head lists, to open: what the review found,
// the chat's claims and the agents the trace started.
const TAG_KINDS = new Set(["finding", "claim", "agent"]);
const TONE_RANK = { danger: 0, warn: 1 };
const PIN_W = 360;
const CHIP_GAP = 6;
// An opened one before it has been measured, and the most of it shown (its
// body scrolls past that).
const PIN_EST = 140;
const PIN_MAX_H = 300;
const HANG_GAP = 12;
const GROWN_W = PIN_W;
const GROWN_H = 320;
// The height the board's corner controls take at the canvas's foot.
const CONTROLS_CLEAR = 40;
// That room is kept clear down the board: one that fits sits centered with as
// much under it, and a taller one scrolls on past its last row by as much, so
// nothing on it ends under the controls.
const boardFits = (h, planeH) => (h || 0) + 2 * CONTROLS_CLEAR <= planeH;
const boardFootY = (h, planeH) => Math.max(0, (h || 0) + CONTROLS_CLEAR + 16 - planeH);
// How far a peek stands off its pin, and how many of a stack's members its
// peek lists.
const PEEK_GAP = 4;
const STACK_PEEK_ROWS = 8;
// A note the chat pinned on a cell stands over the board as a map's pin does:
// a head on a needle whose point touches the cell's ring, the same size at
// every zoom (NotePin). Heads nearer than a head and PIN_GAP stand as one.
const PIN_HEAD = 18;
const PIN_NEEDLE = 8;
const PIN_GAP = 2;
// Half a ringed cell's depth, on the board.
const RING_HALF = 8.5;
// The pointer resting on a card this long, it comes to the front and the
// others fade back under a veil of the ground this thick.
const CARD_DIM_REST_MS = 100;
const NO_Z = new Map();
const CARD_DIM_VEIL = 0.45;
// A card carried by its head follows the pointer frame by frame on its own
// canvas, the board laying it again only when how it is drawn changes, or
// when the pointer rests this long.
const CARRY_REST_MS = 140;
// A wheel gesture keeps to the way it set out, as a page's scroll does: once
// it has moved this far, one way it moves at least twice the other, and it
// keeps to that way until the wheel rests this long.
const WHEEL_RAIL_PX = 4;
const WHEEL_RAIL_RATIO = 2;
const WHEEL_REST_MS = 200;

// One wheel event's move (px), kept to its gesture's way. A trackpad's swipe
// across carries a little up and down in every event (and one down, a little
// across), which shook the whole board as it went. Null while the gesture is
// still too short to have a way; one that sets out at a slant moves both
// ways. A gesture that plainly turns -- the other way carrying the move twice
// running, as a scroll down under the momentum of a swipe across does -- takes
// the other way: held to its first way until the wheel rested, a scroll the
// other way did nothing for as long as the momentum ran. `rail` is the gesture
// under way ({ at, axis, ax, ay, turn }), carried from event to event.
export function railWheel(rail, at, dx, dy) {
  if (at - rail.at > WHEEL_REST_MS) Object.assign(rail, { axis: null, ax: 0, ay: 0, turn: 0 });
  rail.at = at;
  if (!rail.axis) {
    rail.ax += Math.abs(dx);
    rail.ay += Math.abs(dy);
    if (rail.ax + rail.ay < WHEEL_RAIL_PX) return null;
    rail.axis = rail.ax >= WHEEL_RAIL_RATIO * rail.ay ? "x" : rail.ay >= WHEEL_RAIL_RATIO * rail.ax ? "y" : "both";
    rail.turn = 0;
  } else if (rail.axis !== "both") {
    const main = Math.abs(rail.axis === "x" ? dx : dy);
    const other = Math.abs(rail.axis === "x" ? dy : dx);
    rail.turn = other >= WHEEL_RAIL_PX && other >= WHEEL_RAIL_RATIO * main ? (rail.turn || 0) + 1 : 0;
    if (rail.turn >= 2) {
      rail.axis = rail.axis === "x" ? "y" : "x";
      rail.turn = 0;
    }
  }
  return { dx: rail.axis === "y" ? 0 : dx, dy: rail.axis === "x" ? 0 : dy };
}
const SHELF_MIN_W = 380;
const DETAIL_W = 400;
const DETAIL_H = 340;
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITY_WORD = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };
// Severity's colour is the review's own (lib/traceReview.js), on the canvas
// read from the same tokens.
const severityCss = (sev) => SEVERITY_COLOR[behaviourTone(sev)];
const severityInk = (theme, sev) => {
  const tone = behaviourTone(sev);
  return tone === "danger" ? theme.danger : tone === "warn" ? theme.warn : theme.muted;
};
const cssVar = (name, fallback, el = null) => {
  try { return getComputedStyle(el || document.documentElement).getPropertyValue(name).trim() || fallback; } catch { return fallback; }
};
const MAX_READINGS = 16;
// How long a preview's column must hold still before the preview takes its
// new width.
const PREVIEW_REST_MS = 160;
// The board (the workspace): every trace a card on the one axis, as wide as it
// ran at the board's zoom, its lane names on a rail just before it (held at the
// canvas's edge while the card runs on under it), its machine over it; cards
// rise to the first space they fit with everything in view, and keep it.
const BOARD_RAIL = 128;
// Room past the last card as there is before the first (the clock's left pad
// less a card's rail: 9px either side of a card's edge), so a fitted board
// sits centred, its right edge still in reach to take hold of and drag on
// out.
const BOARD_PAD_R = 12;
const BOARD_MARGIN = 16;
const PACK_GAP_X = 20;
const PACK_GAP_Y = 18;
// A card the reader resized: sideways it runs at its own scale (`m` times the
// board's, its own ruler under its head saying so); down, its densest groups
// open while they fit. The edges answer this close to the pointer.
const CARD_RULER_H = 16;
const CARD_MIN_BODY = 60;
// A card's own buttons, over its head's right end while it is in hand; the
// anchor's own button stays, pressed, on the card that anchors the board.
// The buttons are one segmented group, as the canvas's own controls are:
// cells divided by a hairline in one bordered frame, tucked into the head's
// top right corner an even 3px from both edges.
const CARD_TOOL = 22;
// Five cells (their hairlines inside them) and the frame's two edges; a
// machine card's three (fit, fold and take off: it keeps to its time and is
// no anchor).
const CARD_TOOLBAR_W = 5 * CARD_TOOL + 2;
// Less of a card than this in view, and it is drawn without its words.
const SLIVER_W = 80;
const GRAPH_TOOLBAR_W = 3 * CARD_TOOL + 2;
const CARD_TOOLBAR_INSET = 3;
const SCALE_SNAP = 0.06;
const SCALE_MIN = 0.2;
const SCALE_MAX = 40;
// A card on the board reads down one edge: its glyph where its rows' chevrons
// sit, its words where their names start.
const CARD_GLYPH_X = 10;
const CARD_TEXT_X = 24;
// The narrowest a head (and its list) gets on the board, when a wider one
// would push its card under its neighbours.
const HEAD_NARROW_W = 220;
// A head is at least this wide, past the body if the session is short.
const HEAD_MIN_W = 300;
// A group's card (a machine's, a search's, a workspace inside, what the chat
// found) is a list: its body, at the fit, at most this wide, its rows on a
// scale of its own past that. Laid out on its stretch of the clock it
// outweighed the traces it lists against. Its width is its own: one that
// followed the traces' widths moved as sessions came onto the board.
const GROUP_BODY_W = 360;
const CELL = 7;
// The clock's margin: a little air before the first moment.
const PAD_L = 140;
// The margin before a board card that starts with the clock: its body at
// PAD_L, less the 3px it opens early, less its rail.
const BOARD_EDGE_L = PAD_L - 3 - BOARD_RAIL;
// A related session not open in the session graph: a placeholder for it until
// it is loaded (a stub), one line high, as wide as its time or, where there is
// room, its words.
const STUB_H = 26;
const STUB_GAP = 4;
const STUB_MIN_W = 280;
const STUB_NARROW_W = 150;
const MAX_HELPERS = 6;
// The chat's investigation on the board: its claims' badges in a row under
// the axis, and a card for each machine whose work the graph holds outside the
// traces on the board, a row per stretch of that work.
const CLAIM_ROW_H = 22;
// What the board lists of its chat's claims, over the board along its foot
// as an overlay (EvidenceStrip): a card a claim, EVIDENCE_CARD_W wide, in a
// strip EVIDENCE_H deep. Pointed at, the rest of the board fades back under a
// veil of the ground this thick (EVIDENCE_VEIL), clear around what it rests
// on. A claim picked there is brought into view in PICK_GLIDE_MS.
const EVIDENCE_H = 116;
const EVIDENCE_CARD_W = 244;
// A claim picked there opens upward over the board in full, this wide.
const EVIDENCE_OPEN_W = 380;
// The cards stand on a line of their own along the strip, in the order they
// happened, drawn on the board itself: a tick at each card's moment with its
// time beside it, and a break where the next came a while later; its words
// on a halo of the ground's colour, as the board's own are.
const EVIDENCE_GAP = 10;
// What the list covers, ahead of its cards.
const EVIDENCE_CAPTION_W = 150;
const EVIDENCE_LINE_H = 18;
const EVIDENCE_WORDS = { fill: "var(--muted)", fontSize: 10, paintOrder: "stroke", stroke: "var(--bg)", strokeWidth: 3, strokeLinejoin: "round" };
// Focused on the evidence, each card holding it is cut to the stretch of
// time it rests on (FOCUS_SPAN_MS at the least) and drawn across the board,
// FOCUS_EDGE in from its sides.
const FOCUS_SPAN_MS = 4 * 60_000;
const FOCUS_EDGE = 24;
const EVIDENCE_VEIL = 0.62;
const PICK_GLIDE_MS = 420;
const NO_RESTS = new Map();
const NO_MOMENTS = Object.freeze([]);
// A card let go this close to its own time goes back to it.
const MOVE_SNAP_PX = 10;
// Where the pointer sits on a card dropped onto the board from outside: this
// far in from its left edge, at the middle of its head.
const DROP_GRAB_X = 24;
const DROP_GRAB_Y = CARD_HEAD / 2;
// How long a drop waits for the card it brings (a machine's sessions are read
// first) before the board stops waiting for it.
const DROP_KEEP_MS = 60_000;
// Where a drop landed, held for what it brings until its card comes: as wide
// as a card's head and as deep as a card still reading its record is.
const LANDING_W = 300;
const LANDING_H = 40 + 2 * COMPACT_H + 2;
// A workspace inside this one, its card made taller than its list of
// sessions: the room in place of the list shows its own board, a still
// picture of it filling that room, once there is NEST_INSET_MIN_H of it (and
// drawn while NEST_INSET_SHOWN of it shows under a head held over it).
// NEST_MAX_H bounds how tall the card can be made.
const NEST_INSET_MIN_H = 120;
const NEST_INSET_SHOWN = 24;
const NEST_MAX_H = 1400;
// A still picture with room to spare gives it to its cards first, as heights
// that open more of their groups, and is then drawn as large as its room's
// height allows, up to STILL_K_MAX times the board's own size; below
// STILL_ROLL_K its cards are told in brief, then by their heads alone, to be
// drawn larger.
const STILL_K_MAX = 1.25;
const STILL_ROLL_K = 0.75;
// A still picture's sizing takes at most this many steps (each a render):
// two rolls, its cards' heights, its size, three narrowings.
const STILL_STEPS = 10;
const STILL_SHOW_MS = 1500;
// The camera: how far the whole board is scaled on screen. Zooming scales it
// about the pointer, everything keeping its place on the board; a card shows
// more or less of itself only as the reader sizes it. The board's own time
// scale changes only along the strip.
const CAM_MIN = 0.08;
const CAM_MAX = 4;
// Zoomed out past HOLD_CAM a card is read by its head: the head stops
// shrinking there and is laid over the top of the card's rows, which go on
// shrinking under it (headHold). Words smaller on screen than TEXT_GONE_PX are
// not drawn, fading out from TEXT_FULL_PX: that small they are specks, not
// words (fadeSmallText). A screen of two pixels to the point draws them in
// twice the pixels, so they read at half the size there.
const HOLD_CAM = 0.8;
const TEXT_FULL_PX = 7;
const TEXT_GONE_PX = 5.5;
const textDensity = () => Math.max(1, Math.min(2, globalThis.devicePixelRatio || 1));
// A head's name line: its middle down the head, and half its height.
const NAME_MID = 14.5;
const NAME_HALF = 7.5;
// A card's buttons, as tall as they are drawn (their border with them).
const CARD_TOOLS_H = 22;
const GRAPH_ROW_H = 22;
// The sessions a workspace's machine (or search) shows as rows on its card.
const MACHINE_ROWS = 12;
// The reader's loose notes: a note this wide, as tall as its words want up to
// a point.
const NOTE_W = 248;
const NOTE_MIN_H = 76;
const noteHeight = (text) => {
  const lines = String(text || "").split("\n").reduce((n, p) => n + Math.max(1, Math.ceil(p.length / 34)), 0);
  return Math.max(NOTE_MIN_H, Math.min(260, 40 + lines * 17));
};
// A search, a machine or a workspace inside waiting for a card of its own.
const THING_STUB_H = 64;
const CLAIM_BADGE_W = 14;
const CLAIM_BADGE_H = 12;
const K_MAX = 600;
const EMPTY = new Set();
const NO_CELLS = new Map();
const NO_WIRES = [];
const NO_PINS = [];
// What a board given no joins or strings holds: one of each for every board,
// so drawing it again changes nothing it reads.
const NO_JOINS = [];
const NO_STRINGS = [];
// What glides from where it was drawn to where it goes as the board changes.
const GLIDES = new Set(["card", "stub", "pin", "landing", "note", "thingStub"]);
// Whether a card placed again stands as it did: the same scene, the same
// window on the clock, and every number about it the same.
function sameCardGeo(a, b) {
  for (const k of Object.keys(b)) {
    if (k === "win") {
      if (a.win.u0 !== b.win.u0 || a.win.u1 !== b.win.u1) return false;
    } else if (a[k] !== b[k]) return false;
  }
  return Object.keys(a).length === Object.keys(b).length;
}

// The messages one read of a session's record holds (conversationRead's
// RECORD_PAGE_MESSAGES; not imported, for what that module brings with it).
const READ_MESSAGES = 1024;
// How far in from the view's top left corner a note just written goes, on
// screen.
const NOTE_IN_VIEW = 24;
// What on the board keeps its place somewhere other than the cards' sizes (a
// note, its own), or has none (what waits for a card of its own).
const OWN_PLACES = /^(note|search|machine|workspace):/;
// A line fainter than this cannot be seen; it is neither drawn nor pointed at.
const VISIBLE_ALPHA = 1 / 64;
// How long a read of how widely files were touched is kept.
const SPREAD_KEEP_MS = 60_000;
// How long a laid-on session's head and record, once read, are taken from
// the cache rather than read again: the workspace reads them ahead of the
// board (prefetchLaidOn), and a board opened again soon after finds them.
const RECORD_KEEP_MS = 60_000;
const CONVERSATION = "domain:conversation";
// Zooming out once the whole clock is in view goes on by rolling the board
// up, never by shrinking its words: first each card to its conversation and
// one row for the rest, what hangs off it counted on its head; then each
// card to its head, its moments along the head's foot. How far past the
// clock each starts, in the zoom's own steps (a press of - is about a third
// of one), and how far it goes.
const ROLL_ROWS = 0.2;
const ROLL_HEADS = 0.55;
const ROLL_MAX = 0.8;
const ROLLED_ROWS = Object.freeze({ openGroups: EMPTY, openDirs: EMPTY, showAll: EMPTY, only: new Set([CONVERSATION]) });
const rollOf = (d) => (d >= ROLL_HEADS ? 2 : d >= ROLL_ROWS ? 1 : 0);
// How long the cards glide from one roll to the next.
const GLIDE_MS = 260;
// Rolled to heads and zoomed out further still, a workspace entered from
// another goes back out to it: how much further, in the zoom's own steps.
const EXIT_AT = 0.5;
// And zooming in that far over a workspace inside goes into it.
const ENTER_AT = 0.45;

const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}\u2026` : s);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
// Two reports of where a still picture drew its cards, the same to a half pixel.
const samePicture = (a, b) => {
  const x = a?.length ? a : null;
  const y = b?.length ? b : null;
  if (!x || !y) return x === y;
  return x.length === y.length && x.every((r, i) => r.key === y[i].key && r.title === y[i].title
    && Math.abs(r.x - y[i].x) < 0.5 && Math.abs(r.y - y[i].y) < 0.5 && Math.abs(r.w - y[i].w) < 0.5 && Math.abs(r.h - y[i].h) < 0.5);
};
// A census sample's moment: epoch ms, or a timestamp string.
const sampleMs = (at) => (Number.isFinite(at) ? at : typeof at === "string" ? Date.parse(at) : NaN);
// A first ask as a title: links read as their words (or their host), bare
// URLs as their host, pasted images as one word. A title of raw URLs was most
// of the canvas's noise.
const hostOf = (url) => { try { return new URL(url).host; } catch { return url; } };
function cleanTitle(text) {
  return String(text || "")
    .replace(/\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g, (m, words, url) => (words && !/^https?:/i.test(words) ? words : hostOf(url)))
    .replace(/https?:\/\/[^\s)\]]+/g, (url) => hostOf(url))
    .replace(/\[Image #\d+\]/gi, "[image]")
    .replace(/\s+/g, " ")
    .trim();
}
const relText = (ms) => {
  const a = Math.abs(ms);
  const s = a < HOUR ? `${Math.round(a / MINUTE)} min` : a < 48 * HOUR ? `${Math.round(a / HOUR)} h` : `${(a / DAY).toFixed(1).replace(/\.0$/, "")} days`;
  return ms < 0 ? `${s} before` : `${s} after`;
};
const WHEN_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const CLOCK_FMT = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
const HHMM_FMT = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
const DAY_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const whenText = (ms) => (Number.isFinite(ms) ? WHEN_FMT.format(ms) : "");
const clockText = (ms) => CLOCK_FMT.format(ms);
const hhmm = (ms) => HHMM_FMT.format(ms);
const dayText = (ms) => DAY_FMT.format(ms);
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const TICK_STEPS = [MINUTE, 2 * MINUTE, 5 * MINUTE, 10 * MINUTE, 15 * MINUTE, 30 * MINUTE, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY];

function firstAsk(turns) {
  for (const t of turns || []) {
    if (t?.role !== "user") continue;
    const text = (t.blocks || []).find((b) => b?.type === "text" && typeof b.text === "string" && b.text.trim())?.text;
    if (!text) continue;
    const s = text.replace(/\s+/g, " ").trim();
    if (/^\[[^\]]*(NOTIFICATION|NOT USER INPUT|AUTOMATED)/i.test(s) || s.startsWith("<")) continue;
    return clip(s, 140);
  }
  return null;
}

// The canvas a surface draws, for probes and tests: its draw state and view,
// and the controls a reader has (the plane's planeScene, again).
const WORKBENCHES = new WeakMap();
export function workbenchOf(el) {
  return WORKBENCHES.get(el) || null;
}

// Whether a key is the board's (`el`, its surface): with a dialog over the page
// only when the board is in the top one (the Lanes reading's board, under
// the workspace it opened, leaves the workspace its keys); on the page itself
// every key but Esc, which is the board's only while it has focus, the page
// keeping its own.
function boardKeys(el, e) {
  if (!el || typeof document === "undefined") return false;
  const modals = document.querySelectorAll('[aria-modal="true"]');
  const top = modals.length ? modals[modals.length - 1] : null;
  if (top) return top.contains(el);
  // Boards side by side (the workspace home's panes): the one worked in.
  const pane = el.closest?.("[data-deck-pane]");
  if (pane && !pane.hasAttribute("data-deck-active")) return false;
  // Under the one a pane shows, the boards it went in through keep none.
  const level = el.closest?.("[data-deck-level]");
  if (level && !level.hasAttribute("data-deck-board")) return false;
  // Nor does a workspace drawn inside another's card.
  if (el.closest?.("[data-workspace-inset]")) return false;
  return e.key !== "Escape" || el.contains(document.activeElement);
}

// A press on the board lets go of whatever text is selected on the page, as
// a press anywhere on a page does (the board's own presses take the pointer,
// and so would leave it selected). Inside a field, or in what reads as text
// (a cell's detail, a finding opened out), the press is that text's own.
function letGoOfSelection(e) {
  if (e.target?.closest?.("input, textarea, select, [contenteditable='true'], [data-board-readable]")) return;
  const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
  if (sel && sel.rangeCount && !sel.isCollapsed) sel.removeAllRanges();
}

// The record reader, swappable by the synthetic harness (no backend there).
let sessionReader = null;
export function __setSessionReader(fn) { sessionReader = fn || null; }
// Whether a change in where cards sit glides into place; tests that read
// where cards are drawn the moment their data lands, or the moment the
// reader moves them, turn it off.
let dataGlides = true;
export function __setDataGlides(on) { dataGlides = !!on; }
// Where a board last drew each thing on it, on screen (keyed by session for a
// session's card), under its workspace's id: a board that takes its place
// (the workspace's own, once its anchor's record is in, after the board it
// drew meanwhile) glides each card from there to where it goes.
const HANDOFFS = new Map();
const HANDOFF_MS = 4000;

// Where a card put down with its top at `top` lands, given where the rest
// stand (`feet`: key -> { x0, x1, y0, y1 }, as drawn) and its own stretch
// along the board (`x0` to `x1`): right there, where nothing lies under its
// top edge; let go over a card, before that card or after it, whichever of
// its edges is nearer, the card it then covers going on down under it.
export function landTop(top, x0, x1, feet, self = null) {
  let under = null;
  let most = 0;
  for (const [key, f] of feet) {
    if (key === self) continue;
    const across = Math.min(x1, f.x1) - Math.max(x0, f.x0);
    if (across <= 0 || top < f.y0 || top >= f.y1) continue;
    if (across > most) { most = across; under = f; }
  }
  if (!under) return Math.round(top);
  return top < (under.y0 + under.y1) / 2 ? Math.max(0, Math.round(under.y0) - 1) : Math.round(under.y1 + PACK_GAP_Y);
}

// Where each card and block stands as drawn at this zoom (its column across,
// its place and depth down the board, what hangs from it included), but
// those in `skip`: what a card put down lands among.
function feetAsDrawn(items, skip) {
  const out = new Map();
  for (const it of items) {
    if (!it.foot || skip.has(it.key)) continue;
    const hx = it.headX ?? it.x;
    out.set(it.key, { x0: Math.min(it.x, hx), x1: Math.max(it.x + it.w, hx + (it.headW || 0)), y0: it.foot.y0, y1: it.foot.y1 });
  }
  return out;
}

// How far up the card laid out as `it` may grow when sized from its top: to
// just under whatever is over it along the board (where the board packs it),
// which holds still; null with nothing there.
export function roomOver(items, it) {
  const f = it?.foot;
  if (!f) return null;
  let ceil = -Infinity;
  for (const x of items) {
    const g = x.foot;
    if (!g || x.key === it.key) continue;
    if (g.x0 < f.x1 + PACK_GAP_X && f.x0 < g.x1 + PACK_GAP_X && g.y1 <= f.y0 + 1) ceil = Math.max(ceil, g.y1 + PACK_GAP_Y);
  }
  return Number.isFinite(ceil) ? ceil : null;
}

// Where something dropped on the board from outside lands: its left edge
// just left of the pointer, and down the board as a card carried there would
// land among the rest as they are drawn (`skip`: what it brings, if already
// on the board).
function dropLanding(items, skip, bx, by) {
  const x = bx - DROP_GRAB_X;
  return { x, top: landTop(by - DROP_GRAB_Y, x, x + HEAD_NARROW_W, feetAsDrawn(items, skip)) };
}

// A session on the workspace not on the board yet: its title, and that its
// span is still being read (or could not be), standing where it will glide
// from once it takes its place (`onPlace`, its element).
function WaitingCard({ c, onPlace }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) onPlace(c.key, ref.current);
  });
  const failed = c.status === "error";
  return (
    <div ref={ref} className="min-w-0 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[10px] py-[8px]" data-board-waiting-card={c.key}>
      <div className="truncate text-[12.5px] text-[color:var(--ink)]">{c.titleText}</div>
      <div className="mt-[2px] truncate text-[11px] text-[color:var(--muted)]">{failed ? `Its record could not be read${c.error ? ` (${c.error})` : ""}` : "Reading when and where it ran"}</div>
      {failed ? null : (
        <div className="mt-[8px] grid gap-[6px]" aria-hidden="true">
          {[64, 42].map((w, i) => (
            <span key={i} className="h-[8px] rounded-[2px] bg-[color:color-mix(in_srgb,var(--muted)_35%,transparent)] motion-safe:animate-pulse" style={{ width: `${w}%`, animationDelay: `${i * 160}ms` }} />
          ))}
        </div>
      )}
    </div>
  );
}

// The whole board small, in the canvas's corner, drawn as the board is: its
// cards on the ground, the one in hand edged firmer, and what lies outside the
// view faded toward the ground, so the part in view stands out with a border a
// shade up. A press there, or a drag, brings that part of the board into view.
// `extent` is the board's (and the view's) span; `items` what is on it. It
// keeps out of the way at a fraction of its size until the pointer is over it.
const MINI_W = 176;
const MINI_H = 116;
const MINI_REST = 0.5;
const BoardMinimap = memo(function BoardMinimap({ items, extent: given, view, viewW, viewH, focusKey = null, right = 10, onMove }) {
  // Held as the drag began until it ends: the map spans the view too, so a
  // view taken past the board's edge grew it, rescaling it under the
  // pointer, and the view ran on away from it.
  const [held, setHeld] = useState(null);
  const [over, setOver] = useState(false);
  const extent = held || given;
  const w = Math.max(1, extent.x1 - extent.x0);
  const h = Math.max(1, extent.y1 - extent.y0);
  const k = Math.min(MINI_W / w, MINI_H / h) * (over || held ? 1 : MINI_REST);
  const go = (e, at = extent) => {
    const r = e.currentTarget.getBoundingClientRect();
    const aw = Math.max(1, at.x1 - at.x0);
    // At the size it is drawn at, growing or grown.
    const kk = r.width > 0 ? r.width / aw : Math.min(MINI_W / aw, MINI_H / Math.max(1, at.y1 - at.y0));
    const bx = at.x0 + (e.clientX - r.left) / kk;
    const by = at.y0 + (e.clientY - r.top) / kk;
    onMove({ x: bx - viewW / 2, y: by - viewH / 2 });
  };
  // One screen pixel, in the board's units.
  const px = 1 / k;
  const outside = `M${extent.x0} ${extent.y0}h${w}v${h}h${-w}Z M${view.x} ${view.y}v${viewH}h${viewW}v${-viewH}Z`;
  return (
    <div
      className="pointer-events-auto absolute bottom-[10px] z-[8] overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] shadow-[var(--node-shadow)]"
      style={{ right }}
      onPointerEnter={() => setOver(true)}
      onPointerLeave={() => setOver(false)}
      data-board-minimap=""
      data-board-minimap-open={over || held ? "" : undefined}
    >
      <svg
        style={{ width: w * k, height: h * k }}
        viewBox={`${extent.x0} ${extent.y0} ${w} ${h}`}
        className="block cursor-pointer touch-none motion-safe:transition-[width,height] motion-safe:duration-150 motion-safe:ease-out"
        onPointerDown={(e) => { e.stopPropagation(); e.currentTarget.setPointerCapture?.(e.pointerId); setHeld(given); go(e, given); }}
        onPointerMove={(e) => { if (e.buttons & 1) go(e); }}
        onPointerUp={() => setHeld(null)}
        onPointerCancel={() => setHeld(null)}
        onLostPointerCapture={() => setHeld(null)}
        aria-label="The whole board: press to bring a part of it into view"
        role="img"
      >
        {items.map((it) => (
          <rect
            key={it.key}
            x={it.x}
            y={it.y}
            width={Math.max(px, it.w)}
            height={Math.max(px, it.h)}
            rx={1.5 * px}
            fill="var(--panel)"
            stroke={it.key === focusKey ? "color-mix(in srgb, var(--ink) 70%, transparent)" : "var(--line-strong)"}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path d={outside} fillRule="evenodd" fill="color-mix(in srgb, var(--bg) 55%, transparent)" />
        <rect x={view.x} y={view.y} width={viewW} height={viewH} rx={1.5 * px} fill="none" stroke="color-mix(in srgb, var(--muted) 55%, transparent)" strokeWidth={1} vectorEffect="non-scaling-stroke" data-board-minimap-view="" />
      </svg>
    </div>
  );
});

// The card whose middle is nearest `mid` along the board, of those not in
// `skip`: the one the view holds still as the clock changes under it.
function holdCard(items, mid, skip) {
  let best = null;
  for (const it of items) {
    if (it.kind !== "card" || skip.has(it.key)) continue;
    const d = Math.abs(it.x + it.w / 2 - mid);
    if (!best || d < best.d) best = { key: it.key, x: it.x, d };
  }
  return best;
}

// Rolled up, the board shows its cards closed up in the order they were put
// in. One put down among them goes in after the one whose middle is over
// where it was let go (`mid`): a top on the unrolled board just under that
// one's (`topOf`), 0 when it goes first, null when that one has none.
export function orderTopAt(feet, mid, self, topOf) {
  let prev = null;
  let prevMid = -Infinity;
  for (const [key, f] of feet) {
    if (key === self) continue;
    const m = (f.y0 + f.y1) / 2;
    if (m < mid && m > prevMid) { prev = key; prevMid = m; }
  }
  if (prev == null) return 0;
  const t = topOf(prev);
  return Number.isFinite(t) ? t + 1 : null;
}

// A card's saved size from what the reader left it with: only what differs
// from its own (its scale and time, its height, folded, a block's offset
// along its band, its place down the board); null when nothing does. Left
// out, `fold` and `top` stay as they were (`prev`).
// `free`: the place is where the reader let it go, over whatever is there;
// without it, a place the board keeps for a card (the board's own packing
// still moves it down from under one grown over it). `z`: where it stands
// among the cards laid over each other, the last carried on top.
function sizeEntry(prev, { m = null, pin = null, off = null, h = null, fold = undefined, dx = null, top = undefined, free = undefined, z = undefined } = {}) {
  const keepM = Number.isFinite(m) && Math.abs(m - 1) > 0.01;
  const keepH = Number.isFinite(h);
  const keepOff = Number.isFinite(off) && off !== 0;
  const keepFold = fold === undefined ? !!prev?.fold : fold === true;
  const keepDx = Number.isFinite(dx) && Math.round(dx) !== 0;
  const topV = top === undefined ? prev?.top : top;
  const keepTop = Number.isFinite(topV);
  const keepFree = keepTop && (free === undefined ? prev?.free === true : free === true);
  const zV = z === undefined ? prev?.z : z;
  const keepZ = keepTop && Number.isInteger(zV) && zV > 0;
  if (!(keepM || keepH || keepOff || keepFold || keepDx || keepTop)) return null;
  return {
    ...(keepM ? { m } : {}), ...(keepM && pin === "end" ? { pin } : {}), ...(keepOff ? { off } : {}), ...(keepH ? { h: Math.round(h) } : {}),
    ...(keepFold ? { fold: true } : {}), ...(keepDx ? { dx: Math.round(dx) } : {}), ...(keepTop ? { top: Math.max(0, Math.round(topV)) } : {}),
    ...(keepFree ? { free: true } : {}), ...(keepZ ? { z: zV } : {}),
  };
}

// How widely each of `keys` (files, by folder and name) was touched across
// the workspace's range by the sessions `endpoints` (machines) ran: { total,
// df: Map }, those sessions and how many of them touched each. A file asked
// about once keeps its count; only files not yet read are asked about.
// Asked again for other machines (a card laid on from a machine the board
// had none from), the counts it had stand until the new ones come: dropping
// them meanwhile took every file's wire off the board and folded the rows
// they had opened, only to put them back a moment later.
function useFileSpread(keys, range, endpoints) {
  const rangeOnly = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const rangeKey = `${rangeOnly}|${endpoints.join(",")}`;
  const [spread, setSpread] = useState(() => ({ rangeKey: null, range: null, total: 0, df: new Map() }));
  const known = spread.rangeKey === rangeKey ? spread.df : null;
  const missing = keys.filter((k) => !known?.has(k));
  const missingKey = missing.join("\n");
  useEffect(() => {
    if (!missing.length || range == null) return undefined;
    let live = true;
    query("getFileSpread", { range, keys: missing, endpoints }, { cache: true, staleMs: SPREAD_KEEP_MS }).then((r) => {
      if (!live || !r) return;
      setSpread((cur) => {
        const same = cur.rangeKey === rangeKey;
        const df = new Map(same ? cur.df : []);
        for (const k of missing) df.set(k, Number(r.df?.[k]) || 0);
        return { rangeKey, range: rangeOnly, total: Math.max(same ? cur.total : 0, Number(r.total) || 0), df };
      });
    }, () => {});
    return () => { live = false; };
  }, [missingKey, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return known || spread.range === rangeOnly ? spread : null;
}

// What the sessions laid on are (a summary, a model, a machine) and when each
// ran, read for all of them at once from the workspace's range: their cards
// take their places on the clock before any record arrives, and no record is
// read to learn it. A session the range does not hold (`of` gives null) is
// learned from its record as before. `ready(id)` says the read has answered
// for it; off (no range, or the harness's own reader), every id is ready.
function useLaidOnHeads(ids, range) {
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const off = range == null || !!sessionReader;
  const [heads, setHeads] = useState(() => ({ rangeKey: null, known: new Map() }));
  const known = heads.rangeKey === rangeKey ? heads.known : null;
  const missing = off ? [] : ids.filter((id) => !known?.has(id));
  const missingKey = missing.join(",");
  useEffect(() => {
    if (!missing.length) return undefined;
    let live = true;
    const settle = (got) => {
      if (!live) return;
      setHeads((cur) => {
        const next = new Map(cur.rangeKey === rangeKey ? cur.known : []);
        for (const id of missing) {
          const h = got?.[id];
          next.set(id, h && Number.isFinite(h.firstMs) ? h : null);
        }
        return { rangeKey, known: next };
      });
    };
    query("getSessionHeads", { ids: missing, range, whole: true }, { cache: true, staleMs: RECORD_KEEP_MS }).then(settle, () => settle(null));
    return () => { live = false; };
  }, [missingKey, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => ({ of: (id) => known?.get(id) ?? null, ready: (id) => off || !!known?.has(id) }), [off, known]);
}

// The board's first reads of the sessions laid on (their heads, then their
// records, two at a time as the board reads them), made ahead of it by
// whoever knows of them first: the workspace, while its anchor's record is
// still being read. They are the board's own reads, so it finds them in the
// cache when it draws.
export function prefetchLaidOn(ids, range, { signal = null } = {}) {
  if (!ids?.length || range == null || sessionReader) return;
  (async () => {
    const heads = await query("getSessionHeads", { ids, range, whole: true }, { cache: true, staleMs: RECORD_KEEP_MS }).catch(() => null);
    const queue = ids.map((id) => [id, heads?.[id]]).filter(([, h]) => Number.isFinite(h?.firstMs));
    const next = async () => {
      while (queue.length && !signal?.aborted) {
        const [id, h] = queue.shift();
        await query("getSessionPage", { id, sinceMs: h.firstMs - HOUR }, { cache: true, staleMs: RECORD_KEEP_MS }).catch(() => null);
      }
    };
    await Promise.all([next(), next()]);
  })();
}

// How specific a string is to the sessions that share it, from how many of
// the range's `total` sessions carry it (`df`): 1 when only they do, falling
// to 0 as it reaches every session (its information, over the most a string
// could carry).
export function specificity(df, total) {
  if (!(total > 1) || !(df > 0)) return 0;
  return Math.max(0, Math.min(1, Math.log(total / Math.min(df, total)) / Math.log(total)));
}

// Where a session's record holds the strings it shares (`ids`, indexes into
// `needles`): work cells first (the file or call that held the string), then
// the conversation row it was said in, since both are the cell that carries it.
function locateShared(turns, model, ids, needles, turnsToJoinInputs) {
  const strings = ids.map((i) => needles[i]?.needle).filter(Boolean);
  const hits = locateNeedleStrings(turnsToJoinInputs(turns, model), strings);
  const byString = new Map(hits.map((h) => [h.needle, h]));
  const located = new Map();
  for (const i of ids) {
    const h = byString.get(needles[i]?.needle);
    if (h) {
      const known = h.cells.filter((c) => model.laneById.has(c.lane));
      const work = known.filter((c) => !isConversationDomain(model.laneById.get(c.lane).domain));
      located.set(i, { cells: [...work, ...known.filter((c) => !work.includes(c))], turns: h.turns });
    }
  }
  return located;
}

// The judges' reading of a card's session, where a run of one has landed in
// this tab (its lanes view, the workspace home's Reading, a dock reading one
// of its cells), for the card to draw what it read as its lanes view does.
// Finding one means drafting the card's calls for the reading's key, so only
// cards whose session has a reading are drafted, once per record, when the
// board is idle. -> Map(session -> { turns, report })
const sameReadings = (a, b) => a.size === b.size && [...b].every(([id, v]) => a.get(id)?.report === v.report && a.get(id)?.turns === v.turns);
function useCardReadings(reads) {
  const [found, setFound] = useState(() => new Map());
  const [landed, setLanded] = useState(0);
  const drafts = useRef(new Map());
  const judge = useRef(null);
  useEffect(() => subscribeReadingSessions(() => setLanded((n) => n + 1)), []);
  useEffect(() => {
    const todo = [...reads].filter(([id, r]) => r?.status === "done" && r.turns?.length && sessionHasReading(id));
    if (!todo.length) {
      setFound((cur) => (cur.size ? new Map() : cur));
      return undefined;
    }
    let live = true;
    const look = async () => {
      if (!judge.current) {
        try { judge.current = defaultReviewModel(await getChatCreds()) || null; } catch { judge.current = null; }
      }
      if (!live || !judge.current) return;
      const next = new Map();
      for (const [id, r] of todo) {
        let d = drafts.current.get(id);
        if (d?.turns !== r.turns) {
          d = { turns: r.turns, claimsHash: readingClaimsHash(draftReadingCalls(r.turns)) };
          drafts.current.set(id, d);
        }
        const report = d.claimsHash ? readReadingCache(readingCacheKey({ trace: { sessionId: id }, model: judge.current, claimsHash: d.claimsHash })) : null;
        if (report) next.set(id, { turns: r.turns, report });
      }
      if (live) setFound((cur) => (sameReadings(cur, next) ? cur : next));
    };
    const idle = typeof requestIdleCallback === "function";
    const handle = idle ? requestIdleCallback(() => { look(); }, { timeout: 1000 }) : setTimeout(() => { look(); }, 0);
    return () => {
      live = false;
      if (idle) cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [reads, landed]);
  return found;
}

// A related session's record, read the way the Lanes view reads the open trace
// and a spliced helper: code-mode cells unwrapped (after the grammar loads),
// shell calls read for what they consulted, then the strings it shares with the
// open trace located on its own cells. With `refind`, a change in the strings
// sought (a search kept on a workspace with no anchor) looks through every
// record again.
function useRelatedReads(related, wanted, needles, turnsToJoinInputs, { refind = false } = {}) {
  const [reads, setReads] = useState(() => new Map());
  const started = useRef(new Set());
  const sought = refind ? needles.map((n) => n?.needle || "").join("\u0001") : "";
  const soughtWas = useRef(sought);
  if (soughtWas.current !== sought) {
    soughtWas.current = sought;
    started.current = new Set();
  }
  const key = `${wanted.join(",")}|${sought}`;
  useEffect(() => {
    let cancelled = false;
    const queue = wanted.filter((id) => !started.current.has(id));
    // Reads this pass started and has not finished. If the pass is cancelled
    // (the list of sessions to read changed under it), they are released, so
    // the next pass reads them again rather than leaving them "loading".
    const inFlight = new Set();
    const run = async (id) => {
      started.current.add(id);
      inFlight.add(id);
      const r = related.find((x) => x.id === id);
      if (!r) { inFlight.delete(id); started.current.delete(id); return; }
      setReads((m) => new Map(m).set(id, { status: "loading" }));
      try {
        let sinceMs = Number.isFinite(r.firstMs) ? r.firstMs - HOUR : null;
        // A trace laid on by hand has no census row to date it: its head
        // (read with the others') or else its own summary says when it began
        // (an unbounded record read defaults to ninety days and can take
        // minutes), where it ran, and what it was.
        let meta = r.head ? { endpointId: r.head.endpointId || null, summary: r.head.summary || null, model: r.head.model || null, provider: r.head.provider || null } : null;
        if (!sessionReader && !meta && (r.added || sinceMs == null)) {
          try {
            const t = await query("getTrace", { id }, { cache: true });
            if (t) {
              meta = { endpointId: t.endpointId || null, summary: t.summary || null, model: t.model || null, provider: t.provider || null };
              const start = Number.isFinite(t.firstTimestamp) ? t.firstTimestamp : t.timestamp;
              if (Number.isFinite(start)) sinceMs = start - HOUR;
            }
          } catch { meta = null; }
        }
        const page = sessionReader ? await sessionReader(id, r) : await query("getSessionPage", { id, sinceMs }, { cache: true, staleMs: RECORD_KEEP_MS });
        const raw = Array.isArray(page?.turns) ? page.turns : [];
        if (!raw.length) throw new Error("empty record");
        const record = {
          session: true, loaded: raw.length, total: page?.total ?? null, exhausted: page?.exhausted !== false,
          nextCursor: page?.nextCursor ?? null, pages: 1, sinceMs,
        };
        meta = { ...meta, clientAttributions: page.clientAttributions ?? null, clientAttributionsTruncated: Boolean(page.clientAttributionsTruncated) };
        const read = await readOfRecord({ r, raw, record, meta, needles, turnsToJoinInputs });
        if (!cancelled) setReads((m) => new Map(m).set(id, read));
      } catch (e) {
        if (!cancelled) setReads((m) => new Map(m).set(id, { status: "error", error: String(e?.message || e) }));
      } finally {
        if (!cancelled) inFlight.delete(id);
      }
    };
    (async () => {
      for (let i = 0; i < queue.length; i += 2) {
        if (cancelled) return;
        await Promise.all(queue.slice(i, i + 2).map(run));
      }
    })();
    return () => {
      cancelled = true;
      for (const id of inFlight) started.current.delete(id);
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // The page before the oldest turn a card holds, read on asking, as a trace
  // page's "earlier" reads it; what was read stands if it cannot be, and its
  // account says so.
  const readsRef = useRef(reads);
  readsRef.current = reads;
  const asked = useRef({ related, needles, turnsToJoinInputs });
  asked.current = { related, needles, turnsToJoinInputs };
  const earlier = useCallback(async (id) => {
    const cur = readsRef.current.get(id);
    if (cur?.status !== "done" || cur.record?.loading) return;
    const request = sessionPageRequest({ sessionId: id, turns: cur.raw, record: cur.record, firstTimestamp: cur.record?.sinceMs ?? null });
    if (!request?.cursor) return;
    const { related: rel, needles: ns, turnsToJoinInputs: tj } = asked.current;
    const r = rel.find((x) => x.id === id) || { id, needles: [], firstMs: null, lastMs: null };
    setReads((m) => new Map(m).set(id, { ...cur, record: { ...cur.record, loading: true } }));
    // The record read again meanwhile (its strings sought anew) is newer
    // than this page's.
    const mine = (m) => m.get(id)?.raw === cur.raw;
    try {
      const page = sessionReader ? await sessionReader(id, r, request) : await query("getSessionPage", {
        id: request.sessionId, cursor: request.cursor, exclude: request.exclude, sinceMs: request.sinceMs, untilMs: request.untilMs, focalEventId: request.focalEventId,
      });
      const next = applySessionPage({ data: { turns: cur.raw, record: cur.record, clientAttributions: cur.meta?.clientAttributions, clientAttributionsTruncated: cur.meta?.clientAttributionsTruncated } }, page, request).data;
      const meta = { ...cur.meta, clientAttributions: next.clientAttributions, clientAttributionsTruncated: next.clientAttributionsTruncated };
      const read = await readOfRecord({ r, raw: next.turns, record: { ...next.record, sinceMs: cur.record.sinceMs }, meta, needles: ns, turnsToJoinInputs: tj });
      setReads((m) => (mine(m) ? new Map(m).set(id, read) : m));
    } catch (e) {
      setReads((m) => (mine(m) ? new Map(m).set(id, { ...cur, record: { ...cur.record, loading: false, rejected: true, reason: String(e?.message || e) } }) : m));
    }
  }, []);
  return [reads, earlier];
}

// A related session's record as the board reads it, from its raw turns and
// its account of the record (`record`: what of it is held, the cursor to the
// page before, the window it is read from): code-mode cells unwrapped (after
// the grammar loads), shell calls read for what they consulted, its lanes,
// its clock, what ran it, and the strings it shares located on its cells.
async function readOfRecord({ r, raw, record, meta, needles, turnsToJoinInputs }) {
  const grammar = await warmGrammar("javascript").catch(() => false);
  const turns = unwrapCodeModeTurns(raw);
  let extraItems = [];
  try { extraItems = (await readShellWork(turns))?.items || []; } catch { extraItems = []; }
  const model = buildReadLanes(turns, { shellItems: extraItems });
  const codeMode = raw !== turns || !codeModeReadable(raw) ? { unwrapped: codeModeReadable(raw) && grammar !== false } : null;
  const times = turnTimes(turns, { from: r.firstMs, to: r.lastMs });
  const byOrder = stampedShare(turns) < 0.5;
  let tool = "";
  try { tool = deriveTraceTooling(toolingInputs(meta, { turns }))?.label || ""; } catch { tool = ""; }
  const located = locateShared(turns, model, r.needles || [], needles, turnsToJoinInputs);
  const base = recordTurnBase({ session: true, loaded: record.loaded, total: record.total });
  return {
    status: "done", raw, record, turns, model, times, byOrder, located, title: firstAsk(turns), meta, tool, truncated: !record.exhausted,
    codeMode, shellReads: extraItems.length, shellItems: extraItems, base,
  };
}

// Where the pointer is on the page while a tip shows: the tip follows it on
// its own (BoardTip), so a move over the same thing renders the tip alone,
// not the board.
function createPointStore() {
  let at = null;
  const subs = new Set();
  return {
    get: () => at,
    set: (x, y) => {
      if (at && at.x === x && at.y === y) return;
      at = { x, y };
      for (const fn of subs) fn();
    },
    subscribe: (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}
// What a tip says depends on, the same for every move over one thing
// (tipBody reads these of a hit).
const tipKeyOf = (h) => (h
  ? [h.type, h.key, h.laneId, (h.laneIds || []).join(","), h.turn, h.label, h.id, h.count, h.title, h.rowKind, h.rowId, h.row?.key, h.mark, h.index, h.closed, h.close].join("|")
  : "");
// A card's whole footprint on the board (its head may start before its body
// and run wider), in the board's units: what it covers of what lies under it.
const cardRect = (c) => {
  const hx = c.headX ?? c.x;
  const hw = c.headW ?? Math.max(c.w, HEAD_MIN_W);
  return { x0: Math.min(c.x, hx), x1: Math.max(c.x + c.w, hx + hw), y0: c.y, y1: c.y + c.h };
};
// The cards in the order they stand over each other, the last on top: as the
// reader stacked them (zOf), the one in front (rested on, or carried) over
// them all, and the one being sized over that.
export function stackOrder(cards, { zOf = null, front = null, sizing = null } = {}) {
  const list = [...cards].map((c, i) => [c, i]).sort((a, b) => ((zOf?.get(a[0].key) || 0) - (zOf?.get(b[0].key) || 0)) || a[1] - b[1]).map(([c]) => c);
  for (const key of [front, sizing]) {
    const at = key ? list.findIndex((c) => c.key === key) : -1;
    if (at >= 0) list.push(...list.splice(at, 1));
  }
  return list;
}
// For each card (in stacking order), the footprints of the cards over it.
export function coversOf(order) {
  const out = new Map();
  order.forEach((c, i) => out.set(c.key, order.slice(i + 1).map(cardRect)));
  return out;
}
const inRects = (rects, x, y) => !!rects?.some((r) => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1);
// What hangs from a card shows only outside the cards over it: clipped to
// everything but their footprints (board units).
const clipOutside = (rects) => `path(evenodd, "M -100000 -100000 H 100000 V 100000 H -100000 Z ${rects.map((r) => `M ${r.x0} ${r.y0} H ${r.x1} V ${r.y1} H ${r.x0} Z`).join(" ")}")`;

// The attachments on the board as each card's own group (`elements` and
// `nodes` side by side), the groups in the cards' stacking order (`covers`,
// in that order, with the footprints of the cards over each): a card fading
// back takes what hangs from it with it, and a card over another hides what
// hangs from that one where it lies.
export function chipsByCard(elements, nodes, { covers = null, front = null } = {}) {
  const groups = new Map();
  elements.forEach((el, i) => {
    if (!el) return;
    const { key, free } = nodes[i];
    // A block carried out of its card's band keeps its own place down the
    // board, going with its card only along it: a group of its own.
    const id = free ? `free:${key}` : key;
    if (!groups.has(id)) groups.set(id, { key, free: !!free, list: [] });
    groups.get(id).list.push(el);
  });
  // The card in front over the rest, in `covers` or not (the card in hand,
  // drawn where the pointer has it, is not).
  const ranks = new Map([...(covers?.keys() || [])].map((key, i) => [key, i]));
  const rank = (key) => ranks.get(key) ?? (key === front ? Infinity : -1);
  return [...groups.values()].map((grp, i) => [grp, i]).sort((a, b) => rank(a[0].key) - rank(b[0].key) || a[1] - b[1]).map(([{ key, free, list }]) => {
    const faded = !!front && key !== front;
    const over = covers?.get(key);
    const style = faded || over?.length ? { ...(faded ? { opacity: 1 - CARD_DIM_VEIL } : {}), ...(over?.length ? { clipPath: clipOutside(over) } : {}) } : undefined;
    return (
      <div key={`chips:${free ? "free:" : ""}${key}`} className="absolute left-0 top-0 transition-opacity duration-150" style={style} data-chips-of={key} data-chips-free={free ? "" : undefined} data-faded={faded ? "" : undefined}>
        {list}
      </div>
    );
  });
}

// What is about a card (`nd`, one of its attachments) as the page is told of
// it, for a chat to open or close by the board's own id: its kind, its card's
// session, the turn as the board prints it, its lane, a short label (a
// finding's severity and title, a claim's number and statement, a helper's
// name, a note's words), and whether its card is open (`open`).
export function boardThingOf(nd, card, session, open = false) {
  const at = nd.anchors?.[0];
  const turn = Number.isInteger(nd.turn) ? nd.turn : at?.turn;
  const lane = nd.laneLabel || (at?.lane ? card?.model?.lanes?.find((l) => l.id === at.lane)?.label || at.lane : null);
  const label = nd.kind === "finding" ? `${nd.severity ? `${nd.severity}: ` : ""}${nd.title || ""}`
    : nd.kind === "claim" ? `${nd.n ? `claim ${nd.n}: ` : ""}${nd.statement || ""}`
      : nd.kind === "agent" ? nd.label || "agent"
        : nd.text || "";
  return {
    id: nd.id, kind: nd.kind, session: String(session || "").toLowerCase(),
    turn: Number.isInteger(turn) ? turn + (card?.turnBase || 0) : null, lane: lane || null,
    label: label.replace(/\s+/g, " ").trim().slice(0, 160), open: !!open,
  };
}

// How the board is laid out and how much of it shows, one cell of its bar:
// Tidy, By time and Everything in a menu over it, as the workspace's own
// menu at the bar's other end opens.
const VIEW_ITEMS = [
  { id: "tidy", word: "Tidy", hint: "Lay the cards out so none overlap" },
  { id: "by-time", word: "By time", hint: "One to a row, in the order they began" },
  { id: "everything", word: "Everything", hint: "Show the whole board", key: "F" },
];
function BoardViewMenu({ cell, onTidy, onByTime, onEverything }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
    };
    window.addEventListener("pointerdown", away, true);
    window.addEventListener("keydown", esc, true);
    return () => {
      window.removeEventListener("pointerdown", away, true);
      window.removeEventListener("keydown", esc, true);
    };
  }, [open]);
  const run = { tidy: onTidy, "by-time": onByTime, everything: onEverything };
  return (
    <div ref={ref} className="relative flex items-stretch" data-board-view-menu="">
      <button type="button" className={cell} onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} data-board-view="">
        View
        <svg viewBox="0 0 10 10" className="h-[8px] w-[8px] flex-none" aria-hidden="true"><path d="M2 6.5 5 3.5 8 6.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open ? (
        <div role="menu" className="absolute bottom-[calc(100%+6px)] right-0 z-[20] grid w-[240px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] py-[4px] shadow-[var(--node-shadow-lift)]">
          {VIEW_ITEMS.map((it) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                run[it.id]?.();
              }}
              className="flex w-full items-start justify-between gap-[14px] px-[10px] py-[5px] text-left hover:bg-[color:var(--gray-fill)]"
              data-board-view-item={it.id}
            >
              <span className="grid min-w-0 gap-[1px]">
                <span className="text-[12px] leading-[16px] text-[color:var(--ink)]">{it.word}</span>
                <span className="text-[11px] leading-[14px] text-[color:var(--muted)]">{it.hint}</span>
              </span>
              {it.key ? <span className="flex-none text-[11px] leading-[16px] text-[color:var(--muted-soft)]">{it.key}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// The board's band cells, as the dock's tabs are drawn: words muted until
// pointed at, a hairline between, the one in effect lit.
const BAR_CELL = "flex items-center gap-[6px] whitespace-nowrap border-l border-[color:var(--line-soft)] px-[9px] leading-[16px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
const BAR_CELL_ON = "[background-image:linear-gradient(var(--green-soft),var(--green-soft))] text-[color:var(--ink)]";
const BAR_ICON_CELL = "flex w-[26px] items-center justify-center border-l border-[color:var(--line-soft)] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
// The walkthrough's moments and the chat's claims, each listed over the board
// by its own switch in the bar.
const BAR_SWITCHES = [
  { as: "moments", word: "Walkthrough", on: "List the walkthrough's moments over the board, with what the chat claimed and the case's notes each one covers, the board focused on them", off: "Put away the walkthrough's moments, the board back as you left it" },
  { as: "claims", word: "Evidence", on: "List what the chat claimed over the board, the board focused on what it rests on", off: "Put away what the chat claimed, the board back as you left it" },
];
// How far the board's camera is in, for the bar's zoom cells alone.
const BoardZoomPct = createContext(100);
// What the bar's cells do: BoardControls' `acts`, by name.
const BAR_ACTS = ["putBack", "clearLit", "follow", "walk", "toggleList", "clear", "note", "add", "tidy", "byTime", "everything", "layIt", "zoomOut", "zoomReset", "zoomIn", "close"];

// The board's chrome, laid against its frame's foot as the dock's tabs are
// against its head: what the board is doing on the left, what can be done with
// it on the right, a cell each. As tall as the chat's composer band, so beside
// it the two feet rule off at one line. Drawn again only when what it shows
// changes: the board renders for each move of its pointer and each frame of a
// pan or zoom, and the bar's cells and their tips were most of what it made
// anew each time. `acts` (BAR_ACTS) act on the board as it stands at the press.
const BoardControls = memo(function BoardControls({ lead = null, tail = null, removal = null, litTip = "", litText = "", settling = false, status = "", coverR = 0, follow = null, walk = null, moments = false, claims = false, listed = null, clearLabel = null, canNote = false, adding = null, canLay = false, sessionGraph = false, canClose = false, acts }) {
  const switches = BAR_SWITCHES.filter((sw) => (sw.as === "moments" ? moments : claims));
  return (
    <div className="@container flex h-[32px] min-w-0 flex-none items-stretch border-t border-[color:var(--line-soft)] bg-[color:var(--bg)] text-[11px]" data-canvas-controls="">
      <div className="flex min-w-0 flex-1 items-stretch">
        {/* The host's own, first: which workspace this is, where it is shown. */}
        {lead}
        {removal ? (
          <div className="flex min-w-0 items-center gap-[10px] border-r border-[color:var(--line-soft)] px-[9px]" data-canvas-undo="">
            <span className="min-w-0 truncate leading-[16px] text-[color:var(--ink)]">
              {removal.kind === "here" ? `Closed "${clip(removal.title, 34)}"` : `Took "${clip(removal.title, 34)}" off`}
              {removal.nextTitle ? <span className="text-[color:var(--muted)]">{`${DOT}"${clip(removal.nextTitle, 28)}" anchors it now`}</span> : null}
            </span>
            <button type="button" onClick={acts.putBack} className="shrink-0 font-medium text-[color:var(--ink)] hover:text-[color:var(--green)]">Undo</button>
          </div>
        ) : null}
        {litText ? (
          // The lit hand-off, as a lanes view names its own: how many cells
          // it rings and its clear, while the rings are on the board.
          <div className="flex shrink-0 items-center gap-[6px] border-r border-[color:var(--line-soft)] px-[9px]" data-board-follow-note="">
            <HoverTooltip placement="top" label={litTip}>
              <span className="flex items-center gap-[6px] leading-[16px]" style={{ color: "var(--join, #5db2ff)" }}>
                <span aria-hidden="true" className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ border: "1.5px solid var(--join, #5db2ff)" }} />
                {litText}
              </span>
            </HoverTooltip>
            <button
              type="button"
              onClick={acts.clearLit}
              className="shrink-0 cursor-pointer p-0 text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
              data-board-follow-clear=""
            >
              clear
            </button>
          </div>
        ) : null}
        {settling ? (
          <div className="flex min-w-0 items-center gap-[8px] px-[9px] text-[color:var(--muted)]" data-board-settling="" role="status">
            <Working />
            <span className="min-w-0 truncate leading-[16px]">{status}</span>
          </div>
        ) : null}
      </div>
      {/* Over the left side's cells where a narrow board squeezes them
          (the workspace's name keeps a width of its own), so its own
          stay in sight and take their clicks. */}
      <div className="relative flex flex-none items-stretch bg-[color:var(--bg)]" style={coverR ? { marginRight: coverR } : undefined} data-canvas-controls-end="">
        {/* What the page does with this board: follow its chat, clear
            it, lay a trace on. */}
        {follow != null ? (
          <HoverTooltip placement="top" label={follow ? `The sessions this workspace's chat looks into are laid on as it finds them. Click to stop.` : "Lay on the sessions this workspace's chat looks into, as it finds them"}>
            <button type="button" className={`${BAR_CELL} ${follow ? BAR_CELL_ON : ""}`} aria-pressed={follow} onClick={acts.follow} data-workspace-follow="">
              <span className={`h-[6px] w-[6px] flex-none rounded-full ${follow ? "bg-[color:var(--green)]" : "border border-[color:var(--muted-soft)]"}`} aria-hidden="true" />
              {follow ? "Following the chat" : "Follow the chat"}
            </button>
          </HoverTooltip>
        ) : null}
        {walk ? (
          <HoverTooltip placement="top" label={walk === "running" ? "A walkthrough of this workspace is being written; its moments stand on the board when it is done" : "Walks you through what is on this workspace: the story its traces tell, numbered on the board, with what the chat claimed and what an investigation found"}>
            <button type="button" className={BAR_CELL} disabled={walk === "running"} onClick={acts.walk} data-board-walk={walk === "running" ? "running" : ""}>
              {walk === "running" ? <span className="h-[6px] w-[6px] flex-none rounded-full bg-[color:var(--ink)] motion-safe:animate-pulse" aria-hidden="true" /> : null}
              {walk === "running" ? "Walking through it" : "Walk me through it"}
            </button>
          </HoverTooltip>
        ) : null}
        {/* The walkthrough's moments and the chat's claims, each listed
            over the board by its own switch: pressing the one listed
            puts the list away, the other lists it instead. */}
        {switches.map((sw) => {
          const shown = listed === sw.as;
          return (
            <HoverTooltip key={sw.as} placement="top" label={shown ? sw.off : sw.on}>
              <button
                type="button"
                className={`${BAR_CELL} ${shown ? BAR_CELL_ON : ""}`}
                aria-pressed={shown}
                onClick={() => acts.toggleList(sw.as, shown)}
                data-board-evidence-toggle={sw.as}
              >
                {sw.word}
              </button>
            </HoverTooltip>
          );
        })}
        {clearLabel != null ? (
          <HoverTooltip placement="top" label={clearLabel}>
            <button type="button" className={BAR_CELL} onClick={acts.clear}>Clear</button>
          </HoverTooltip>
        ) : null}
        {canNote ? (
          <HoverTooltip placement="top" label="Write a note of your own on this workspace, along its top">
            <button type="button" className={BAR_CELL} onClick={acts.note} data-workspace-note="">+ Note</button>
          </HoverTooltip>
        ) : null}
        {adding != null ? (
          <button type="button" className={`${BAR_CELL} ${adding ? BAR_CELL_ON : ""}`} aria-pressed={adding} onClick={acts.add} data-workspace-add-open="">+ Add</button>
        ) : null}
        <BoardViewMenu cell={BAR_CELL} onTidy={acts.tidy} onByTime={acts.byTime} onEverything={acts.everything} />
        {canLay ? (
          <HoverTooltip placement="top" label="Lay it on this trace's workspace, to keep it there">
            <button type="button" className={BAR_CELL} onClick={acts.layIt}>Lay it on the workspace</button>
          </HoverTooltip>
        ) : null}
        <BoardZoom acts={acts} />
        <HoverTooltip
          wide
          placement="top"
          label={(
            <div className="grid gap-[3px] text-[11.5px] leading-[1.4]">
              {sessionGraph ? <div>Every session that shares content with this one, at the time it ran; the wires join the cells that carry the same string. The ones joined most strongly open on their own; the rest are placeholders, dashed and empty until a click loads one here. What opens here is laid on the workspace only when you ask.</div> : null}
              <div>By time puts every card back in the order it began, one to a row, at the board&apos;s time scale. Move or size any card and By time puts it back.</div>
              <div>Drag the board to move around it; scroll to move down it. Pinch, or Cmd or Ctrl and scroll, to zoom; + and - zoom, 0 or the zoom in the bar goes back to 100%, F shows everything. Double-click a card to zoom to it.</div>
              <div>Zoomed out, every card keeps its place; its head stays big enough to read, and words too small to read give way.</div>
              <div>Drag along the strip&apos;s time scale to zoom into a stretch while the rest keeps its scale; scroll over it to zoom it further or back, double-click it to take it off.</div>
              <div>Drag a card by its head to move it: it stays where you let it go, over another card if you like. Drag its sides to widen its time, its top or foot to give it more rows. Tidy lays every card out again, none over another.</div>
              <div>Click a cell to read it, and Keep what matters. Esc closes.</div>
              <div>+ Note writes a note of your own. Drag its pin onto a cell to pin it there, on as many cells as you like; a line ties each pin to the note. Click a pin to read it or take it off.</div>
            </div>
          )}
        >
          <button type="button" className={BAR_ICON_CELL} aria-label={sessionGraph ? "How the session graph works" : "How the board works"}>?</button>
        </HoverTooltip>
        {canClose ? <button type="button" className={BAR_CELL} onClick={acts.close}>Lanes</button> : null}
        {tail}
      </div>
    </div>
  );
});

// The bar's zoom cells: out, how far in (back to the board's own size), in.
function BoardZoom({ acts }) {
  const zoomPct = useContext(BoardZoomPct);
  return (
    <>
      <button type="button" className={BAR_ICON_CELL} onClick={acts.zoomOut} aria-label="Zoom out">&minus;</button>
      {/* How far the camera is in, and back to the board's own size. */}
      <HoverTooltip placement="top" label={zoomPct === 100 ? "The board at its own size; - and + zoom" : "Back to the board at its own size (0)"}>
        <button type="button" className={`${BAR_ICON_CELL} w-[46px] tabular-nums ${zoomPct === 100 ? "" : "text-[color:var(--ink)]"}`} onClick={acts.zoomReset} aria-label={`Zoomed to ${zoomPct}%: back to 100%`} data-board-zoom="">
          {`${zoomPct}%`}
        </button>
      </HoverTooltip>
      <button type="button" className={BAR_ICON_CELL} onClick={acts.zoomIn} aria-label="Zoom in">+</button>
    </>
  );
}

// A tip, where the pointer is.
function BoardTip({ store, label }) {
  const at = useSyncExternalStore(store.subscribe, store.get, store.get);
  const anchor = useMemo(() => (at ? { top: at.y - 7, bottom: at.y + 7, left: at.x - 7, right: at.x + 7, width: 14, height: 14 } : null), [at]);
  return anchor && label ? <HoverTooltip wide maxWidth={440} placement="top" anchorRect={anchor} label={label} /> : null;
}

// What the reader's own pins are called where a pin says who left it.
const READER_NOTE = Object.freeze({ key: "reader", title: "Your note" });
// A note's own pin id on a cell (`mine:<note>:<link>`), and back.
const readerPinId = (noteId, linkId) => `mine:${noteId}:${linkId}`;
const readerPinOf = (id) => {
  const m = /^mine:([^:]+):([^:]+)$/.exec(String(id || ""));
  return m ? { note: m[1], link: m[2] } : null;
};
const newLinkId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// A map pin, as the board's pins stand: a head on a needle.
function PinGlyph() {
  return (
    <svg viewBox="0 0 10 14" width="8" height="11" aria-hidden="true" className="flex-none">
      <circle cx="5" cy="4.5" r="3.5" fill="currentColor" />
      <path d="M5 8v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Machine names the way the trace byline resolves them, reported upward: one
// hook per endpoint needs one component per endpoint.
// A note the reader wrote, loose on the board: its words, written in place and
// kept a beat after the last key; carried anywhere on the board by its head
// (`onPut` with where it was let go, as drawn); the cross takes it off. Its
// pin, drawn from its head onto a cell (`onLink`: { move, end }, by where the
// pointer is), pins it there, tied to it by a line; `pinned`, on how many.
// `onLit`: the pointer on it or off it.
const LooseNote = memo(function LooseNote({ it, cam = 1, pinned = 0, onChange, onPut, onOff, onLink = null, onLit = null }) {
  const [text, setText] = useState(it.text);
  const [carry, setCarry] = useState(null);
  const drag = useRef(null);
  const linking = useRef(false);
  const linkDone = (e, put) => {
    if (!linking.current) return;
    linking.current = false;
    onLink?.end(it.id, e.clientX, e.clientY, put);
  };
  useEffect(() => { setText(it.text); }, [it.text]);
  useEffect(() => {
    if (text === it.text) return undefined;
    const t = setTimeout(() => onChange?.(it.id, { text }), 400);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  // Let go (or the press lost), it is put where it was carried to; called
  // off, it goes back.
  const letGo = (put) => {
    const g = drag.current;
    if (!g) return;
    drag.current = null;
    setCarry(null);
    if (put && g.moved) onPut?.(it.id, it.x + g.dx, it.y + g.dy);
  };
  return (
    <div
      className={`pointer-events-auto absolute flex flex-col rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-[var(--node-shadow)] ${carry ? "z-[1]" : ""}`}
      style={{ left: it.x + (carry?.dx || 0), top: it.y + (carry?.dy || 0), width: it.w, height: it.h }}
      onMouseEnter={() => onLit?.(it.id)}
      onMouseLeave={() => onLit?.(null)}
      data-loose-note={it.id}
      data-carried={carry ? "" : undefined}
    >
      <div
        className="flex h-[22px] flex-none cursor-grab select-none items-center gap-[6px] border-b border-[color:var(--line-soft)] px-[8px] active:cursor-grabbing"
        onPointerDown={(e) => {
          if (e.button !== 0 || e.target.closest("button")) return;
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
          drag.current = { x: e.clientX, y: e.clientY, dx: 0, dy: 0, moved: false };
        }}
        // Its layer is under the camera: the pointer's move, in the board's
        // units. A move with the button up is a let-go the page missed.
        onPointerMove={(e) => {
          const g = drag.current;
          if (!g) return;
          if (!(e.buttons & 1)) { letGo(true); return; }
          g.dx = (e.clientX - g.x) / (cam || 1);
          g.dy = (e.clientY - g.y) / (cam || 1);
          g.moved = g.moved || Math.abs(e.clientX - g.x) + Math.abs(e.clientY - g.y) > 2;
          if (g.moved) setCarry({ dx: g.dx, dy: g.dy });
        }}
        onPointerUp={() => letGo(true)}
        onPointerCancel={() => letGo(false)}
        onLostPointerCapture={() => letGo(true)}
      >
        <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--eyebrow)]">Note</span>
        {onLink ? (
          <HoverTooltip placement="top" label={pinned ? `Pinned on ${pinned} cell${pinned === 1 ? "" : "s"}. Drag onto another cell to pin it there too` : "Drag onto a cell to pin this note there, tied to it by a line"}>
            <button
              type="button"
              aria-label="Pin this note on a cell: drag it onto one"
              className="ml-auto flex h-[16px] cursor-crosshair touch-none items-center gap-[3px] rounded-[2px] border border-transparent px-[3px] text-[10.5px] tabular-nums leading-none text-[color:var(--muted)] transition-colors hover:border-[color:var(--line)] hover:text-[color:var(--ink)]"
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                e.stopPropagation();
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
                linking.current = true;
                onLink.move(it.id, e.clientX, e.clientY);
              }}
              onPointerMove={(e) => {
                if (!linking.current) return;
                if (!(e.buttons & 1)) { linkDone(e, true); return; }
                onLink.move(it.id, e.clientX, e.clientY);
              }}
              onPointerUp={(e) => linkDone(e, true)}
              onPointerCancel={(e) => linkDone(e, false)}
              onLostPointerCapture={(e) => linkDone(e, true)}
              data-loose-note-pin=""
            >
              <PinGlyph />
              {pinned ? <span>{pinned}</span> : null}
            </button>
          </HoverTooltip>
        ) : null}
        <button type="button" onClick={() => onOff?.(it.id)} aria-label="Take the note off the workspace" className={`${onLink ? "" : "ml-auto "}grid h-[16px] w-[16px] place-items-center text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]`}>&times;</button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Anything: what you are after, a path to follow, a hunch"
        autoFocus={!it.text}
        className="min-h-0 flex-1 resize-none select-text bg-transparent px-[8px] py-[6px] text-[12.5px] leading-[17px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
        data-loose-note-text=""
      />
    </div>
  );
});

// A search, a machine or a workspace inside, along the board's top while it
// has no card on the clock: what it is and why it has none. A workspace goes
// in from here as from its card.
const STUB_EYEBROW = { search: "Search", machine: "Machine", workspace: "Workspace" };
function ThingStub({ it, lit, onEnter, onOff }) {
  const inner = it.thing === "workspace";
  const ref = useRef(null);
  const go = onEnter && inner ? () => onEnter(it.id, ref.current?.getBoundingClientRect() || null) : null;
  return (
    <div
      ref={ref}
      className={`pointer-events-auto absolute flex flex-col rounded-[2px] border bg-[color:var(--panel)] ${lit ? "border-[color:var(--green)]" : inner ? "border-[color:var(--line-strong)] shadow-[var(--node-shadow)]" : "border-dashed border-[color:var(--line-strong)]"}`}
      style={{ left: it.x, top: it.y, width: it.w, height: it.h }}
      onDoubleClick={go || undefined}
      data-thing-stub={it.thing}
      data-workspace-stub={inner ? it.id : undefined}
    >
      <div className="flex h-[22px] flex-none items-center gap-[6px] border-b border-[color:var(--line-soft)] px-[8px]">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--eyebrow)]">{STUB_EYEBROW[it.thing]}</span>
        {go ? <button type="button" onClick={go} className="ml-auto text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">Go in</button> : null}
        {onOff ? <button type="button" onClick={() => onOff(it)} aria-label="Take it off this workspace" className={`${go ? "" : "ml-auto "}grid h-[16px] w-[16px] place-items-center text-[12px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]`}>&times;</button> : null}
      </div>
      <div className="min-w-0 truncate px-[8px] pt-[6px] text-[12.5px] leading-[16px] text-[color:var(--ink)]">{it.label}</div>
      <div className="min-w-0 truncate px-[8px] text-[11px] leading-[15px] text-[color:var(--muted)]">{it.said}</div>
    </div>
  );
}

const EndpointName = memo(function EndpointName({ id, onName }) {
  const entry = useDimensionEntry(id ? "endpoints" : null, id);
  const label = entry?.label && entry.label !== String(id) ? entry.label : null;
  useEffect(() => { if (label) onName(id, label); }, [id, label, onName]);
  return null;
});

// Where the lane a pick landed on actually is: a folded row stands for its
// lanes, so the pick reads the first of them with anything in that bucket.
function resolvePick(card, hit) {
  if (!card || hit?.turn == null) return null;
  const ids = hit.laneIds?.length ? hit.laneIds : [hit.laneId];
  for (const id of ids) {
    const lane = card.lanes.get(id);
    const ts = lane?.bucketTurns?.get(hit.turn);
    if (ts?.length) return { laneId: id, turn: ts[0], turns: ts };
  }
  return null;
}

// The rest of the calls behind a card's cell, as the Lanes plane rings them
// (traceLanes.callCellsAt): every other subject those calls touched, at the
// column its turn falls in on the card ({ lane, turn: column }). A folded
// head's cell stands for every lane under it, and none rings another; nor is
// the cell itself ringed.
export function cardCallCells(card, model, cell) {
  if (!card?.lanes || !model?.actions || cell?.turn == null) return [];
  const ids = cell.laneIds?.length ? cell.laneIds : [cell.laneId];
  const focused = new Set(ids);
  const row = (id) => card.scene?.centers?.get(id);
  const out = new Map();
  for (const id of ids) {
    for (const t of card.lanes.get(id)?.bucketTurns?.get(cell.turn) || []) {
      for (const s of sameCallLanes(model, id, t)) {
        if (focused.has(s.laneId)) continue;
        const { bucket } = bucketAt(card.tu?.[s.turn], card.win, card.n);
        if (bucket == null || (bucket === cell.turn && row(s.laneId) === row(id))) continue;
        out.set(`${s.laneId}@${bucket}`, { lane: s.laneId, turn: bucket });
      }
    }
  }
  return [...out.values()];
}

// What happened in a picked bucket, as the lanes view's own op lines: the
// lane's when the row is one lane, each lane's (named) when it is a folded
// group.
function bucketLines(card, hit, max = 5) {
  if (!card || hit?.turn == null) return { lines: [], more: 0 };
  const ids = hit.laneIds?.length ? hit.laneIds : [hit.laneId];
  const named = ids.length > 1;
  const lines = [];
  let total = 0;
  for (const id of ids) {
    const lane = card.lanes.get(id);
    for (const item of lane?.cells?.get(hit.turn) || []) {
      total += 1;
      if (lines.length >= max) continue;
      let line = "";
      try { line = opLine(item); } catch { line = ""; }
      if (!line) continue;
      lines.push(clip(named ? `${lane.label}: ${line}` : line, 130));
    }
  }
  return { lines, more: Math.max(0, total - lines.length) };
}

// A point along a route's curve, the cubic at t.
function routeAt(r, t) {
  const u = 1 - t;
  return {
    x: u * u * u * r.ea.x + 3 * u * u * t * r.c0.x + 3 * u * t * t * r.c1.x + t * t * t * r.eb.x,
    y: u * u * u * r.ea.y + 3 * u * u * t * r.c0.y + 3 * u * t * t * r.c1.y + t * t * t * r.eb.y,
  };
}

function distToRoute(r, x, y) {
  let best = Infinity;
  let p = r.ea;
  for (let i = 1; i <= 24; i++) {
    const q = routeAt(r, i / 24);
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const len = dx * dx + dy * dy || 1;
    const k = Math.max(0, Math.min(1, ((x - p.x) * dx + (y - p.y) * dy) / len));
    best = Math.min(best, Math.hypot(x - (p.x + k * dx), y - (p.y + k * dy)));
    p = q;
  }
  return best;
}

// The ticks for the visible stretch of the clock: where each burst begins (its
// day, or its time on the day before's), and inside a burst wide enough for
// them, the hours or minutes.
function rulerTicks(clock, k, vx0, vx1) {
  const out = [];
  const X = (t) => PAD_L + clock.at(t) * k;
  let prevT0 = null;
  for (const s of clock.iv) {
    if (s.gap) continue;
    const sameDayAsPrev = prevT0 != null && sameDay(prevT0, s.t0);
    prevT0 = s.t0;
    const x0 = PAD_L + s.u0 * k;
    const x1 = PAD_L + s.u1 * k;
    if (x1 < vx0 - 200 || x0 > vx1 + 200) continue;
    const head = s.first !== false ? { x: x0, label: sameDayAsPrev ? hhmm(s.t0) : dayText(s.t0), strong: false } : null;
    if (head) out.push(head);
    if (s.t1 <= s.t0) continue;
    const pxPerMs = (x1 - x0) / (s.t1 - s.t0);
    const step = TICK_STEPS.find((st) => st * pxPerMs >= 84);
    if (!step) continue;
    const start = Math.ceil(s.t0 / step) * step;
    for (let t = start; t <= s.t1; t += step) {
      const x = X(t);
      if (x < vx0 - 80 || x > vx1 + 80) continue;
      if (head && Math.abs(x - head.x) < 70) continue;
      const d = new Date(t);
      const midnight = d.getHours() === 0 && d.getMinutes() === 0;
      out.push({ x, label: step >= DAY || midnight ? dayText(t) : hhmm(t), strong: false, minor: true, day: midnight });
    }
  }
  return out;
}

// The call a lane item records, read off the turns: what it was asked to do
// and the start of what came back.
function callOf(turns, it) {
  const b = turns?.[it?.turnIndex]?.blocks?.[it?.blockIndex];
  if (!b || b.type !== "tool_use") return null;
  let result = null;
  for (let k = it.turnIndex; k < Math.min(turns.length, it.turnIndex + 6) && !result; k++) {
    for (const r of turns[k]?.blocks || []) {
      if (r?.type === "tool_result" && r.tool_use_id === b.id) { result = r; break; }
    }
  }
  const text = (c) => (typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n") : "");
  const inp = b.input || {};
  const input = typeof inp.command === "string" ? inp.command
    : inp.old_string != null ? `${inp.file_path || ""}\n- ${clip(String(inp.old_string), 300)}\n+ ${clip(String(inp.new_string ?? ""), 300)}`
      : inp.file_path || inp.path || inp.pattern || inp.query || clip(JSON.stringify(inp), 300);
  return { input: String(input || ""), result: clip(text(result?.content).trim(), 700), failed: !!result?.isError };
}

// What an annotation says, for its row and for the panel that opens it: its
// colour, what kind it is, its words, where it is, what it came to, and (for
// the panel) what it holds.
function attachColor(node) {
  if (node.kind === "note") return NOTE_TONE[node.tone] ? severityCss(NOTE_TONE[node.tone]) : "var(--ink)";
  return node.kind === "finding" ? severityCss(node.severity) : node.kind === "reading" || node.kind === "agent" ? "var(--ink)" : "var(--graph)";
}
// A note that says what its moment means takes a finding's colours for it:
// what the review and its check missed or got wrong as loud as a high
// finding, what matters as a medium one, what does not in the quiet tone.
const NOTE_TONE = { missed: "high", corrects: "high", matters: "medium", doesnt: "low" };
const NOTE_KIND_WORD = { missed: "Missed", corrects: "Corrects the check", matters: "Matters", doesnt: "Doesn't matter" };

// The cells an attachment rings on the board, each with the attachment whose
// colour it is ringed in: a count of them rings what it holds, each cell in
// the colour of the one about it, never a colour of its own.
export function attachRings(node) {
  if (node.members) return node.members.flatMap((m) => (m.pts || []).map((pt) => [pt, m]));
  return (node.pts || []).map((pt) => [pt, node]);
}

// How much of a card's record the board holds, when it is not all of it (the
// pages before the ones it read, which its "earlier" reads on asking): its
// newest messages, of how many. Null once the record is whole.
export function partOf(record) {
  if (!record?.session) return null;
  const loaded = Number(record.loaded) || 0;
  const total = Number(record.total);
  return Number.isFinite(total) && total > loaded ? `newest ${loaded} of ${total} messages` : null;
}

// The page before the oldest turn a card holds, as the trace page's
// "earlier" offers it, from the card's account of its record: how many
// messages are still to read, read on a click, retried when it could not be.
// Null when the card holds its whole record.
function earlierOf(rec) {
  if (!rec?.session) return null;
  if (rec.loading) return { label: "loading", busy: true, help: "Reading the page before the oldest turn on this card." };
  if (rec.rejected) {
    return { label: "retry earlier", failed: true, help: `The page before the oldest turn on this card could not be read${rec.reason ? ` (${rec.reason})` : ""}. Read it again.` };
  }
  if (rec.exhausted || !rec.nextCursor) return null;
  const remaining = Number.isFinite(rec.total) ? Math.max(0, rec.total - (Number(rec.loaded) || 0)) : null;
  return {
    label: remaining != null ? `${formatCompact(remaining)} earlier` : "earlier",
    help: remaining != null
      ? `Read the page before the oldest turn on this card: ${remaining.toLocaleString()} of its record's ${rec.total.toLocaleString()} messages are not on it yet.`
      : "Read the page before the oldest turn on this card.",
  };
}

// A turn on a card as the workspace prints it: its place in its trace's
// record, the messages before the page the board read counted in, so the
// number is the one the dock's Lanes view prints (and the chat names).
export const turnLabel = (card, t) => `T${(card?.turnBase || 0) + t}`;

// A kept reading on its card: the turns it covers, counted from the page the
// card read (readingOfCell, the other way).
export function readingOnCard(card, r) {
  const base = card?.turnBase || 0;
  return { ...r, turn: Number.isInteger(r.turn) ? r.turn - base : r.turn, turns: r.turns?.map((t) => t - base) };
}

// A card on the board in a shape whose head and list are `colW` wide: one the
// reader has not sized, shorter than that, runs its body across them from
// where it starts, its stretch of the clock spread past the board's scale (by
// `stretch`) until the zoom makes it that wide. `g` is its geometry at the
// board's scale: widths at the fit (`*WF`) and at this zoom.
export function fillColumn(g, colW) {
  if (g.sizedW || !(colW > g.cardWF)) return g;
  const bodyWF = Math.max(g.bodyWF, colW - BOARD_RAIL);
  const bodyW = Math.max(g.bodyW, colW - BOARD_RAIL);
  return { ...g, bodyWF, cardWF: BOARD_RAIL + bodyWF, bodyW, cardW: BOARD_RAIL + bodyW, stretch: bodyW / g.bodyW };
}

// A cell on a card kept as a reading: by its place in its trace's record, as
// turnLabel numbers it, and with the moments it covers, so another read of
// the trace finds it.
export function readingOfCell(card, session, cell) {
  const base = card?.turnBase || 0;
  const at = (cell.turns?.length ? cell.turns : [cell.turn]).map((t) => card?.times?.[t]).filter(Number.isFinite);
  return {
    id: `reading:${session}:${cell.laneId}:${base + cell.turn}`, session, lane: cell.laneId,
    turn: base + cell.turn, turns: cell.turns?.map((t) => base + t),
    t0: at.length ? Math.min(...at) : null, t1: at.length ? Math.max(...at) : null,
  };
}

// The cells an attachment is on, in words: the lane, the turn and its time.
// The cells an attachment is on, on its own card, as its footer names them:
// four at most, each with the anchor that opens it in the dock.
function cellsOn(node, card) {
  const out = [];
  for (const a of node.anchors || []) {
    if (a.key !== node.key || out.length >= 4) continue;
    const lane = card?.model?.laneById?.get(a.lane)?.label || "a lane";
    const t = card?.times?.[a.turn];
    const text = `${lane} ${turnLabel(card, a.turn)}${Number.isFinite(t) ? ` ${DOT} ${hhmm(t)}` : ""}`;
    if (!out.some((c) => c.text === text)) out.push({ text, anchor: a });
  }
  return out;
}

// What an opened attachment's one control says it does.
function closeWords(node) {
  if (node.kind === "reading") return { label: "Take it off", tip: "Take it off (its cell can be kept again)" };
  if (readerPinOf(node.id)) return { label: "Unpin the note from this cell", tip: "Unpin the note from this cell" };
  if (node.kind === "note") return { label: "Close", tip: "Close: its pin opens it again" };
  return { label: "Close", tip: "Close: its card's tag, or its mark on its cell, opens it again" };
}

function annotationParts(node, card, recordTurns) {
  const color = attachColor(node);
  const when = (t) => (t != null && Number.isFinite(card?.times?.[t]) ? hhmm(card.times[t]) : null);
  const words = (text) => <div data-clamp="" className="whitespace-pre-wrap break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{text}</div>;
  if (node.kind === "finding") {
    return {
      color,
      lead: <SeverityChip severity={node.severity} />,
      text: node.title,
      trail: node.verdict ? <span className="shrink-0"><VerdictChip verdict={node.verdict} /></span> : null,
      where: node.anchors.length ? [node.turn != null ? turnLabel(card, node.turn) : null, when(node.turn)].filter(Boolean).join(DOT) : node.earlier ? "in earlier messages" : "not located",
      body: (
        <>
          {node.rationale ? words(clip(node.rationale, 1200)) : null}
          {node.quotes.map((q, i) => <div key={i} data-clamp="" className="mt-[8px] whitespace-pre-wrap break-words font-mono text-[10.5px] leading-[1.35] text-[color:var(--muted)]">{clip(q, 400)}</div>)}
          <div className="mt-[8px] text-[11px] text-[color:var(--muted)]">{node.verdict ? `Triage: ${liteVerdictLabel(node.verdict)}` : "Not triaged yet"}</div>
        </>
      ),
    };
  }
  if (node.kind === "claim") {
    const supported = node.status === "supported";
    return {
      color,
      lead: <span className={`shrink-0 ${supported ? "text-[color:var(--green)]" : "text-[color:var(--severity-medium)]"}`}>{supported ? "supported" : "not yet supported"}</span>,
      text: node.statement,
      where: node.anchors.length ? `${node.anchors.length} cell${node.anchors.length === 1 ? "" : "s"}` : "the trace",
      body: words(node.statement),
    };
  }
  if (node.kind === "note") {
    // Named for the chat that left it, and for what it says of its moment
    // where it says.
    const by = node.chat?.title || "Chat";
    const kind = NOTE_KIND_WORD[node.tone];
    return {
      color,
      lead: <span className="min-w-0 max-w-[60%] truncate" style={{ color: kind ? color : "var(--muted)" }}>{kind ? `${by} ${DOT} ${kind}` : by}</span>,
      text: node.text,
      where: node.inside ? "" : node.earlier ? "on a page of the record not read yet" : [node.laneLabel || null, turnLabel(card, node.turn)].filter(Boolean).join(DOT),
      body: words(node.text),
    };
  }
  if (node.kind === "agent") {
    // An agent the trace started: what it was asked, and what reached the
    // trace from it (each result, the last one first to be read).
    const status = node.failed ? "returned an error" : node.reported ? `returned${Number.isFinite(node.reportAt) && Number.isFinite(node.spawnAt) ? ` after ${latencyText(node.reportAt - node.spawnAt).slice(1)}` : ""}` : "no result in this trace";
    const label = (text, meta = null) => (
      <div className="mb-[3px] flex items-baseline gap-[6px] text-[10.5px] text-[color:var(--muted-soft)]">
        <span className="font-medium text-[color:var(--muted)]">{text}</span>
        {meta ? <span className="tabular-nums">{meta}</span> : null}
      </div>
    );
    const quiet = (text) => <div className="text-[12px] leading-[1.45] text-[color:var(--muted)]">{text}</div>;
    const blockText = (r) => {
      const b = recordTurns?.[r.turn]?.blocks?.[r.block];
      const c = b?.type === "tool_result" ? b.content : null;
      return typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n") : "";
    };
    const results = (node.results || []).slice(-3);
    const lastText = results.length ? String(results[results.length - 1].report ?? blockText(results[results.length - 1])).replace(/\s+/g, " ").trim() : "";
    return {
      color,
      chip: {
        title: node.label,
        where: [node.turn != null ? turnLabel(card, node.turn) : null, when(node.turn)].filter(Boolean).join(DOT),
        detail: [node.agentType, status, lastText ? clip(lastText, 160) : node.prompt ? `asked: ${clip(String(node.prompt).replace(/\s+/g, " "), 140)}` : null].filter(Boolean).join(DOT),
      },
      lead: <span className="shrink-0 text-[color:var(--muted)]">Agent</span>,
      text: node.label,
      trail: <span className={`shrink-0 ${node.failed ? "text-[color:var(--severity-high)]" : "text-[color:var(--muted)]"}`}>{status}</span>,
      where: [node.agentType || null, node.turn != null ? turnLabel(card, node.turn) : null, when(node.turn)].filter(Boolean).join(DOT),
      body: (
        <>
          <div className="mb-[10px]">
            {label("Asked")}
            {node.prompt ? words(clip(node.prompt, 1600)) : quiet(node.sealed ? "Its task is sealed in this record: only its name can be read." : "Its task text is not in this record.")}
          </div>
          {results.length ? results.map((r, i) => {
            const text = (r.report ?? blockText(r)).trim();
            return (
              <div key={i} className="mb-[10px] last:mb-0">
                {label(r.ok ? "Returned" : "Returned an error", [r.turn != null ? turnLabel(card, r.turn) : null, when(r.turn)].filter(Boolean).join(DOT))}
                {text
                  ? <div data-clamp="" className={`whitespace-pre-wrap break-words text-[12px] leading-[1.45] ${r.ok ? "text-[color:var(--ink)]" : "text-[color:var(--severity-high)]"}`}>{clip(text, 1600)}</div>
                  : quiet("It returned without text this record can read.")}
              </div>
            );
          }) : (
            <div>
              {label("Returned")}
              {quiet("Nothing it returned reaches this trace.")}
            </div>
          )}
        </>
      ),
    };
  }
  // A reading of a cell: what the lane did there, the calls it made and the
  // start of what came back.
  const anchor = node.anchors[0];
  const lane = card?.model?.laneById?.get(anchor.lane);
  const turns = (node.turns?.length ? node.turns : [anchor.turn]).slice(0, 8);
  const items = [];
  for (const t of turns) for (const it of lane?.cells?.get(t) || []) items.push({ t, it });
  let first = "";
  try { first = items[0] ? opLine(items[0].it) : ""; } catch { first = ""; }
  return {
    color,
    lead: <span className="shrink-0 font-medium text-[color:var(--ink)]">{lane?.label || "cell"}</span>,
    text: first || (node.found === false ? "Not in this read of the trace" : "Nothing recorded in this cell"),
    where: [`${turnLabel(card, anchor.turn)}${turns.length > 1 ? `\u2013${(card?.turnBase || 0) + turns[turns.length - 1]}` : ""}`, when(anchor.turn)].filter(Boolean).join(DOT),
    body: items.length ? items.map(({ t, it }, i) => {
      let line = "";
      try { line = opLine(it); } catch { line = ""; }
      const spoken = isConversationDomain(it.domain);
      const said = typeof it.op?.text === "string" ? it.op.text : "";
      const call = spoken ? null : callOf(recordTurns, it);
      return (
        <div key={i} className="mb-[10px]">
          {turns.length > 1 ? <div className="text-[10px] text-[color:var(--muted-soft)]">{turnLabel(card, t)}</div> : null}
          {spoken
            ? <div className="whitespace-pre-wrap break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{clip(said || line, 1200)}</div>
            : <div className="break-words font-mono text-[11px] text-[color:var(--ink)]">{line}</div>}
          {call?.input && call.input !== line ? (
            <div className="mt-[2px] whitespace-pre-wrap break-words font-mono text-[10.5px] leading-[1.35] text-[color:var(--muted)]">{clip(call.input, 800)}</div>
          ) : null}
          {call?.result ? (
            <div className={`mt-[3px] whitespace-pre-wrap break-words border-t border-[color:var(--line-soft)] pt-[3px] font-mono text-[10.5px] leading-[1.35] ${call.failed ? "text-[color:var(--severity-high)]" : "text-[color:var(--muted-soft)]"}`}>{call.result}</div>
          ) : null}
        </div>
      );
    }) : <div className="text-[12px] text-[color:var(--muted)]">{node.found === false ? "This cell is not in the trace as it is read now: kept on another read of it, which numbered its turns differently. Keep it again from the card." : "Nothing recorded in this cell."}</div>,
  };
}

// Where a card's opened attachments stand in its band: along its top, each
// at its moment (or as far from it as the reader set it along the band,
// pixels), in the order they were opened (the ones open from the start first,
// left to right), each right under the ones already open over its width, so
// cards opened at one moment stack down it, one opened later never moves one
// already open, and nothing is swept sideways. One the reader set along the
// band may run on past its end (the board lays the band out wide enough to
// take it); one at its moment moves in from the end. The ones carried out of
// the band are blocks of their own.
export function hangAttachments(items, { x0, x1, y, isStanding, pinnedHeights = null, carriedOut = null, pinOffsets = null, openRank = null }) {
  const out = [];
  const within = (x, w) => Math.max(x0, Math.min(x1 - w, x));
  const pinX = (it) => (it.ax ?? x0 + 14) - 14 + (pinOffsets?.get(it.nd.id) || 0);
  const rankOf = (it) => openRank?.get(it.nd.id) ?? -1;
  const opened = [];
  const floorOf = (x, w) => opened.reduce((m, o) => (x < o.x + PIN_W + CHIP_GAP && o.x < x + w + CHIP_GAP ? Math.max(m, o.bottom + CHIP_GAP) : m), 0);
  const here = items.filter((it) => isStanding(it.nd) && !carriedOut?.has(it.nd.id)).sort((a, b) => rankOf(a) - rankOf(b) || pinX(a) - pinX(b));
  for (const it of here) {
    const h = pinnedHeights?.get(it.nd.id) ?? PIN_EST;
    const x = pinOffsets?.get(it.nd.id) ? Math.max(x0, pinX(it)) : within(pinX(it), PIN_W);
    const top = floorOf(x, PIN_W);
    opened.push({ x, bottom: top + h });
    out.push({ nd: it.nd, ax: it.ax, level: "stand", x, y: y + top, w: PIN_W, h });
  }
  return out;
}

// What an attachment is, as a mark for a line of words: a claim's number in a
// pill (the chat's own), a numbered note's its pin's head, else a square in
// the colour of what it is.
function InlineMark({ node, color }) {
  if (node.kind === "claim" || (node.kind === "note" && node.n != null)) {
    return (
      <span className="grid h-[14px] min-w-[16px] shrink-0 place-items-center rounded-full px-[3px] text-[9.5px] font-semibold leading-none text-[color:var(--bg)]" style={{ background: color }} aria-hidden="true">
        {node.n ?? "?"}
      </span>
    );
  }
  return <span className="h-[8px] w-[8px] shrink-0 rounded-[2px]" style={{ background: color }} aria-hidden="true" />;
}

// Work under way, as the board's own status line shows it: three dots pulsing
// in turn.
function Working({ size = 4 }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-[3px]" aria-hidden="true">
      {[0, 160, 320].map((d) => <span key={d} className="inline-block rounded-full bg-[color:var(--muted)] motion-safe:animate-pulse" style={{ width: size, height: size, animationDelay: `${d}ms` }} />)}
    </span>
  );
}

// What an agent's chip says while its session is being laid on.
const MOUNT_WORDS = { finding: "Finding its session", reading: "Reading its session" };

const AGENT_ACTION = "shrink-0 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[2px] text-[11.5px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]";
// The same, beside a line of a list.
const AGENT_ACTION_SMALL = "shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-[11px] leading-[16px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]";
const closeIcon = <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" /></svg>;

// The workspace's close (the host's `onClose`), in the strip's head row at
// its right end: a small action of the board's own, named, as far off the
// frame's top as its side, the strip running on under it. The row's words
// and the investigation's steps in it stop this far short of the strip's end
// to leave it room (drawBoardStrip, BoardSteps).
export const CLOSE_ROOM = 56;
export function BoardClose({ onClose }) {
  return (
    <HoverTooltip placement="bottom" label="Close this workspace">
      <button type="button" onClick={onClose} aria-label="Close this workspace" className={`${AGENT_ACTION_SMALL} absolute right-[4px] top-[4px] z-[10] flex items-center gap-[5px]`} data-board-close="">
        {closeIcon}
        Close
      </button>
    </HoverTooltip>
  );
}

// An attachment open: what it is and where, its words, what it holds and the
// cells it is on. Peeked at by the pointer or focus over its pin, it opens
// beside it and the pointer passes through it; opened, the same card stands
// under its card (or, carried out by its head as a card is, is a block of its
// own on the board), reporting its depth so the board makes room for it. An
// opened card has one control, its close: what the review found, the chat's
// claims and the agents a trace started go back to its head's tag and their
// marks on its cells, a note to its pin; a kept reading comes off, and the
// reader's own note is unpinned from its cell. Its body scrolls past its
// depth. Each cell it names opens that message in the dock (`onCell`).
const AttachmentCard = memo(function AttachmentCard({ node, card, turns: recordTurns, style = null, x = 0, y = 0, w = PIN_W, lifted = false, pinned = false, hint = null, onClose = null, onHeight = null, onGrab = null, onCell = null, mount = null, mountState = null, onMount = null, onShowSession = null, onChat = null }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!pinned || !el) return undefined;
    const report = () => onHeight?.(node.id, Math.ceil(el.getBoundingClientRect().height));
    report();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(report) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [pinned, onHeight, node.id]);
  const parts = annotationParts(node, card, recordTurns);
  const cells = cellsOn(node, card);
  const body = node.kind === "claim" || node.kind === "note" ? null : parts.body;
  const close = closeWords(node);
  return (
    <div
      ref={ref}
      className={`absolute flex flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] ${pinned ? `pointer-events-auto ${lifted ? "shadow-[var(--node-shadow-lift)]" : "shadow-[var(--node-shadow)]"}` : "pointer-events-none z-[9] shadow-[var(--node-shadow-lift)]"}`}
      style={pinned
        ? { left: x, top: y, width: w, zIndex: lifted ? 6 : undefined, maxHeight: PIN_MAX_H }
        : style}
      data-canvas-card={node.kind}
      data-pinned={pinned ? "" : undefined}
    >
      <div
        className={`flex shrink-0 items-start gap-[8px] border-b border-[color:var(--line-soft)] px-[12px] pb-[8px] pt-[9px] text-[11.5px] ${pinned && onGrab ? (lifted ? "cursor-grabbing" : "cursor-grab") : ""}`}
        onPointerDown={pinned && onGrab ? (e) => onGrab(e, node) : undefined}
        data-pin-head={pinned ? "" : undefined}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-[6px] gap-y-[2px]">
            <InlineMark node={node} color={parts.color} />
            {parts.lead}
            {parts.trail}
            <span className="tabular-nums text-[color:var(--muted)]">{parts.where}</span>
          </div>
          {node.kind !== "reading" ? <div className="mt-[4px] whitespace-pre-wrap break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{parts.text}</div> : null}
        </div>
        {pinned && onClose ? (
          <HoverTooltip placement="top" label={close.tip}>
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onClose(node.id)} aria-label={close.label} className="grid h-[18px] w-[18px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]" data-card-close="">
              {closeIcon}
            </button>
          </HoverTooltip>
        ) : null}
      </div>
      {node.kind === "agent" && (onMount || mount) ? (
        // The agent's own session: what it did is there, not in this record.
        <div className="flex min-h-[34px] shrink-0 items-center gap-[8px] border-b border-[color:var(--line-soft)] px-[12px] py-[6px] text-[11.5px] text-[color:var(--muted)]" role="status">
          {mountState === "finding" || mountState === "reading" ? (
            <span className="inline-flex min-w-0 flex-1 items-center gap-[7px]">{MOUNT_WORDS[mountState]}<Working /></span>
          ) : mountState === "on" ? (
            <>
              <span className="min-w-0 flex-1">Its session is on the workspace</span>
              {onMount ? <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onShowSession?.(mount.id)} className={AGENT_ACTION}>Show it</button> : null}
            </>
          ) : mount?.status === "missed" ? (
            <>
              <span className="min-w-0 flex-1">{mount.note}</span>
              {onMount ? <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onMount(node)} className={AGENT_ACTION}>Try again</button> : null}
            </>
          ) : (
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onMount(node)} className={AGENT_ACTION}>Lay its session on the workspace</button>
          )}
        </div>
      ) : null}
      {body ? <div className={`min-h-0 flex-1 px-[12px] pb-[10px] pt-[9px] text-[11.5px] leading-[1.4] ${pinned ? "overflow-y-auto" : "overflow-hidden"}`} data-canvas-card-body="">{body}</div> : null}
      <div className="flex shrink-0 items-center gap-[8px] border-t border-[color:var(--line-soft)] px-[12px] py-[6px] text-[11px] text-[color:var(--muted)]">
        <span className="min-w-0 flex-1 truncate">
          {node.inside ? `In ${node.inside.workspace}`
            : !cells.length ? "On the whole trace"
              : pinned && onCell ? (
                <>
                  {"On "}
                  {cells.map((c, i) => (
                    <span key={c.text}>
                      {i ? ", " : ""}
                      <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onCell(node, c.anchor)} className="cursor-pointer p-0 text-[color:var(--ink)] underline decoration-dotted underline-offset-2 hover:decoration-solid" data-card-cell="">{c.text}</button>
                    </span>
                  ))}
                </>
              ) : `On ${cells.map((c) => c.text).join(", ")}`}
        </span>
        {!pinned && hint ? <span className="shrink-0 text-[color:var(--muted-soft)]">{hint}</span> : null}
        {pinned && onChat ? (
          <HoverTooltip placement="top" label="Puts this into the chat in front, to ask about it there">
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onChat(node)} className={AGENT_ACTION_SMALL} data-card-add-to-chat="">Add to chat</button>
          </HoverTooltip>
        ) : null}
      </div>
    </div>
  );
});

// A stack of attachments open: its members, one a line. Grown by the pointer
// the pointer passes through it; held by a click, a member is picked from it
// and pinned.
function StackList({ node, card, turns: recordTurns, style, held = false, onRelease, onMember = null, onMemberHover = null, pick = "pin", head = null, foot = null, act = null, chat = null }) {
  // Framed as an attachment's card is, its members one a line between the
  // same head and foot. Peeked at, the first few; held, all of them to pick
  // (with `onMember`; without it, to read, `foot` saying where they are).
  const shown = held ? node.members : node.members.slice(0, STACK_PEEK_ROWS);
  const pickable = held && !!onMember;
  const rest = node.members.length - shown.length;
  return (
    <div className={`absolute z-[9] flex flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-[var(--node-shadow-lift)] ${held ? "pointer-events-auto" : "pointer-events-none"}`} style={style} data-canvas-grown="cluster">
      <div className="flex shrink-0 items-center gap-[8px] border-b border-[color:var(--line-soft)] px-[12px] pb-[7px] pt-[8px] text-[11.5px] text-[color:var(--muted)]">
        <span className="min-w-0 flex-1">{head ?? `${node.members.length} here`}</span>
        {held ? (
          <button type="button" onClick={onRelease} aria-label="Close" className="grid h-[18px] w-[18px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">
            {closeIcon}
          </button>
        ) : null}
      </div>
      <div className={`min-h-0 flex-1 py-[4px] ${held ? "overflow-y-auto" : "overflow-hidden"}`}>
        {shown.map((m) => {
          const p = annotationParts(m, card, recordTurns);
          const doing = act ? act(m) : null;
          return (
            <div
              key={m.id}
              role={pickable ? "button" : undefined}
              tabIndex={pickable ? 0 : -1}
              onClick={pickable ? () => onMember(m.id) : undefined}
              onKeyDown={pickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onMember(m.id); } } : undefined}
              onMouseEnter={() => onMemberHover?.(m.id)}
              onMouseLeave={() => onMemberHover?.(null)}
              className={`flex h-[26px] items-center gap-[8px] px-[12px] text-[11.5px] outline-none hover:bg-[color:var(--gray-fill)] focus-visible:bg-[color:var(--gray-fill)] ${pickable ? "cursor-pointer" : ""}`}
            >
              <InlineMark node={m} color={p.color} />
              {p.lead}
              <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">{p.text}</span>
              <span className="min-w-0 max-w-[45%] shrink truncate whitespace-nowrap tabular-nums text-[color:var(--muted)]">{p.where}</span>
              {held && doing ? (
                <button type="button" onClick={(e) => { e.stopPropagation(); doing.run(); }} className={AGENT_ACTION_SMALL} data-stack-act={m.id}>{doing.label}</button>
              ) : null}
              {held && chat ? (
                <button type="button" onClick={(e) => { e.stopPropagation(); chat(m); }} className={AGENT_ACTION_SMALL} data-stack-add-to-chat={m.id}>Add to chat</button>
              ) : null}
            </div>
          );
        })}
        {rest > 0 ? <div className="px-[12px] py-[4px] text-[11px] text-[color:var(--muted)]">{`and ${rest} more`}</div> : null}
      </div>
      <div className="flex shrink-0 items-center border-t border-[color:var(--line-soft)] px-[12px] py-[6px] text-[11px] text-[color:var(--muted-soft)]">
        <span className="min-w-0 flex-1">{foot ?? (held ? `Click one to ${pick} it` : `Click to pick one to ${pick}`)}</span>
      </div>
    </div>
  );
}

// How long a pin's needle is: one standing above another at the same place
// reaches past it, PIN_LIFT_GAP over that pin's head.
const PIN_LIFT_GAP = 4;
const pinNeedle = (group) => PIN_NEEDLE + (group.lift || 0) * (PIN_HEAD + PIN_LIFT_GAP);

// A walkthrough's moment numbers, runs of them as ranges: 1-3, 5.
// Which of a board's notes stand on it, and its walkthrough's moments'
// numbers: only `walkthroughKey`'s walkthrough's moments stand, numbered in
// its order, every one counted whether or not it lands on its card (one
// across sessions carries its number; a trace's own counts per session).
// Another walkthrough's stay off; any other note stands, unnumbered.
export function walkthroughMoments(notes, walkthroughKey = WALKTHROUGH_BY.key) {
  const n = new Map();
  const counted = new Map();
  for (const nt of notes || []) {
    if (nt.chat?.key !== walkthroughKey) continue;
    if (Number.isInteger(nt.n)) {
      n.set(nt.id, nt.n);
      continue;
    }
    const k = (counted.get(nt.session) || 0) + 1;
    counted.set(nt.session, k);
    n.set(nt.id, k);
  }
  const stands = (nt) => !String(nt.chat?.key || "").startsWith(WALKTHROUGH_BY.key) || nt.chat.key === walkthroughKey;
  return { n, stands };
}

export function momentsText(nums) {
  const runs = [];
  for (const n of [...nums].sort((a, b) => a - b)) {
    const last = runs[runs.length - 1];
    if (last && n === last[1] + 1) last[1] = n;
    else runs.push([n, n]);
  }
  return runs.map(([a, b]) => (a === b ? String(a) : `${a}\u2013${b}`)).join(", ");
}
// What pins standing as one hold, said in their list's head.
// What a card's tag list holds, counted by kind: "4 findings, 1 agent".
export function tagWords(members) {
  const n = (kind, word) => {
    const k = members.filter((m) => m.kind === kind).length;
    return k ? plural(k, word) : null;
  };
  return [n("finding", "finding"), n("claim", "claim"), n("agent", "agent")].filter(Boolean).join(", ");
}

export function stackWords(members) {
  const moments = members.filter((m) => m.n != null).length;
  const others = members.length - moments;
  return [
    moments ? `${moments} moment${moments === 1 ? "" : "s"}` : null,
    others ? `${others} note${others === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" and ") + " here";
}

// A note over the board, as a map's pin: a head holding its number (a
// walkthrough's moment; any other note a dot), for pins standing as one the
// moments' numbers (1-6) or how many notes (3 notes), on a needle whose point
// touches its cell's ring, in its colour (what the review missed or got wrong
// as a high finding's, what matters as a medium one's, a plain note in ink),
// over everything else on the board and the same size at every zoom. Opened,
// its head is hollow: its card stands under the trace's. The pointer or focus
// on its head opens what it says (a stack's members); a click opens it under
// its card or folds it back (holds a stack open to pick from). Standing above
// another pin at the same place, its needle passes behind that pin's head, so
// it never crosses it even when in front. Placed where its point is, in the
// board's units scaled to the screen's.
// `covered`: its cell lies under a card over its own, so it stands hidden.
const NotePin = memo(function NotePin({ group, hot = false, held = false, faded = false, covered = false, onHover, onPick }) {
  const first = group.members[0];
  const more = group.members.length - 1;
  const moments = group.kind === "moment" ? momentsText(group.members.map((m) => m.n)) : null;
  const label = moments ?? (more ? `${group.members.length} notes` : null);
  const worst = group.members.map((m) => NOTE_TONE[m.tone]).filter(Boolean)
    .sort((a, b) => (SEVERITY_RANK[a] ?? 9) - (SEVERITY_RANK[b] ?? 9))[0];
  const color = worst ? severityCss(worst) : "var(--ink)";
  const open = !more && !!first.standing;
  // On a page the card has not read: it stands at the card's first turn,
  // pointing back, and a click reads that page in.
  const earlier = group.members.every((m) => m.earlier);
  // In hand (pointed at here or from a list of the notes), a halo a shade
  // up from the ground, the head in front of any it stands among.
  const ring = open ? `0 0 0 1px ${color}` : hot || held ? "0 0 0 1px var(--bg), 0 0 0 3px color-mix(in srgb, var(--muted) 70%, transparent)" : "0 0 0 1px var(--bg)";
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={earlier ? `${more ? stackWords(group.members).replace(/ here$/, "") : moments ? `Moment ${moments}` : "A note"} on a page not read yet: read it in` : more ? stackWords(group.members) : `${moments ? `Moment ${moments}` : "Note"}: ${first.text}`}
      data-pin-kind={group.kind}
      aria-expanded={more || first.inside ? held : open}
      onMouseEnter={() => onHover(group.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(group.id)}
      onBlur={() => onHover(null)}
      onClick={() => onPick(group)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(group); } }}
      className="pointer-events-none absolute flex -translate-x-1/2 -translate-y-full flex-col items-center outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)]"
      style={{ left: group.sx, top: group.sy, zIndex: hot || held ? 1 : undefined, opacity: faded ? 1 - CARD_DIM_VEIL : undefined, visibility: covered ? "hidden" : undefined }}
      data-note-pin={first.id}
      data-faded={faded ? "" : undefined}
      data-covered={covered ? "" : undefined}
      data-pin-count={more ? group.members.length : undefined}
      data-hot={hot || held ? "" : undefined}
      data-pin-earlier={earlier ? "" : undefined}
    >
      <span
        className={`pointer-events-auto inline-flex min-w-[18px] cursor-pointer items-center justify-center gap-[3px] whitespace-nowrap rounded-full px-[5px] text-[10px] font-semibold tabular-nums leading-none ${earlier ? "opacity-70" : ""}`}
        style={{ height: PIN_HEAD, background: open ? "var(--panel)" : color, color: open ? color : "var(--bg)", boxShadow: `${ring}, var(--node-shadow)` }}
      >
        {earlier ? <span aria-hidden="true">{"\u2039"}</span> : null}
        {label ? <span>{label}</span> : <span aria-hidden="true" className="h-[6px] w-[6px] rounded-full" style={{ background: open ? color : "var(--bg)" }} data-pin-dot="" />}
      </span>
      <span className="flex flex-col items-center" style={{ height: pinNeedle(group) }} aria-hidden="true" data-pin-needle="">
        {group.lift ? (
          <>
            <span className="w-[2px]" style={{ height: PIN_LIFT_GAP, background: color }} />
            <span style={{ height: PIN_HEAD }} />
          </>
        ) : null}
        <span className="w-[2px]" style={{ height: PIN_NEEDLE, background: color }} />
      </span>
    </div>
  );
});

// Where a peek opens: under its chip from the chip's left edge, or over the
// chip when what it shows would not fit under it and fits better over; never
// on the chip, so the chip stays under the pointer. Its depth is measured
// before it paints, so it never shows in the wrong place first. What it holds
// fills it (PEEK_FILL) and takes the pointer only where it asks to (a stack
// held open to pick from).
const PEEK_FILL = { position: "relative", left: 0, top: 0, width: "100%", maxHeight: "inherit" };
function PeekFrame({ at, children }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  // Its own depth, uncapped (capped, what it holds shrinks to the cap), on
  // every render: what it shows grows as an agent's session is found.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cap = el.style.maxHeight;
    el.style.maxHeight = "none";
    const h = el.firstElementChild?.getBoundingClientRect().height || 0;
    el.style.maxHeight = cap;
    const next = h > at.below && at.above > at.below;
    if (next !== over) setOver(next);
  });
  const style = over
    ? { left: at.left, bottom: at.layerH - at.top + PEEK_GAP, width: GROWN_W, maxHeight: Math.max(80, at.above) }
    : { left: at.left, top: at.bottom + PEEK_GAP, width: GROWN_W, maxHeight: Math.max(80, at.below) };
  return <div ref={ref} className="pointer-events-none absolute z-[9] flex select-text flex-col" style={style} data-canvas-peek={over ? "over" : "under"} data-board-readable="">{children}</div>;
}

// What a cell the reader opened holds, beside its card: one at a time, over
// the canvas, until it is closed or another is opened. A cell can be kept: it
// stands under its card, tied to the cell.
// The cells of a read session that hold a string (its read's located
// cells), the ones on a row named for it first (a file's own row, for its
// path), each lot in time order.
function cellsHolding(read, loc, needle) {
  const at = (c) => read.times?.[c.turn] ?? Infinity;
  const n = String(needle || "").toLowerCase();
  const own = (c) => !!n && String(c.lane || "").toLowerCase().includes(n);
  return [...(loc?.cells || [])].sort((a, b) => (own(b) - own(a)) || at(a) - at(b));
}

// A file as sessions share it: its folder and name, which a clone elsewhere
// keeps the same.
function fileKey(path) {
  return String(path || "").replace(/\\/g, "/").split("/").filter(Boolean).slice(-2).join("/").toLowerCase();
}

// A search for what a cell's row is about, as other sessions name it: a
// file by its last two segments (its directory and its name, which a clone
// elsewhere keeps), any path ending so; a skill or a tool by its name. A name
// with a space or a quote in it has no such search.
function followOf(lane) {
  const plain = (v) => !!v && !/[\s"]/.test(v);
  if (lane?.domain === "files" && lane.path) {
    const tail = String(lane.path).replace(/\\/g, "/").split("/").filter(Boolean).slice(-2).join("/");
    return plain(tail) ? { kind: "search", q: `file:*${tail}`, label: `file:*${tail}`, what: "file", whose: "touched it" } : null;
  }
  const name = String(lane?.id || "").split(":").slice(2).join(":");
  if (lane?.domain === "skill" && plain(name)) return { kind: "search", q: `skill:${name}`, label: `skill:${name}`, what: "skill", whose: "used it" };
  if (lane?.domain === "other" && plain(name)) return { kind: "search", q: `tool:${name}`, label: `tool:${name}`, what: "tool", whose: "called it" };
  return null;
}

// `onFollow` ({ what, whose, go }): keeps a search for what the cell's row is
// about (a file, a skill, a tool) on the workspace, to find the other
// sessions that touched it.
function DetailPanel({ node, card, turns: recordTurns, style, onClose, onKeep = null, onFollow = null }) {
  const { color, lead, text, where, trail = null, body } = annotationParts(node, card, recordTurns);
  return (
    <div className="pointer-events-auto absolute z-10 flex select-text flex-col border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg" style={style} data-canvas-detail={node.kind} data-board-readable="">
      <div className="flex shrink-0 items-start gap-[8px] border-b border-[color:var(--line-soft)] px-[10px] py-[8px] text-[11.5px]">
        <span className="flex h-[16px] shrink-0 items-center"><InlineMark node={node} color={color} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-[6px]">
            {lead}
            {trail}
            <span className="tabular-nums text-[color:var(--muted)]">{where}</span>
          </div>
          {node.kind !== "claim" && node.kind !== "note" ? <div className="mt-[4px] break-words text-[12px] text-[color:var(--ink)]">{text}</div> : null}
        </div>
        {onFollow ? (
          <HoverTooltip placement="top" label={`Keep a search for this ${onFollow.what} on the workspace: the other sessions that ${onFollow.whose}, to lay on and see wired to it`}>
            <button type="button" onClick={onFollow.go} className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[8px] py-[1px] text-[11px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]" data-detail-follow="">
              {`Follow ${onFollow.what}`}
            </button>
          </HoverTooltip>
        ) : null}
        {onKeep ? (
          <button type="button" onClick={onKeep} className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[8px] py-[1px] text-[11px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]">
            Keep
          </button>
        ) : null}
        <button type="button" onClick={onClose} aria-label="Close" className="grid h-[18px] w-[18px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">
          {closeIcon}
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-[10px] py-[8px] pl-[38px] text-[11.5px] leading-[1.4]">{body}</div>
    </div>
  );
}

// The heights a card can be given: its head over its one "+k groups" row, to
// its head over every group open.
function cardHeightLimits(model, ruler) {
  const shape = new Map(model.lanes.map((l) => [l.id, { id: l.id, label: l.label, domain: l.domain, cells: NO_CELLS }]));
  const rowsH = (opts) => layoutRows(cardRows({ model, lanesById: shape, openDirs: EMPTY, showAll: EMPTY, ...opts })).height;
  const base = CARD_HEAD + ruler + 2;
  return {
    min: base + rowsH({ openGroups: EMPTY, only: new Set() }),
    max: base + rowsH({ openGroups: new Set((model.groups || []).map((g) => g.id)), only: null }),
  };
}

// A card the reader resized sideways: its own clock's ticks along a strip under
// its head, and how many times the board's scale it runs, with a way back.
// A card's scale against the board's, as its ruler or its head says it.
const scaleText = (s) => `\u00d7${s < 10 ? s.toFixed(1).replace(/\.0$/, "") : Math.round(s)}`;
// How far a card's body is spread across its head and list past the board's
// scale, once that shows.
const stretchText = (s) => (s >= 1.05 ? scaleText(s) : null);
// A card on a clock of its own: sized or moved by the reader, or spread
// across a head and list wider than its stretch of the board's.
const ownClock = (c) => !!c.scaled || c.stretch > 1.001;

// The stretch of the clock a card covers where the board draws it, in the
// clock's units: its trace's own stretch [a, b], moved along by `off` and
// sized by `m` (from its end, when the reader sized it from the left), as the
// board's layout places its body.
export function cardSpanU(a, b, { m = 1, off = 0, pinEnd = false } = {}) {
  const len = m * Math.max(0, b - a);
  const start = pinEnd ? b + off - len : a + off;
  return [start, start + len];
}

// A card head's second line, laid out between x0 and x1 so nothing in it
// runs into anything else, however little of the head is in view: how much
// lies off to the left and to the right (controls, at its two ends), how the
// review found it, how far its clock is spread, where it ran, what ran it.
// When it is short of room the two ends shorten to their arrows first, then
// the review's chip is clipped (and, with no room for even a word, left out),
// and the spread, where and what ran it take only what is left. `measure` and
// `clip` read text in a given font.
export const HEAD_FONT = { label: `10.5px ${SANS}`, chip: `500 10px ${SANS}`, where: `500 10.5px ${SANS}` };
const CHIP_PAD = 10;
const CHIP_MIN_W = 40;
const WHERE_MIN_W = 24;
const SUB_MIN_W = 40;
export function layoutHeadLine({ earlier = null, chip = null, later = null, spread = null, where = "", sub = null }, { x0, x1, measure, clip }) {
  const out = { earlier: null, chip: null, later: null, spread: null, where: null, sub: null };
  const room = x1 - x0;
  const chipW = chip ? measure(chip, HEAD_FONT.chip) + CHIP_PAD : 0;
  const whole = (earlier ? measure(earlier, HEAD_FONT.label) + 10 : 0) + (chip ? chipW + 7 : 0) + (later ? measure(later, HEAD_FONT.label) + 12 : 0);
  const short = whole > room;
  const nav = (text, arrow) => (short ? arrow : text);
  let limit = x1;
  if (later) {
    const text = nav(later, "\u203a");
    const w = measure(text, HEAD_FONT.label);
    if (w <= room) {
      out.later = { text, x: x1 - w, w };
      limit = x1 - w - 12;
    }
  }
  let mx = x0;
  if (earlier) {
    const text = nav(earlier, "\u2039");
    const w = measure(text, HEAD_FONT.label);
    if (mx + w <= limit) {
      out.earlier = { text, x: mx, w };
      mx += w + 10;
    }
  }
  if (chip) {
    if (mx + chipW <= limit) {
      out.chip = { text: chip, x: mx, w: chipW };
      mx += chipW + 7;
    } else if (limit - mx >= CHIP_MIN_W) {
      const w = limit - mx;
      out.chip = { text: clip(chip, w - CHIP_PAD, HEAD_FONT.chip), x: mx, w };
      mx = limit;
    }
  }
  if (spread) {
    const w = measure(spread, HEAD_FONT.label);
    if (limit - w - 10 >= mx + WHERE_MIN_W) {
      out.spread = { text: spread, x: limit - w, w };
      limit -= w + 10;
    }
  }
  if (where && limit - mx >= WHERE_MIN_W) {
    const text = clip(where, limit - mx, HEAD_FONT.where);
    const w = measure(text, HEAD_FONT.where);
    out.where = { text, x: mx, w };
    mx += w;
    if (sub && limit - mx > SUB_MIN_W) {
      const said = clip(`${DOT}${sub}`, limit - mx, HEAD_FONT.label);
      out.sub = { text: said, x: mx, w: measure(said, HEAD_FONT.label) };
    }
  }
  return out;
}

// The first of `texts` that fits in `room`, for a label with a shorter form.
export function firstThatFits(texts, room, measure) {
  for (const t of texts) if (measure(t) <= room) return t;
  return null;
}

// The moments a card puts on the board's clock: its turns' times once they
// are admitted (its record read, and every trace on the board in), else its
// census span spread evenly (no gap inside it wide enough to close), else its
// two ends.
export function clockPointsOf(c, admitted) {
  if (c.times && admitted.has(c.key)) return c.times;
  // Until every record is in, its span evenly: the session's own, as its head
  // gives it, even once its newest page is in (the clock keeps the room it
  // stood in by, rather than moving every card as each long record lands),
  // with as many points as its record will likely bring where its head says
  // how many requests it made (two messages each, as many as one read holds).
  const head = c.r?.head;
  const from = Number.isFinite(head?.firstMs) ? Math.min(head.firstMs, c.firstMs) : c.firstMs;
  if (Number.isFinite(from) && Number.isFinite(c.lastMs) && c.lastMs > from) {
    const requests = Number(head?.requests) || 0;
    const n = requests > 0 ? Math.max(2, Math.min(READ_MESSAGES, 2 * requests)) : 600;
    return Array.from({ length: n + 1 }, (_, i) => from + ((c.lastMs - from) * i) / n);
  }
  return [c.firstMs, c.lastMs];
}

// How a card `h` tall holds its head at camera `cam` (HOLD_CAM): drawn `k`
// times its own size from the card's top, both its lines (`lines` 2); or, the
// card too short for both to read, its name alone, its middle `mid` down the
// card. `foot` is where the head ends down the card, `tools` the size its
// buttons are held at.
export function headHold(cam, h) {
  if (!(cam < HOLD_CAM)) return { k: 1, lines: 2, mid: NAME_MID, foot: CARD_HEAD, tools: 1 };
  const want = HOLD_CAM / cam;
  const tools = (k) => Math.max(1, Math.min(k, (h - CARD_TOOLBAR_INSET) / (CARD_TOOLS_H + CARD_TOOLBAR_INSET)));
  const k2 = Math.max(1, Math.min(want, h / CARD_HEAD));
  // Both lines while its name reads at most a step smaller than held.
  if (k2 * cam >= HOLD_CAM * 0.85) return { k: k2, lines: 2, mid: NAME_MID * k2, foot: CARD_HEAD * k2, tools: tools(k2) };
  const k = Math.max(1, Math.min(want, (h - 4) / (2 * NAME_HALF)));
  const mid = Math.min(h / 2, NAME_MID * k);
  return { k, lines: 1, mid, foot: Math.min(h, mid + NAME_HALF * k + 2), tools: tools(k) };
}

// The scale the words being drawn are held at (holdHead, a held label), for
// fadeSmallText: drawing is synchronous, so one value serves every canvas.
let heldText = 1;

// Lays a card's head, held (`hold`, headHold), over the top of its rows: the
// card's own surface under it, cut to the card, and what is drawn until
// `done` laid out at the head's own size from (`edge`, `cy`). Unheld, nothing.
function holdHead(ctx, theme, hold, { x0, x1, cy, h, edge }) {
  if (hold.k === 1 && hold.lines === 2) return () => {};
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, cy, x1 - x0, h);
  ctx.clip();
  ctx.fillStyle = theme.panel;
  ctx.fillRect(x0 + 1, cy + 1, x1 - x0 - 2, hold.foot - 1);
  ctx.translate(edge, cy + hold.mid);
  ctx.scale(hold.k, hold.k);
  ctx.translate(-edge, -(cy + NAME_MID));
  heldText = hold.k;
  return () => {
    heldText = 1;
    ctx.restore();
  };
}

// Words drawn from here until the returned call fade out as the camera
// (`cam`) makes them too small on screen to read (TEXT_FULL_PX,
// TEXT_GONE_PX); held ones (heldText) read at the size they are held at.
export function fadeSmallText(ctx, cam) {
  if (!(cam < 1)) return () => {};
  const density = textDensity();
  const own = Object.prototype.hasOwnProperty.call(ctx, "fillText");
  const fill = ctx.fillText;
  let font = null;
  let px = 12;
  ctx.fillText = function fadedText(text, x, y, ...rest) {
    if (this.font !== font) {
      font = this.font;
      px = Number.parseFloat(/(\d+(?:\.\d+)?)px/.exec(String(font))?.[1]) || 12;
    }
    const s = px * cam * heldText * density;
    if (s >= TEXT_FULL_PX) return fill.call(this, text, x, y, ...rest);
    if (s <= TEXT_GONE_PX) return undefined;
    const a = this.globalAlpha;
    this.globalAlpha = a * ((s - TEXT_GONE_PX) / (TEXT_FULL_PX - TEXT_GONE_PX));
    fill.call(this, text, x, y, ...rest);
    this.globalAlpha = a;
    return undefined;
  };
  return () => {
    if (own) ctx.fillText = fill;
    else delete ctx.fillText;
  };
}

function drawCardRuler(ctx, c, st, theme, hits, { cx, cy, W, SX }) {
  const y0 = cy + CARD_HEAD;
  const x0 = Math.max(cx + (c.rail || 0), MACH_W);
  const x1 = Math.min(cx + c.w, W);
  if (x1 <= x0) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, y0, x1 - x0, CARD_RULER_H);
  ctx.clip();
  ctx.fillStyle = theme.lineSoft;
  ctx.fillRect(x0, y0 + CARD_RULER_H - 1, x1 - x0, 1);
  const base = c.x + (c.rail || 0) - c.win.u0 * c.kx;
  const ticks = rulerTicks(st.clock, c.kx, PAD_L + c.win.u0 * c.kx - 60, PAD_L + c.win.u1 * c.kx + 60);
  ctx.font = `10px ${SANS}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  let last = -Infinity;
  for (const t of ticks) {
    const x = SX(base + (t.x - PAD_L));
    if (x < x0 - 2 || x > x1) continue;
    ctx.fillStyle = theme.lineStrong;
    ctx.fillRect(Math.round(x), y0 + CARD_RULER_H - 4, 1, 3);
    const tw = ctx.measureText(t.label).width;
    if (x + 3 < last + 8 || x + 3 + tw > x1 - 70) continue;
    ctx.fillStyle = t.minor ? theme.mutedSoft : theme.muted;
    ctx.fillText(t.label, x + 3, y0 + 11);
    last = x + 3 + tw;
  }
  const zoomed = Math.abs((c.scale || 1) - 1) > 0.01;
  ctx.font = `500 10px ${SANS}`;
  // Where the ruler in view is short, what it was changed by gives way to
  // "reset" alone, and that to nothing rather than run off the card's edge.
  // Cut for the focus on the evidence, there is nothing of the reader's to
  // reset.
  const said = c.focus ? null : firstThatFits([
    [zoomed ? scaleText(c.scale) : null, c.moved ? "moved" : null, "reset"].filter(Boolean).join(DOT),
    "reset",
  ], x1 - x0 - 12, (t) => ctx.measureText(t).width);
  if (said) {
    const tw = ctx.measureText(said).width;
    ctx.fillStyle = theme.bg;
    ctx.fillRect(x1 - tw - 10, y0 + 1, tw + 8, CARD_RULER_H - 3);
    ctx.fillStyle = st.hoverReset === c.key ? theme.ink : theme.green;
    ctx.fillText(said, x1 - tw - 6, y0 + 11);
  }
  ctx.restore();
  if (said) hits.resets.push({ key: c.key, x0: x1 - ctx.measureText(said).width - 10, x1, y0, y1: y0 + CARD_RULER_H });
}

// A card on the board, drawn as one surface: its head (what it was, where it
// ran, how the review found it) over its rows, a hairline between. The head's
// glyph and words sit on the rows' own grid, so the card reads down one edge;
// where the card runs off the canvas its head says how much more there is that
// way. Returns the rows' held rail position, for the hit test.
function drawBoardCard(ctx, c, st, theme, hits, { cx, cy, hx0, headW, W, H, SX, SY }) {
  const data = st.cards.get(c.key);
  const selected = st.sel?.key === c.key && c.key !== FOCAL;
  const hovered = st.hoverKey === c.key;
  const bodyY = SY(c.bodyY);
  const bodyH = c.y + c.h - c.bodyY - 2;
  const x0 = Math.min(cx, hx0);
  const x1 = Math.max(cx + c.w, hx0 + headW);
  const y1 = cy + c.h;
  // Run all but off the board, it shows as its surface and edge alone: words
  // cut to a few letters there read as a fault, not a card.
  const sliver = Math.min(x1, W) - Math.max(x0, MACH_W) < SLIVER_W;
  const surface = { ...theme, bg: theme.panel };
  ctx.save();
  // A preview in hand lifts its card off the ground, as one carried on the
  // board lifts, within the margins the preview gives it.
  const inHand = !!(st.preview && st.previewHot);
  if (st.resizingKey === c.key || st.movingKey === c.key) {
    ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 4;
  } else if (inHand) {
    ctx.shadowColor = theme.nodeShadowLift;
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 4;
  } else {
    ctx.shadowColor = theme.nodeShadow;
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
  }
  ctx.fillStyle = theme.panel;
  roundRect(ctx, x0, cy, x1 - x0, c.h, 2);
  ctx.fill();
  ctx.restore();
  // The rows, their names held at the canvas's left edge while the card runs
  // on under them.
  const railPin = Math.min(Math.max(cx, MACH_W), cx + c.w - (c.rail || 0));
  if (!c.folded && !sliver) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.max(cx, 0), bodyY, Math.max(0, Math.min(cx + c.w, W) - Math.max(cx, 0)), bodyH);
    ctx.clip();
    const common = { scrollY: 0, viewW: W, viewH: H, oy: bodyY, pinY: false, railH: bodyH, upperGroups: false, neutralMarks: true };
    if (railPin > cx) drawScene(ctx, c.scene, surface, { ...common, scrollX: railPin - cx, ox: railPin, pinX: true });
    else drawScene(ctx, c.scene, surface, { ...common, scrollX: 0, ox: cx, pinX: false });
    ctx.restore();
  }
  if (c.scaled && !c.folded && !sliver) drawCardRuler(ctx, c, st, surface, hits, { cx, cy, W, SX });
  // What the chat found inside it, along the foot of its head at their times.
  if (c.evidence?.length && !sliver) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.max(cx + (c.rail || 0), MACH_W), cy, Math.max(0, Math.min(cx + c.w, W) - Math.max(cx + (c.rail || 0), MACH_W)), c.h);
    ctx.clip();
    for (const m of c.evidence) {
      const mx = SX(m.x);
      const my = SY(m.y);
      const lit = m.isNew || (st.claimFocus && m.claims?.includes(st.claimFocus));
      ctx.fillStyle = theme.panel;
      ctx.fillRect(mx - 4, my - 4, 8, 8);
      drawGlyph(ctx, m.kind, mx, my, lit ? theme.graph : alphaMix(theme.ink, 60));
      hits.graphMarks.push({ key: c.key, mark: m, x0: mx - 5, x1: mx + 5, y0: my - 6, y1: my + 6 });
    }
    ctx.restore();
  }
  const hold = headHold(st.cam || 1, c.h);
  if (!sliver) {
    // The head, on the rows' grid; zoomed far out, held at a size to read.
    const edge = x0 < cx ? Math.max(x0, MACH_W) : railPin;
    const done = holdHead(ctx, theme, hold, { x0, x1, cy, h: c.h, edge });
    // Where a place in the held head is on the board.
    const back = (x, y) => [edge + (x - edge) * hold.k, cy + hold.mid + (y - cy - NAME_MID) * hold.k];
    const gx = edge + CARD_GLYPH_X;
    const tx = edge + CARD_TEXT_X;
    const right = edge + (Math.min(x1, W) - edge) / hold.k - 10;
    if (c.key === FOCAL) {
      ctx.fillStyle = theme.ink;
      ctx.beginPath();
      ctx.moveTo(gx, cy + 9);
      ctx.lineTo(gx + 4.5, cy + 13.5);
      ctx.lineTo(gx, cy + 18);
      ctx.lineTo(gx - 4.5, cy + 13.5);
      ctx.closePath();
      ctx.fill();
    } else if (data?.hue) {
      ctx.fillStyle = data.hue;
      ctx.beginPath();
      ctx.arc(gx, cy + 13.5, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    // Its buttons sit over the head's right end while it is in hand; the
    // anchor keeps its own, pressed, there.
    const controls = hovered || selected || st.resizingKey === c.key || st.toolbarKey === c.key || inHand;
    // A preview's host can lay its own control over the anchor's head
    // (`preview.headRoom`): the title stops short of it.
    const room = Math.max((controls || (c.folded && !c.rolled) ? CARD_TOOLBAR_W + 4 : c.key === FOCAL ? CARD_TOOL + 10 : 0) * (hold.tools / hold.k), c.key === FOCAL && st.preview ? st.headRoom || 0 : 0);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.font = `500 12.5px ${SANS}`;
    ctx.fillStyle = theme.ink;
    ctx.fillText(clipText(ctx, data?.titleText || "", Math.max(0, right - tx - room)), tx, cy + 18);
    if (hold.lines === 2) {
      // Its second line: how much of it lies off to the left, how the review
      // found it, where it ran (and that it anchors the board), what ran it; at
      // its end, how much lies off to the right.
      // What lies off either side, read on its own clock when it has one.
      const tAt = ownClock(c) && c.win && c.kx
        ? (sx) => st.clock.inv(c.win.u0 + (sx - cx - (c.rail || 0)) / c.kx)
        : (sx) => st.clock.inv((sx - MACH_W + st.view.x - PAD_L) / st.k);
      // Cut to a stretch of its time (focused on the evidence), what the cut
      // leaves out either side too.
      const hidLeft = cx < MACH_W - 1 && Number.isFinite(data?.firstMs) ? tAt(MACH_W + (c.rail || 0)) - data.firstMs : c.crop?.before || 0;
      const hidRight = cx + c.w > W + 1 && Number.isFinite(data?.lastMs) ? data.lastMs - tAt(W - 8) : c.crop?.after || 0;
      const flag = st.tags?.get(c.key);
      const line = layoutHeadLine({
        earlier: hidLeft > 0 ? `\u2039 ${spanText(hidLeft)} earlier` : null,
        chip: flag?.text || null,
        later: hidRight > 0 ? `${spanText(hidRight)} later \u203a` : null,
        spread: stretchText(c.stretch),
        where: data?.machine || "Unknown machine",
        sub: c.folded && !c.rolled ? [`folded${c.attachCount ? `, ${c.attachCount} attached` : ""}`, data?.subText].filter(Boolean).join(DOT)
          : c.rolled && c.attachCount ? [`${c.attachCount} attached`, data?.subText].filter(Boolean).join(DOT) : data?.subText,
      }, {
        x0: tx, x1: c.key === FOCAL && st.preview && st.headRoom ? Math.max(tx, right - st.headRoom) : right,
        measure: (t, font) => { ctx.font = font; return ctx.measureText(t).width; },
        clip: (t, w, font) => { ctx.font = font; return clipText(ctx, t, w); },
      });
      ctx.textAlign = "left";
      for (const [side, at] of [["l", line.earlier], ["r", line.later]]) {
        if (!at) continue;
        ctx.font = HEAD_FONT.label;
        ctx.fillStyle = st.hoverCont === `${c.key}:${side}` ? theme.green : theme.ink;
        ctx.fillText(at.text, at.x, cy + 34);
        const [ax0, ay0] = back(at.x - 3, cy + 23);
        const [ax1, ay1] = back(at.x + at.w + 3, cy + 39);
        hits.cont.push({ key: c.key, side, x0: ax0, x1: ax1, y0: ay0, y1: ay1 });
      }
      if (line.chip) {
        sevChip(ctx, theme, line.chip.text, severityInk(theme, flag.severity), line.chip.x, cy + 30, line.chip.w);
        // A click on the tag lists what it counts, to open (a preview's
        // tag only says it).
        if (!st.preview && !st.still) {
          const [fx0, fy0] = back(line.chip.x - 2, cy + 23);
          const [fx1, fy1] = back(line.chip.x + line.chip.w + 2, cy + 39);
          hits.flags.push({ key: c.key, x0: fx0, x1: fx1, y0: fy0, y1: fy1 });
        }
      }
      if (line.spread) {
        ctx.font = HEAD_FONT.label;
        ctx.fillStyle = theme.muted;
        ctx.fillText(line.spread.text, line.spread.x, cy + 34);
      }
      if (line.where) {
        ctx.font = HEAD_FONT.where;
        ctx.fillStyle = alphaMix(theme.ink, 82);
        ctx.fillText(line.where.text, line.where.x, cy + 34);
      }
      if (line.sub) {
        ctx.font = HEAD_FONT.label;
        ctx.fillStyle = theme.muted;
        ctx.fillText(line.sub.text, line.sub.x, cy + 34);
      }
    }
    done();
  }
  // The hairline between head and rows, and the card's one edge.
  ctx.fillStyle = theme.lineSoft;
  if (!c.folded && hold.foot < c.h - 2) ctx.fillRect(x0 + 1, cy + hold.foot, x1 - x0 - 2, 1);
  const lit = st.litKey === c.key;
  ctx.strokeStyle = lit ? theme.green : selected ? alphaMix(theme.ink, 70) : hovered || inHand ? theme.lineStrong : theme.line;
  ctx.lineWidth = lit ? 2 : 1;
  roundRect(ctx, x0 + 0.5, cy + 0.5, x1 - x0 - 1, c.h - 1, 2);
  ctx.stroke();
  ctx.lineWidth = 1;
  // Its head answers as a head everywhere but where its buttons sit.
  hits.cards.push({ key: c.key, x0, x1, bodyX1: x1, y0: cy, y1, headY1: Math.max(bodyY, cy + hold.foot), bodyY, ox: railPin, rail: c.rail || 0, cx });
}

// A card still reading its record, where it will be and at the depth it will
// likely take: its frame dashed, as the page draws what is still to come, its
// head (its glyph, its name, that it is reading). The rows under it pulse in
// the page (data-card-loading).
function drawLoadingCard(ctx, lc, st, theme, { SX, SY, W }) {
  const data = st.cards.get(lc.key);
  const x0 = SX(Math.min(lc.x, lc.headX ?? lc.x));
  const x1 = SX(Math.max(lc.x + lc.w, (lc.headX ?? lc.x) + (lc.headW ?? lc.w)));
  const cy = SY(lc.y);
  ctx.fillStyle = theme.panel;
  roundRect(ctx, x0, cy, x1 - x0, lc.h, 2);
  ctx.fill();
  ctx.save();
  ctx.setLineDash([4, 3]);
  ctx.strokeStyle = theme.lineStrong;
  ctx.lineWidth = 1;
  roundRect(ctx, x0 + 0.5, cy + 0.5, x1 - x0 - 1, lc.h - 1, 2);
  ctx.stroke();
  ctx.restore();
  const edge = Math.max(x0, MACH_W);
  const hold = headHold(st.cam || 1, lc.h);
  const done = holdHead(ctx, theme, hold, { x0, x1, cy, h: lc.h, edge });
  const right = edge + (Math.min(x1, W) - edge) / hold.k - 10;
  if (data?.hue) {
    ctx.fillStyle = data.hue;
    ctx.beginPath();
    ctx.arc(edge + CARD_GLYPH_X, cy + 13.5, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = `500 12.5px ${SANS}`;
  ctx.fillStyle = theme.ink;
  ctx.fillText(clipText(ctx, data?.titleText || "", Math.max(40, right - edge - CARD_TEXT_X)), edge + CARD_TEXT_X, cy + 18);
  if (hold.lines === 2) {
    ctx.font = `10.5px ${SANS}`;
    ctx.fillStyle = theme.muted;
    const said = lc.status === "error" ? `Its record could not be read${data?.error ? ` (${data.error})` : ""}` : lc.status === "unread" ? "Waiting to read its record" : "Reading its record";
    ctx.fillText(clipText(ctx, [data?.machine, said].filter(Boolean).join(DOT), Math.max(40, right - edge - CARD_TEXT_X)), edge + CARD_TEXT_X, cy + 34);
  }
  done();
  ctx.fillStyle = theme.lineSoft;
  if (hold.foot < lc.h - 2) ctx.fillRect(x0 + 1, cy + hold.foot, x1 - x0 - 2, 1);
}

// What the chat's graph holds, drawn as the chat's knowledge graph draws it: a
// square per process, a dot per message, a diamond per prompt.
function drawGlyph(ctx, kind, x, y, fill) {
  ctx.fillStyle = fill;
  if (kind === "message") {
    ctx.beginPath();
    ctx.arc(x, y, 2.6, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (kind === "prompt") {
    ctx.beginPath();
    ctx.moveTo(x, y - 3.3);
    ctx.lineTo(x + 3.3, y);
    ctx.lineTo(x, y + 3.3);
    ctx.lineTo(x - 3.3, y);
    ctx.closePath();
    ctx.fill();
    return;
  }
  ctx.fillRect(x - 2.4, y - 2.4, 4.8, 4.8);
}

// How many of a machine's or a search's sessions are on the workspace as
// cards, and how many are not yet (the rows shown of them, when fewer).
function ownCounts(c) {
  const on = c.members || 0;
  const rest = c.rows.length - on + (c.more || 0);
  const shown = c.more ? `${c.rows.length - on} shown` : null;
  if (!on) return [`${rest} session${rest === 1 ? "" : "s"} not on the workspace yet`, shown];
  return [`${on} on the workspace`, rest ? `${rest} not yet` : null, shown];
}

// A list card, a row each on the board's own axis: a machine's sessions and
// work, a search's matches, a workspace's sessions. Its head reads like a
// trace's.
function drawGraphCard(ctx, c, st, theme, hits, { cx, cy, hx0, headW, W, SX, SY }) {
  const hovered = st.hoverKey === c.key;
  const x0 = Math.min(cx, hx0);
  const x1 = Math.max(cx + c.w, hx0 + headW);
  // As a trace's card, run all but off the board it is its surface alone.
  const sliver = Math.min(x1, W) - Math.max(x0, MACH_W) < SLIVER_W;
  ctx.save();
  if (st.movingKey === c.key) {
    ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 4;
  } else {
    ctx.shadowColor = theme.nodeShadow;
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
  }
  ctx.fillStyle = theme.panel;
  roundRect(ctx, x0, cy, x1 - x0, c.h, 2);
  ctx.fill();
  ctx.restore();
  const railPin = Math.min(Math.max(cx, MACH_W), cx + c.w - c.rail);
  const edge = x0 < cx ? Math.max(x0, MACH_W) : railPin;
  // Zoomed in on, a workspace inside pushes toward going in.
  const push = c.workspace && st.entering?.key === c.key ? st.entering.p : 0;
  const hold = headHold(st.cam || 1, c.h);
  // Under a head held larger than the card's own, its list starts below it,
  // as its picture does: a short list would be all under the head.
  const drop = c.folded || c.inset ? 0 : Math.max(0, hold.foot - CARD_HEAD);
  // A workspace inside: the ground its picture lies on (the picture draws its
  // cards alone), and where each of them is, to point at.
  const under = c.inset ? Math.max(0, c.y + hold.foot - c.inset.y) : 0;
  if (c.inset && c.inset.h - under >= NEST_INSET_SHOWN) {
    const ix = SX(c.inset.x);
    const iy = SY(c.inset.y + under);
    const ih = c.inset.h - under;
    ctx.fillStyle = theme.bg;
    ctx.fillRect(ix, iy, c.inset.w, ih);
    const dots = getDotTilePattern(ctx, { color: theme.diagramDot || theme.lineSoft, ...GROUND_DOTS });
    if (dots) {
      ctx.save();
      ctx.translate(ix, iy);
      ctx.fillStyle = dots;
      ctx.fillRect(0, 0, c.inset.w, ih);
      ctx.restore();
    }
    // Carried, the card takes its picture with it: what the picture last drew.
    const pc = st.movingKey === c.key ? st.pictureCanvas?.(c.key) : null;
    const pw = pc ? parseFloat(pc.style.width) : 0;
    const ph = pc ? parseFloat(pc.style.height) : 0;
    if (pw > 0 && ph > 0 && pc.width > 0) {
      const k = Math.min(c.inset.w / pw, ih / ph);
      ctx.drawImage(pc, ix, iy, pw * k, ph * k);
    }
    for (const r of st.pictures?.get(c.key) || []) {
      if (r.y >= ih) continue;
      hits.insetCards.push({ key: c.key, inner: r.key, title: r.title, x0: ix + r.x, x1: ix + r.x + r.w, y0: iy + r.y, y1: iy + Math.min(ih, r.y + r.h) });
    }
  }
  // Its head, drawn once its rows are: zoomed far out it is held at a size to
  // read, over the top of them.
  const drawHead = () => {
    if (sliver) return;
    const done = holdHead(ctx, theme, hold, { x0, x1, cy, h: c.h, edge });
    const gx = edge + CARD_GLYPH_X;
    const tx = edge + CARD_TEXT_X;
    const right = edge + (Math.min(x1, W) - edge) / hold.k - 10;
    ctx.strokeStyle = c.isNew ? theme.graph : theme.muted;
    ctx.lineWidth = 1.2;
    if (c.search) {
      ctx.beginPath();
      ctx.arc(gx, cy + 13.5, 3.6, 0, Math.PI * 2);
      ctx.stroke();
    } else if (c.workspace) {
      // A workspace inside: one board behind another.
      ctx.strokeRect(gx - 2, cy + 8.5, 6, 6);
      ctx.strokeRect(gx - 4.5, cy + 11, 6, 6);
    } else ctx.strokeRect(gx - 3.5, cy + 10, 7, 7);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.font = `500 12.5px ${SANS}`;
    ctx.fillStyle = theme.ink;
    const room = (hovered || st.toolbarKey === c.key || c.folded ? GRAPH_TOOLBAR_W + 4 : 0) * (hold.tools / hold.k);
    ctx.fillText(clipText(ctx, c.label || "", Math.max(40, right - tx - room)), tx, cy + 18);
    if (hold.lines === 2) {
      const counts = new Map();
      for (const r of c.rows) for (const e of r.events) counts.set(e.kind, (counts.get(e.kind) || 0) + 1);
      const said = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => findWords(k, n));
      const people = (c.people || []).map((p) => p.label).filter(Boolean);
      const sessions = c.rows.filter((r) => r.kind === "session").length;
      const allSessions = sessions + (c.more || 0);
      ctx.font = `10.5px ${SANS}`;
      ctx.fillStyle = theme.muted;
      const spread = stretchText(c.stretch);
      const spreadW = spread ? ctx.measureText(spread).width + 10 : 0;
      if (spread) {
        ctx.textAlign = "right";
        ctx.fillText(spread, right, cy + 34);
        ctx.textAlign = "left";
      }
      // A workspace inside: what it holds, where and when, then the worst
      // the review found in it and how many of its sessions are here too.
      const f = c.workspace && !push ? c.facts : null;
      const what = c.workspace ? (push ? ["Keep zooming in to go in"]
        : f?.sessions ? [`${plural(f.sessions, "session")}${f.machines ? ` on ${plural(f.machines, "machine")}` : ""}`]
          : [c.sub || "A workspace"])
        : c.machine || c.search
          ? [c.machine && people.length ? people.slice(0, 2).join(", ") : null, ...ownCounts(c)]
          : [c.folded ? `folded, ${plural(c.rows.length, "row")}` : null, allSessions ? `${plural(allSessions, "session")} not on this workspace` : null, c.more ? `${sessions} shown` : null, ...said, people.length ? `run as ${people.slice(0, 3).join(", ")}` : null];
      const end = right - spreadW;
      const lead = clipText(ctx, what.filter(Boolean).join(DOT), Math.max(24, end - tx));
      ctx.fillText(lead, tx, cy + 34);
      let fx = tx + ctx.measureText(lead).width + 8;
      if (f?.flag) {
        ctx.font = `500 10px ${SANS}`;
        const fw = ctx.measureText(f.flag.text).width + 10;
        if (fx + fw <= end) {
          sevChip(ctx, theme, f.flag.text, severityInk(theme, f.flag.severity), fx, cy + 30, fw);
          fx += fw + 8;
        }
      }
      // Then how many are here too and when, as much as fits: on a narrow
      // card the worst finding keeps its place over them.
      const rest = f ? [f.onBoard ? `${f.onBoard} also on this workspace` : null, rangeText(c.firstMs, c.lastMs)].filter(Boolean).join(DOT) : "";
      if (rest && fx + 40 <= end) {
        ctx.font = `10.5px ${SANS}`;
        ctx.fillStyle = theme.muted;
        ctx.fillText(clipText(ctx, rest, end - fx), fx, cy + 34);
      }
    }
    done();
    ctx.fillStyle = theme.lineSoft;
    if (!c.folded && hold.foot < c.h - 2) ctx.fillRect(x0 + 1, cy + hold.foot, x1 - x0 - 2, 1);
  };
  // Its rows: what each is on the rail, held at the canvas's edge; its span
  // and its marks along the axis. Folded, only its marks, along its head's foot.
  // A machine's, a search's or a workspace's rows are sessions: when each
  // began on the rail, its name beside its span.
  const own = c.machine || c.search || c.workspace;
  const hotRow = st.hoverGrow?.key === c.key ? st.hoverGrow.row : null;
  ctx.save();
  ctx.beginPath();
  if (c.folded) ctx.rect(Math.max(cx, 0), cy, Math.max(0, Math.min(cx + c.w, W) - Math.max(cx, 0)), c.h);
  else ctx.rect(Math.max(cx, 0), SY(c.bodyY), Math.max(0, Math.min(cx + c.w, W) - Math.max(cx, 0)), c.h - CARD_HEAD - 1);
  ctx.clip();
  const listed = !c.folded && !c.inset;
  if (listed) c.rows.forEach((r, i) => {
    const ry = SY(r.y + drop);
    const hot = hotRow === r.key;
    if (hot) {
      ctx.fillStyle = alphaMix(theme.ink, 6);
      ctx.fillRect(railPin + 1, ry, cx + c.w - railPin - 2, GRAPH_ROW_H);
    }
    if (i) {
      ctx.fillStyle = alphaMix(theme.lineSoft, 70);
      ctx.fillRect(railPin + 6, ry, cx + c.w - railPin - 12, 1);
    }
    if (r.spanX) {
      const a = SX(r.spanX.a);
      const bw = Math.max(3, SX(r.spanX.b) - a);
      // On the workspace as a card: a little brighter; its mark on the rail
      // is the colour of the wire to it.
      ctx.fillStyle = alphaMix(theme.ink, r.member ? (hot ? 30 : 20) : hot ? 24 : 10);
      roundRect(ctx, a, ry + GRAPH_ROW_H / 2 - 4, bw, 8, 2);
      ctx.fill();
      if (own && !sliver) {
        // On a long span, over it; else after it where it fits, else before
        // it where there is more room.
        const label = [r.label, r.detail].filter(Boolean).join(" ");
        const lo = railPin + c.rail + 6;
        const hi = Math.min(cx + c.w, W) - 8;
        const after = hi - (a + bw + 6);
        const before = a - 6 - lo;
        const over = Math.min(a + bw, hi) - Math.max(a, lo) - 8;
        ctx.font = `10.5px ${SANS}`;
        ctx.fillStyle = hot || r.member ? theme.ink : alphaMix(theme.ink, 80);
        if (over >= 90) {
          ctx.fillText(clipText(ctx, label, over), Math.max(a, lo) + 6, ry + 14.5);
        } else if (after >= Math.min(90, ctx.measureText(label).width) || after >= before) {
          if (after > 14) ctx.fillText(clipText(ctx, label, after), a + bw + 6, ry + 14.5);
        } else if (before > 14) {
          ctx.textAlign = "right";
          ctx.fillText(clipText(ctx, label, before), a - 6, ry + 14.5);
          ctx.textAlign = "left";
        }
      }
    }
  });
  const claimOf = new Map((st.investigation?.claims || []).map((cl) => [cl.id, cl]));
  c.marks.forEach((m, i) => {
    const mx = SX(m.x);
    const my = SY(m.y + drop);
    if (mx < railPin + c.rail - 2) return;
    const focus = st.claimFocus && m.claims?.includes(st.claimFocus);
    drawGlyph(ctx, m.kind, mx, my, m.isNew || focus ? theme.graph : alphaMix(theme.ink, 72));
    // What it backs: the claims it is evidence for, numbered as the claims'
    // row numbers them, filled where the graph bears the claim out.
    const backs = (m.claims || []).map((id) => claimOf.get(id)).filter(Boolean).slice(0, 2);
    // It answers across its badges as well as its glyph.
    let reach = mx + 5;
    if (backs.length) {
      ctx.font = `600 9px ${SANS}`;
      ctx.textAlign = "center";
      let bx = mx + 6;
      for (const cl of backs) {
        const bw = Math.max(11, ctx.measureText(String(cl.n)).width + 5);
        const shown = cl.status === "supported";
        ctx.fillStyle = shown ? theme.graph : theme.panel;
        ctx.fillRect(bx, my - 5.5, bw, 11);
        ctx.strokeStyle = theme.graph;
        ctx.lineWidth = st.claimFocus === cl.id ? 2 : 1;
        ctx.strokeRect(bx + 0.5, my - 5, bw - 1, 10);
        ctx.fillStyle = shown ? theme.bg : theme.graph;
        ctx.fillText(String(cl.n), bx + bw / 2, my + 3);
        reach = bx + bw;
        bx += bw + 2;
      }
      ctx.textAlign = "left";
      ctx.lineWidth = 1;
    } else if (m.events.length > 1) {
      ctx.font = `9px ${SANS}`;
      const label = String(m.events.length);
      const next = c.marks[i + 1];
      if (!next || next.row !== m.row || SX(next.x) - 5 - (mx + 5) > ctx.measureText(label).width + 2) {
        ctx.fillStyle = theme.muted;
        ctx.fillText(label, mx + 5, my + 3);
      }
    }
    hits.graphMarks.push({ key: c.key, mark: m, x0: mx - 5, x1: reach, y0: my - 7, y1: my + 7 });
  });
  ctx.restore();
  // The rail over its rows.
  if (listed) {
    ctx.fillStyle = theme.panel;
    ctx.fillRect(railPin + 1, SY(c.bodyY) + 1, c.rail - 1, c.h - CARD_HEAD - 3);
    ctx.fillStyle = theme.lineSoft;
    ctx.fillRect(railPin + c.rail, SY(c.bodyY) + 1, 1, c.h - CARD_HEAD - 3);
  }
  ctx.font = `10.5px ${SANS}`;
  if (listed) for (const r of c.rows) {
    const ry = SY(r.y + drop);
    // Opening, only the rows its frame has reached.
    if (r.y + drop + GRAPH_ROW_H > c.y + c.h) break;
    if (hotRow === r.key) {
      ctx.fillStyle = alphaMix(theme.ink, 6);
      ctx.fillRect(railPin + 1, ry, c.rail - 1, GRAPH_ROW_H);
    }
    // The chat's rows: a session by its name, a machine's own work by the
    // machine's.
    const label = c.workspace && r.machine ? r.machine
      : own ? (r.span ? `${DAY_FMT.format(r.span.from)} ${hhmm(r.span.from)}` : "")
        : [r.label, r.detail].filter(Boolean).join(" ");
    const layable = !own && hotRow === r.key && r.kind === "session" && !r.member && !sliver;
    const layW = layable ? ctx.measureText("Lay it on").width + 24 : 0;
    ctx.fillStyle = own && !r.member ? theme.muted : theme.ink;
    if (!sliver) ctx.fillText(clipText(ctx, label, c.rail - CARD_TEXT_X + 4 - (layW ? layW + 4 : 0)), railPin + CARD_TEXT_X, ry + 14.5);
    if (r.kind === "session" && !sliver) drawGlyph(ctx, "message", railPin + CARD_GLYPH_X, ry + GRAPH_ROW_H / 2, r.member ? theme.join : alphaMix(theme.ink, 45));
    else if (!sliver) {
      ctx.strokeStyle = alphaMix(theme.ink, 45);
      ctx.lineWidth = 1;
      ctx.strokeRect(railPin + CARD_GLYPH_X - 2.5, ry + GRAPH_ROW_H / 2 - 2.5, 5, 5);
    }
    // A row of sessions answers across the card, its name being there.
    hits.graphRows.push({ key: c.key, row: r, x0: railPin, x1: Math.min(cx + c.w, W), y0: ry, y1: ry + GRAPH_ROW_H });
    // A session the chat found and the board does not hold, pointed at: the
    // way to lay it on, at the end of its name.
    if (layable) {
      const said = "Lay it on";
      const px1 = railPin + c.rail - 4;
      const px0 = px1 - layW;
      const py0 = ry + 3;
      const ph = GRAPH_ROW_H - 6;
      ctx.fillStyle = theme.panel;
      roundRect(ctx, px0, py0, px1 - px0, ph, 2);
      ctx.fill();
      ctx.strokeStyle = theme.lineStrong;
      ctx.lineWidth = 1;
      roundRect(ctx, px0 + 0.5, py0 + 0.5, px1 - px0 - 1, ph - 1, 2);
      ctx.stroke();
      ctx.fillStyle = theme.ink;
      ctx.fillRect(px0 + 7, py0 + ph / 2 - 0.5, 7, 1);
      ctx.fillRect(px0 + 10, py0 + ph / 2 - 3.5, 1, 7);
      ctx.fillText(clipText(ctx, said, px1 - px0 - 22), px0 + 18, ry + 14.5);
      hits.graphLays.push({ key: c.key, row: r, x0: px0, x1: px1, y0: py0, y1: py0 + ph });
    }
  }
  drawHead();
  const lit = st.litKey === c.key;
  if (push) {
    ctx.fillStyle = theme.green;
    ctx.fillRect(x0 + 1, cy + hold.foot - 1, Math.max(2, (x1 - x0 - 2) * push), 2);
  }
  ctx.strokeStyle = lit || push ? theme.green : hovered ? theme.lineStrong : theme.line;
  ctx.lineWidth = lit ? 2 : 1;
  roundRect(ctx, x0 + 0.5, cy + 0.5, x1 - x0 - 1, c.h - 1, 2);
  ctx.stroke();
  ctx.lineWidth = 1;
  hits.cards.push({ key: c.key, graph: true, x0, x1, bodyX1: x1, y0: cy, y1: cy + c.h, headY1: Math.max(SY(c.bodyY), cy + hold.foot), bodyY: SY(c.bodyY), ox: railPin, rail: c.rail, cx, toggleX0: Infinity, closeY0: cy + 24 });
}

// A place on the board on the screen, and back: the board is drawn scaled by
// its camera about the plane's corner, the strip over it at the screen's own
// size, so whatever the strip draws over the board's places goes through
// these.
const screenX = (st, x) => MACH_W + (x - st.view.x) * (st.cam || 1);
const boardX = (st, sx) => st.view.x + (sx - MACH_W) / (st.cam || 1);

// The claims' badges along the axis, each at the earliest moment it rests on,
// shifted right past the one before where they would touch; a claim nothing
// dates sits at the axis's start.
function claimBadgeSpots(st, W) {
  const inv = st.investigation;
  if (!inv?.claims?.length || !st.clock) return [];
  const AX = (tm) => screenX(st, PAD_L + st.clock.at(tm) * st.k);
  const dated = inv.claims.filter((c) => Number.isFinite(c.at)).map((c) => ({ c, x: AX(c.at) }));
  dated.sort((a, b) => a.x - b.x || a.c.n - b.c.n);
  let end = -Infinity;
  for (const sp of dated) {
    sp.x = Math.max(sp.x, end + CLAIM_BADGE_W / 2 + 3);
    end = sp.x + CLAIM_BADGE_W / 2;
  }
  // The undated stay by the row's name, whatever the scroll, marked apart.
  const undated = inv.claims.filter((c) => !Number.isFinite(c.at)).map((c, i) => ({ c, x: MACH_W + 76 + i * (CLAIM_BADGE_W + 3), undated: true }));
  return [...undated, ...dated.filter((sp) => sp.x > MACH_W + 76 + undated.length * (CLAIM_BADGE_W + 3) && sp.x < W + CLAIM_BADGE_W)];
}

// A card head's flag: how bad the review found the trace, in its colour.
function sevChip(ctx, theme, text, color, x, y, width = null) {
  ctx.font = `500 10px ${SANS}`;
  const w = width ?? ctx.measureText(text).width + 10;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  roundRect(ctx, x + 0.5, y - 6.5, w - 1, 13, 2);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillText(text, x + 5, y + 3.5);
  return w;
}

// Where a card's time is along the board (board px): across its body on a
// clock of its own where the reader sized or moved it along the board; else
// on the board's axis, over its own stretch of it (a short card spread
// across its head still begins and ends where its trace did).
function cardClock(c, st) {
  const x0 = c.x + (c.rail || 0);
  if (c.scaled && c.win && c.kx) return { x0, x1: c.x + c.w, own: true, xOf: (u) => x0 + (u - c.win.u0) * c.kx, uOf: (x) => c.win.u0 + (x - x0) / c.kx };
  const x1 = c.stretch > 1.001 && c.win ? Math.min(c.x + c.w, PAD_L + c.win.u1 * st.k) : c.x + c.w;
  return { x0, x1, own: false, xOf: (u) => PAD_L + u * st.k, uOf: (x) => (x - PAD_L) / st.k };
}
// Whether the board's axis holds anything: a card on it, or nothing placed yet.
function axisInUse(st) {
  const cs = [...(st.geo?.cards?.values() || [])];
  return !cs.length || cs.some((c) => !c.scaled);
}
// The board as its strip lays it out, in the strip's units (a place along the
// board over its scale, as the window is): the stretches of its cards' time
// with work in them, and the pauses the clock closed inside them, each where
// its card has it.
export function stripGround(st) {
  const B = (x) => (x - PAD_L) / st.k;
  const busy = [];
  const pauses = [];
  for (const c of st.geo?.cards?.values() || []) {
    const cc = cardClock(c, st);
    const ua = cc.uOf(cc.x0);
    const ub = cc.uOf(cc.x1);
    for (const s of st.clock.iv) {
      if (s.gap) {
        const mid = (s.u0 + s.u1) / 2;
        if (mid > ua && mid < ub) pauses.push(B(cc.xOf(mid)));
        continue;
      }
      const a = Math.max(s.u0, ua);
      const b = Math.min(s.u1, ub);
      if (b > a) busy.push([B(cc.xOf(a)), B(cc.xOf(b))]);
    }
  }
  // A card still reading its record: its whole stretch, no pause closed in it.
  for (const l of st.geo?.loading || []) busy.push([B(l.x + BOARD_RAIL), B(l.x + l.w)]);
  // A session graph's stub, at the time it ran.
  for (const l of st.geo?.stubs?.values() || []) busy.push([B(l.x0), B(l.x1)]);
  busy.sort((p, q) => p[0] - q[0]);
  const merged = [];
  for (const [a, b] of busy) {
    const last = merged[merged.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  }
  return { busy: merged, pauses: pauses.sort((p, q) => p - q) };
}
// Where the board has a moment, in the strip's units: on each card that holds
// it, where that card has it; on no card, on the axis while the axis holds
// anything.
export function momentAt(st, t) {
  const u = st.clock.at(t);
  const out = [];
  for (const c of st.geo?.cards?.values() || []) {
    const cc = cardClock(c, st);
    const x = cc.xOf(u);
    if (x >= cc.x0 && x <= cc.x1) out.push((x - PAD_L) / st.k);
  }
  if (!out.length && axisInUse(st)) out.push(u);
  return out;
}
// The moment the board has at a place along it (the strip's units): the
// axis's where a card on it runs there, else the first card's there on its own
// clock; off every card, the axis's while the axis holds anything.
export function timeAt(st, bu) {
  const x = PAD_L + bu * st.k;
  const U = st.clock.U;
  let own = null;
  for (const c of st.geo?.cards?.values() || []) {
    const cc = cardClock(c, st);
    if (x < cc.x0 || x > cc.x1) continue;
    if (!cc.own) return st.clock.inv(Math.max(0, Math.min(U, bu)));
    if (own == null) own = Math.max(c.win.u0, Math.min(c.win.u1, cc.uOf(x)));
  }
  if (own != null) return st.clock.inv(Math.max(0, Math.min(U, own)));
  return axisInUse(st) && bu >= 0 && bu <= U ? st.clock.inv(bu) : null;
}

// The clock's place at a point along the strip's time scale: stretched as the
// track is with everything in the window, else the board's on screen.
function scaleU(sc, st, sx) {
  return sc.whole ? sc.u0 + ((sx - sc.x0) / Math.max(1e-9, sc.w)) * sc.U : (boardX(st, sx) - PAD_L) / st.k;
}

// The moment under the pointer on the strip: the board's there.
function pointerTime(st, planeW) {
  const px = st.pointerX;
  const py = st.pointerY;
  if (px == null || py == null || py >= st.rulerH || !st.clock || !st.win) return null;
  const tr = stripTrack(planeW);
  if (px < tr.x0 || px > tr.x0 + tr.w) return null;
  const dom = st.stripDom || { u0: 0, u1: st.clock.U };
  return timeAt(st, dom.u0 + ((px - tr.x0) / tr.w) * (dom.u1 - dom.u0));
}

// The board's strip: the board's whole reach stretched across the canvas,
// the work in its cards' time and the pauses closed inside it as its ground,
// each trace on it in its hue, the window on screen framed on it, the cursor
// and the pointer's moment where the cards have them, and its time scale
// along its foot. Its label says what the window holds.
// The strip's head row: what the window holds on its left, the board's own
// actions on its right (BoardClose), its words' baseline; the track under it.
const HEAD_H = 28;
const HEAD_BASE = 18;
const STRIP_TRACK_Y = HEAD_H + 4;
const STRIP_TRACK_H = 20;
// The room a pause's length takes on the scale beside its mark ("23h", the
// gap before it, the clearance after the mark before).
const PAUSE_ROOM_PX = 28;
// What the window holds, in time: each card's stretch in view, read on its own
// clock where it has one (one moved or sized along the board, or spread
// across its column, runs on its own ruler), else on the board's.
export function windowTimes(st, W) {
  let t0 = Infinity;
  let t1 = -Infinity;
  // What of the board the screen holds, in the board's px.
  const lo = boardX(st, MACH_W);
  const hi = boardX(st, W);
  for (const c of st.geo?.cards?.values() || []) {
    // Its row names are held at the canvas's edge while it runs on under
    // them: what is in view begins past them.
    const rail = c.rail || 0;
    const a = Math.min(Math.max(c.x, lo), c.x + c.w - rail) + rail;
    const b = Math.min(hi, c.x + c.w);
    if (b <= a) continue;
    const tAt = ownClock(c) && c.win && c.kx
      ? (x) => st.clock.inv(c.win.u0 + (x - c.x - rail) / c.kx)
      : (x) => st.clock.inv((x - PAD_L) / st.k);
    const ta = tAt(a);
    const tb = tAt(b);
    if (Number.isFinite(ta)) t0 = Math.min(t0, ta);
    if (Number.isFinite(tb)) t1 = Math.max(t1, tb);
  }
  return t1 > t0 ? { t0, t1 } : null;
}
// The strip's track: across the canvas, the board's whole reach stretched
// along it.
export function stripTrack(planeW) {
  return { x0: MACH_W + 16, w: Math.max(40, planeW - 32) };
}
// Where the strip has a place on the board (board px, as the board lays its
// cards out).
export function stripPlace(st, planeW, x) {
  const tr = stripTrack(planeW);
  const dom = st.stripDom || { u0: 0, u1: st.clock.U };
  return tr.x0 + (((x - PAD_L) / st.k - dom.u0) / Math.max(1e-9, dom.u1 - dom.u0)) * tr.w;
}
// A time scale's ticks and the pauses the clock closed along it, over the
// stretches it runs over: each its clock units [a, b], px per unit, and where
// it has a moment of the clock.
function scaleMarks(st, pieces) {
  const ticks = [];
  const pauses = [];
  for (const p of pieces) {
    if (!(p.b > p.a) || !(p.k > 0)) continue;
    for (const tk of rulerTicks(st.clock, p.k, PAD_L + p.a * p.k - 60, PAD_L + p.b * p.k + 60)) {
      const u = (tk.x - PAD_L) / p.k;
      if (u >= p.a && u <= p.b) ticks.push({ ...tk, x: p.place(u) });
    }
    for (const br of st.clock.breaks || []) {
      const mid = (br.u0 + br.u1) / 2;
      if (mid > p.a && mid < p.b) pauses.push({ x: p.place(mid), br });
    }
  }
  return { ticks: ticks.sort((p, q) => p.x - q.x), pauses: pauses.sort((p, q) => p.x - q.x) };
}
// The strip's own time scale, stretched along it as the board's reach is:
// the axis the cards share while any is on it; else each card's own clock,
// over its card. In strip px.
export function stripAxis(st, planeW) {
  const at = (x) => stripPlace(st, planeW, x);
  const cards = [...(st.geo?.cards?.values() || [])];
  const shared = cards.filter((c) => !c.scaled);
  const dom = st.stripDom || { u0: 0, u1: st.clock.U };
  const pieces = [];
  if (shared.length) {
    pieces.push({ a: Math.max(0, dom.u0), b: Math.min(st.clock.U, dom.u1), k: at(PAD_L + st.k) - at(PAD_L), place: (u) => at(PAD_L + u * st.k) });
  } else {
    for (const c of cards) {
      const cc = cardClock(c, st);
      if (!cc.own) continue;
      pieces.push({ a: c.win.u0, b: c.win.u1, k: (at(cc.xOf(c.win.u0 + 1)) - at(cc.xOf(c.win.u0))), place: (u) => at(cc.xOf(u)) });
    }
  }
  return { shared: shared.length > 0, ...scaleMarks(st, pieces) };
}
// The board's time scale while it is zoomed in, over the cards it has on
// screen: the axis the cards in view share while any is on it, across the
// canvas past the row names held at its edge; else each card's own clock over
// its stretch in view. In canvas px, over the cards.
export function boardAxis(st, W) {
  const cam = st.cam || 1;
  const SX = (x) => screenX(st, x);
  const lo = boardX(st, MACH_W) + BOARD_RAIL;
  const hi = boardX(st, W) - BOARD_PAD_R;
  const inView = [...(st.geo?.cards?.values() || [])].map((c) => ({ c, cc: cardClock(c, st) })).filter(({ cc }) => cc.x1 > lo && cc.x0 < hi);
  const shared = inView.some(({ c }) => !c.scaled);
  const pieces = [];
  if (shared) {
    pieces.push({ a: Math.max(0, (lo - PAD_L) / st.k), b: Math.min(st.clock.U, (hi - PAD_L) / st.k), k: st.k * cam, place: (u) => SX(PAD_L + u * st.k) });
  } else {
    for (const { c, cc } of inView) {
      if (!cc.own) continue;
      pieces.push({ a: cc.uOf(Math.max(cc.x0, lo)), b: cc.uOf(Math.min(cc.x1, hi)), k: c.kx * cam, place: (u) => SX(cc.xOf(u)) });
    }
  }
  return { shared, ...scaleMarks(st, pieces) };
}
function drawBoardStrip(ctx, st, theme, { W, planeW, hits, t, guide = null, badges = [] }) {
  const RULER_H = st.rulerH;
  // The axis's foot: the ruler's own foot, or, with the chat's claims in a
  // row under it, the top of that row.
  const AX_Y = RULER_BOARD;
  ctx.fillStyle = theme.bg;
  ctx.fillRect(MACH_W, 0, planeW, RULER_H);
  // The strip's edge is a band's, as the foot's controls and the dock's tabs
  // are ruled off.
  ctx.fillStyle = theme.lineSoft;
  ctx.fillRect(0, RULER_H - 1, W, 1);
  hits.strip = null;
  hits.overview = [];
  const clk = st.clock;
  const win = st.win;
  if (!clk || !win) return;
  const tr = stripTrack(planeW);
  const dom = st.stripDom || { u0: 0, u1: clk.U };
  const UX = (u) => tr.x0 + ((u - dom.u0) / Math.max(1e-9, dom.u1 - dom.u0)) * tr.w;
  // A moment on the strip: wherever a card has it, each a mark.
  const marksAt = (tm) => [...new Set(momentAt(st, tm).filter((u) => u >= dom.u0 && u <= dom.u1).map((u) => Math.round(UX(u))))];
  const onClock = (u) => Math.max(0, Math.min(clk.U, u));
  // The window holds what its cards in view draw, each on its own clock.
  const held = windowTimes(st, W);
  const t0 = held ? held.t0 : clk.inv(onClock(win.u0));
  const t1 = held ? held.t1 : clk.inv(onClock(win.u1));
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = `10px ${SANS}`;
  ctx.letterSpacing = "0.08em";
  ctx.fillStyle = theme.eyebrow;
  ctx.fillText("WINDOW", tr.x0, HEAD_BASE);
  const ew = ctx.measureText("WINDOW").width;
  ctx.letterSpacing = "0px";
  ctx.font = `11px ${SANS}`;
  ctx.fillStyle = theme.ink;
  const whole = win.u0 <= dom.u0 + 0.001 && win.u1 >= dom.u1 - 0.001;
  // Scrolled before the first trace (or past the last) where no card was
  // carried, the window holds no time at all.
  const beyond = held ? null : win.u1 <= 0.0001 ? "Before the first trace" : win.u0 >= clk.U - 0.0001 ? "After the last trace" : null;
  const label = beyond || `${whole ? "Everything" : rangeText(t0, t1)}${DOT}${spanText(t1 - t0)}${st.roll === 2 ? `${DOT}heads only${st.canExit ? "; zoom out again to go back out" : ""}` : st.roll === 1 ? `${DOT}cards in brief` : ""}`;
  // The head row's words end short of the board's close where it has one.
  const wordsEnd = tr.x0 + tr.w - (st.closeRoom || 0);
  const shown = clipText(ctx, label, Math.max(0, wordsEnd - (tr.x0 + ew + 10)));
  ctx.fillText(shown, tr.x0 + ew + 10, HEAD_BASE);
  if (Number.isFinite(st.cursor)) {
    // The strip's right end holds the chat's steps when it has any; the
    // cursor's moment then follows the window's.
    const said = `${dayText(st.cursor)} ${clockText(st.cursor)}${DOT}later work dimmed`;
    ctx.fillStyle = theme.green;
    if (st.investigation?.steps?.length) {
      const x = tr.x0 + ew + 10 + ctx.measureText(shown).width + 16;
      ctx.fillText(clipText(ctx, said, Math.max(0, wordsEnd - x)), x, HEAD_BASE);
    } else {
      ctx.textAlign = "right";
      ctx.fillText(said, wordsEnd, HEAD_BASE);
      ctx.textAlign = "left";
    }
  }
  // The track: the work in the cards' time as ground, the pauses closed
  // inside it as breaks, each where its card has it.
  const ty = STRIP_TRACK_Y;
  const th = STRIP_TRACK_H;
  const ground = stripGround(st);
  ctx.fillStyle = alphaMix(theme.ink, 5);
  for (const [a, b] of ground.busy) {
    if (b < dom.u0 || a > dom.u1) continue;
    const xa = UX(Math.max(a, dom.u0));
    ctx.fillRect(xa, ty, Math.max(1, UX(Math.min(b, dom.u1)) - xa), th);
  }
  ctx.fillStyle = theme.mutedSoft;
  let lastPause = -Infinity;
  for (const p of ground.pauses) {
    if (p < dom.u0 || p > dom.u1) continue;
    const x = Math.round(UX(p));
    if (x - lastPause < 2) continue;
    lastPause = x;
    ctx.fillRect(x, ty + 3, 1, th - 6);
  }
  // A lens: its stretch of the track tinted, as it is down the canvas.
  ctx.fillStyle = alphaMix(theme.green, 12);
  for (const l of clk.lenses || []) {
    const xa = Math.max(tr.x0, UX(l.u0));
    const xb = Math.min(tr.x0 + tr.w, UX(l.u1));
    if (xb > xa) ctx.fillRect(xa, ty, xb - xa, th);
  }
  // Every trace where its time is on the board (moved or sized along it, that
  // is its card's own stretch, not its trace's), and every session graph stub
  // where it ran, packed into rows.
  const spans = [...st.cards.values()]
    .filter((c) => st.geo?.cards?.has(c.key) && Number.isFinite(c.firstMs) && Number.isFinite(c.lastMs))
    .map((c) => {
      const cc = cardClock(st.geo.cards.get(c.key), st);
      const ua = (cc.x0 - PAD_L) / st.k;
      const ub = (cc.x1 - PAD_L) / st.k;
      return { key: c.key, x0: Math.max(tr.x0, UX(ua)), x1: Math.min(tr.x0 + tr.w, UX(ub)), hue: c.focal ? theme.ink : c.hue || theme.muted };
    })
    .concat([...(st.geo?.stubs?.values() || [])].map((l) => ({
      key: l.key, x0: Math.max(tr.x0, UX((l.x0 - PAD_L) / st.k)), x1: Math.min(tr.x0 + tr.w, UX((l.x1 - PAD_L) / st.k)), hue: l.hue || theme.muted,
    })))
    .sort((a, b) => a.x0 - b.x0);
  const rowEnd = [];
  for (const sp of spans) {
    let row = rowEnd.findIndex((e) => e + 3 <= sp.x0);
    if (row < 0) row = rowEnd.length < 4 ? rowEnd.length : rowEnd.indexOf(Math.min(...rowEnd));
    rowEnd[row] = Math.max(sp.x1, sp.x0 + 3);
    const y = ty + 3 + row * 4;
    const hot = st.hoverKey === sp.key || st.sel?.key === sp.key;
    ctx.fillStyle = hot ? sp.hue : alphaMix(sp.hue, 80);
    ctx.fillRect(sp.x0, y, Math.max(3, sp.x1 - sp.x0), 3);
    hits.overview.push({ key: sp.key, x0: sp.x0 - 2, x1: Math.max(sp.x0 + 3, sp.x1) + 2, y0: y - 2, y1: y + 4 });
  }
  // The window, framed, the track outside it dimmed, its edges to drag.
  const wa = Math.max(tr.x0, UX(win.u0));
  const wb = Math.min(tr.x0 + tr.w, UX(win.u1));
  ctx.fillStyle = alphaMix(theme.bg, 60);
  ctx.fillRect(tr.x0, ty, Math.max(0, wa - tr.x0), th);
  ctx.fillRect(wb, ty, Math.max(0, tr.x0 + tr.w - wb), th);
  const hot = st.hoverStrip === "body" || st.hoverStrip === "edge-l" || st.hoverStrip === "edge-r";
  ctx.strokeStyle = hot ? theme.ink : alphaMix(theme.ink, 65);
  ctx.lineWidth = 1;
  ctx.strokeRect(Math.round(wa) + 0.5, ty - 2.5, Math.max(2, Math.round(wb - wa) - 1), th + 5);
  // The frame's edges, as handles to grip.
  const handle = (x, hot) => {
    const hx = Math.round(x);
    ctx.fillStyle = theme.panel;
    roundRect(ctx, hx - 4, ty + th / 2 - 9, 8, 18, 2);
    ctx.fill();
    ctx.strokeStyle = hot ? theme.green : theme.lineStrong;
    ctx.lineWidth = 1;
    roundRect(ctx, hx - 3.5, ty + th / 2 - 8.5, 7, 17, 2);
    ctx.stroke();
    ctx.fillStyle = hot ? theme.green : theme.muted;
    ctx.fillRect(hx - 1.5, ty + th / 2 - 4, 1, 8);
    ctx.fillRect(hx + 0.5, ty + th / 2 - 4, 1, 8);
  };
  handle(wa, st.hoverStrip === "edge-l");
  handle(wb, st.hoverStrip === "edge-r");
  hits.strip = { x0: tr.x0, x1: tr.x0 + tr.w, y0: ty - 5, y1: ty + th + 5, wa, wb, u0: dom.u0, U: dom.u1 - dom.u0, w: tr.w };
  // Along its foot, a time scale: where the clock's stretches begin and their
  // hours, on the axis the cards share or, with none on it, on each card's
  // own clock. With everything in the window, the strip's, stretched as the
  // track is; zoomed in, the board's, over the cards on screen.
  // Where the scale skips a pause the clock closed; its labels never run
  // through those marks.
  const axis = whole ? stripAxis(st, planeW) : boardAxis(st, W);
  const inTrack = whole
    ? (x) => x >= tr.x0 - 2 && x <= tr.x0 + tr.w + 2
    : (x) => x >= MACH_W + BOARD_RAIL * (st.cam || 1) - 2 && x <= W - BOARD_PAD_R * (st.cam || 1) + 2;
  // Pauses closer together than a gap's words need are one mark, at the
  // longest of them, saying how long they closed between them: two marks a
  // label apart read as a stutter, the label wedged between them.
  const pauses = [];
  for (const p of axis.pauses) {
    if (!inTrack(p.x)) continue;
    const prev = pauses[pauses.length - 1];
    if (prev && p.x - prev.x0 < PAUSE_ROOM_PX) {
      prev.ms += p.br.ms;
      prev.x1 = p.x;
      if (p.br.ms > prev.longest) { prev.longest = p.br.ms; prev.x = p.x; }
      continue;
    }
    pauses.push({ x: p.x, x0: p.x, x1: p.x, ms: p.br.ms, longest: p.br.ms });
  }
  // The lenses along the scale, wherever it has their stretch: a band each,
  // its factor at its start and a way to take it off at its end, those words
  // claiming their room before the scale's labels; and the stretch being
  // dragged out for a new one. Dragged along, the scale draws a lens.
  const footY0 = ty + th + 3;
  const footH = AX_Y - 1 - footY0;
  const footX = whole ? UX : (u) => screenX(st, PAD_L + u * st.k);
  const footL = whole ? tr.x0 : MACH_W;
  const footR = whole ? tr.x0 + tr.w : W;
  hits.lenses = [];
  hits.scale = st.lensable ? { y0: ty + th + 5, y1: RULER_H, whole, x0: tr.x0, w: tr.w, u0: dom.u0, U: dom.u1 - dom.u0 } : null;
  const lensMarks = [];
  const claimed = [];
  for (const l of clk.lenses || []) {
    const x0 = Math.max(footL, footX(l.u0));
    const x1 = Math.min(footR, footX(l.u1));
    if (x1 <= x0) continue;
    const hotLens = st.hoverLens === l.id;
    ctx.fillStyle = alphaMix(theme.green, hotLens ? 26 : 16);
    ctx.fillRect(x0, footY0, x1 - x0, footH);
    ctx.font = `500 10px ${SANS}`;
    const label = scaleText(l.zoom);
    const lw = ctx.measureText(label).width;
    const m = { x0, x1, lx: x0 + 4, lw, cx: x1 - 8, hot: hotLens, label };
    m.showLabel = x1 - x0 >= lw + 26;
    m.showClose = x1 - x0 >= 16;
    if (m.showLabel) claimed.push([m.lx - 2, m.lx + lw + 4]);
    if (m.showClose) claimed.push([m.cx - 6, m.cx + 6]);
    lensMarks.push(m);
    hits.lenses.push({ id: l.id, x0, x1, closeX0: m.showClose ? m.cx - 7 : Infinity, y0: ty + th + 5, y1: RULER_H });
  }
  let lastEnd = -Infinity;
  ctx.font = `10px ${SANS}`;
  const said = [];
  for (const tk of axis.ticks) {
    const x = tk.x;
    if (!inTrack(x)) continue;
    const tw = ctx.measureText(tk.label).width;
    let lx = x + 3;
    // A label running into a pause after its moment gives way; one after a
    // pause steps clear of it.
    for (const p of pauses) {
      if (!(lx < p.x1 + 6 && lx + tw > p.x0 - 6)) continue;
      lx = x < p.x0 ? null : p.x1 + 7;
      if (lx == null) break;
    }
    if (lx == null || lx < lastEnd + 10 || lx + tw > W - 4) continue;
    if (claimed.some(([a, b]) => lx < b && lx + tw > a)) continue;
    ctx.fillStyle = tk.minor ? theme.mutedSoft : theme.muted;
    ctx.fillText(tk.label, lx, AX_Y - 5);
    ctx.fillStyle = theme.lineStrong;
    ctx.fillRect(Math.round(x), AX_Y - 4, 1, 3);
    lastEnd = lx + tw;
    said.push([lx - 5, lx + tw + 2]);
  }
  // Each pause: its mark, quieter than the times it divides, and how long it
  // was where there is room to say it clear of every other mark.
  ctx.font = `9.5px ${SANS}`;
  for (const { x } of pauses) said.push([x - 5, x + 5]);
  for (const { x, ms } of pauses) {
    ctx.strokeStyle = theme.mutedSoft;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - 3.5, AX_Y - 2);
    ctx.lineTo(x - 1, AX_Y - 10);
    ctx.moveTo(x + 1, AX_Y - 2);
    ctx.lineTo(x + 3.5, AX_Y - 10);
    ctx.stroke();
    const label = gapText(ms);
    const gw = label ? ctx.measureText(label).width : 0;
    const gx0 = x - gw - 7;
    if (gw && gx0 > MACH_W + 2 && !said.some(([a, b]) => gx0 - 4 < b && x - 6 > a)) {
      ctx.fillStyle = theme.mutedSoft;
      ctx.textAlign = "left";
      ctx.fillText(label, gx0, AX_Y - 5);
      said.push([gx0 - 2, x - 6]);
    }
  }
  for (const m of lensMarks) {
    if (m.showLabel) {
      ctx.font = `500 10px ${SANS}`;
      ctx.fillStyle = theme.green;
      ctx.textAlign = "left";
      ctx.fillText(m.label, m.lx, AX_Y - 5);
    }
    if (m.showClose) {
      const cy = AX_Y - 9;
      ctx.strokeStyle = m.hot ? theme.ink : theme.muted;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(m.cx - 3, cy - 3);
      ctx.lineTo(m.cx + 3, cy + 3);
      ctx.moveTo(m.cx + 3, cy - 3);
      ctx.lineTo(m.cx - 3, cy + 3);
      ctx.stroke();
    }
  }
  if (st.lensDraft) {
    const a = Math.min(st.lensDraft.x0, st.lensDraft.x1);
    const e = Math.max(st.lensDraft.x0, st.lensDraft.x1);
    ctx.fillStyle = alphaMix(theme.green, 22);
    ctx.fillRect(a, footY0, e - a, footH);
    ctx.fillStyle = theme.green;
    ctx.fillRect(Math.round(a), footY0, 1, footH);
    ctx.fillRect(Math.round(e), footY0, 1, footH);
  }
  if (Number.isFinite(st.cursor)) {
    ctx.fillStyle = theme.green;
    for (const x of marksAt(st.cursor)) ctx.fillRect(x, ty - 3, 1, th + 6);
  }
  if (Number.isFinite(t)) {
    ctx.fillStyle = alphaMix(theme.ink, 60);
    for (const x of marksAt(t)) ctx.fillRect(x, ty - 3, 1, th + 6);
  }
  // Along the scale, on the axis the cards share: the card in hand's own
  // stretch of it, however wide the card is drawn. And the moment under the
  // pointer, said where the scale has the pointer's place.
  const hk = st.hoverKey || (st.sel?.key && !st.sel.laneId ? st.sel.key : null);
  const hc = hk ? st.cards.get(hk) : null;
  const onScale = whole ? (tm) => UX(clk.at(tm)) : (tm) => screenX(st, PAD_L + clk.at(tm) * st.k);
  const scaleL = whole ? tr.x0 : MACH_W;
  const scaleR = whole ? tr.x0 + tr.w : W;
  if (axis.shared && hc && Number.isFinite(hc.firstMs) && Number.isFinite(hc.lastMs)) {
    const a = Math.max(scaleL, onScale(hc.firstMs));
    const b = Math.min(scaleR, onScale(hc.lastMs));
    if (b > a) {
      ctx.fillStyle = hc.focal ? theme.ink : hc.hue || theme.muted;
      ctx.fillRect(a, AX_Y - 3, Math.max(2, b - a), 2);
    }
  }
  if (guide?.onBoard && Number.isFinite(guide.t) && st.pointerX != null) {
    // Over the board the pointer is kept in the board's own frame.
    const x = whole ? stripPlace(st, planeW, st.pointerX - MACH_W + st.view.x) : MACH_W + (st.pointerX - MACH_W) * (st.cam || 1);
    if (x >= MACH_W && x <= W) {
      const said = `${dayText(guide.t)} ${clockText(guide.t)}`;
      ctx.font = `10px ${SANS}`;
      const tw = ctx.measureText(said).width;
      const bx = Math.max(MACH_W + 2, Math.min(W - tw - 12, x - tw / 2 - 5));
      ctx.fillStyle = theme.panel;
      roundRect(ctx, bx, AX_Y - 18, tw + 10, 14, 2);
      ctx.fill();
      ctx.strokeStyle = theme.lineStrong;
      ctx.lineWidth = 1;
      roundRect(ctx, bx + 0.5, AX_Y - 17.5, tw + 9, 13, 2);
      ctx.stroke();
      ctx.fillStyle = theme.ink;
      ctx.textAlign = "left";
      ctx.fillText(said, bx + 5, AX_Y - 7.5);
    }
  }
  // The chat's claims, a numbered badge each at the moment it rests on: filled
  // when the graph bears it out, outlined when not yet; the one in hand ringed.
  hits.claimBadges = [];
  hits.claimRow = null;
  if (RULER_H > AX_Y) {
    hits.claimRow = { y0: AX_Y, y1: RULER_H };
    ctx.fillStyle = theme.lineSoft;
    ctx.fillRect(MACH_W, AX_Y, planeW, 1);
    ctx.font = `10px ${SANS}`;
    ctx.letterSpacing = "0.08em";
    ctx.fillStyle = theme.eyebrow;
    ctx.textAlign = "left";
    ctx.fillText("CLAIMS", MACH_W + 16, AX_Y + 15);
    ctx.letterSpacing = "0px";
    const cy = AX_Y + (RULER_H - AX_Y) / 2;
    for (const { c, x } of badges) {
      const bx = Math.round(x - CLAIM_BADGE_W / 2);
      const by = Math.round(cy - CLAIM_BADGE_H / 2);
      const held = st.claimFocus === c.id || st.claimRing === c.id;
      const shown = c.status === "supported";
      ctx.fillStyle = shown ? theme.graph : theme.bg;
      ctx.fillRect(bx, by, CLAIM_BADGE_W, CLAIM_BADGE_H);
      ctx.strokeStyle = theme.graph;
      ctx.lineWidth = held ? 2 : 1;
      ctx.strokeRect(bx + 0.5, by + 0.5, CLAIM_BADGE_W - 1, CLAIM_BADGE_H - 1);
      ctx.font = `600 9.5px ${SANS}`;
      ctx.textAlign = "center";
      ctx.fillStyle = shown ? theme.bg : theme.graph;
      ctx.fillText(String(c.n), bx + CLAIM_BADGE_W / 2, by + 9.5);
      ctx.textAlign = "left";
      // The claim the evidence along the board's foot points at: ringed in
      // ink, as its cells are.
      if (st.evidenceIds?.has(c.id)) {
        ctx.strokeStyle = theme.ink;
        ctx.lineWidth = 1.5;
        roundRect(ctx, bx - 2.5, by - 2.5, CLAIM_BADGE_W + 5, CLAIM_BADGE_H + 5, 3);
        ctx.stroke();
        ctx.lineWidth = 1;
      }
      if (c.isNew && !held) {
        ctx.fillStyle = theme.graph;
        ctx.fillRect(bx, by + CLAIM_BADGE_H + 2, CLAIM_BADGE_W, 1.5);
      }
      hits.claimBadges.push({ id: c.id, n: c.n, x0: bx - 2, x1: bx + CLAIM_BADGE_W + 2, y0: by - 3, y1: by + CLAIM_BADGE_H + 3 });
    }
  }
}

// The moment under the pointer on the board: in a card's body, that card's
// own time there (a card sized sideways runs on a scale of its own); on the
// ground, the axis's; on the strip, the strip's.
function boardGuide(st, W) {
  const RULER_H = st.rulerH;
  const px = st.pointerX;
  const py = st.pointerY;
  if (px == null || py == null || !st.clock || !st.geo || st.movingKey || st.resizingKey) return null;
  if (py < RULER_H) {
    const t = pointerTime(st, W - MACH_W);
    return Number.isFinite(t) ? { t, onBoard: false } : null;
  }
  if (px < MACH_W || px > W) return null;
  const bx = px - MACH_W + st.view.x;
  const by = py - RULER_H + st.view.y;
  for (const c of st.geo.cards.values()) {
    if (by < c.y || by > c.y + c.h || bx < c.x || bx > c.x + c.w) continue;
    // A machine card on the board's scale runs on the axis itself.
    if (c.graph && !ownClock(c)) {
      if (by < c.bodyY) return null;
      break;
    }
    // Its head and its rail (held at the canvas's edge while the card runs
    // on under it) are no moment.
    const rail = c.rail || 0;
    const railAt = Math.min(Math.max(c.x, st.view.x), c.x + c.w - rail);
    if (by < c.bodyY || bx < railAt + rail || !c.win || !c.kx) return null;
    const u = c.win.u0 + (bx - c.x - rail) / c.kx;
    return { t: st.clock.inv(Math.max(c.win.u0, Math.min(c.win.u1, u))), onBoard: true, key: c.key };
  }
  // The ground has the axis's time only while a card is on the axis.
  const u = (bx - PAD_L) / st.k;
  return u >= 0 && u <= st.clock.U && axisInUse(st) ? { t: st.clock.inv(u), onBoard: true } : null;
}

// That moment down the board: one faint line on the axis the cards share,
// through the ground and every card on the board's own scale (while any is);
// a card on a clock of its own has it at its own place, where the line steps
// across.
function drawGuide(ctx, st, theme, t, { W, H, SX, SY, planeW }) {
  const RULER_H = st.rulerH;
  const u = st.clock.at(t);
  const ax = Math.round(SX(PAD_L + u * st.k));
  const own = [...st.geo.cards.values()].filter(ownClock);
  ctx.save();
  ctx.beginPath();
  ctx.rect(MACH_W, RULER_H, planeW, H - RULER_H);
  for (const c of own) ctx.rect(SX(c.x), SY(c.y), c.w, c.h);
  ctx.clip("evenodd");
  ctx.fillStyle = alphaMix(theme.ink, 26);
  if (ax >= MACH_W && ax <= W && axisInUse(st)) ctx.fillRect(ax, RULER_H, 1, H - RULER_H);
  ctx.restore();
  ctx.fillStyle = alphaMix(theme.ink, 48);
  for (const c of own) {
    if (!c.win || !c.kx || u < c.win.u0 || u > c.win.u1) continue;
    const x = Math.round(SX(c.x + (c.rail || 0) + (u - c.win.u0) * c.kx));
    if (x < MACH_W || x > W) continue;
    const foot = headHold(st.cam || 1, c.h).foot;
    ctx.fillRect(x, SY(c.y + foot), 1, c.h - foot - 2);
  }
}

// The cursor down the board, as the guide is: past it on the axis the cards
// share, the ground and every card on the board's own scale dimmed (while
// any card is on it); a card on a clock of its own dimmed past where it has
// the cursor, all of it when its work comes after.
function drawBoardCursor(ctx, st, theme, t, { W, H, SX, SY, planeW }) {
  const RULER_H = st.rulerH;
  const u = st.clock.at(t);
  const dim = alphaMix(theme.bg, 62);
  const dashed = (x, y0, y1) => {
    ctx.strokeStyle = theme.green;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(x + 0.5, y0);
    ctx.lineTo(x + 0.5, y1);
    ctx.stroke();
    ctx.setLineDash([]);
  };
  const own = [...st.geo.cards.values()].filter((c) => ownClock(c) && c.win && c.kx);
  if (axisInUse(st)) {
    const ax = SX(PAD_L + u * st.k);
    ctx.save();
    ctx.beginPath();
    ctx.rect(MACH_W, RULER_H, planeW, H - RULER_H);
    for (const c of own) ctx.rect(SX(c.x), SY(c.y), c.w, c.h);
    ctx.clip("evenodd");
    if (ax < W) {
      ctx.fillStyle = dim;
      ctx.fillRect(Math.max(MACH_W, ax), RULER_H, W - Math.max(MACH_W, ax), H - RULER_H);
      if (ax >= MACH_W) dashed(ax, RULER_H, H);
    }
    ctx.restore();
  }
  for (const c of own) {
    if (u > c.win.u1) continue;
    const x0 = SX(c.x);
    const x1 = SX(c.x + c.w);
    const x = u < c.win.u0 ? x0 : SX(c.x + (c.rail || 0) + (u - c.win.u0) * c.kx);
    const a = Math.max(MACH_W, x);
    const b = Math.min(W, x1);
    if (b <= a) continue;
    ctx.fillStyle = dim;
    ctx.fillRect(a, SY(c.y), b - a, c.h);
    if (u >= c.win.u0 && x >= MACH_W) dashed(x, SY(c.y), SY(c.y + c.h));
  }
}

// The board's chat's claims, over the board along its foot as the page's
// overlays stand over it (the ground's colour, a strong frame, a deep
// shadow), a card a claim: its number as the strip's badges have it, where it
// rests (the session, when), the words the answer cites it with (else its
// own), and what it rests on there. Pointing at a card fades the rest of the
// board back, rings what it rests on and draws a line to it. A click opens it
// upward over the board in full (its words, the claim's own where the answer
// says it otherwise, everywhere it rests), until its one control closes it.
// An open card keeps the board faded back around what it rests on and steps
// to the claim before or after it. The cards scroll along the strip by a
// drag, the wheel, or the round arrows over its ends where it runs on; the
// board between them takes its own clicks. The cards stand in the order their
// claims happened (`listed`: in the list's own order, as a walkthrough tells
// its moments), on one hairline: a tick under each with its time beside it
// (in a run, how long after the one before), broken where the next came a
// while later and saying how long (as the board's own axis does). One pointed
// at from outside the strip (`outside`: its line in the walkthrough's story
// in the chat) comes into sight along it and points as under the pointer.
const cardIn = (sc, id) => (id && sc ? [...sc.querySelectorAll("[data-evidence]")].find((el) => el.dataset.evidence === id) || null : null);
const bringIntoSight = (sc, el) => {
  const left = el.offsetLeft - 10;
  const right = el.offsetLeft + el.offsetWidth + 10 - sc.clientWidth;
  if (sc.scrollLeft > left) sc.scrollLeft = left;
  else if (sc.scrollLeft < right) sc.scrollLeft = right;
};
const EvidenceStrip = memo(function EvidenceStrip({ list, listed = false, noun = "claim", rests, picked, pointed, outside = null, onPoint, onPick, onClose, onOpenAt, openMax, stripRef, onChat = null, onClaim = null, onMoment = null, caption = "" }) {
  const scroller = useRef(null);
  const ordered = useMemo(() => (listed ? inListedOrder(list) : inTimeOrder(list)), [list, listed]);
  // Picked from outside the strip (a ref in the chat, a badge), its card comes
  // into sight along the strip, the board itself left where it is.
  useEffect(() => {
    const sc = scroller.current;
    const el = cardIn(sc, picked);
    if (el) bringIntoSight(sc, el);
  }, [picked]);
  // The card pointed at, held so its line follows it as the strip moves.
  const pointedCard = useRef(null);
  const point = useCallback((id, el = null) => {
    pointedCard.current = id && el ? { id, el } : null;
    onPoint(id, el);
  }, [onPoint]);
  const outsideHeld = useRef(false);
  useEffect(() => {
    const sc = scroller.current;
    const el = cardIn(sc, outside);
    if (!el) {
      if (outsideHeld.current) point(null);
      outsideHeld.current = false;
      return;
    }
    bringIntoSight(sc, el);
    outsideHeld.current = true;
    point(outside, el);
  }, [outside]); // eslint-disable-line react-hooks/exhaustive-deps
  // Over the strip, the wheel scrolls the strip (an open card's words, up and
  // down, their own), and never pans the board under it, the gaps between the
  // cards too: taken on its way down to the board, before the board's own.
  // A pinch still zooms the board.
  useEffect(() => {
    const host = stripRef?.current?.parentElement;
    if (!host) return undefined;
    const onWheel = (e) => {
      const sc = scroller.current;
      if (!sc || e.ctrlKey || e.metaKey) return;
      const box = sc.getBoundingClientRect();
      const inCard = !!e.target?.closest?.("[data-board-evidence] [data-evidence]");
      const inBand = e.clientX >= box.left && e.clientX <= box.right && e.clientY <= box.bottom && e.clientY >= box.bottom - (EVIDENCE_H + EVIDENCE_LINE_H + 6);
      if (!inCard && !inBand) return;
      e.stopPropagation();
      const across = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const body = e.target?.closest?.("[data-evidence-body]");
      if (!across && body && body.scrollHeight > body.clientHeight) return;
      e.preventDefault();
      sc.scrollLeft += across ? e.deltaX : e.deltaY;
    };
    host.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () => host.removeEventListener("wheel", onWheel, { capture: true });
  }, [stripRef]);
  // Which ways the strip runs on past its edges, for the arrows there; and
  // where an open card and the card pointed at stand, for the board to keep
  // their lines to them.
  const [over, setOver] = useState({ back: false, on: false });
  const measure = useCallback(() => {
    const sc = scroller.current;
    if (!sc) return;
    const back = sc.scrollLeft > 2;
    const on = sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 2;
    setOver((cur) => (cur.back === back && cur.on === on ? cur : { back, on }));
    const open = sc.querySelector("[data-evidence-open]");
    const box = stripRef?.current?.parentElement?.getBoundingClientRect?.();
    if (!open || !box) onOpenAt?.(null);
    else {
      const r = open.getBoundingClientRect();
      onOpenAt?.({ x: r.left + r.width / 2 - box.left, y: r.top - box.top });
    }
    const pointed = pointedCard.current;
    if (pointed?.el?.isConnected) onPoint(pointed.id, pointed.el);
  }, [onOpenAt, onPoint, stripRef]);
  useEffect(() => {
    const sc = scroller.current;
    if (!sc) return undefined;
    sc.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(sc);
    return () => {
      sc.removeEventListener("scroll", measure);
      ro?.disconnect();
    };
  }, [measure]);
  useEffect(() => { measure(); }, [list, picked, measure]);
  const scrollOn = (dir) => {
    const sc = scroller.current;
    if (sc) sc.scrollBy({ left: dir * Math.max(200, sc.clientWidth * 0.8), behavior: "smooth" });
  };
  // Dragged along, the strip follows the hand, and the drag opens nothing it
  // ends on. An open card's words are taken by hand instead.
  const [dragging, setDragging] = useState(false);
  const dragFrom = (e) => {
    const sc = scroller.current;
    if (e.button !== 0 || !sc || e.target?.closest?.("[data-evidence-open]")) return;
    const d = { x: e.clientX, left: sc.scrollLeft, moved: false };
    const move = (ev) => {
      const dx = ev.clientX - d.x;
      if (!d.moved && Math.abs(dx) < 4) return;
      if (!d.moved) {
        d.moved = true;
        setDragging(true);
      }
      sc.scrollLeft = d.left - dx;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (!d.moved) return;
      setDragging(false);
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 250);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };
  // A small round button over either end where the strip runs on that way,
  // level with the cards however tall an open one stands.
  const arrow = (dir) => (
    <button
      type="button"
      onClick={() => scrollOn(dir)}
      aria-label={dir < 0 ? `Scroll to earlier ${noun}s` : `Scroll to later ${noun}s`}
      className="pointer-events-auto absolute z-[1] grid h-[26px] w-[26px] place-items-center rounded-full border border-[color:var(--line-strong)] bg-[color:var(--bg)] text-[color:var(--muted)] shadow-lg hover:border-[color:var(--muted)] hover:text-[color:var(--ink)]"
      style={{ [dir < 0 ? "left" : "right"]: 8, bottom: EVIDENCE_LINE_H + 4 + (EVIDENCE_H - 18) / 2 - 13 }}
      data-evidence-scroll={dir < 0 ? "back" : "on"}
    >
      <StepChevron back={dir < 0} />
    </button>
  );
  // An open card steps on to the claim before or after it along the strip.
  const at = ordered.findIndex((x) => x.id === picked);
  const before = at > 0 ? ordered[at - 1] : null;
  const after = at >= 0 && at + 1 < ordered.length ? ordered[at + 1] : null;
  const stepBtn = "grid h-[16px] w-[16px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] disabled:opacity-40";
  // A claim's badge as the strip under the axis draws it; a moment's, its
  // pin's head in its note's colour.
  const badge = (e) => (e.status === "moment" ? (
    <span className="inline-grid h-[16px] min-w-[16px] shrink-0 place-items-center rounded-full px-[4px] text-[10px] font-semibold tabular-nums" style={{ background: NOTE_TONE[e.tone] ? severityCss(NOTE_TONE[e.tone]) : "var(--ink)", color: "var(--bg)" }} aria-hidden="true">{e.n}</span>
  ) : (
    <span className={`inline-grid h-[14px] min-w-[16px] shrink-0 place-items-center rounded-[2px] px-[3px] text-[9.5px] font-semibold ${e.status === "supported" ? "bg-[color:var(--graph)] text-[color:var(--bg)]" : "border border-[color:var(--graph)] text-[color:var(--graph)]"}`} aria-hidden="true">{e.n}</span>
  ));
  return (
    <div ref={stripRef} className={`pointer-events-none absolute inset-x-0 bottom-0 z-[9] ${dragging ? "select-none [&_*]:!cursor-grabbing" : ""}`} onPointerDown={(e) => e.stopPropagation()} data-board-evidence="">
      {over.back ? arrow(-1) : null}
      {over.on ? arrow(1) : null}
      <div ref={scroller} onPointerDown={dragFrom} className="pointer-events-none relative flex items-end overflow-x-auto overflow-y-hidden px-[12px] pb-[4px] pt-[6px] [&::-webkit-scrollbar]:hidden" style={{ gap: EVIDENCE_GAP, scrollbarWidth: "none" }}>
        {/* What the list covers, ahead of it, level with the cards. It takes
            the pointer, never the board under it. */}
        {caption ? (
          <div className="pointer-events-none flex shrink-0 flex-col" style={{ width: EVIDENCE_CAPTION_W }}>
            <div
              className="pointer-events-auto flex w-full shrink-0 flex-col justify-end gap-[2px] rounded-[2px] bg-[color:var(--bg)] px-[10px] py-[7px]"
              style={{ height: EVIDENCE_H - 18 }}
              data-evidence-caption=""
            >
              <span className="text-[10px] uppercase leading-[14px] tracking-[0.08em] text-[color:var(--muted-soft)]">{listed ? "Walkthrough" : "Evidence"}</span>
              <span className="text-[12px] leading-[1.4] text-[color:var(--ink)]">{caption}</span>
            </div>
            <div style={{ height: EVIDENCE_LINE_H }} aria-hidden="true" />
          </div>
        ) : null}
        {ordered.map((e, i) => {
          const r = rests.get(e.id) || [];
          const first = r[0] || null;
          const lit = picked === e.id;
          const pointAt = { onPointerEnter: (ev) => point(e.id, ev.currentTarget), onPointerLeave: () => point(null) };
          const w = lit ? EVIDENCE_OPEN_W : EVIDENCE_CARD_W;
          const prev = ordered[i - 1];
          const day = (t) => new Date(t).toDateString();
          const timed = Number.isFinite(e.at);
          // The moment beside its tick: whole where a run starts (and with its
          // day where the day turns, or where it goes back), else how long
          // after the one before.
          const words = !timed ? null
            : !prev || !Number.isFinite(prev.at) || day(prev.at) !== day(e.at) || e.at < prev.at ? `${dayText(e.at)} ${hhmm(e.at)}`
              : prev.gap ? hhmm(e.at)
                : e.at - prev.at >= 1000 ? latencyText(e.at - prev.at) : "";
          const next = ordered[i + 1];
          const run = timed && next && Number.isFinite(next.at) ? w + EVIDENCE_GAP : w;
          const line = (
            <svg className="block shrink-0 overflow-visible" width={w} height={EVIDENCE_LINE_H} aria-hidden="true" data-evidence-when={e.id}>
              {timed ? (
                <>
                  <line x1={0} x2={run} y1={9.5} y2={9.5} style={{ stroke: "var(--line-strong)" }} />
                  <line x1={0.5} x2={0.5} y1={5} y2={14} style={{ stroke: "var(--muted)" }} />
                  {words ? <text x={5} y={13} style={EVIDENCE_WORDS}>{words}</text> : null}
                  {e.gap ? <text x={w + EVIDENCE_GAP / 2 + 6} y={13} textAnchor="end" style={{ ...EVIDENCE_WORDS, fill: "var(--muted-soft)" }}>{`${gapText(e.gap)} //`}</text> : null}
                </>
              ) : null}
            </svg>
          );
          if (lit) {
            return (
              <div key={e.id} className="pointer-events-none flex shrink-0 flex-col" style={{ width: w }}>
              <div
                {...pointAt}
                className="pointer-events-auto flex shrink-0 select-text flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
                style={{ width: EVIDENCE_OPEN_W, maxHeight: openMax }}
                data-evidence={e.id}
                data-evidence-open=""
              >
                <div className="flex shrink-0 items-start gap-[6px] border-b border-[color:var(--line-soft)] px-[10px] pb-[6px] pt-[7px] text-[11px] leading-[16px]">
                  <span className="mt-[1px]">{badge(e)}</span>
                  <span className="min-w-0 flex-1 break-words text-[color:var(--ink)]">{first ? first.on : "Not on this workspace"}</span>
                  {first && Number.isFinite(first.at) ? <span className="shrink-0 tabular-nums text-[color:var(--muted)]">{hhmm(first.at)}</span> : null}
                  <button type="button" onClick={() => before && onPick(before.id)} disabled={!before} aria-label={`The ${noun} before`} className={stepBtn} data-evidence-step="back"><StepChevron back /></button>
                  <button type="button" onClick={() => after && onPick(after.id)} disabled={!after} aria-label={`The ${noun} after`} className={stepBtn} data-evidence-step="on"><StepChevron /></button>
                  <button type="button" onClick={onClose} aria-label="Close it" className="grid h-[16px] w-[16px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">{closeIcon}</button>
                </div>
                <div className="min-h-0 overflow-y-auto px-[10px] pb-[9px] pt-[7px]" data-evidence-body="">
                  <div className="whitespace-pre-wrap break-words text-[12px] leading-[1.45] text-[color:var(--ink)]" data-evidence-words="">{e.words}</div>
                  {e.statement && e.statement !== e.words ? (
                    <>
                      <div className="mt-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">The claim</div>
                      <div className="mt-[2px] whitespace-pre-wrap break-words text-[12px] leading-[1.45] text-[color:var(--ink)]">{e.statement}</div>
                    </>
                  ) : null}
                  {e.walk?.length ? (
                    <div data-evidence-walked="">
                      <div className="mt-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">In the walkthrough</div>
                      {e.walk.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => onMoment?.(m.id)}
                          disabled={!onMoment}
                          className="mt-[2px] block w-full text-left text-[11.5px] leading-[1.4] text-[color:var(--ink)] enabled:cursor-pointer enabled:hover:text-[color:var(--muted)]"
                          data-evidence-walked-moment={m.id}
                        >
                          {`Moment ${m.n}`}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {e.covers?.length ? (
                    <div data-evidence-covers="">
                      <div className="mt-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Covers</div>
                      {e.covers.map((c) => (c.kind === "claim" ? (
                        <button
                          key={`claim:${c.id}`}
                          type="button"
                          onClick={() => onClaim?.(c.id)}
                          disabled={!c.here || !onClaim}
                          className="mt-[2px] block w-full break-words text-left text-[11.5px] leading-[1.4] text-[color:var(--muted)] enabled:cursor-pointer enabled:hover:text-[color:var(--ink)]"
                          data-evidence-cover={c.id}
                        >
                          <span className="text-[color:var(--ink)]">{c.n ? `Claim ${c.n}` : "A claim"}</span>
                          {c.statement ? `${DOT}${c.statement.length > 160 ? `${c.statement.slice(0, 157)}...` : c.statement}` : c.here ? "" : `${DOT}no longer in the chat`}
                        </button>
                      ) : (
                        <div key={`case:${c.id}`} className="mt-[2px] break-words text-[11.5px] leading-[1.4] text-[color:var(--muted)]" data-evidence-cover-note={c.id}>
                          <span className="text-[color:var(--ink)]">The case's note</span>
                          {c.text ? `${DOT}${c.text}` : ""}
                        </div>
                      )))}
                    </div>
                  ) : null}
                  {r.length ? (
                    <>
                      <div className="mt-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Rests on</div>
                      {r.map((x, i) => (
                        <div key={i} className="mt-[2px] break-words text-[11.5px] leading-[1.4] text-[color:var(--muted)]">
                          <span className="text-[color:var(--ink)]">{x.on}</span>
                          {`${DOT}${x.what}${Number.isFinite(x.at) ? `${DOT}${hhmm(x.at)}` : ""}`}
                        </div>
                      ))}
                    </>
                  ) : null}
                  {onChat ? (
                    <div className="mt-[10px] flex">
                      <HoverTooltip placement="top" label={`Puts this ${noun} into the chat in front, to ask about it there`}>
                        <button type="button" onClick={() => onChat(e)} className={AGENT_ACTION_SMALL} data-evidence-add-to-chat={e.id}>Add to chat</button>
                      </HoverTooltip>
                    </div>
                  ) : null}
                </div>
              </div>
              {line}
              </div>
            );
          }
          return (
            <div key={e.id} className="pointer-events-none flex shrink-0 flex-col" style={{ width: w }}>
            <button
              type="button"
              onClick={() => onPick(e.id)}
              {...pointAt}
              className={`pointer-events-auto flex shrink-0 cursor-pointer flex-col gap-[3px] overflow-hidden rounded-[2px] border px-[10px] py-[7px] text-left shadow-lg ${pointed === e.id ? "border-[color:var(--muted)] bg-[color:var(--bg)]" : "border-[color:var(--line-strong)] bg-[color:var(--bg)] hover:border-[color:var(--muted)]"}`}
              style={{ width: EVIDENCE_CARD_W, height: EVIDENCE_H - 18 }}
              data-evidence={e.id}
            >
              <span className="flex min-w-0 items-center gap-[6px] text-[11px] leading-[14px]">
                {badge(e)}
                <span className="min-w-0 truncate text-[color:var(--ink)]">{first ? first.on : "Not on this workspace"}</span>
              </span>
              <span className="line-clamp-3 text-[12px] leading-[1.4] text-[color:var(--ink)]" data-evidence-words="">{e.words}</span>
              {first ? <span className="mt-auto min-w-0 truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{`${first.what}${r.length > 1 ? `${DOT}${r.length - 1} more` : ""}`}</span> : null}
            </button>
            {line}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// The investigation's steps over the board's strip: the picture after any of
// the chat's steps, following the chat until one is picked. `inset`: room
// kept at their right for the board's close (BoardClose), as the row's words
// keep it.
export const BoardSteps = memo(function BoardSteps({ steps, step, following, onPick, inset = 0 }) {
  const cur = steps[step];
  const btn = "grid h-[16px] w-[16px] place-items-center rounded-[2px] text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)] disabled:opacity-40";
  return (
    <div className="absolute top-[6px] z-[5] flex h-[16px] max-w-[46%] items-center gap-[6px] text-[10.5px]" style={{ right: 16 + inset }} data-board-steps="">
      <HoverTooltip placement="bottom" label={cur ? `Step ${step + 1}: ${stepText(cur)}` : "The chat's steps"}>
        <span className="min-w-0 truncate text-[color:var(--muted)]">{cur ? stepText(cur) : ""}</span>
      </HoverTooltip>
      <button type="button" className={btn} aria-label="Previous step" disabled={step <= 0} onClick={() => onPick(step - 1)}><StepChevron back /></button>
      <span className="shrink-0 tabular-nums text-[color:var(--ink)]">{`step ${step + 1} of ${steps.length}`}</span>
      <button type="button" className={btn} aria-label="Next step" disabled={step >= steps.length - 1} onClick={() => onPick(step + 1 >= steps.length - 1 ? null : step + 1)}><StepChevron /></button>
      <div className="flex h-[6px] w-[96px] shrink-0 gap-[1px]" role="group" aria-label="Steps">
        {steps.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`Step ${i + 1}: ${stepText(s)}`}
            onClick={() => onPick(i >= steps.length - 1 ? null : i)}
            className={`h-full min-w-[2px] flex-1 cursor-pointer border-0 p-0 ${i === step ? "bg-[color:var(--graph)]" : i < step ? "bg-[color:var(--line-strong)]" : "bg-[color:var(--line-soft)]"}`}
          />
        ))}
      </div>
      {/* On the chat's last step it says nothing more (its last segment is
          lit); back on an earlier one, it offers the way back. "Following the
          chat" is the corner control's, for laying on what the chat finds. */}
      {following ? null : <button type="button" className="shrink-0 text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]" onClick={() => onPick(null)}>latest</button>}
    </div>
  );
});

// A card's grips and buttons over the board, in the board's own coordinates:
// the layer holding them moves as the board scrolls, so scrolling draws none of
// them again. Each edge's grip sits at that edge's middle; the top edge leaves
// its right end to the buttons.
const toolIcon = (d) => <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
// A workspace inside's picture in the room its card gives it. The picture is
// laid out for a room it keeps until the card's room has held still a moment
// (INSET_SETTLE_MS), and is scaled to the room as it changes until then: a
// card being sized, a head held larger as the board zooms out, the board
// settling around it draw the picture again once, not at every step.
const INSET_SETTLE_MS = 160;
function InsetRoom({ w, h, children }) {
  const [room, setRoom] = useState(() => ({ w, h }));
  useEffect(() => {
    if (Math.abs(room.w - w) < 1 && Math.abs(room.h - h) < 1) return undefined;
    const t = setTimeout(() => setRoom({ w, h }), INSET_SETTLE_MS);
    return () => clearTimeout(t);
  }, [w, h, room.w, room.h]);
  const k = Math.min(w / room.w, h / room.h);
  return (
    <div className="absolute left-0 top-0" style={{ width: room.w, height: room.h, transform: Math.abs(k - 1) > 1e-3 ? `scale(${k})` : undefined, transformOrigin: "0 0" }}>
      {children(room)}
    </div>
  );
}

const CardHandles = memo(function CardHandles({ cg, inHand, edge, settle, isAnchor, canAnchor = true, canBranch = false, canEnter = false, folded = false, graph = false, own = false, here = false, groupsLabel, tbX, tbW = CARD_TOOLBAR_W, hold = 1, acts, earlier = null }) {
  // A workspace inside is made taller to show what is inside it.
  const nest = graph && canEnter;
  const downLabel = nest ? "Drag to make it taller, showing what is inside it; double-click to reset" : "Drag to give it more or fewer rows; double-click to reset";
  const left0 = cg.x;
  const right1 = Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w));
  const top = cg.y;
  const bottom = cg.y + cg.h;
  const w = right1 - left0;
  const on = (e) => !!edge && edge.includes(e);
  const bar = (lit) => `absolute rounded-[2px] bg-[color:var(--ink)] transition-opacity ${lit ? "opacity-80" : inHand ? "opacity-30 group-hover:opacity-80" : "opacity-0 group-hover:opacity-80"}`;
  const tool = (pressed = false, shown = true) => `grid h-[20px] w-[22px] place-items-center ${shown ? "" : "invisible"} ${pressed ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]"}`;
  const topW = Math.max(0, w - 24 - CARD_TOOLBAR_W);
  // Both grips across the card on its middle line, whatever each zone spans.
  const midX = (zoneLeft, zoneW) => Math.max(0, Math.min(zoneW - 32, w / 2 - (zoneLeft - left0) - 16));
  const zones = [
    ["l", { left: left0 - 6, top: top + 12, width: 12, height: Math.max(0, cg.h - 24) }, "cursor-ew-resize", { left: 4.5, top: Math.max(0, (cg.h - 24) / 2 - 16), width: 3, height: 32 }, "Drag to widen or narrow its time from its start; double-click to reset"],
    ["r", { left: right1 - 6, top: top + 12, width: 12, height: Math.max(0, cg.h - 24) }, "cursor-ew-resize", { left: 4.5, top: Math.max(0, (cg.h - 24) / 2 - 16), width: 3, height: 32 }, "Drag to widen or narrow its time; double-click to reset"],
    ["t", { left: left0 + 12, top: top - 14, width: topW, height: 20 }, "cursor-ns-resize", topW >= 32 ? { left: midX(left0 + 12, topW), top: 12.5, width: 32, height: 3 } : null, downLabel],
    ["b", { left: left0 + 12, top: bottom - 6, width: Math.max(0, w - 24), height: 6 + HANG_GAP + 4 }, "cursor-ns-resize", { left: midX(left0 + 12, Math.max(0, w - 24)), top: 4.5, width: 32, height: 3 }, downLabel],
    ["tl", { left: left0 - 10, top: top - 14, width: 22, height: 24 }, "cursor-nwse-resize", null, "Drag to size it"],
    ["tr", { left: right1 - 12, top: top - 14, width: 22, height: 24 }, "cursor-nesw-resize", null, "Drag to size it"],
    ["bl", { left: left0 - 10, top: bottom - 10, width: 22, height: 24 }, "cursor-nesw-resize", null, "Drag to size it"],
    ["rb", { left: right1 - 12, top: bottom - 10, width: 22, height: 24 }, "cursor-nwse-resize", null, "Drag to size it"],
  ];
  return (
    <>
      {settle ? (
        <div className="pointer-events-none absolute rounded-[2px] border border-dashed border-[color:var(--line-strong)]" style={{ left: left0, top: settle.top, width: w, height: settle.h }} data-card-settle="" />
      ) : null}
      {/* Folded to its head, it is sized along its time only; a machine's or
          a search's card keeps to its time and its rows. Laid out for a list
          over the board (cut to the stretch the list rests on and drawn
          across the board), its time is the list's while the list is up:
          sized along it then, its own time would be read off that cut, and
          kept. It still grows into more of its rows. */}
      {(cg.focus ? zones.filter(([e]) => e === "t" || e === "b") : (graph && !nest) || folded ? zones.filter(([e]) => e === "l" || e === "r") : zones).map(([e, style, cursor, grip, label]) => (
        <div
          key={e}
          role="separator"
          aria-label={label}
          className={`group pointer-events-auto absolute ${cursor}`}
          style={style}
          onPointerDown={(ev) => acts.startResize(ev, cg, e)}
          onPointerMove={acts.moveResize}
          onPointerUp={acts.endResize}
          onDoubleClick={() => acts.resetSize(cg.key, e)}
        >
          {grip ? <span className={bar(on(e))} style={grip} /> : null}
        </div>
      ))}
      <div
        className={`pointer-events-auto absolute flex items-stretch divide-x overflow-hidden rounded-[2px] border ${inHand ? "divide-[color:var(--line)] border-[color:var(--line-strong)] bg-[color:var(--panel)]" : "divide-transparent border-transparent"}`}
        // Held with its head (headHold) from its right end.
        style={{ left: tbX, top: top + CARD_TOOLBAR_INSET * hold, transform: hold !== 1 ? `scale(${hold})` : undefined, transformOrigin: "100% 0" }}
        onMouseEnter={() => acts.toolbarEnter(cg.key)}
        onMouseLeave={() => acts.toolbarLeave(cg.key)}
        data-card-toolbar=""
      >
        {canEnter ? (
          <HoverTooltip placement="top" label="Go into this workspace (or zoom in on it, or double-click it); zoom out past its heads, or its name in the bar, to come back">
            <button type="button" className={tool(false, inHand)} aria-label="Go in" onClick={() => acts.enter(cg.key)}>{toolIcon("M6.5 3.5h6v9h-6M2.5 8h7M7.5 5.5 10 8l-2.5 2.5")}</button>
          </HoverTooltip>
        ) : (
          <HoverTooltip placement="top" label="Zoom to it (double-click the card)">
            <button type="button" className={tool(false, inHand)} aria-label="Zoom to it" onClick={() => acts.fit(cg.key)}>{toolIcon("M2.5 6V2.5H6M13.5 6V2.5H10M2.5 10v3.5H6M13.5 10v3.5H10")}</button>
          </HoverTooltip>
        )}
        {graph ? null : (
          <HoverTooltip placement="top" label={groupsLabel}>
            <button type="button" className={tool(false, inHand)} aria-label="Groups" onClick={() => acts.toggleGroups(cg.key)}>{toolIcon("M3 4.5h10M3 8h10M3 11.5h6")}</button>
          </HoverTooltip>
        )}
        <HoverTooltip placement="top" label={folded ? "Open it: its rows, and what hangs off it" : "Fold it to its head; what hangs off it waits until it opens"}>
          <button type="button" className={tool(folded, inHand || folded)} aria-label={folded ? "Open it" : "Fold it to its head"} aria-pressed={folded} onClick={() => acts.fold(cg.key)}>
            {toolIcon(folded ? "M4 6.5l4 4 4-4" : "M4 9.5l4-4 4 4")}
          </button>
        </HoverTooltip>
        {!graph && isAnchor && !canAnchor ? null : here ? (
          <HoverTooltip placement="top" label="Close it; it folds back to its head here">
            <button type="button" className={tool(false, inHand)} aria-label="Close it" onClick={() => acts.takeOff(cg.key)}>{toolIcon("M4.5 4.5l7 7M11.5 4.5l-7 7")}</button>
          </HoverTooltip>
        ) : (
          <HoverTooltip placement="top" label={own ? "Take it off the workspace; Undo in the bar brings it back" : graph ? "Take it off the workspace; the chat's graph keeps it" : isAnchor ? "Take it off the workspace; the next most important trace anchors it" : "Take it off the workspace"}>
            <button type="button" className={tool(false, inHand)} aria-label="Take it off the workspace" onClick={() => acts.takeOff(cg.key)}>{toolIcon("M4.5 4.5l7 7M11.5 4.5l-7 7")}</button>
          </HoverTooltip>
        )}
        {!graph && !isAnchor && !canAnchor && canBranch ? (
          <HoverTooltip placement="top" label="Out to its machine: a card of the machine's sessions on this workspace">
            <button type="button" className={tool(false, inHand)} aria-label="Its machine" onClick={() => acts.branch(cg.key)}>{toolIcon("M3 3.5h10v6.5H3zM6 13h4M8 10v3")}</button>
          </HoverTooltip>
        ) : null}
        {graph || (!isAnchor && !canAnchor) ? null : (
          <HoverTooltip placement="top" label={isAnchor ? "This trace anchors the workspace: its strings are what the wires find" : "Make it the anchor"}>
            <button type="button" className={tool(isAnchor, inHand || isAnchor)} aria-label={isAnchor ? "The anchor" : "Make it the anchor"} aria-pressed={isAnchor} onClick={() => acts.anchor(cg.key)}>
              {isAnchor ? <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M8 2.5 13.5 8 8 13.5 2.5 8Z" fill="currentColor" /></svg> : toolIcon("M8 2.5 13.5 8 8 13.5 2.5 8Z")}
            </button>
          </HoverTooltip>
        )}
      </div>
      {earlier ? (
        // The page before the oldest turn it holds, beside the toolbar and
        // shown with it (the card's left edge is often the board's), or
        // while it reads or after it failed. Held with the toolbar's scale,
        // its right end at the toolbar's left.
        <div
          className="pointer-events-auto absolute"
          style={{ left: tbX + tbW * (1 - hold) - 4 * hold, top: top + CARD_TOOLBAR_INSET * hold, transform: `translateX(-100%)${hold !== 1 ? ` scale(${hold})` : ""}`, transformOrigin: "100% 0" }}
          onMouseEnter={() => acts.toolbarEnter(cg.key)}
          onMouseLeave={() => acts.toolbarLeave(cg.key)}
          data-card-earlier={cg.key}
        >
          <HoverTooltip wide placement="top" label={earlier.help}>
            {earlier.busy ? (
              <span tabIndex={0} className="flex h-[22px] items-center whitespace-nowrap rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[7px] text-[9px] uppercase leading-none tracking-[0.07em] text-[color:var(--muted-soft)]">
                {earlier.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => acts.earlier(cg.key)}
                className={`flex h-[22px] items-center whitespace-nowrap rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[7px] text-[9px] uppercase leading-none tracking-[0.07em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] ${inHand || earlier.failed ? "" : "invisible"}`}
              >
                {earlier.label}
              </button>
            )}
          </HoverTooltip>
        </div>
      ) : null}
    </>
  );
});

// What a draw of the board can be pointed at, by kind. The card being
// carried is drawn with the same lists, so its draw has every list a card's
// draw fills.
const boardHits = () => ({ cards: [], stubs: [], wires: [], cont: [], flags: [], resets: [], graphMarks: [], graphRows: [], graphLays: [], claimBadges: [], insetCards: [] });

const NO_SHIFT = Object.freeze({ dx: 0, dy: 0 });

// The pauses the clock closed: a hairline at each through the cards it cuts
// (each `shift` from where the board laid it, as the card in hand is).
function drawPauses(ctx, st, theme, cards, { SX, SY, W, shift = NO_SHIFT }) {
  ctx.fillStyle = theme.lineSoft;
  for (const br of st.clock.breaks) {
    const x = SX(PAD_L + ((br.u0 + br.u1) / 2) * st.k);
    if (x < MACH_W || x > W) continue;
    for (const cg of cards) {
      if (x <= SX(cg.x + shift.dx) || x >= SX(cg.x + shift.dx + cg.w)) continue;
      ctx.fillRect(Math.round(x), SY(cg.bodyY + shift.dy), 1, cg.y + cg.h - cg.bodyY);
    }
  }
}

// A wire's curve, under the cards and lines it joins, with a dot where it
// leaves each card.
function drawWireCurve(ctx, w, theme, SX, SY) {
  ctx.globalAlpha = w.alpha;
  ctx.strokeStyle = w.hue || theme.join;
  ctx.lineWidth = w.lw;
  ctx.setLineDash(w.dashed ? [5, 4] : []);
  ctx.beginPath();
  ctx.moveTo(SX(w.route.ea.x), SY(w.route.ea.y));
  ctx.bezierCurveTo(SX(w.route.c0.x), SY(w.route.c0.y), SX(w.route.c1.x), SY(w.route.c1.y), SX(w.route.eb.x), SY(w.route.eb.y));
  ctx.stroke();
  ctx.fillStyle = w.hue || theme.join;
  for (const e of [w.route.ea, w.route.eb]) {
    ctx.beginPath();
    ctx.arc(SX(e.x), SY(e.y), 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

// A wire's drop at the cell's own time, and the points it lands on.
function drawWireEnds(ctx, w, theme, SX, SY) {
  ctx.globalAlpha = w.alpha;
  const hue = w.hue || theme.join;
  ctx.strokeStyle = hue;
  ctx.lineWidth = 1;
  if (w.lead) {
    // From a card in a workspace's picture, its way out of that card.
    ctx.lineWidth = w.lw;
    ctx.setLineDash(w.dashed ? [5, 4] : []);
    ctx.beginPath();
    ctx.moveTo(SX(w.A.x), SY(w.A.y));
    ctx.lineTo(SX(w.route.ea.x), SY(w.route.ea.y));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
  } else if (w.label || w.drop) {
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(SX(w.A.x), SY(w.A.y));
    ctx.lineTo(SX(w.route.ea.x), SY(w.route.ea.y));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  for (const P of [w.A, w.B]) {
    ctx.fillStyle = hue;
    ctx.beginPath();
    ctx.arc(SX(P.x), SY(P.y), 3.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = theme.bg;
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
}

// A wire with an end on the card in hand, that end carried on with it (the
// board routes it again when it lays the card).
function carriedWire(w, key, { dx, dy }) {
  const on = (p) => ({ x: p.x + dx, y: p.y + dy });
  const a = w.aKey === key ? on : (p) => p;
  const b = w.bKey === key ? on : (p) => p;
  const r = w.route;
  return { ...w, A: a(w.A), B: b(w.B), route: { ...r, a: a(r.a), ea: a(r.ea), c0: a(r.c0), c1: b(r.c1), eb: b(r.eb), b: b(r.b) } };
}

// What hangs from the cards, drawn through SX and SY: a note's pin rings its
// cell; an attachment rings the cells it is about (firmly while it is in
// hand), and in hand, or standing, it is tied up from its moment to each. In
// rows (compared) they are tied only in hand. A count's cells are ringed in
// the colours of the ones it holds. What hangs from a card fades back with it
// (st.dimKey), and shows only where no card over it lies (`covers`).
function attachmentInk(ctx, st, theme, { SX, SY, W, H, cam, covers = null }) {
  const g = st.geo;
  const RULER_H = st.rulerH;
  const inkOf = (n) => (n.kind === "finding" ? severityInk(theme, n.severity)
    : n.kind === "note" ? (NOTE_TONE[n.tone] ? severityInk(theme, NOTE_TONE[n.tone]) : theme.ink)
      : n.kind === "reading" || n.kind === "agent" || n.kind === "cluster" ? theme.ink : theme.graph);
  // A card's cells under its head, held over them zoomed far out, are neither
  // ringed nor tied to.
  const underHead = (key, y) => {
    if (!(cam < HOLD_CAM)) return false;
    const cg = g.cards.get(key);
    return !!cg && y < cg.y + headHold(cam, cg.h).foot;
  };
  const ringCell = (pt, color, inHand) => {
    const px = Math.round(SX(pt.x));
    const py = Math.round(SY(pt.y));
    if (py < RULER_H - 12 || py > H + 12 || px < MACH_W - 20 || px > W + 20) return;
    const cw = Math.max(9, Math.min(28, (pt.w || 8) - 1));
    ctx.strokeStyle = inHand ? color : alphaMix(color, 70);
    ctx.lineWidth = inHand ? 2 : 1;
    ctx.strokeRect(px - cw / 2 - 1.5, py - RING_HALF, cw + 3, 2 * RING_HALF);
  };
  const fadeOf = (key) => (st.dimKey && key !== st.dimKey ? 1 - CARD_DIM_VEIL : 1);
  const underOthers = (key) => {
    const rs = covers?.get(key);
    if (!rs?.length) return false;
    ctx.save();
    ctx.beginPath();
    ctx.rect(-1e5, -1e5, 2e5, 2e5);
    for (const r of rs) ctx.rect(SX(r.x0), SY(r.y0), r.x1 - r.x0, r.y1 - r.y0);
    ctx.clip("evenodd");
    return true;
  };
  // A pinned note's cell is ringed as an attachment's is; its pin stands over
  // the board in the page's own elements (NotePin). An opened one's card
  // rings it already.
  const pin = (grp, p) => {
    ctx.globalAlpha = fadeOf(p.key);
    const clipped = underOthers(p.key);
    const inHand = st.hoverNode === grp.id || st.hoverNode === p.id || st.heldId === grp.id;
    for (const pt of p.pts) if (!underHead(p.key, pt.y)) ringCell(pt, inkOf(p), inHand);
    if (clipped) ctx.restore();
  };
  // NX and NY place the attachment itself (a block carried out of its card's
  // band keeps its own place down the board).
  const drawTies = (nd, NX, NY) => {
    const color = inkOf(nd);
    const inHand = st.hoverNode === nd.id || st.heldId === nd.id;
    const rings = attachRings(nd).filter(([pt, owner]) => !underHead(owner.key ?? nd.key, pt.y));
    for (const [pt, owner] of rings) ringCell(pt, inkOf(owner), inHand);
    const mx = NX(nd.ax != null ? Math.max(nd.x + 12, Math.min(nd.x + nd.w - 12, nd.ax)) : nd.x + 12);
    const my = NY(nd.y);
    const fx = SX(nd.ax ?? 0);
    const fy = SY(nd.foot);
    ctx.lineWidth = 1;
    if (nd.ax != null) {
      // A pinned block may be carried anywhere: its line back to its moment
      // is drawn as its ties are, to be followed from wherever it went.
      ctx.strokeStyle = inHand ? color : nd.standing ? alphaMix(theme.ink, 50) : theme.lineStrong;
      ctx.beginPath();
      ctx.moveTo(fx, fy);
      ctx.lineTo(mx, my);
      ctx.stroke();
    }
    if (!(inHand || nd.standing) || !rings.length) return;
    for (const [pt, owner] of rings.slice(0, 6)) {
      const px = SX(pt.x);
      const py = SY(pt.y) + 9;
      ctx.strokeStyle = inHand ? alphaMix(inkOf(owner), 85) : alphaMix(theme.ink, 50);
      ctx.beginPath();
      if (nd.ax == null) {
        const bend = Math.max(14, Math.abs(my - py) * 0.5);
        ctx.moveTo(mx, my);
        ctx.bezierCurveTo(mx, my - bend, px, py + bend, px, py);
      } else {
        ctx.moveTo(fx, fy);
        ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
  };
  const ties = (nd, { NX = SX, NY = SY } = {}) => {
    ctx.globalAlpha = fadeOf(nd.key);
    const clipped = underOthers(nd.key);
    drawTies(nd, NX, NY);
    if (clipped) ctx.restore();
  };
  return { pin, ties, underHead };
}

// The card being carried, over everything on the board: the lists are the
// page's, above the board's own canvas, so it is drawn on one above them,
// with what is tied to it (its wires, the rings and ties of what hangs from
// it) under it as the board draws them. Carried on since the board last laid
// it (`shift`, in the board's units), all of it is drawn where the pointer
// has it.
function drawLift(ctx, st, theme, { W, H, dpr, view, shift = null }) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const g = st.geo;
  const c = st.movingKey ? g?.cards?.get(st.movingKey) : null;
  if (!c) return;
  const RULER_H = st.rulerH;
  const d = shift?.key === c.key ? shift : NO_SHIFT;
  const BX = (x) => MACH_W + x - view.x;
  const BY = (y) => RULER_H + y - view.y;
  const SX = (x) => BX(x + d.dx);
  const SY = (y) => BY(y + d.dy);
  ctx.save();
  ctx.beginPath();
  ctx.rect(MACH_W, RULER_H, W - MACH_W, H - RULER_H);
  ctx.clip();
  const cam = st.cam || 1;
  if (cam !== 1) {
    ctx.translate(MACH_W, RULER_H);
    ctx.scale(cam, cam);
    ctx.translate(-MACH_W, -RULER_H);
    W = MACH_W + (W - MACH_W) / cam;
    H = RULER_H + (H - RULER_H) / cam;
  }
  const unfade = fadeSmallText(ctx, cam);
  if (st.clock) drawPauses(ctx, st, theme, [c], { SX: BX, SY: BY, W, shift: d });
  const wires = (g.wires || []).filter((w) => w.aKey === c.key || w.bKey === c.key).map((w) => carriedWire(w, c.key, d));
  for (const w of wires) drawWireCurve(ctx, w, theme, BX, BY);
  ctx.globalAlpha = 1;
  ctx.setLineDash([]);
  const ink = attachmentInk(ctx, st, theme, { SX, SY, W, H, cam });
  for (const grp of st.pinGroups || []) for (const p of grp.members) if (!p.standing && p.key === c.key) ink.pin(grp, p);
  // A block carried out of the card's band goes along the board with it,
  // keeping its own place down it.
  const along = { NX: (x) => BX(x + d.dx), NY: BY };
  for (const nd of g.nodes || []) if (nd.key === c.key) ink.ties(nd, nd.free ? along : undefined);
  ctx.globalAlpha = 1;
  const cx = SX(c.x);
  const hx0 = c.headX != null ? SX(c.headX) : cx;
  const scratch = boardHits();
  const args = { cx, cy: SY(c.y), hx0, headW: c.headW ?? Math.max(c.w, HEAD_MIN_W), W, H, SX, SY };
  if (c.graph) drawGraphCard(ctx, c, st, theme, scratch, args);
  else drawBoardCard(ctx, c, st, theme, scratch, args);
  for (const w of wires) drawWireEnds(ctx, w, theme, BX, BY);
  ctx.globalAlpha = 1;
  unfade();
  ctx.restore();
}

// What ties one thing to another across the board, over every card and
// attachment so a line never ducks under one: the claim in hand's stems from
// its badge to everything it rests on, each end ringed, and the open panel's
// line to the cell it came from. Each runs on a halo of the board's own
// colour, so it reads over whatever it crosses.
function drawConnectors(ctx, st, theme, { W, H, dpr, view }) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const g = st.geo;
  if (!g) return;
  const RULER_H = st.rulerH;
  const cam = st.cam || 1;
  const SX = (x) => MACH_W + (x - view.x) * cam;
  const SY = (y) => RULER_H + (y - view.y) * cam;
  ctx.save();
  ctx.beginPath();
  ctx.rect(MACH_W, RULER_H, W - MACH_W, H - RULER_H);
  ctx.clip();
  const held = st.claimFocus ? claimBadgeSpots(st, W).find((b) => b.c.id === st.claimFocus) : null;
  const pts = held ? g.claimPoints?.get(held.c.id) || [] : [];
  if (pts.length) {
    const stems = () => {
      ctx.beginPath();
      for (const p of pts) {
        ctx.moveTo(held.x, RULER_H);
        ctx.lineTo(SX(p.x), SY(p.y));
      }
      ctx.stroke();
    };
    ctx.strokeStyle = theme.bg;
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = 3.5;
    stems();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = theme.graph;
    ctx.lineWidth = 1.25;
    stems();
    ctx.lineWidth = 1;
    for (const p of pts) {
      const px = SX(p.x);
      const py = SY(p.y);
      const r = p.cell ? 6 : 5;
      ctx.fillStyle = theme.bg;
      ctx.globalAlpha = 0.85;
      ctx.beginPath();
      ctx.arc(px, py, r + 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = theme.graph;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = theme.graph;
      ctx.beginPath();
      ctx.arc(px, py, 1.8, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // The claim the evidence along the board's foot points at: pointed at from
  // its card, the rest of the board under a veil clear around what it rests
  // on, and a line from the card to the first of them in sight above the
  // strip; what it rests on ringed in ink over a halo of the board's colour.
  if (st.evidenceIds?.size) {
    const pts = [...st.evidenceIds].flatMap((id) => st.evidencePoints?.get(id) || []);
    const from = st.evidenceFrom;
    if (from) {
      ctx.save();
      ctx.fillStyle = theme.bg;
      ctx.globalAlpha = EVIDENCE_VEIL;
      ctx.fillRect(MACH_W, RULER_H, W - MACH_W, H - RULER_H);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "destination-out";
      for (const p of pts) {
        ctx.beginPath();
        ctx.arc(SX(p.x), SY(p.y), (p.cell ? 9 : 7) + 9, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    const near = from ? pts.map((p) => ({ x: SX(p.x), y: SY(p.y), r: p.cell ? 9 : 7 })).find((q) => q.x >= MACH_W && q.x <= W && q.y >= RULER_H && q.y < from.y - 4) : null;
    if (near) {
      const dx = near.x - from.x;
      const dy = near.y - from.y;
      const d = Math.max(1, Math.hypot(dx, dy));
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(near.x - (dx / d) * near.r, near.y - (dy / d) * near.r);
      ctx.strokeStyle = theme.bg;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 3.5;
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1.25;
      ctx.stroke();
    }
    for (const p of pts) {
      const r = p.cell ? 9 : 7;
      ctx.beginPath();
      ctx.arc(SX(p.x), SY(p.y), r, 0, Math.PI * 2);
      ctx.strokeStyle = theme.bg;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.lineWidth = 1;
  }
  if (st.detailLine) {
    const d = st.detailLine;
    ctx.strokeStyle = alphaMix(theme.ink, 55);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(d.ax, d.ay);
    ctx.lineTo(d.bx, d.by);
    ctx.stroke();
    ctx.fillStyle = theme.ink;
    ctx.beginPath();
    ctx.arc(d.ax, d.ay, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// Everything the canvas draws, in screen space: the plane scrolls under a
// rail of machines and a ruler, and the clock's zoom `k` is how many pixels a
// clock unit takes, so zooming in spreads the work out instead of magnifying
// it. Returns where the pointer can land, read back by the hit test.
// The board's ground: the app's diagram sheet texture (Sheet's
// --diagram-dot dots, 0.9px on a 7px pitch).
const GROUND_DOTS = { size: 7, dotX: 3.5, dotY: 3.5, dotRadius: 0.9 };

function drawFocus(ctx, st, theme, { W, H, dpr, view }) {
  const RULER_H = st.rulerH;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // A still picture lies on the ground of the card it is drawn in.
  if (st.still) ctx.clearRect(0, 0, W, H);
  else {
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, W, H);
  }
  const hits = boardHits();
  const g = st.geo;
  if (!g || !st.clock) return hits;
  const SX = (x) => MACH_W + x - view.x;
  const SY = (y) => RULER_H + y - view.y;
  // The strip and the claims' badges over it keep the screen's own size.
  const screen = { W, H, planeW: W - MACH_W };
  const badges = claimBadgeSpots(st, W);

  ctx.save();
  ctx.beginPath();
  ctx.rect(MACH_W, RULER_H, screen.planeW, H - RULER_H);
  ctx.clip();
  // Under the camera the board is drawn at its own size into a canvas as
  // big as the view holds of it (W, H, planeW below), scaled onto the screen
  // about the plane's corner; what it records to hit is in the same units.
  const cam = st.cam || 1;
  if (cam !== 1) {
    ctx.translate(MACH_W, RULER_H);
    ctx.scale(cam, cam);
    ctx.translate(-MACH_W, -RULER_H);
    W = MACH_W + screen.planeW / cam;
    H = RULER_H + (screen.H - RULER_H) / cam;
  }
  const unfade = fadeSmallText(ctx, cam);
  // Zoomed far out, the board's own words (a tab to a card off the view, a
  // wire's label) are held at a size to read, as cards' heads are.
  const holdK = cam < HOLD_CAM ? HOLD_CAM / cam : 1;
  const planeW = W - MACH_W;

  // The ground the traces lie on: a sheet of dots fixed to the board. A
  // board's cards keep times and scales of their own, so the clock's ticks run
  // only along the strip, and the pointer's guide gives the time down every
  // card. Laid from the board's own origin (on a whole device pixel, so a
  // glide that stops between pixels leaves them crisp), so the dots move with
  // it.
  const dots = st.still ? null : getDotTilePattern(ctx, { color: theme.diagramDot || theme.lineSoft, ...GROUND_DOTS });
  if (dots) {
    const ox = Math.round(SX(0) * dpr) / dpr;
    const oy = Math.round(SY(0) * dpr) / dpr;
    ctx.save();
    ctx.translate(ox, oy);
    ctx.fillStyle = dots;
    ctx.fillRect(MACH_W - ox, RULER_H - oy, planeW, H - RULER_H);
    ctx.restore();
  }
  // A lens: its stretch faintly tinted, its edges where the scale changes.
  for (const l of st.clock.lenses || []) {
    const a = SX(PAD_L + l.u0 * st.k);
    const e = SX(PAD_L + l.u1 * st.k);
    if (e < MACH_W || a > W) continue;
    ctx.fillStyle = alphaMix(theme.green, 5);
    ctx.fillRect(a, RULER_H, e - a, H - RULER_H);
    ctx.fillStyle = alphaMix(theme.green, 35);
    ctx.fillRect(Math.round(a), RULER_H, 1, H - RULER_H);
    ctx.fillRect(Math.round(e), RULER_H, 1, H - RULER_H);
  }

  // The card in hand takes along what is tied to it: drawn with it on the
  // canvas above (drawLift), and left out here.
  const carried = st.movingKey && g.cards.has(st.movingKey) ? st.movingKey : null;
  const stays = (w) => !carried || (w.aKey !== carried && w.bKey !== carried);

  // The pauses the clock closed: a hairline at each through the cards it
  // cuts, the strip saying how long. Down the whole canvas, a month's clock
  // drew a comb of them over empty ground.
  drawPauses(ctx, st, theme, [...g.cards.values()].filter((cg) => cg.key !== carried), { SX, SY, W });

  // The wires' curves, under the cards and lines they join.
  for (const w of g.wires) if (stays(w)) drawWireCurve(ctx, w, theme, SX, SY);
  ctx.globalAlpha = 1;
  ctx.setLineDash([]);

  // The cards: the trace, and any session opened on the canvas, each its head
  // over its rows on one surface, the last carried over the rest (st.zOf).
  // The one the pointer rests on comes over them all while it does (the
  // pointer then finds it wherever it lies over another); the card in hand
  // draws last; the one being carried draws over the page's lists as well,
  // on the canvas above them (drawLift).
  const drawOrder = stackOrder([...g.cards.values()].filter((c) => c.key !== st.movingKey), { zOf: st.zOf || NO_Z, front: st.dimKey, sizing: st.resizingKey });
  const covers = coversOf(drawOrder);
  for (const c of drawOrder) {
    const cx = SX(c.x);
    const cy = SY(c.y);
    // On the board a head can start before its body: a short trace late in
    // the window keeps its head on the canvas.
    const hx0 = c.headX != null ? SX(c.headX) : cx;
    const headW = c.headW ?? Math.max(c.w, HEAD_MIN_W);
    if (cy > H || cy + c.h < RULER_H) continue;
    if (Math.min(cx, hx0) > W || Math.max(cx + c.w, hx0 + headW) < MACH_W) {
      // A card scrolled off the board sideways leaves a tab at that edge, at
      // its place down the board: what it is, how far away, a way to it.
      const data = st.cards.get(c.key) || (c.graph ? { titleText: c.label, firstMs: c.firstMs, lastMs: c.lastMs } : null);
      const left = Math.max(cx + c.w, hx0 + headW) < MACH_W;
      const tAt = (sx) => st.clock.inv((sx - MACH_W + st.view.x - PAD_L) / st.k);
      const far = left ? tAt(MACH_W + PAD_L) - data?.lastMs : data?.firstMs - tAt(W - 8);
      ctx.save();
      ctx.font = `11px ${SANS}`;
      // Folded to its way and how far until the pointer is on it: drawn in
      // full, a title laid over another card's rows read as part of that card.
      const open = st.hoverCont === `${c.key}:${left ? "l" : "r"}`;
      const span = Number.isFinite(far) && far > 0 ? spanText(far) : "";
      const tail = span ? `${DOT}${span} ${left ? "earlier" : "later"}` : "";
      const title = open ? clipText(ctx, data?.titleText || "", 220) : "";
      const said = open
        ? (left ? `\u2039 ${title}${tail}` : `${title}${tail} \u203a`)
        : (left ? `\u2039${span ? ` ${span}` : ""}` : `${span ? `${span} ` : ""}\u203a`);
      const tw = ctx.measureText(said).width;
      // Against the view's edge at its own size, held from there.
      const ax = left ? MACH_W + 6 : W - 6;
      const by = cy + 6;
      const bx = left ? ax : ax - tw - 16;
      ctx.translate(ax, by);
      ctx.scale(holdK, holdK);
      ctx.translate(-ax, -by);
      heldText = holdK;
      ctx.fillStyle = theme.panel;
      roundRect(ctx, bx, by, tw + 16, 20, 2);
      ctx.fill();
      ctx.strokeStyle = st.hoverCont === `${c.key}:${left ? "l" : "r"}` ? theme.ink : theme.lineStrong;
      ctx.lineWidth = 1;
      roundRect(ctx, bx + 0.5, by + 0.5, tw + 15, 19, 2);
      ctx.stroke();
      ctx.fillStyle = theme.ink;
      ctx.textAlign = "left";
      ctx.fillText(said, bx + 8, by + 14);
      heldText = 1;
      ctx.restore();
      const tabW = (tw + 16) * holdK;
      hits.cont.push({ key: c.key, side: left ? "l" : "r", x0: left ? ax : ax - tabW, x1: left ? ax + tabW : ax, y0: by, y1: by + 20 * holdK });
      continue;
    }
    if (c.graph) drawGraphCard(ctx, c, st, theme, hits, { cx, cy, hx0, headW, W, H, SX, SY });
    else drawBoardCard(ctx, c, st, theme, hits, { cx, cy, hx0, headW, W, H, SX, SY });
    // The pointer resting on another card, this one fades back under a veil
    // of the ground, so the one pointed at reads among the rest.
    if (st.dimKey && c.key !== st.dimKey) {
      const x0 = Math.min(cx, hx0);
      ctx.save();
      ctx.globalAlpha = CARD_DIM_VEIL;
      ctx.fillStyle = theme.bg;
      ctx.fillRect(x0 - 1, cy - 1, Math.max(cx + c.w, hx0 + headW) - x0 + 2, c.h + 2);
      ctx.restore();
    }
  }

  // Attachments: each hangs from its card's foot at its moment by a hairline
  // down to its mark, its cells ringed and tied to (attachmentInk); a pinned
  // note's cell ringed as an attachment's is.
  const ink = attachmentInk(ctx, st, theme, { SX, SY, W, H, cam, covers });
  for (const grp of st.pinGroups || []) for (const p of grp.members) if (!p.standing && p.key !== carried) ink.pin(grp, p);
  for (const nd of g.nodes || []) if (nd.key !== carried) ink.ties(nd);
  ctx.globalAlpha = 1;

  for (const lc of g.loading || []) {
    if (SY(lc.y + lc.h) < RULER_H || SY(lc.y) > H) continue;
    drawLoadingCard(ctx, lc, st, theme, { SX, SY, W });
  }

  // In the session graph, every related session not open: a stub, a
  // placeholder for it until it is loaded, an empty slot on the ground framed
  // dashed. On its one line its hue and its title, where it ran, then what
  // ran it, when and how much it shares, and Load; its time on its foot, with
  // a dot where it hands off to the anchor.
  for (const sb of g.stubs?.values() || []) {
    const y = SY(sb.y);
    if (y > H || y + sb.h < RULER_H) continue;
    const bx = SX(sb.x);
    if (Math.min(bx + sb.w, W + 4) < MACH_W || bx > W) continue;
    const hot = st.hoverKey === sb.key || st.sel?.key === sb.key;
    const dim = st.focusKey && st.focusKey !== sb.key;
    ctx.globalAlpha = dim ? 0.6 : 1;
    ctx.fillStyle = hot ? overMix(theme.ink, 5, theme.bg) : theme.bg;
    roundRect(ctx, bx, y, sb.w, sb.h, 2);
    ctx.fill();
    ctx.strokeStyle = hot ? theme.lineStrong : theme.line;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    roundRect(ctx, bx + 0.5, y + 0.5, sb.w - 1, sb.h - 1, 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Held at the canvas's left edge while the stub runs on past it.
    const left = Math.max(bx, MACH_W);
    const right = Math.min(bx + sb.w, W) - 10;
    const base = y + 16.5;
    ctx.fillStyle = sb.hue || theme.muted;
    ctx.beginPath();
    ctx.arc(left + CARD_GLYPH_X, y + 12.5, 3.5, 0, Math.PI * 2);
    ctx.fill();
    const tx = left + CARD_TEXT_X;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "right";
    ctx.font = HEAD_FONT.label;
    ctx.fillStyle = hot ? theme.ink : theme.muted;
    const load = "Load \u203a";
    ctx.fillText(load, right, base);
    const end = right - ctx.measureText(load).width - 10;
    ctx.textAlign = "left";
    // Its title gives where it ran room, down to a few words of its own.
    ctx.font = HEAD_FONT.where;
    const whereText = `${DOT}${sb.machine || "Unknown machine"}${sb.sameMachine ? " (same machine)" : ""}`;
    const whereW = ctx.measureText(whereText).width;
    ctx.font = `500 12px ${SANS}`;
    ctx.fillStyle = hot ? theme.ink : alphaMix(theme.ink, 70);
    const title = clipText(ctx, sb.label || "", Math.max(Math.min(90, end - tx), end - tx - whereW));
    ctx.fillText(title, tx, base);
    let sx2 = tx + ctx.measureText(title).width;
    if (end - sx2 > 40) {
      ctx.font = HEAD_FONT.where;
      ctx.fillStyle = theme.muted;
      const where = clipText(ctx, whereText, end - sx2);
      ctx.fillText(where, sx2, base);
      sx2 += ctx.measureText(where).width;
    }
    if (sb.sub && end - sx2 > 40) {
      ctx.font = HEAD_FONT.label;
      ctx.fillStyle = theme.muted;
      ctx.fillText(clipText(ctx, `${DOT}${sb.sub}`, end - sx2), sx2, base);
    }
    // Its time, on its foot's edge.
    const a = SX(sb.x0);
    const b = SX(sb.x1);
    ctx.fillStyle = overMix(sb.hue || theme.muted, hot ? 85 : 55, theme.panel);
    ctx.fillRect(a, SY(sb.barY) - 1.5, Math.max(3, b - a), 2.5);
    const dotX = g.dots?.get(sb.key);
    if (dotX != null) {
      ctx.fillStyle = sb.hue || theme.join;
      ctx.beginPath();
      ctx.arc(SX(dotX), SY(sb.barY), 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = theme.bg;
      ctx.lineWidth = 1.2;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    hits.stubs.push({ key: sb.key, x0: bx, x1: bx + sb.w, y0: y, y1: y + sb.h });
  }

  // Each wire's drop at the cell's own time, and the points it lands on. On
  // the board only the wires of the trace in focus drop through their card;
  // the rest show where they land.
  for (const w of g.wires) {
    if (!stays(w)) continue;
    drawWireEnds(ctx, w, theme, SX, SY);
    hits.wires.push(w);
  }
  ctx.globalAlpha = 1;

  // Hand-off labels on the curves of the session in focus, never on top of
  // one another, and only in the ground between cards, never over a card or
  // an annotation's row.
  const placed = [];
  const blocked = [];
  for (const c of g.cards.values()) blocked.push({ x0: SX(Math.min(c.x, c.headX ?? c.x)), x1: SX(Math.max(c.x + c.w, (c.headX ?? c.x) + (c.headW ?? c.w))), y0: SY(c.y), y1: SY(c.y + c.h) });
  for (const nd of g.nodes || []) blocked.push({ x0: SX(nd.x), x1: SX(nd.x + nd.w), y0: SY(nd.y), y1: SY(nd.y + nd.h) });
  for (const sb of g.stubs?.values() || []) blocked.push({ x0: SX(sb.x), x1: SX(sb.x + sb.w), y0: SY(sb.y), y1: SY(sb.y + sb.h) });
  const overlaps = (box, q) => box.x0 < q.x1 && q.x0 < box.x1 && box.y0 < q.y1 && q.y0 < box.y1;
  for (const w of g.wires) {
    if (!w.label || !stays(w)) continue;
    ctx.font = `10px ${SANS}`;
    const tw = ctx.measureText(w.label).width;
    let at = null;
    for (const t of [0.5, 0.35, 0.65, 0.2, 0.8, 0.1, 0.9]) {
      const p = routeAt(w.route, t);
      const box = { x0: SX(p.x) - (tw / 2 + 8) * holdK, x1: SX(p.x) + (tw / 2 + 8) * holdK, y0: SY(p.y) - 10 * holdK, y1: SY(p.y) + 10 * holdK };
      if (!placed.some((q) => overlaps(box, q)) && !blocked.some((q) => overlaps(box, q))) {
        at = { x: SX(p.x), y: SY(p.y) };
        placed.push(box);
        break;
      }
    }
    if (!at) continue;
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.scale(holdK, holdK);
    ctx.translate(-at.x, -at.y);
    heldText = holdK;
    ctx.fillStyle = overMix(theme.controlBg, 100, theme.bg);
    roundRect(ctx, at.x - tw / 2 - 6, at.y - 9, tw + 12, 18, 9);
    ctx.fill();
    ctx.strokeStyle = w.hue || theme.join;
    roundRect(ctx, at.x - tw / 2 - 6, at.y - 9, tw + 12, 18, 9);
    ctx.stroke();
    ctx.fillStyle = theme.ink;
    ctx.textAlign = "center";
    ctx.fillText(w.label, at.x, at.y + 3.5);
    ctx.textAlign = "left";
    heldText = 1;
    ctx.restore();
  }

  // The hovered cell, outlined.
  if (st.hoverCell) {
    const c = g.cards.get(st.hoverCell.key);
    const cy = c ? c.scene.centers.get(st.hoverCell.laneIds?.[0] ?? st.hoverCell.laneId) ?? c.scene.centers.get(st.hoverCell.laneId) : null;
    if (c && cy != null && !ink.underHead(c.key, c.bodyY + cy)) {
      // After the card's own rail (the board's cards keep one).
      const x = SX(c.x + (c.rail || 0) + st.hoverCell.turn * c.cellW);
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1;
      ctx.strokeRect(x - 0.5, SY(c.bodyY + cy) - 10, c.cellW + 1, 20);
    }
  }
  // The rest of its call, in the Lanes plane's call ring (the anchor ring's
  // glyph, lighter), on each mark the call touched.
  if (st.callRings) {
    const c = g.cards.get(st.callRings.key);
    if (c?.scene) {
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1;
      for (const r of st.callRings.rings) {
        const cy = c.scene.centers.get(r.lane);
        if (cy == null || ink.underHead(c.key, c.bodyY + cy)) continue;
        const held = c.scene.index.cells.find((m) => (m.laneId === r.lane || m.laneIds?.includes(r.lane)) && m.first <= r.turn && r.turn <= m.last);
        const x = held ? (held.x0 + held.x1) / 2 : (c.rail || 0) + (r.turn + 0.5) * c.cellW;
        ctx.beginPath();
        ctx.arc(SX(c.x + x), SY(c.bodyY + cy), 6.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }

  // The claim in hand (its badge hovered or clicked): the cards it rests on
  // outlined; its stems are drawn over the lists (drawConnectors).
  const held = st.claimFocus ? badges.find((b) => b.c.id === st.claimFocus) : null;
  if (held) {
    const on = new Set((g.claimPoints?.get(held.c.id) || []).map((p) => p.key).filter(Boolean));
    ctx.strokeStyle = alphaMix(theme.graph, 70);
    ctx.lineWidth = 1.5;
    for (const key of on) {
      const cg = g.cards.get(key);
      if (!cg) continue;
      const x0 = SX(Math.min(cg.x, cg.headX ?? cg.x));
      const x1 = SX(Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w)));
      roundRect(ctx, x0 - 1.5, SY(cg.y) - 1.5, x1 - x0 + 3, cg.h + 3, 3);
      ctx.stroke();
    }
  }
  const guide = boardGuide(st, W);
  const linkedT = guide?.t ?? null;
  // The pointer's moment down the board, where every card has it.
  if (guide?.onBoard) drawGuide(ctx, st, theme, guide.t, { W, H, SX, SY, planeW });
  // Past the cursor, the work had not happened yet: on every card at once.
  if (Number.isFinite(st.cursor)) drawBoardCursor(ctx, st, theme, st.cursor, { W, H, SX, SY, planeW });
  unfade();
  ctx.restore();

  if (!st.preview && !st.still) drawBoardStrip(ctx, st, theme, { W: screen.W, planeW: screen.planeW, hits, t: linkedT, guide, badges });
  return hits;
}

function hitFocus(hits, st, sx, sy) {
  const RULER_H = st.rulerH;
  if (!hits || !st.geo) return null;
  if (sy < RULER_H) {
    if (sx < MACH_W) return null;
    for (const b of hits.claimBadges || []) if (sx >= b.x0 && sx <= b.x1 && sy >= b.y0 && sy <= b.y1) return { type: "claimbadge", id: b.id, n: b.n };
    if (hits.claimRow && sy >= hits.claimRow.y0) return { type: "claimrow" };
    for (const l of hits.lenses || []) if (sx >= l.x0 && sx <= l.x1 && sy >= l.y0 && sy <= l.y1) return { type: "lens", id: l.id, close: sx >= l.closeX0 };
    const sc = hits.scale;
    if (sc && sy >= sc.y0 && sy <= sc.y1) return { type: "scale", u: scaleU(sc, st, sx) };
    // The strip: the window's edges and body, a trace's span, the track.
    const sp = hits.strip;
    const o = (hits.overview || []).find((it) => sx >= it.x0 && sx <= it.x1 && sy >= it.y0 && sy <= it.y1);
    if (!sp || sy < sp.y0 || sy > sp.y1 || sx < sp.x0 - 6 || sx > sp.x1 + 6) return { type: "ruler" };
    const part = Math.abs(sx - sp.wa) <= 5 ? "edge-l" : Math.abs(sx - sp.wb) <= 5 ? "edge-r" : sx > sp.wa && sx < sp.wb ? "body" : "track";
    return { type: "strip", part, key: o?.key || null, u: sp.u0 + Math.max(0, Math.min(sp.U, ((sx - sp.x0) / sp.w) * sp.U)) };
  }
  const cam = st.cam || 1;
  sx = MACH_W + (sx - MACH_W) / cam;
  sy = RULER_H + (sy - RULER_H) / cam;
  for (const m of hits.resets || []) if (sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "reset", key: m.key };
  for (const m of hits.cont || []) if (sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "cont", key: m.key, side: m.side };
  for (const m of hits.flags || []) if (sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "cardflag", key: m.key };
  for (let i = hits.cards.length - 1; i >= 0; i--) {
    const c = hits.cards[i];
    if (sx < Math.max(c.x0, MACH_W) || sx > c.x1 || sy < c.y0 || sy > c.y1) continue;
    // Beside a short body, under its wider head, is the canvas, not the card.
    if (sy >= c.headY1 && sx > c.bodyX1) continue;
    for (const m of hits.graphMarks || []) if (m.key === c.key && sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "gmark", key: c.key, mark: m.mark };
    if (sy < c.headY1) return { type: "cardhead", key: c.key };
    if (c.graph) {
      for (const m of hits.graphLays || []) if (m.key === c.key && sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "glay", key: c.key, row: m.row };
      for (const m of hits.insetCards || []) if (m.key === c.key && sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "insetcard", key: c.key, inner: m.inner, title: m.title };
      for (const m of hits.graphMarks || []) if (m.key === c.key && sx >= m.x0 && sx <= m.x1 && sy >= m.y0 && sy <= m.y1) return { type: "gmark", key: c.key, mark: m.mark };
      for (const r of hits.graphRows || []) if (r.key === c.key && sx >= r.x0 && sx <= r.x1 && sy >= r.y0 && sy <= r.y1) return { type: "grow", key: c.key, row: r.row };
      return { type: "gcard", key: c.key };
    }
    const card = st.geo.cards.get(c.key);
    const local = sx - c.ox;
    const x = local >= 0 && local < (c.rail || 0) ? local : sx - c.cx;
    const inner = hitTestScene(card.scene, x, sy - c.bodyY);
    return inner ? { ...inner, key: c.key } : { type: "cardhead", key: c.key };
  }
  for (const l of hits.stubs || []) if (sx >= l.x0 && sx <= l.x1 && sy >= l.y0 && sy <= l.y1) return { type: "stub", key: l.key };
  let best = null;
  const view = st.view;
  const toScreen = (r) => ({
    ea: { x: MACH_W + r.ea.x - view.x, y: RULER_H + r.ea.y - view.y },
    c0: { x: MACH_W + r.c0.x - view.x, y: RULER_H + r.c0.y - view.y },
    c1: { x: MACH_W + r.c1.x - view.x, y: RULER_H + r.c1.y - view.y },
    eb: { x: MACH_W + r.eb.x - view.x, y: RULER_H + r.eb.y - view.y },
  });
  hits.wires.forEach((w, index) => {
    const d = distToRoute(toScreen(w.route), sx, sy);
    if (d <= 6 && (!best || d < best.d)) best = { d, index };
  });
  if (best) return { type: "wire", index: best.index, key: hits.wires[best.index].key };
  return null;
}

// The review's findings on each trace on the board, read once per session
// (through the trace page's cache) and placed on that trace's own cells.
// A read that fails leaves the trace without them: the list is additive.
function useCanvasFindings(sources, turnsToJoinInputs, enabled) {
  const [rows, setRows] = useState(() => new Map());
  const asked = useRef(new Set());
  const alive = useRef(true);
  // The board's reads of its cards' findings, cancelled when it goes, and
  // asked again should it come back (as a strict remount does): each goes on
  // from what the last one kept.
  const reading = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      reading.current?.abort();
      reading.current = null;
      asked.current.clear();
    };
  }, []);
  // Each trace's findings are read back as far as its record holds messages
  // (its first message's time), and on again when the record reaches further.
  const wantKey = enabled ? sources.map((src) => `${src.sessionId || ""}@${src.firstMs}`).join(",") : "";
  useEffect(() => {
    if (!enabled) return;
    if (!reading.current) reading.current = new AbortController();
    const { signal } = reading.current;
    const keep = (id, got) => setRows((m) => (m.get(id) === got ? m : new Map(m).set(id, got)));
    for (const src of sources) {
      const id = src.sessionId;
      const ask = `${id}@${src.firstMs}`;
      if (!id || asked.current.has(ask)) continue;
      asked.current.add(ask);
      const have = findingsCovering(id, src.firstMs);
      if (have) {
        keep(id, have);
        continue;
      }
      const partial = readFindingsCache(id);
      if (partial) keep(id, partial);
      readTraceReviewFindings({ sessionId: id, sinceMs: src.firstMs, signal, fetch: fetchTraceReviewFindings }).then((res) => {
        if (alive.current && !signal.aborted) keep(id, res);
      }, (err) => {
        if (!signal.aborted) console.warn("[board] trace review findings unavailable:", err?.message || err);
      });
    }
  }, [wantKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Each card's report is kept while its findings, turns and lanes hold: a
  // trace laid on or taken off places only its own findings, not every
  // card's again.
  const made = useRef(new Map());
  return useMemo(() => {
    const out = new Map();
    const kept = new Map();
    for (const src of sources) {
      const got = src.sessionId ? rows.get(src.sessionId) : null;
      if (!got?.rows?.length || !src.turns?.length || !src.model) continue;
      const was = made.current.get(src.key);
      if (was && was.got === got && was.turns === src.turns && was.model === src.model) {
        out.set(src.key, was.report);
        kept.set(src.key, was);
        continue;
      }
      try {
        const report = buildFindingsReport(got.rows, turnsToJoinInputs(src.turns, src.model, { raw: true }), { truncated: got.truncated });
        out.set(src.key, report);
        kept.set(src.key, { got, turns: src.turns, model: src.model, report });
      } catch { /* a record the report cannot place reads without it */ }
    }
    made.current = kept;
    return out;
  }, [sources, rows, turnsToJoinInputs]);
}

// `layered` is the traces laid on by hand, the host's to hold (a trace's
// workspace keeps them across reloads); `onLayer`/`onUnlayer`/`onAnchor` hand
// the reader's changes back to it. What the reader did there (readings,
// hidden, sizes, order) is the host's too where it passes it, else the
// canvas's own. `publish` makes this the board the page's sidebar and add
// sheet read: the one in front. `onEnter(id, from)` goes into a workspace
// inside this one (`from`, its card's place on screen), `onExit` back out;
// `flash` ({ key, n }) points a card out. `range`: the workspace's range, the
// sessions a shared file's spread is read across. `workspaceId`: the
// workspace the board shows, under which it leaves where it drew things (a
// board taking its place glides from there) and hears where what is dropped
// onto the workspace was let go. `nestInset`: what draws a workspace inside
// this one in its card ({ workspaceId, range, width }), once the card is tall
// enough.
// `titles`: what the workspace calls its sessions (by id), until their own
// summaries are read. `onCardSelect(card, key)`: the trace last selected on
// the board, read beside it ({ key, session, title, anchor, trace, turns });
// null, with its key, once its card is gone. `still`: a still picture of the
// board, its cards and notes alone, By time, laid out and drawn to fill the
// room it is given (`still` px wide until it is measured): a workspace inside
// another, in its card; nothing on it to take hold of.
export default function SessionCanvasView({
  trace, turns, model, joinRows = NO_JOINS, joinStatus = "done", needles = NO_STRINGS, joinHues = null, turnsToJoinInputs, kg = null,
  onCellPick = null, onCardSelect = null, initialFocus = null, onSelection = null, onClose = null, publish = true, sessionGraph = false,
  layered = null, onLayer = null, onUnlayer = null, onAnchor = null,
  readings: savedReadings = null, onReadings = null, hidden: savedHidden = null, onHidden = null,
  notes = null, onNotes = null, sizes: savedSizes = null, onSizes = null, order: savedOrder = null, onOrder = null,
  focalWork = null, boardBar = null, turnBase = 0, focalRecord = null, onFocalEarlier = null, focalFindings = null, preview = null,
  padMachines = null, padSearches = null, pinnedFindings = null, looseNotes = null, onLooseNote = null, onThingOff = null, onThingBack = null,
  onBranch = null, padNested = null, onEnter = null, onExit = null, flash = null, range = null, workspaceId = null, nestInset = null, titles = null,
  still = null, onPicture = null, cellDock = null, light = null, landing = null,
  // Pins on the focal card's cells: [{ turn, laneId, tone, count, title }].
  focalPins = null,
  // The walkthrough whose moments stand on this board: a trace's own, or its
  // workspace's across its sessions (`walkthrough:ws:<id>`). Another's
  // moments on the same sessions stay off it; none while this browser holds
  // none. `walkKey`: that walkthrough's own key, for the bar to walk through
  // by (lib/traceWalkthrough.js useBoardWalk).
  walkthroughKey = WALKTHROUGH_BY.key,
  walkKey = null,
  // A workspace's walkthrough reads the sessions inside the workspaces it
  // holds too: each of those sessions by the workspace here it is in (that
  // workspace's card on this board), for its moments in them to stand there.
  nestedHomes = null,
}) {
  // A preview (the trace page's) is the board of the anchor alone.
  const previewOn = !!preview;
  // The walkthrough beside this board (lib/traceWalkthrough.js boardWalkOf):
  // the bar walks through where none is held, and says so while one is
  // written, the walkthrough put in the chat in front as the card its own
  // call shows (lib/chatJobs.js). The bar runs it rather than asking the
  // chat, which may answer from what it knows already. One landing lists
  // its moments over the board.
  const boardWalk = useBoardWalk(walkKey);
  const walkFromBar = useCallback(() => {
    if (!boardWalk?.start) return;
    boardWalk.start();
    postChatJob({ kind: "walkthrough", key: boardWalk.walks || boardWalk.key });
  }, [boardWalk]);
  // A preview in hand (pointed at or focused) shows what a click does at its
  // card's head, where a board card shows its buttons.
  const [previewHot, setPreviewHot] = useState(false);
  // The chat's claims listed over the board along its foot: shown or put
  // away, the one picked (`n` counts picks, so picking it again brings it into
  // view again), and the one pointed at with where its card stands.
  const [evidenceOn, setEvidenceOn] = useState(true);
  const [evidencePick, setEvidencePick] = useState(null);
  const [evidencePoint, setEvidencePoint] = useState(null);
  // What the list holds where the board holds a walkthrough and the chat's
  // claims both: the walkthrough's moments (the story, which the claims it
  // settles are part of), unless the reader turned to the claims or asked
  // for one no moment covers.
  const [claimsListed, setClaimsListed] = useState(false);
  // Where the card picked stands open in the list, for its line and the fade
  // to stay while it is open.
  const [evidenceOpenAt, setEvidenceOpenAt] = useState(null);
  // While the list is over the board it focuses the board on what it rests
  // on: a card it rests on opens the groups it rests on to their rows and
  // folds the rest into one, a card it does not rest on folds all its groups
  // into that one row, and the camera comes to them (no closer than the
  // board's own size). Everything lets go of the focus; putting the list away
  // gives the board back as the reader left it.
  const [evidenceFocus, setEvidenceFocus] = useState(true);
  // Where the camera was before the focus brought it to the evidence, for
  // putting the list away to take it back there.
  const preFocusCam = useRef(null);
  // The chat's claims take a row of their own under the axis, while they are
  // not the list over the board: put away, or a walkthrough's moments listed
  // in their place (the list's own rule, below, read here from the notes the
  // board holds: its walkthrough's moments, unless the reader turned to the
  // claims).
  const walkListed = useMemo(() => !!publish && !previewOn && !claimsListed && walkthroughMoments(notes, walkthroughKey).n.size > 0, [publish, previewOn, claimsListed, notes, walkthroughKey]);
  const claimRow = (kg?.graph?.claims?.length || 0) > 0 && (!evidenceOn || walkListed);
  const RULER_H = preview || still ? 0 : RULER_BOARD + (claimRow ? CLAIM_ROW_H : 0);
  // Down the board, the room along its top and foot, over and under whatever
  // it holds (cards, notes, pins, what waits): a preview's card sits as far
  // from its frame's top and foot as from its sides (the clock's pad less a
  // card's rail); the workspace's board keeps more there, for what it lays
  // along them.
  const MARGIN_Y = preview ? BOARD_EDGE_L : BOARD_MARGIN;
  // Each card keeps its own open groups, open directories and levels shown in
  // full; nothing opens anything else.
  const [openGroups, setOpenGroups] = useState(() => new Map());
  const [openDirs, setOpenDirs] = useState(() => new Map());
  const [showAll, setShowAll] = useState(() => new Map());
  const [allGroups, setAllGroups] = useState(() => new Set());
  const [cursor, setCursor] = useState(null);
  const [sel, setSel] = useState(null);
  const [hover, setHover] = useState(null);
  // The card the pointer rests on, a beat after it comes to rest: it comes
  // over the rest while it does and they fade back a little (drawFocus), so
  // it reads among cards laid over each other. A card carried does the same
  // at once (st.dimKey); a card sized or the board moved, neither.
  const [dimKey, setDimKey] = useState(null);
  // A note's pin being drawn onto a cell: from the note to where the pointer
  // is (board units); and the note the pointer is on, its lines lit.
  const [linkDraft, setLinkDraft] = useState(null);
  const [noteLit, setNoteLit] = useState(null);
  // What the tip under the pointer is about ({ hit }), and where the pointer
  // is for it (tipAt, the tip's own to follow).
  const [tip, setTip] = useState(null);
  const tipAt = useMemo(() => createPointStore(), []);
  // Where the pointer is over the board, for the clock's cursor and the
  // moment under it on the strip: kept out of the board's state, so a move
  // that changes nothing else draws the canvas again (once a frame) and
  // renders nothing. The canvas reads it as it draws (st.pointerX, pointerY).
  const pointerRef = useRef({ x: null, y: null });
  const drawNowRef = useRef(null);
  const drawSoon = useRef(0);
  const setPointer = (x, y) => {
    const cur = pointerRef.current;
    if (cur.x === x && cur.y === y) return;
    pointerRef.current = { x, y };
    if (drawSoon.current) return;
    const frame = globalThis.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
    drawSoon.current = frame(() => {
      drawSoon.current = 0;
      drawNowRef.current?.();
    });
  };
  // The reader's readings of cells ({ id, session, lane, turn, turns }), and
  // the nodes they closed, on the workspace: the canvas's own when the page
  // keeps them (each canvas saves its own), here otherwise.
  const [ownReadings, setOwnReadings] = useState([]);
  const [ownHidden, setOwnHidden] = useState([]);
  // The attachment the pointer or focus is on (grown), the one a click holds
  // open, which were pulled out to stand or put back (a kept reading stands
  // until it is put back), and the cell open beside its card ({ kind: "cell",
  // key, laneId, turn, turns, bucket }).
  const [hoverNodeDom, setHoverNodeDom] = useState(null);
  const [heldId, setHeldId] = useState(null);
  // A card's tag (or a mark on a cell holding several findings) opened to
  // pick from: { key, ids (null for all the tag counts), at (where it was
  // clicked, on the canvas) }. It closes as the board moves.
  const [tagList, setTagList] = useState(null);
  const [agentMounts, setAgentMounts] = useState(() => new Map());
  const [standOver, setStandOver] = useState(() => new Map());
  // Pinned ones the reader carried out of their band, blocks of their own on
  // the board; the rest are pinned in place.
  const [freePins, setFreePins] = useState(() => new Set());
  const [pinH, setPinH] = useState(() => new Map());
  const onPinHeight = useCallback((id, h) => setPinH((cur) => (Math.abs((cur.get(id) ?? -1) - h) < 1 ? cur : new Map(cur).set(id, h))), []);
  const [detail, setDetail] = useState(null);
  const lastCellOpen = useRef({ at: -Infinity, before: null });
  // Which click opened a cell: a dock reading it opens it again on a new one,
  // as a lanes view does, and holds its place while what it reads moves on.
  const cellClicks = useRef(0);
  // The hand-off lit on a card (handoffLight.js): the host's, when it reads
  // the board's own trace in a dock of its own, else the board's.
  const ownLight = useMemo(() => createHandoffLight(), []);
  const handoff = light || ownLight;
  const lit = useSyncExternalStore(handoff.subscribe, handoff.get, handoff.get);
  // The sizes the reader gave cards (by session id, the page's to keep when it
  // keeps them), and the one being dragged.
  const [ownSizes, setOwnSizes] = useState(() => ({}));
  const sizesSaved = savedSizes || ownSizes;
  const [sizeDraft, setSizeDraft] = useState(null);
  // The order the reader stacked the cards in (by session id, the page's to
  // keep), and the card being carried: it follows the pointer over the board,
  // which holds still under it until it is let go.
  const [ownOrder, setOwnOrder] = useState(() => []);
  const orderSaved = savedOrder || ownOrder;
  // `moving` is where the board last laid what is carried; the ref is where
  // the pointer has it, running on ahead of it between renders.
  const [moving, setMoving] = useState(null);
  const movingRef = useRef(null);
  // What was dropped on the board from outside (a session, a machine, a
  // workspace), by the card key it brings: where on the board it was let go
  // (`bx`, `by`), so its card lands there, and when (`at`).
  const [drops, setDrops] = useState(() => new Map());
  // Where each session laid on from a cell came from (`landFrom`).
  const [cameFrom, setCameFrom] = useState(() => new Map());
  // Something carried over the board from outside: where (board coords) and
  // what, so the board can show where it would land.
  const [dragOver, setDragOver] = useState(null);
  // Where everything stood when the board was last drawn (`lastFeet`, at the
  // fit), and last drawn unrolled (`unrolledTops`), for placing what is put
  // down; and, for a card dropped on the board, where under the pointer it
  // comes from.
  const lastFeet = useRef(new Map());
  const unrolledTops = useRef(new Map());
  const dropFrom = useRef(new Map());
  // Where each card no one put anywhere first took its place: it keeps that
  // place (the layout's handTop) until the reader moves it or arranges the
  // board again, and the board keeps it with the places the reader gave once
  // every record on it is in. And the depth each card still reading its
  // record first took.
  const settledTops = useRef(new Map());
  const loadingHs = useRef(new Map());
  // The shape each card first took (0 its wider head and list, 1 the
  // narrower one beside a neighbour): it keeps it, rather than going wide and
  // narrow again as what is around it comes and goes.
  const settledShapes = useRef(new Map());
  // Each card's scene, placed geometry and hung band as last built (placed):
  // kept while what they are built from holds still.
  const sceneCache = useRef(new Map());
  const cardCache = useRef(new Map());
  const bandCache = useRef(new Map());
  // The moment the view holds still on screen as the clock changes under the
  // cards (`t`, and its place along the board when last drawn, `x`), and the
  // card the reader last asked to see, which it holds still by first.
  const xHold = useRef(null);
  const holdPick = useRef(null);
  // The investigation's step the board shows (null follows the chat), and
  // the claim held by a click on its badge.
  const [graphStepPick, setGraphStepPick] = useState(null);
  // A card being sized: which, by which edge; the board holds still under it
  // (every card's place down the board as the drag began) until it is let go.
  const [resizing, setResizing] = useState(null);
  const [frozenTops, setFrozenTops] = useState(null);
  const [resizeChip, setResizeChip] = useState(null);
  const rzRef = useRef(null);
  // The card whose buttons the pointer is on, so they stay while it is.
  const [toolbarKey, setToolbarKey] = useState(null);
  const [panning, setPanning] = useState(false);
  const readings = savedReadings || ownReadings;
  const hiddenList = savedHidden || ownHidden;
  const hiddenNodes = useMemo(() => new Set(hiddenList), [hiddenList]);
  const readingsRef = useRef(readings);
  readingsRef.current = readings;
  const hiddenRef = useRef(hiddenNodes);
  hiddenRef.current = hiddenNodes;
  const saveRef = useRef({ onReadings, onHidden });
  saveRef.current = { onReadings, onHidden };
  const setReadings = useCallback((fn) => {
    const next = typeof fn === "function" ? fn(readingsRef.current) : fn;
    if (next === readingsRef.current) return;
    if (saveRef.current.onReadings) saveRef.current.onReadings(next);
    else setOwnReadings(next);
  }, []);
  const setHiddenNodes = useCallback((fn) => {
    const next = typeof fn === "function" ? fn(hiddenRef.current) : fn;
    if (next === hiddenRef.current) return;
    // Held at once, so calls before the next render (a stack's members, one
    // by one) each add to the last rather than to the set as it was drawn.
    hiddenRef.current = next;
    if (saveRef.current.onHidden) saveRef.current.onHidden([...next]);
    else setOwnHidden([...next]);
  }, []);
  const [names, setNames] = useState(() => new Map());
  const [size, setSize] = useState({ w: 0, h: 0 });
  // The clock's zoom (pixels per clock unit) and the scroll. A null zoom is
  // fitted: the whole clock across the plane, re-fitted as the records arrive.
  const [k, setK] = useState(null);
  const [view, setViewState] = useState({ x: 0, y: 0 });
  const [cam, setCam] = useState(1);
  // How far the board is rolled up past the whole clock (`rollBy`, in zoom
  // steps), and so what each card shows (`roll`: 0, 1 or 2).
  // This board, among others on the page: what it takes off is its own to
  // put back.
  const boardId = useMemo(() => `board:${Math.random().toString(36).slice(2, 10)}`, []);
  const [roll, setRoll] = useState(0);
  const rollBy = useRef(0);
  // A still picture is laid out this many times narrower than its room and
  // drawn this many times larger (below 1, wider and smaller), so its cards
  // reach across the room as far as they reach down it; null until sized.
  const [stillK, setStillK] = useState(null);
  const stillTries = useRef(0);
  const stillGrown = useRef(false);
  const stillKey = useRef("");
  const stillSeen = useRef(new Set());
  const stillSteps = useRef(0);
  // A still picture shows once its cards are in and it is sized (or after
  // STILL_SHOW_MS, whatever is still coming), not while it lays itself out.
  const [stillShown, setStillShown] = useState(false);
  // Across a change in what the cards show, each card glides from where it
  // stood on screen to where it goes: where each stood (`glideFrom`, screen y
  // by key), and how far along the glide is (0 to 1).
  const glideFrom = useRef(null);
  const [glideT, setGlideT] = useState(1);
  const [glideRun, setGlideRun] = useState(0);
  // Stretches of the clock the reader zoomed into, each at its own factor:
  // [{ id, t0, t1, zoom }], and the one being dragged out along the strip's
  // scale. Everything outside them keeps its scale; every card on the clock
  // takes them, a card on a clock of its own as its own stretch of it.
  const [lenses, setLenses] = useState(() => []);
  // How the board is arranged, the view's own: one card to a row in order (the
  // session graph opens so, and By time arranges so), or packed, each card
  // rising to the first place it fits (the workspace opens so). The order is
  // the reader's where they moved a card, else time's.
  const [stacked, setStacked] = useState(() => !!sessionGraph || !!still);
  // Each arranging By time, which brings the opening view back.
  const [arranged, setArranged] = useState(0);
  const [lensDraft, setLensDraft] = useState(null);
  // Traces the reader laid onto the canvas by hand, by id: the host's list.
  const added = useMemo(() => (layered || []).map((id) => String(id).toLowerCase()).filter((id) => id !== String(trace?.sessionId || "").toLowerCase()), [layered, trace?.sessionId]);
  const heads = useLaidOnHeads(added, range);
  const onName = useCallback((id, label) => setNames((m) => (m.get(id) === label ? m : new Map(m).set(id, label))), []);
  const themeVersion = useThemeVersion();
  // The board is drawn on the ground of where it sits (a host may tint its own,
  // as the workspace home does), read once it is in the page.
  const [themeHost, setThemeHost] = useState(null);
  useLayoutEffect(() => {
    if (wrapRef.current && wrapRef.current !== themeHost) setThemeHost(wrapRef.current);
  });
  const theme = useMemo(() => {
    const base = readTheme(themeHost);
    const v = (name, fallback) => cssVar(name, fallback, themeHost);
    return {
      ...base, danger: v("--danger", "#d4503e"), warn: v("--warn", "#c08a2d"), panel: v("--panel", base.bg),
      diagramDot: v("--diagram-dot", "rgba(232,224,215,0.11)"), nodeShadow: v("--node-shadow-ink", "rgba(0,0,0,0.12)"),
      nodeShadowLift: v("--node-shadow-lift-ink", "rgba(0,0,0,0.3)"),
    };
  }, [themeVersion, themeHost]); // eslint-disable-line react-hooks/exhaustive-deps

  const focalSessionId = String(trace?.sessionId || "").toLowerCase();
  const focalTimes = useMemo(() => turnTimes(turns), [turns]);

  const kgSessions = useMemo(() => {
    const g = kg?.graph;
    const out = new Map();
    if (!g?.nodes?.size) return out;
    const endpointOf = new Map();
    for (const e of g.edges.values()) {
      const a = g.nodes.get(e.from), b = g.nodes.get(e.to);
      if (!a || !b) continue;
      if (a.kind === "session" && b.kind === "endpoint") endpointOf.set(a.key, b.id);
      if (b.kind === "session" && a.kind === "endpoint") endpointOf.set(b.key, a.id);
    }
    for (const n of g.nodes.values()) {
      if (n.kind !== "session") continue;
      const id = String(n.id || "").toLowerCase();
      if (!id || id === focalSessionId) continue;
      out.set(id, { id, firstMs: Number.isFinite(n.attrs?.at) ? n.attrs.at : null, lastMs: Number.isFinite(n.attrs?.last) ? n.attrs.last : null, endpointId: endpointOf.get(n.key) || null });
    }
    return out;
  }, [kg?.graph, kg?.version, focalSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const related = useMemo(() => {
    const out = (joinRows || [])
      .filter((r) => r?.session_id && !r.observer && String(r.session_id).toLowerCase() !== focalSessionId)
      .map((r) => {
        const id = String(r.session_id).toLowerCase();
        return {
          id, traceId: r.trace_id || null, endpointId: r.endpoint_id || null,
          agent: r.agent_type && r.agent_type !== "Unspecified" ? r.agent_type : null, model: r.model || null,
          evidence: Number(r.evidence) || 0, shared: Number(r.sharedTotal) || 0,
          firstMs: Date.parse(r.first_seen ?? ""), lastMs: Date.parse(r.last_seen ?? ""),
          needles: (r.matchedNeedles || []).filter((i) => needles[i]),
          direction: candidateDirectionLabel(r), same: !!r.sameEndpoint, raw: r, inGraph: kgSessions.has(id),
        };
      });
    for (const s of kgSessions.values()) {
      if (out.some((r) => r.id === s.id)) continue;
      out.push({ id: s.id, traceId: s.id, endpointId: s.endpointId, agent: null, model: null, evidence: 0, shared: 0, firstMs: s.firstMs, lastMs: s.lastMs, needles: [], direction: "in the investigation", same: false, raw: { session_id: s.id }, inGraph: true, kgOnly: true });
    }
    // A trace laid on by hand: its place on the clock comes from its own
    // record, and every string the open trace shares is looked for in it.
    const all = needles.map((_, i) => i).filter((i) => needles[i]);
    for (const id of added) {
      const hit = out.find((r) => r.id === id);
      const head = heads.of(id);
      // Already related (by the census or the chat's investigation), and laid
      // on as well: it is looked for the way a laid-on trace is. A trace the
      // investigation names carries no strings of its own to look for.
      if (hit) {
        hit.added = true;
        hit.needles = all;
        hit.head = head;
        // What the relation did not say of it (the chat's investigation says
        // nothing of the model), its head does.
        if (!hit.model && head?.model) hit.model = head.model;
        if (!hit.endpointId && head?.endpointId) hit.endpointId = head.endpointId;
        // Its span, where its head knows it: the relation's is where it saw
        // what the two share, so the card stood in at one size and grew to
        // another as its record came, and its record was read from there.
        if (Number.isFinite(head?.firstMs)) {
          hit.firstMs = head.firstMs;
          hit.lastMs = Number.isFinite(head.lastMs) ? head.lastMs : head.firstMs;
        }
        continue;
      }
      out.push({
        id, traceId: id, endpointId: head?.endpointId || "added", agent: null, model: head?.model || null, evidence: 0, shared: 0,
        firstMs: head ? head.firstMs : NaN, lastMs: head && Number.isFinite(head.lastMs) ? head.lastMs : head ? head.firstMs : NaN,
        needles: all, direction: "laid on by hand", same: false, raw: { session_id: id }, inGraph: false, added: true, head,
      });
    }
    return out.filter((r) => r.added || Number.isFinite(r.firstMs)).sort((a, b) => (b.added - a.added) || (b.inGraph - a.inGraph) || b.evidence - a.evidence || b.shared - a.shared);
  }, [joinRows, needles, focalSessionId, kgSessions, added, heads]);

  const hueOf = useMemo(() => {
    const m = new Map();
    related.slice(0, HUES.length).forEach((r, i) => m.set(r.id, joinHues?.get?.(r.raw.session_id) || HUES[i]));
    return m;
  }, [related, joinHues]);
  // The related sessions the board shows outside its traces: joined on enough
  // strings (or in the chat's investigation), at a known time, not laid on.
  const strong = useMemo(() => related
    .filter((r) => !r.added && !r.kgOnly && (r.inGraph || r.evidence >= STRONG) && Number.isFinite(r.firstMs)), [related]);
  const laidOn = useMemo(() => related.filter((r) => r.added), [related]);
  // In the Lanes reading's session graph (`sessionGraph`) the strongest of
  // them are read too, to find the ones joined to the anchor on a cell at both
  // ends; and any the reader opens there.
  const candidates = useMemo(() => (sessionGraph ? strong.slice(0, EAGER_READS) : []), [sessionGraph, strong]);
  // What the reader opened, and closed, in the session graph, for this anchor.
  const [here, setHere] = useState(() => ({ sid: focalSessionId, open: new Set(), closed: new Set() }));
  const hereNow = useMemo(() => (here.sid === focalSessionId ? here : { sid: focalSessionId, open: EMPTY, closed: EMPTY }), [here, focalSessionId]);
  const wanted = useMemo(() => [...new Set([...laidOn.filter((r) => heads.ready(r.id)).map((r) => r.id), ...candidates.map((r) => r.id), ...hereNow.open])], [laidOn, candidates, hereNow, heads]);
  const [recordReads, readEarlier] = useRelatedReads(related, wanted, needles, turnsToJoinInputs, { refind: !trace });
  // Each card's record with its session's reading on it, where it has one:
  // its lanes as its lanes view builds them, and its shared strings placed on
  // them.
  const cardReadings = useCardReadings(recordReads);
  const readLanes = useRef(new Map());
  const reads = useMemo(() => {
    if (!cardReadings.size) return recordReads;
    const out = new Map(recordReads);
    for (const [id, got] of cardReadings) {
      const r = recordReads.get(id);
      if (r?.status !== "done" || r.turns !== got.turns) continue;
      let built = readLanes.current.get(id);
      if (built?.turns !== r.turns || built.report !== got.report) {
        built = { turns: r.turns, report: got.report, model: buildReadLanes(r.turns, { report: got.report, shellItems: r.shellItems }) };
        readLanes.current.set(id, built);
      }
      const ids = related.find((x) => x.id === id)?.needles || [];
      out.set(id, { ...r, model: built.model, located: locateShared(r.turns, built.model, ids, needles, turnsToJoinInputs) });
    }
    return out;
  }, [recordReads, cardReadings, related, needles, turnsToJoinInputs]);

  // Wires: one per shared string, a few per session, each from the cell that
  // carries it here to the cell that carries it there. The pair closest in
  // time stands for the string: that is the hand-off.
  const allWires = useMemo(() => {
    const out = [];
    for (const r of [...laidOn, ...candidates]) {
      const read = reads.get(r.id);
      if (read?.status !== "done" || !read.times || !focalTimes) continue;
      const pairs = [];
      for (const i of r.needles) {
        const loc = read.located.get(i);
        const mine = (needles[i]?.cells || []).filter((c) => model?.laneById?.has(c.lane)).slice(0, 4);
        if (!loc || !mine.length) continue;
        const theirs = loc.cells.length ? loc.cells.slice(0, 4) : loc.turns.slice(0, 1).map((t) => ({ lane: read.model.lanes.find((l) => l.cells?.has(t))?.id || null, turn: t, approx: true }));
        let best = null;
        for (const a of mine) {
          for (const b of theirs) {
            if (!b.lane) continue;
            const ta = focalTimes[a.turn];
            const tb = read.times[b.turn];
            if (!Number.isFinite(ta) || !Number.isFinite(tb)) continue;
            const d = Math.abs(tb - ta);
            if (!best || d < best.d) best = { d, a: { key: FOCAL, lane: a.lane, turn: a.turn, t: ta }, b: { key: r.id, lane: b.lane, turn: b.turn, t: tb }, exact: !b.approx };
          }
        }
        if (best) pairs.push({ ...best, needle: i });
      }
      pairs.sort((p, q) => p.d - q.d);
      pairs.slice(0, WIRES_PER_SESSION).forEach((p, n) => out.push({
        ...p, key: r.id, primary: n === 0, hue: hueOf.get(r.id) || null,
        w: 0.8 + 2.4 * Math.sqrt(Math.min(21, r.evidence) / 21),
      }));
    }
    return out;
  }, [laidOn, candidates, reads, needles, model, focalTimes, hueOf]);
  // The files two or more of the sessions laid on touched, each by its folder
  // and name (fileKey), its spellings in one session taken together, with
  // where each session first and last touched it; one a search already
  // joins them on is not joined twice.
  const sharedFiles = useMemo(() => {
    const sought = needles.map((n) => String(n?.needle || "").toLowerCase()).filter(Boolean);
    const files = new Map();
    for (const r of laidOn) {
      const read = reads.get(r.id);
      if (read?.status !== "done" || !read.times) continue;
      for (const lane of read.model.lanes) {
        if (lane.domain !== "files" || !lane.path) continue;
        const key = fileKey(lane.path);
        if (!key || sought.some((n) => key.includes(n))) continue;
        const turns = (lane.turns || []).filter((t) => Number.isFinite(read.times[t])).sort((a, b) => read.times[a] - read.times[b]);
        if (!turns.length) continue;
        // Asked about as one session spelled it: the match is the gateway's.
        if (!files.has(key)) files.set(key, { key, ask: String(lane.path).replace(/\\/g, "/").split("/").filter(Boolean).slice(-2).join("/"), stops: new Map(), wrote: false });
        const f = files.get(key);
        f.wrote = f.wrote || lane.items.some((it) => /write|edit/.test(String(it.op?.kind || "")));
        const end = (t) => ({ key: r.id, lane: lane.id, turn: t, t: read.times[t], approx: false });
        const first = end(turns[0]);
        const last = end(turns[turns.length - 1]);
        const cur = f.stops.get(r.id);
        if (!cur) f.stops.set(r.id, { first, last });
        else {
          if (first.t < cur.first.t) cur.first = first;
          if (last.t > cur.last.t) cur.last = last;
        }
      }
    }
    for (const [key, f] of files) if (f.stops.size < 2) files.delete(key);
    return files;
  }, [needles, laidOn, reads]);
  const sharedKeys = useMemo(() => [...new Set([...sharedFiles.values()].map((f) => f.ask))].sort(), [sharedFiles]);
  // How common a file is, is read against the work of the machines the
  // sessions laid on ran on: a file one of them touches every day joins
  // nothing, one they rarely touch joins strongly.
  const spreadOn = useMemo(() => {
    const eps = new Set();
    for (const r of laidOn) {
      // As its card names it: its record's machine, else the census's.
      const ep = reads.get(r.id)?.meta?.endpointId || r.endpointId;
      if (ep && ep !== "unknown" && ep !== "added") eps.add(String(ep));
    }
    return [...eps].sort();
  }, [laidOn, reads]);
  const spread = useFileSpread(sharedKeys, range, spreadOn);
  // The sessions laid on are joined to each other on what they share: on a
  // workspace with no anchor, each string its searches look for (a path,
  // words); on any, each file two or more of them touched. Each is wired from
  // one session to the next in time, from the last cell that carries it in
  // one to the first in the next, where it was handed on. A file's line is
  // as strong as the file is specific to them (`spec`, from how many of the
  // range's sessions touched it): one every session touches fades to nothing.
  const chainWires = useMemo(() => {
    const out = [];
    const chain = (stops, extra) => {
      stops.sort((a, b) => a.first.t - b.first.t);
      for (let k = 1; k < stops.length; k++) {
        const a = stops[k - 1].last;
        const b = stops[k].first;
        out.push({ a, b, key: b.key, from: a.key, d: Math.abs(b.t - a.t), primary: true, chain: true, exact: !a.approx && !b.approx, hue: null, w: 1.6, ...extra });
      }
    };
    if (!trace) needles.forEach((nd, i) => {
      if (!nd?.needle) return;
      const stops = [];
      for (const r of laidOn) {
        const read = reads.get(r.id);
        const loc = read?.status === "done" ? read.located.get(i) : null;
        if (!loc || !read.times) continue;
        const located = (loc.cells.length ? loc.cells : loc.turns.map((t) => ({ lane: read.model.lanes.find((l) => l.cells?.has(t))?.id || null, turn: t, approx: true })))
          .filter((c) => c.lane && Number.isFinite(read.times[c.turn]));
        if (!located.length) continue;
        // On its own row where the read has one (a file's, for its path).
        const ownRow = cellsHolding(read, { cells: located }, nd.needle).filter((c) => String(c.lane).toLowerCase().includes(String(nd.needle).toLowerCase()));
        const cells = (ownRow.length ? ownRow : located).sort((a, b) => read.times[a.turn] - read.times[b.turn]);
        const end = (c) => ({ key: r.id, lane: c.lane, turn: c.turn, t: read.times[c.turn], approx: !!c.approx });
        stops.push({ first: end(cells[0]), last: end(cells[cells.length - 1]) });
      }
      chain(stops, { needle: i, text: nd.needle });
    });
    if (spread?.total > 1) {
      for (const f of sharedFiles.values()) {
        const counted = spread.df.get(f.ask);
        if (counted == null) continue;
        // At least the sessions here that touched it, whatever the range holds.
        const df = Math.max(counted, f.stops.size);
        const spec = specificity(df, spread.total);
        if (spec * 0.95 < VISIBLE_ALPHA) continue;
        chain([...f.stops.values()], { text: f.key, shared: true, wrote: f.wrote, spec, df, total: spread.total, onMachines: spreadOn.length > 0, w: 0.8 + 1.6 * spec });
      }
    }
    return out.length ? out : NO_WIRES;
  }, [trace, needles, laidOn, reads, sharedFiles, spread, spreadOn]);

  // The strongest of those joined on a cell at both ends open on their own as
  // cards, in this view only: they are not laid on the workspace, whose
  // machines' cards keep them as rows. Chosen once the reads (and the search
  // for related sessions) are in, so they do not swap under the reader as
  // later records land; one the reader closes goes back to its machine's card.
  const choosing = candidates.length > 0
    && !((joinStatus === "done" || joinStatus === "error") && wanted.every((id) => reads.get(id)?.status === "done" || reads.get(id)?.status === "error"));
  // Held per anchor: a board kept as the page moves to another trace chooses
  // again for it.
  const autoHold = useMemo(() => ({ picked: null }), [focalSessionId]);
  const autoPicked = useMemo(() => {
    if (autoHold.picked) return autoHold.picked;
    const exact = new Set(allWires.filter((w) => w.exact).map((w) => w.key));
    return candidates.filter((r) => exact.has(r.id)).slice(0, AUTO_JOIN).map((r) => r.id);
  }, [allWires, candidates, autoHold]);
  useEffect(() => {
    if (!choosing && candidates.length && !autoHold.picked && allWires.length) autoHold.picked = autoPicked;
  }, [choosing, candidates.length, autoPicked, allWires.length, autoHold]);
  // Open in this view: the strongest it opened on its own, less any the
  // reader closed, and the ones the reader opened.
  const opened = useMemo(() => new Set([...autoPicked.filter((id) => !hereNow.closed.has(id)), ...hereNow.open]), [autoPicked, hereNow]);
  // Of those, the ones the workspace does not hold: a look in this view, kept
  // only when the reader lays it on.
  const peeks = useMemo(() => new Set([...opened].filter((id) => !laidOn.some((r) => r.id === id))), [opened, laidOn]);
  // The board's cards are the anchor, the traces laid on (by the reader or the
  // chat that follows) and, in the session graph, the ones open there; there
  // the other related sessions are stubs at their time.
  const shown = useMemo(() => [...laidOn, ...strong.filter((r) => opened.has(r.id))], [laidOn, strong, opened]);
  const wires = useMemo(() => {
    const on = new Set(shown.map((r) => r.id));
    const own = allWires.filter((w) => on.has(w.key));
    return chainWires.length ? [...own, ...chainWires.filter((w) => on.has(w.key) && on.has(w.from))] : own;
  }, [allWires, chainWires, shown]);

  // The chat's claims, per session: the open trace's overlay places its cells,
  // a read session in the graph gets its own overlay.
  const overlays = useMemo(() => {
    const g = kg?.graph;
    const out = new Map();
    if (!g?.nodes?.size) return out;
    if (kg.overlay) out.set(FOCAL, kg.overlay);
    for (const r of shown) {
      if (!r.inGraph) continue;
      const read = reads.get(r.id);
      if (read?.status !== "done") continue;
      try {
        const ov = buildTraceGraphOverlay(g, { trace: { sessionId: r.id, id: r.id }, turns: read.turns, turnInputs: turnsToJoinInputs(read.turns, read.model), laneOfToolUse: toolUseLanes(read.model, read.turns) });
        if (ov) out.set(r.id, ov);
      } catch { /* a session the graph cannot place stays unannotated */ }
    }
    return out;
  }, [kg?.graph, kg?.version, kg?.overlay, shown, reads, turnsToJoinInputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const machineName = useCallback((ep) => (ep === "added" ? "Laid on by hand"
    : names.get(ep) || (ep && ep !== "unknown" ? `machine ${String(ep).slice(0, 8)}` : "Unknown machine")), [names]);

  // One entry per session: what it draws and what its head says.
  const cards = useMemo(() => {
    const out = new Map();
    const f0 = focalTimes?.[0];
    const f1 = focalTimes?.[focalTimes.length - 1];
    // The trace's own facts ride on its card, the way every other card
    // carries its own: what ran it, on which model, under whose login (else
    // the person a host says is registered to the machine). The machine is
    // the band it sits in.
    let tool = "";
    try {
      tool = deriveTraceTooling(toolingInputs(trace, { turns }))?.label || "";
    } catch { tool = ""; }
    const origin = trace?.sessionOrigin || null;
    const login = origin?.account ? (origin.domain && String(origin.domain).toLowerCase() !== "local" ? `${origin.domain}\\${origin.account}` : origin.account) : "";
    const focalEp = trace?.endpointId || "unknown";
    const badgeOf = (ep) => (ep && ep !== "unknown" ? machineName(ep) : null);
    // A workspace started from anything but a session has no anchor: every
    // session on it is laid on.
    if (trace) out.set(FOCAL, {
      key: FOCAL, focal: true, model, times: focalTimes, status: focalTimes ? "done" : "notimes", turnBase,
      endpointId: focalEp, hue: null, machine: badgeOf(focalEp), sameMachine: true,
      titleText: String(trace?.summary || firstAsk(turns) || "This session"),
      subText: [tool || null, Array.isArray(trace?.clientAttributions) ? `Model: ${modelLabel(trace?.model, trace?.provider, true)}` : trace?.model || null, login || trace?.registeredUser || null, partOf(focalRecord) || `${turns?.length || 0} turns`, Number.isFinite(f0) ? rangeText(f0, f1) : null].filter(Boolean).join(DOT),
      firstMs: f0, lastMs: f1, sessionId: trace?.sessionId || trace?.id || null,
    });
    for (const r of shown) {
      const read = reads.get(r.id);
      const done = read?.status === "done" && !!read.times;
      // What ran it and on which model: its own record's reading where it has
      // been read, else what the census said.
      const sourceModel = r.model || read?.meta?.model;
      const who = [read?.tool || r.agent, Array.isArray(read?.meta?.clientAttributions) ? `Model: ${modelLabel(sourceModel, read?.meta?.provider, true)}` : sourceModel].filter(Boolean).join(DOT);
      out.set(r.id, {
        key: r.id, focal: false, r, read, who, model: done ? read.model : null, times: done ? read.times : null, turnBase: done ? read.base || 0 : 0,
        status: done ? "done" : read?.status === "error" ? "error" : read ? "loading" : "unread", error: read?.error || null,
        endpointId: read?.meta?.endpointId || r.endpointId || "unknown", hue: hueOf.get(r.id) || null,
        machine: badgeOf(read?.meta?.endpointId || (r.endpointId === "added" ? null : r.endpointId)),
        sameMachine: !!(read?.meta?.endpointId || r.endpointId) && (read?.meta?.endpointId || r.endpointId) === trace?.endpointId,
        titleText: cleanTitle(read?.meta?.summary) || cleanTitle(r.head?.summary) || cleanTitle(read?.title) || cleanTitle(titles?.[r.id]) || who || `session ${r.id.slice(0, 8)}`,
        subText: (() => {
          const t0 = done ? read.times[0] : r.firstMs;
          return [who || null, Number.isFinite(t0) ? whenText(t0) : null, Number.isFinite(f0) && Number.isFinite(t0) ? relText(t0 - f0) : null,
            done ? partOf(read.record) : null, r.added ? null : r.kgOnly ? "in the investigation" : `${r.shared} shared`].filter(Boolean).join(DOT);
        })(),
        firstMs: done ? read.times[0] : r.firstMs, lastMs: done ? read.times[read.times.length - 1] : r.lastMs,
      });
    }
    return out;
  }, [model, focalTimes, trace, turns, shown, reads, hueOf, machineName, turnBase, focalRecord, titles]);
  // The lit hand-off's cells on its card as its record stands: found again
  // from its strings once the card has read more of its record, so earlier
  // turns that carry them ring too and the rest stay on their messages.
  const litCard = lit ? lit.key ?? FOCAL : null;
  const litTurns = litCard === FOCAL ? turns : litCard ? cards.get(litCard)?.read?.turns || null : null;
  const litModel = litCard ? cards.get(litCard)?.model || null : null;
  const litCells = useMemo(
    () => (lit?.cellsOf && litTurns?.length && litModel ? lit.cellsOf(litTurns, litModel) : lit?.cells || []),
    [lit, litTurns, litModel],
  );

  // What is selected on the board -- a card, or a cell on it -- for the
  // page's chat to be told.
  useEffect(() => {
    if (!onSelection) return;
    if (!sel?.key || !cards.has(sel.key)) { onSelection(null); return; }
    const c = cards.get(sel.key);
    const lane = sel.laneId ? c?.model?.laneById?.get(sel.laneId) : null;
    onSelection({
      session: sel.key === FOCAL ? focalSessionId : sel.key, title: c?.titleText || "",
      lane: sel.laneId || null, laneLabel: lane?.label || null, turn: Number.isInteger(sel.turn) ? (c?.turnBase || 0) + sel.turn : null,
    });
  }, [onSelection, sel?.key, sel?.laneId, sel?.turn, cards, focalSessionId]);

  // The trace last selected on the board, for a dock beside it to read: it
  // stays chosen when the selection goes (a click on the board's ground),
  // until another is selected or its card leaves the board.
  const [chosenKey, setChosenKey] = useState(null);
  useEffect(() => {
    if (sel?.key && cards.has(sel.key)) setChosenKey(sel.key);
  }, [sel?.key, cards]);
  const chosenCard = chosenKey ? cards.get(chosenKey) || null : null;
  const chosenTurns = chosenKey === FOCAL ? turns : chosenCard?.read?.turns || null;
  const chosenTitle = chosenCard?.titleText || "";
  const chosenMeta = chosenCard?.read?.meta || null;
  const chosenEp = chosenCard?.endpointId || null;
  useEffect(() => {
    if (!onCardSelect || !chosenKey) return;
    if (!chosenCard) {
      onCardSelect(null, chosenKey);
      setChosenKey(null);
      return;
    }
    if (chosenKey === FOCAL) {
      onCardSelect({ key: FOCAL, anchor: true, session: focalSessionId, title: chosenTitle, trace: null, turns: null });
      return;
    }
    onCardSelect({
      key: chosenKey, anchor: false, session: chosenKey, title: chosenTitle,
      trace: chosenTurns ? { id: chosenKey, sessionId: chosenKey, summary: chosenMeta?.summary || chosenTitle, model: chosenMeta?.model || "", endpointId: chosenEp } : null,
      turns: chosenTurns,
    });
  }, [onCardSelect, chosenKey, !!chosenCard, chosenTitle, chosenTurns, chosenMeta, chosenEp, focalSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The related sessions not on the board as cards, strongest first, for
  // their machines' cards: each a row at the time it ran, laid on by a click.
  // Where each shares the anchor's strings comes from the census's own samples
  // of them, so a row says where its hand-off lands (and, pointed at, draws
  // its wires) without its record read.
  const relatedRows = useMemo(() => (sessionGraph ? strong.filter((r) => !opened.has(r.id)).slice(0, MAX_RELATED_ROWS) : []), [sessionGraph, strong, opened]);
  const rowSpan = useMemo(() => {
    const ts = relatedRows.flatMap((r) => [r.firstMs, r.lastMs]).filter(Number.isFinite);
    return ts.length ? { from: Math.min(...ts), to: Math.max(...ts) } : null;
  }, [relatedRows]);
  const rowTitles = useSessionTitles(relatedRows.map((r) => r.id), rowSpan);
  // A stub's words: its title (or its first ask, once read), and where and
  // when it ran.
  const stubInfo = useMemo(() => new Map(sessionGraph ? relatedRows.map((r) => {
    const who = [r.agent, r.model].filter(Boolean).join(DOT);
    const ep = r.endpointId && r.endpointId !== "unknown" && r.endpointId !== "added" ? r.endpointId : null;
    return [r.id, {
      label: cleanTitle(rowTitles.get(r.id)) || cleanTitle(reads.get(r.id)?.title) || who || `session ${r.id.slice(0, 8)}`,
      hue: hueOf.get(r.id) || null, machine: ep ? machineName(ep) : null, sameMachine: !!ep && ep === trace?.endpointId,
      sub: [who || null, Number.isFinite(r.firstMs) ? whenText(r.firstMs) : null, `${r.shared} shared`].filter(Boolean).join(DOT),
    }];
  }) : []), [sessionGraph, relatedRows, rowTitles, reads, hueOf, machineName, trace?.endpointId]);
  const rowWires = useMemo(() => {
    const out = [];
    if (!focalTimes) return out;
    for (const r of relatedRows) {
      const samples = (r.raw?.samples || []).map((sm) => ({ needle: sm.needle, t: sampleMs(sm.at) })).filter((sm) => Number.isFinite(sm.t));
      if (!samples.length) continue;
      const pairs = [];
      for (const i of r.needles) {
        const str = needles[i]?.needle;
        const mine = (needles[i]?.cells || []).filter((c) => model?.laneById?.has(c.lane)).slice(0, 4);
        let best = null;
        for (const a of mine) {
          const ta = focalTimes[a.turn];
          if (!Number.isFinite(ta)) continue;
          for (const b of samples) {
            if (b.needle !== str) continue;
            const d = Math.abs(b.t - ta);
            if (!best || d < best.d) best = { d, a: { key: FOCAL, lane: a.lane, turn: a.turn, t: ta }, b: { key: r.id, lane: null, turn: null, t: b.t }, exact: false };
          }
        }
        if (best) pairs.push({ ...best, needle: i });
      }
      pairs.sort((p, q) => p.d - q.d);
      pairs.slice(0, WIRES_PER_SESSION).forEach((p, n) => out.push({
        ...p, key: r.id, primary: n === 0, row: true, hue: hueOf.get(r.id) || null,
        w: 0.8 + 2.4 * Math.sqrt(Math.min(21, r.evidence) / 21),
      }));
    }
    return out;
  }, [relatedRows, needles, model, focalTimes, hueOf]);
  // The groups each joined trace shows: its conversation, and the groups its
  // joins land on. The rest fold into one row until the reader asks for them.
  // A group a wire opened stays open while its card is on the board, until
  // the reader asks for the board again (letShapeGo): wires come and go as
  // records and counts arrive, and a card that folded a row back up as one
  // went would change its size under the reader for nothing they did.
  const joinsHeld = useRef(new Map());
  const [joinsEpoch, setJoinsEpoch] = useState(0);
  const joinedGroups = useMemo(() => {
    const out = new Map();
    const land = (key, lane) => {
      const model = cards.get(key)?.model;
      if (!model) return;
      if (!out.has(key)) out.set(key, { of: groupOfLane(model), ids: new Set([CONVERSATION]) });
      const g = out.get(key).of.get(lane);
      if (g) out.get(key).ids.add(g);
    };
    for (const w of wires) {
      if (w.shared && w.spec * 0.95 < VISIBLE_ALPHA) continue;
      land(w.key, w.b.lane);
      if (w.chain) land(w.from, w.a.lane);
    }
    const held = joinsHeld.current;
    for (const key of [...held.keys()]) if (!cards.get(key)?.model) held.delete(key);
    for (const [key, v] of out) held.set(key, new Set([...(held.get(key) || []), ...v.ids]));
    return new Map([...held].map(([k, ids]) => [k, new Set(ids)]));
  }, [wires, cards, joinsEpoch]);
  // A card's size as the reader left it (or is dragging it): { m, h }.
  const sizeOf = useCallback((key) => {
    if (sizeDraft?.key === key) return sizeDraft;
    return sizesSaved?.[key === FOCAL ? focalSessionId : key] || null;
  }, [sizeDraft, sizesSaved, focalSessionId]);
  // A card taken off leaves the rest as they were: its moments stay on the
  // clock (so no pause the card kept open closes under the others, nor the
  // clock's ends move), and the rows the wires to it opened stay open on the
  // others, until the reader asks for the board again (Everything, By time,
  // Clear).
  const [heldTimes, setHeldTimes] = useState(() => []);
  const [keptGroups, setKeptGroups] = useState(() => new Map());

  // The chat's investigation as it stood after the step shown: its claims, and
  // what it found inside the traces on the board, drawn on their cards. What
  // it found off the board (the sessions it looked into, a machine's work
  // outside any session) stays in the chat's graph panel.
  const investigation = useMemo(() => {
    const g = kg?.graph;
    if (!g?.nodes?.size) return null;
    let view;
    try { view = timelineView(g, { step: graphStepPick }); } catch { return null; }
    const onBoard = new Set([...cards.keys()].map((k) => (k === FOCAL ? focalSessionId : String(k).toLowerCase())));
    const sessionOf = (rowKey) => (String(rowKey).startsWith("session:") ? String(rowKey).slice(8).toLowerCase() : null);
    const events = new Map();
    const spans = new Map();
    // The events the chat found inside the traces on the board, by trace.
    const inTraces = new Map();
    for (const grp of view.groups) {
      for (const r of grp.rows) {
        for (const e of r.events) events.set(e.key, { ...e, row: r.key });
        if (r.span) spans.set(r.key, r.span);
        if (r.kind === "session" && onBoard.has(sessionOf(r.key)) && r.events.length) inTraces.set(sessionOf(r.key), r.events);
      }
    }
    // A claim's moment: the earliest of what it rests on -- a dated event, a
    // trace on the board (when it began), the cells it was placed on, a
    // session's span. A claim resting on nothing dated has none.
    const cardOf = new Map([...cards.values()].map((c) => [c.key === FOCAL ? focalSessionId : String(c.key).toLowerCase(), c]));
    const onCells = new Map();
    for (const [key, ov] of overlays) {
      const times = cards.get(key)?.times;
      if (!times) continue;
      for (const cl of ov?.claims || []) {
        for (const cell of cl.cells || []) {
          const tm = times[cell.turn];
          if (Number.isFinite(tm)) onCells.set(cl.id, Math.min(onCells.get(cl.id) ?? Infinity, tm));
        }
      }
    }
    // What it names that happened at a moment (an event, a cell) places it;
    // only a claim resting on nothing but sessions sits where they began.
    const claims = view.claims.map((c, i) => {
      let at = onCells.get(c.id) ?? Infinity;
      let loose = Infinity;
      for (const k of c.evidence) {
        const e = events.get(k);
        if (e && Number.isFinite(e.at)) at = Math.min(at, e.at);
        const card = sessionOf(k) ? cardOf.get(sessionOf(k)) : null;
        if (card && Number.isFinite(card.firstMs)) loose = Math.min(loose, card.firstMs);
        else if (spans.get(k)) loose = Math.min(loose, spans.get(k).from);
      }
      const t = Number.isFinite(at) ? at : loose;
      return { ...c, n: i + 1, at: Number.isFinite(t) ? t : null };
    });
    return { steps: view.steps, step: view.step, claims, claimIds: new Set(claims.map((c) => c.id)), events, inTraces };
  }, [kg?.graph, kg?.version, graphStepPick, cards, focalSessionId, overlays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where each workspace inside this one drew its cards in its card, by that
  // card's key: its picture's own report.
  const [pictures, setPictures] = useState(() => new Map());
  // The pictures' layer, and the canvas each picture drew on, for the card
  // being carried to draw its picture with it. A picture once drawn stays
  // (out of sight while its card has no room for it) where it was last shown,
  // so it is not read and laid out again each time the card shows it again.
  const insetsRef = useRef(null);
  const insetsSeen = useRef(new Map());
  const pictureCanvas = useCallback((key) => insetsRef.current?.querySelector(`[data-workspace-inset="${key}"] canvas`) || null, []);
  const pictureFns = useRef(new Map());
  const pictureFor = (key) => {
    if (!pictureFns.current.has(key)) {
      pictureFns.current.set(key, (pic) => setPictures((cur) => {
        if (samePicture(cur.get(key), pic)) return cur;
        const next = new Map(cur);
        if (pic?.length) next.set(key, pic);
        else next.delete(key);
        return next;
      }));
    }
    return pictureFns.current.get(key);
  };
  // A card per machine the workspace holds (the host's `padMachines`): its
  // sessions, a row each at the time it ran. Those on the board as cards
  // each keep a row (`member`, wired to its card); of the rest, the ones that
  // most need a look first and then the newest, a handful, a click laying
  // one on.
  const padCards = useMemo(() => {
    const out = new Map();
    if (!padMachines?.length && !padSearches?.length && !padNested?.length) return out;
    const onBoard = new Set([...cards.keys()].map((k) => (k === FOCAL ? focalSessionId : String(k).toLowerCase())));
    const idOf = (x) => String(x.id).toLowerCase();
    const cardKeyOf = (id) => (id === focalSessionId ? FOCAL : id);
    // Whether a session on the board holds a string (a needle's index): its
    // record's read located it, or, the anchor, the census did.
    const holds = (id, i) => (id === focalSessionId ? !!needles[i]?.cells?.length : !!reads.get(id)?.located?.has(i));
    // A search the workspace holds (a file path, a topic, any query) is a
    // card of the sessions it matches, as a machine's is of its own.
    for (const m of [...(padMachines || []), ...(padSearches || []).map((q) => ({ ...q, search: true })), ...(padNested || []).map((w) => ({ ...w, workspace: true }))]) {
      const key = `graph:${m.search ? "search" : m.workspace ? "workspace" : "machine"}:${m.id}`;
      if (hiddenNodes.has(key)) continue;
      const members = (m.sessions || []).filter((x) => x?.id && onBoard.has(idOf(x))).map((x) => ({ ...x, via: "listed" }));
      // The read behind a machine's or a search's sessions keeps the newest
      // of a range: a card on the board that ran on the machine, or holds
      // what the search looks for, is one of them all the same.
      const sought = m.search && m.strings?.length ? needles.map((n, i) => (n && m.strings.includes(n.needle) ? i : -1)).filter((i) => i >= 0) : [];
      for (const [k, c] of cards) {
        const id = k === FOCAL ? focalSessionId : String(k).toLowerCase();
        if (!id || members.some((x) => idOf(x) === id)) continue;
        const ranThere = !m.search && !m.workspace && c.endpointId && String(c.endpointId).toLowerCase() === String(m.id).toLowerCase();
        if (ranThere || sought.some((i) => holds(id, i))) members.push({ id, title: c.titleText, firstMs: c.firstMs, lastMs: c.lastMs, via: ranThere ? "machine" : "content" });
      }
      const list = (m.sessions || []).filter((x) => x?.id && !onBoard.has(idOf(x)) && Number.isFinite(x.firstMs));
      const shown = [...list].sort((a, b) => (b.findings?.length || 0) - (a.findings?.length || 0) || b.firstMs - a.firstMs)
        .slice(0, MACHINE_ROWS);
      const rowOf = (x, member) => {
        const id = idOf(x);
        const card = member ? cards.get(cardKeyOf(id)) : null;
        const from = Number.isFinite(x.firstMs) ? x.firstMs : card?.firstMs;
        const to = Number.isFinite(x.lastMs) ? x.lastMs : card?.lastMs;
        const ep = x.endpointId || card?.endpointId || null;
        return {
          machine: ep && ep !== "unknown" ? machineName(ep) : null,
          key: `session:${id}`, kind: "session", label: cleanTitle(x.title) || cleanTitle(card?.titleText) || `session ${id.slice(0, 8)}`,
          detail: x.findings?.length ? `${DOT.trim()} ${x.findings.length} finding${x.findings.length === 1 ? "" : "s"}` : "",
          span: { from, to: Number.isFinite(to) ? Math.max(to, from) : from }, events: [],
          ...(member ? { member: true, card: cardKeyOf(id), via: x.via } : {}),
        };
      };
      const rows = [...members.map((x) => rowOf(x, true)), ...shown.map((x) => rowOf(x, false))]
        .filter((r) => Number.isFinite(r.span.from)).sort((a, b) => a.span.from - b.span.from);
      if (!rows.length) continue;
      const first = Math.min(...rows.map((r) => r.span.from));
      const last = Math.max(...rows.map((r) => r.span.to));
      // A workspace inside, in a line: its sessions and the machines they
      // ran on, the worst the review found in them, how many are on this
      // workspace too.
      let facts = null;
      if (m.workspace) {
        const all = (m.sessions || []).filter((x) => x?.id);
        const eps = new Set(all.map((x) => x.endpointId || cards.get(cardKeyOf(idOf(x)))?.endpointId).filter((ep) => ep && ep !== "unknown"));
        const found = all.flatMap((x) => x.findings || []);
        let worst = null;
        for (const f of found) if ((SEVERITY_RANK[f.severity] ?? 9) < (SEVERITY_RANK[worst] ?? 9)) worst = f.severity;
        facts = {
          sessions: all.length, machines: eps.size, onBoard: members.length,
          flag: found.length ? { severity: worst, text: `${SEVERITY_WORD[worst] || "Flagged"}${DOT}${found.length} finding${found.length === 1 ? "" : "s"}` } : null,
        };
      }
      out.set(key, {
        key, graph: true, machine: !m.search && !m.workspace, search: !!m.search, workspace: !!m.workspace, sub: m.sub || "",
        label: m.name || (m.search ? "A search" : m.workspace ? "Untitled workspace" : "Machine not known"),
        people: m.person ? [{ label: m.person }] : [], isNew: false, rows, more: list.length - shown.length, members: rows.filter((r) => r.member).length,
        strings: m.strings || null, firstMs: first, lastMs: Math.max(last, first + 1000), facts,
      });
    }
    return out;
  }, [padMachines, padSearches, padNested, cards, focalSessionId, hiddenNodes, needles, reads, machineName]);
  // Where each session on the board as a card holds what a search the
  // workspace holds looks for (a file's path, words): the first cell that
  // does, which its row's wire lands on.
  const memberCells = useMemo(() => {
    const out = new Map();
    if (!needles.length) return out;
    for (const [key, gc] of padCards) {
      if (!gc.strings?.length || !gc.members) continue;
      const idx = needles.map((n, i) => (n && gc.strings.includes(n.needle) ? i : -1)).filter((i) => i >= 0);
      if (!idx.length) continue;
      for (const r of gc.rows) {
        if (!r.member) continue;
        let cell = null;
        if (r.card === FOCAL) cell = idx.map((i) => needles[i]?.cells?.[0]).find(Boolean) || null;
        else {
          const read = reads.get(r.card);
          for (const i of idx) {
            const loc = read?.status === "done" ? read.located.get(i) : null;
            const firstCell = loc?.cells?.length ? cellsHolding(read, loc, needles[i]?.needle)[0] : null;
            if (firstCell) { cell = firstCell; break; }
          }
        }
        if (cell) out.set(`${key}|${r.card}`, cell);
      }
    }
    return out;
  }, [padCards, needles, reads]);
  // The board's list cards: the ones its workspace holds.
  const machineCards = padCards;
  // Zoomed out far enough that heads are held over their cards, a head held
  // over a group's card pushes its list or picture down the card, and the
  // wires from them with it.
  const holdCam = machineCards.size && cam < HOLD_CAM ? cam : 1;
  // The findings the workspace holds stand open on their sessions' cards,
  // until the reader puts one back.
  useEffect(() => {
    if (!pinnedFindings?.length) return;
    setStandOver((cur) => {
      let next = null;
      for (const f of pinnedFindings) {
        const sid = String(f.session || "").toLowerCase();
        const id = `finding:${sid === focalSessionId ? FOCAL : sid}:${f.id}`;
        if (cur.has(id)) continue;
        next = next || new Map(cur);
        next.set(id, true);
      }
      return next || cur;
    });
  }, [pinnedFindings, focalSessionId]);

  // The clock: every trace's turns, and what the machines' cards hold.
  // A trace's own times shape the clock only once no trace on it
  // is still reading: until then each stands in by its census span, even
  // (steady, never closed as a pause), so the cards are read at their size and
  // the board settles once, when they are all in, rather than at every read.
  const [admitted, setAdmitted] = useState(() => new Set([FOCAL]));
  const reading = [...cards.values()].some((c) => c.status === "loading" || c.status === "unread");
  useEffect(() => {
    if (reading) return;
    const ready = [...cards.values()].filter((c) => c.times).map((c) => c.key);
    setAdmitted((cur) => (ready.every((k) => cur.has(k)) ? cur : new Set([...cur, ...ready])));
  }, [reading, cards]);
  const clock = useMemo(() => {
    const pts = [...heldTimes];
    for (const c of cards.values()) for (const t of clockPointsOf(c, admitted)) pts.push(t);
    // What the machines' cards hold takes its place on it too, and the
    // session graph's stubs.
    for (const gc of machineCards.values()) {
      pts.push(gc.firstMs, gc.lastMs);
      for (const r of gc.rows) {
        for (const e of r.events) pts.push(e.at);
        if (r.span) pts.push(r.span.from, r.span.to);
      }
    }
    for (const r of sessionGraph ? relatedRows : []) pts.push(r.firstMs, r.lastMs);
    // Every trace gets the same clock and the window does the zooming; a lens
    // the reader draws gives one stretch of it more room for every card at
    // once, the rest keeping its scale. A preview's host may name a stretch
    // to open on, given the lens's share of the card.
    const kept = pts.filter(Number.isFinite);
    const hostLens = preview?.lens;
    if (!lenses.length && hostLens && Number.isFinite(hostLens.t0) && Number.isFinite(hostLens.t1) && hostLens.t1 > hostLens.t0) {
      const plain = buildClock(kept);
      if (plain) {
        const span = plain.at(hostLens.t1) - plain.at(hostLens.t0);
        const whole = plain.at(Math.max(...kept)) - plain.at(Math.min(...kept));
        const zoom = span > 0 ? Math.min(96, (LENS_SHARE * (whole - span)) / ((1 - LENS_SHARE) * span)) : 1;
        if (zoom > 1.25) return buildClock(kept, { lenses: [{ id: "host", t0: hostLens.t0, t1: hostLens.t1, zoom }] });
      }
    }
    return buildClock(kept, { lenses });
  }, [cards, lenses, machineCards, admitted, sessionGraph, relatedRows, heldTimes, preview?.lens]);
  const turnUs = useMemo(() => {
    const m = new Map();
    if (!clock) return m;
    for (const c of cards.values()) if (c.times) m.set(c.key, c.times.map(clock.at));
    return m;
  }, [clock, cards]);
  // The review's findings on every trace on the board, by card.
  // The anchor's findings come placed from its Lanes view when it has them
  // (the same report, from the same turns and lanes): the board places the
  // other traces' own.
  // A preview whose host already says what the review found (Triage's)
  // reads none of it; nor does a still picture, which shows no findings.
  const reviewShown = preview?.review !== false && !still;
  const findingSources = useMemo(() => [...cards.values()]
    .filter((c) => reviewShown && c.model && Number.isFinite(c.firstMs) && !(c.focal && focalFindings))
    .map((c) => ({ key: c.key, sessionId: String((c.focal ? c.sessionId : c.key) || "").toLowerCase() || null, firstMs: c.firstMs, turns: c.focal ? turns : c.read?.turns, model: c.model })), [cards, turns, focalFindings, reviewShown]);
  const ownReports = useCanvasFindings(findingSources, turnsToJoinInputs, true);
  const findingReports = useMemo(() => (focalFindings ? new Map(ownReports).set(FOCAL, focalFindings) : ownReports), [ownReports, focalFindings]);
  const findingIds = useMemo(() => [...findingReports.values()].flatMap((rep) => (rep.incidents || []).map((inc) => inc.id)), [findingReports]);
  const verdicts = useFindingVerdicts(findingIds);
  const flags = useMemo(() => {
    const out = new Map();
    for (const [key, rep] of findingReports) {
      const incs = rep.incidents || [];
      if (!incs.length) continue;
      let worst = null;
      for (const i of incs) if ((SEVERITY_RANK[i.behaviour?.severity] ?? 9) < (SEVERITY_RANK[worst] ?? 9)) worst = i.behaviour.severity;
      out.set(key, { severity: worst, text: `${SEVERITY_WORD[worst] || "Flagged"}${DOT}${incs.length} finding${incs.length === 1 ? "" : "s"}` });
    }
    return out;
  }, [findingReports]);
  // The board's annotations, by the card each is listed under, in the order
  // it lists them: the chat's notes, the reader's kept readings, the review's
  // findings (worst first), the chat's claims (under the first trace each
  // cites, numbered as the chat numbers them), the helpers the anchor spawned.
  // The reader's own notes, pinned on the cells they were drawn onto: a pin
  // each among the chat's, tied to its note by a line (noteLines).
  const readerPins = useMemo(() => {
    const focalLow = String(focalSessionId || "").toLowerCase();
    return (looseNotes || []).flatMap((n) => (n.links || []).map((l) => ({
      id: readerPinId(n.id, l.id), session: l.session === focalLow ? focalSessionId : l.session, printed: l.turn, lane: l.lane || null,
      text: n.text || "", chat: READER_NOTE, kind: null,
    })));
  }, [looseNotes, focalSessionId]);
  const nodesByCard = useMemo(() => {
    const out = new Map();
    if (preview || still) return out;
    const lists = new Map();
    const hang = (key, rank, nd) => {
      if (!lists.has(key)) lists.set(key, [[], [], [], [], []]);
      lists.get(key)[rank].push(nd);
    };
    const { n: noteN, stands } = walkthroughMoments(notes, walkthroughKey);
    // The chat's notes (and the reader's own pins), on the turn it named (by
    // message id, or as the canvas prints it, from its record's start) and
    // the lane it named, else the turn's conversation lane.
    for (const nt of [...(notes || []), ...readerPins]) {
      if (!stands(nt)) continue;
      const key = nt.session === focalSessionId ? FOCAL : nt.session;
      const c = cards.get(key);
      if (!c?.model) continue;
      const recs = key === FOCAL ? turns : c.read?.turns;
      let turn = Number.isInteger(nt.printed) ? nt.printed - (c.turnBase || 0) : null;
      if (nt.messageId && recs) {
        const at = recs.findIndex((t) => t?.messageId === nt.messageId);
        if (at >= 0) turn = at;
      }
      // One on a message the card has not read yet (its record read from the
      // newest page, the rest read on asking) stands at the card's first
      // turn, to be read in from there.
      const rec = key === FOCAL ? focalRecord : c.read?.record;
      const earlier = (turn == null || turn < 0) && !!rec?.session && (!!rec.loading || (!rec.exhausted && !!rec.nextCursor)) && (c.times?.length ?? 0) > 0;
      if (earlier) turn = 0;
      if (turn == null || turn < 0 || turn >= (c.times?.length ?? 0)) continue;
      const want = nt.lane ? String(nt.lane).toLowerCase() : null;
      const lanes = c.model.lanes || [];
      const named = want ? lanes.find((l) => String(l.id).toLowerCase() === want || String(l.label || "").toLowerCase() === want) : null;
      // A turn no lane charts (a tool result, say) points at the nearest one
      // that does, the conversation's first.
      let lane = null;
      let at = turn;
      for (let d = 0; d <= 4 && !lane; d++) {
        for (const t of d ? [turn - d, turn + d] : [turn]) {
          const hit = named?.cells?.has(t) ? named
            : !named ? lanes.find((l) => l.cells?.has(t) && isConversationDomain(l.domain)) || lanes.find((l) => l.cells?.has(t)) : null;
          if (hit) { lane = hit; at = t; break; }
        }
      }
      if (!lane && named) lane = named;
      if (!lane) continue;
      hang(key, 0, { id: nt.id, kind: "note", key, anchors: [{ key, lane: lane.id, turn: at }], text: nt.text, laneLabel: lane.label, turn, chat: nt.chat || null, tone: nt.kind || null, n: noteN.get(nt.id) ?? null, earlier, ...(nt.covers ? { covers: nt.covers } : {}) });
    }
    for (const r of readings) {
      const key = r.session === focalSessionId ? FOCAL : r.session;
      const card = cards.get(key);
      if (hiddenNodes.has(r.id) || !card?.model) continue;
      // Kept by its place in the record: on the card, from the turns it holds.
      const onCard = readingOnCard(card, r);
      const at = readingTurns(card, onCard);
      hang(key, 2, { id: r.id, kind: "reading", key, anchors: [{ key, lane: r.lane, turn: at.turns[0] ?? onCard.turn }], turns: at.turns, found: at.found });
    }
    for (const [key, rep] of findingReports) {
      if (!cards.get(key)?.model) continue;
      const incs = [...(rep.incidents || [])].sort((a, b) => (SEVERITY_RANK[a.behaviour?.severity] ?? 9) - (SEVERITY_RANK[b.behaviour?.severity] ?? 9)
        || (a.uncited ? 1 : 0) - (b.uncited ? 1 : 0) || (a.turns?.[0] ?? Infinity) - (b.turns?.[0] ?? Infinity));
      for (const inc of incs) {
        const id = `finding:${key}:${inc.id || inc.title}`;
        const seen = new Set();
        const anchors = [];
        for (const cell of inc.cells || []) {
          const at = `${cell.lane} ${cell.turn}`;
          if (seen.has(at) || anchors.length >= 6) continue;
          seen.add(at);
          anchors.push({ key, lane: cell.lane, turn: cell.turn });
        }
        // A quote the verifier relocated may not read verbatim in its message:
        // the message's turn is known, so the finding hangs on that turn's cells.
        if (!anchors.length && inc.turns?.length) {
          const { turn, lanes } = lanesAtTurn(cards.get(key).model, inc.turns[0] - 1);
          for (const l of lanes.slice(0, 6)) anchors.push({ key, lane: l.id, turn });
        }
        const v = verdicts[String(inc.id)];
        hang(key, 3, {
          id, kind: "finding", key, anchors,
          severity: inc.behaviour?.severity || null, title: inc.title, rationale: inc.rationale || null,
          quotes: (inc.citations || []).filter((c) => c.verified !== "missing").map((c) => c.quote).slice(0, 3),
          verdict: v?.verdict ? v : null, turn: inc.turns?.length ? inc.turns[0] - 1 : null, earlier: !!inc.earlier,
        });
      }
    }
    const byId = new Map();
    const order = new Map((kg?.graph?.claims || []).map((c, i) => [c.id, i + 1]));
    for (const [key, ov] of overlays) {
      if (!cards.get(key)?.model) continue;
      for (const cl of ov?.claims || []) {
        const id = `claim:${cl.id}`;
        if (investigation && !investigation.claimIds.has(cl.id)) continue;
        const anchors = (cl.cells || []).slice(0, 6).map((cell) => ({ key, lane: cell.lane, turn: cell.turn }));
        if (!anchors.length) continue;
        const had = byId.get(id);
        if (had) { had.anchors.push(...anchors); continue; }
        const nd = { id, kind: "claim", key, anchors, n: order.get(cl.id) || null, status: cl.status, statement: cl.statement };
        byId.set(id, nd);
        hang(key, 1, nd);
      }
    }
    // A claim naming a trace on the board (the session itself, the machine it
    // ran on, or events the chat found in it) that no cell holds is listed
    // under that trace all the same.
    if (investigation) {
      for (const cl of investigation.claims) {
        const id = `claim:${cl.id}`;
        if (byId.has(id)) continue;
        const key = [...cards.keys()].find((k) => {
          const sid = k === FOCAL ? focalSessionId : String(k).toLowerCase();
          const ep = cards.get(k)?.endpointId;
          return cl.evidence.some((e) => {
            const low = String(e).toLowerCase();
            return low === `session:${sid}` || (ep && ep !== "unknown" && low === `endpoint:${String(ep).toLowerCase()}`)
              || investigation.events.get(e)?.row === `session:${sid}` || String(investigation.events.get(e)?.row || "").toLowerCase() === `session:${sid}`;
          });
        });
        if (!key || !cards.get(key)?.model) continue;
        const nd = { id, kind: "claim", key, anchors: [], n: cl.n, status: cl.status, statement: cl.statement, whole: true };
        byId.set(id, nd);
        hang(key, 1, nd);
      }
    }
    // The helpers each trace spawned, under its card, each to lay its own
    // session on from.
    for (const [key, c] of cards) {
      if (!c.model) continue;
      for (const hp of helpersOf(c.model, c.times).slice(0, MAX_HELPERS)) {
        const id = key === FOCAL ? `agent:${hp.laneId}` : `agent:${key}:${hp.laneId}`;
        hang(key, 4, {
          id, kind: "agent", key, anchors: hp.spawnTurn != null ? [{ key, lane: hp.laneId, turn: hp.spawnTurn }] : [],
          label: hp.label || "agent", agentType: hp.agentType, reported: hp.reported, failed: hp.failed, spawnAt: hp.spawnAt, reportAt: hp.reportAt, turn: hp.spawnTurn,
          prompt: hp.prompt, sealed: hp.sealed, results: hp.results, laneId: hp.laneId,
        });
      }
    }
    // A claim merged its anchors across traces after it was listed: the list
    // keeps the node it was given.
    for (const [key, ranks] of lists) out.set(key, ranks.flat().map((nd) => (nd.kind === "claim" ? { ...nd, anchors: byId.get(nd.id)?.anchors || nd.anchors } : nd)));
    return out;
  }, [readings, notes, readerPins, hiddenNodes, cards, overlays, kg?.graph, focalSessionId, turns, findingReports, verdicts, investigation, focalRecord, walkthroughKey]);
  // A workspace's walkthrough's moments in sessions with no card here that
  // are inside a workspace this one holds, by that workspace's card: the one
  // listing the session as a row, else the one it is in (deeper in, or not
  // among the rows listed). Numbered as the walkthrough numbers them.
  const nestedMoments = useMemo(() => {
    const out = new Map();
    if (preview || still || !nestedHomes?.size || walkthroughKey === WALKTHROUGH_BY.key) return out;
    const { n: noteN } = walkthroughMoments(notes, walkthroughKey);
    for (const nt of notes || []) {
      const sid = String(nt.session || "").toLowerCase();
      if (nt.chat?.key !== walkthroughKey || !nestedHomes.has(sid) || sid === focalSessionId || cards.has(sid)) continue;
      const row = `session:${sid}`;
      const listing = [...machineCards.values()].find((gc) => gc.workspace && gc.rows.some((r) => r.key === row));
      const gc = listing || machineCards.get(`graph:workspace:${nestedHomes.get(sid)}`);
      if (!gc) continue;
      if (!out.has(gc.key)) out.set(gc.key, []);
      out.get(gc.key).push({
        id: nt.id, kind: "note", key: gc.key, anchors: [], text: nt.text, chat: nt.chat || null, tone: nt.kind || null, n: noteN.get(nt.id) ?? null,
        inside: { session: sid, row: listing ? row : null, workspace: gc.label },
      });
    }
    return out;
  }, [preview, still, nestedHomes, walkthroughKey, notes, focalSessionId, cards, machineCards]);
  const isStanding = useCallback((nd) => (standOver.has(nd.id) ? standOver.get(nd.id) : nd.kind === "reading"), [standOver]);
  // The agent a trace started, by its card and its own lane.
  const agentOfLane = useCallback((key, laneId) => (nodesByCard.get(key) || []).find((nd) => nd.kind === "agent" && nd.laneId === laneId) || null, [nodesByCard]);
  // The order the pinned ones were opened in (the last opened, or set back
  // in its band, last), for their bands to lay them out in.
  const openRank = useMemo(() => {
    const out = new Map();
    for (const [id, on] of standOver) if (on) out.set(id, out.size);
    return out;
  }, [standOver]);
  // What a card's band holds: the attachments opened under it, a card each.
  // Nothing else hangs from a card: what the review found, the chat's claims
  // and the agents it started are reached from its head's tag and the marks
  // on their cells, and a note stands over its cell as a pin. Opened ones
  // carried out are blocks of their own on the board (the layout's "pin"
  // items).
  const attachBand = useCallback((key) => (nodesByCard.get(key) || []).filter((nd) => isStanding(nd)), [nodesByCard, isStanding]);
  // Each trace card's tag on its head: the worst the review found and how
  // many findings, else how many of the chat's claims and of the agents the
  // trace started it holds. A click on it lists them to open.
  const tags = useMemo(() => {
    // A preview lists nothing about its card, but its tag still says what the
    // review found.
    const out = new Map(flags);
    for (const [key, list] of nodesByCard) {
      if (out.has(key)) continue;
      const n = (kind) => list.filter((nd) => nd.kind === kind).length;
      const text = [n("claim") ? plural(n("claim"), "claim") : null, n("agent") ? plural(n("agent"), "agent") : null].filter(Boolean).join(DOT);
      if (text) out.set(key, { severity: null, text });
    }
    return out;
  }, [nodesByCard, flags]);
  // The marks on the cells what the review found is about: a finding's first
  // cell on its card, at its severity's tone, by card. A card's list is kept
  // while what it marks stands as it did, so its scene is not built again.
  const pinsKept = useRef(new Map());
  const findingPinsOf = useMemo(() => {
    const out = new Map();
    for (const [key, list] of nodesByCard) {
      const pins = [];
      for (const nd of list) {
        if (nd.kind !== "finding") continue;
        const a = (nd.anchors || []).find((x) => x.key === key);
        if (a) pins.push({ laneId: a.lane, turn: a.turn, tone: behaviourTone(nd.severity), count: 1, title: nd.title || "", ids: [nd.id] });
      }
      if (!pins.length) continue;
      const sig = pins.map((q) => `${q.ids[0]}|${q.laneId}|${q.turn}|${q.tone}`).join("\n");
      const had = pinsKept.current.get(key);
      out.set(key, had?.sig === sig ? had.pins : pins);
      if (had?.sig !== sig) pinsKept.current.set(key, { sig, pins });
    }
    return out;
  }, [nodesByCard]);
  const focalFirst = cards.get(FOCAL)?.firstMs;
  const focalLast = cards.get(FOCAL)?.lastMs;

  // The machines the sessions ran on, named once each: the board's traces'
  // (their names ride on their heads) and the related sessions' (they title
  // those machines' cards).
  const machines = useMemo(() => {
    const ids = new Set([
      trace?.endpointId || "unknown",
      ...shown.map((r) => reads.get(r.id)?.meta?.endpointId || r.endpointId || "unknown"),
      ...relatedRows.map((r) => r.endpointId || "unknown"),
      ...(padNested || []).flatMap((w) => (w.sessions || []).map((x) => x.endpointId).filter(Boolean)),
    ]);
    return [...ids].map((id) => ({ id, name: machineName(id) }));
  }, [trace?.endpointId, shown, reads, relatedRows, padNested, machineName]);

  // Heights come from the rows' SHAPE, never from the zoom or the scroll.
  // The chat's claims listed over the board: the ones the answer a ref was
  // last clicked in cites, until the chat answers again; else the ones its
  // latest answer cites, where that answer rests on this board's graph; else
  // all of them.
  const chatAnswer = useChatAnswer();
  const [askedAnswer, setAskedAnswer] = useState(null);
  const latestHere = chatAnswer.graph && chatAnswer.graph === kg?.graph ? chatAnswer.text : null;
  const answerHere = askedAnswer ?? latestHere ?? "";
  const claimList = useMemo(() => (investigation?.claims?.length ? evidenceList(investigation.claims, answerHere) : []), [investigation?.claims, answerHere]);
  // A new answer from the chat on this board's graph lists the claims it
  // rests on, whatever was listed before: the chat's latest evidence leads.
  const answerSeen = useRef(latestHere);
  useEffect(() => {
    if (!latestHere || latestHere === answerSeen.current) return;
    answerSeen.current = latestHere;
    setAskedAnswer(null);
    setClaimsListed(true);
  }, [latestHere]);
  // A walkthrough on the board is the list instead: its moments, numbered as
  // their pins are, each in its note's words, on its cell, with the chat's
  // claims and the case's notes each one covers (the claims as the board
  // numbers them now). One on a message its card has not read in yet stands
  // at the card's first turn, at no moment the board knows.
  const momentNodes = useMemo(() => {
    if (!publish || previewOn) return NO_MOMENTS;
    const out = [];
    for (const list of nodesByCard.values()) {
      for (const nd of list) if (nd.kind === "note" && nd.chat?.key === walkthroughKey && Number.isInteger(nd.n) && nd.anchors?.length) out.push(nd);
    }
    return out.length ? out.sort((a, b) => a.n - b.n) : NO_MOMENTS;
  }, [publish, previewOn, nodesByCard, walkthroughKey]);
  const claimsById = useMemo(() => new Map((investigation?.claims || []).map((c) => [String(c.id).toLowerCase(), c])), [investigation?.claims]);
  const momentList = useMemo(() => momentNodes.map((nd) => {
    const a = nd.anchors[0];
    const at = nd.earlier ? null : cards.get(a.key)?.times?.[a.turn];
    const covers = (nd.covers || []).map((c) => {
      if (c.kind !== "claim") return c;
      const cl = claimsById.get(String(c.id).toLowerCase());
      return { ...c, id: String(c.id).toLowerCase(), n: cl?.n ?? c.n ?? null, statement: cl?.statement || "", here: !!cl };
    });
    return { id: nd.id, n: nd.n, words: String(nd.text || ""), statement: "", cited: true, status: "moment", tone: nd.tone || null, at: Number.isFinite(at) ? at : null, covers };
  }), [momentNodes, cards, claimsById]);
  // How much of the board the walkthrough's moments cover, at the head of
  // their list: how many there are, and on how many of the traces here.
  const momentsCaption = useMemo(() => {
    if (!momentNodes.length) return "";
    const traces = [...cards.values()].filter((c) => c.focal || c.r?.added).length;
    const on = new Set(momentNodes.map((nd) => nd.key)).size;
    const many = `${momentNodes.length} moment${momentNodes.length === 1 ? "" : "s"}`;
    if (traces < 2) return many;
    return on >= traces ? `${many}, across ${traces === 2 ? "both" : `all ${traces}`} traces here` : `${many}, on ${on} of the ${traces} traces here`;
  }, [momentNodes, cards]);
  // Each claim with the walkthrough's moments that cover it, for its card to
  // name them.
  const claimsWalked = useMemo(() => {
    const by = new Map();
    for (const m of momentList) for (const c of m.covers || []) if (c.kind === "claim") by.set(c.id, [...(by.get(c.id) || []), { id: m.id, n: m.n }]);
    return by.size ? claimList.map((e) => (by.has(e.id) ? { ...e, walk: by.get(e.id) } : e)) : claimList;
  }, [momentList, claimList]);
  const listing = momentList.length && !(claimsListed && claimList.length) ? "moments" : "claims";
  const evidence = listing === "moments" ? momentList : claimsWalked;
  // A walkthrough finishing lists its moments again (moments read in as
  // their cards are read leave the list the reader turned to).
  const walkStatus = boardWalk?.status || null;
  const walkStatusWas = useRef(walkStatus);
  useEffect(() => {
    if (walkStatusWas.current === "running" && walkStatus === "done") setClaimsListed(false);
    walkStatusWas.current = walkStatus;
  }, [walkStatus]);
  const evidenceShown = evidenceOn && evidence.length > 0 && !previewOn && !still;
  // Focused on the evidence, what the listed claims rest on, card by card: the
  // rows they rest on and their groups, and the moments they rest on
  // (`times`); a card holding one only by an event in it has that event's
  // moment, by itself none.
  const focusOn = evidenceShown && evidenceFocus;
  const focusLanes = useMemo(() => {
    if (!focusOn) return null;
    const out = new Map();
    const fresh = () => ({ groups: new Set(), lanes: new Set(), times: [] });
    if (listing === "moments") {
      for (const nd of momentNodes) {
        for (const a of nd.anchors) {
          const c = cards.get(a.key);
          if (!c?.model) continue;
          if (!out.has(a.key)) out.set(a.key, fresh());
          const f = out.get(a.key);
          const g = groupOfLane(c.model).get(a.lane);
          if (g) f.groups.add(g);
          f.lanes.add(a.lane);
          const tm = c.times?.[a.turn];
          if (Number.isFinite(tm)) f.times.push(tm);
        }
      }
      return out.size ? out : null;
    }
    if (!investigation) return null;
    const ids = new Set(evidence.map((e) => e.id));
    for (const [key, ov] of overlays) {
      const model = cards.get(key)?.model;
      if (!model) continue;
      const of = groupOfLane(model);
      for (const cl of ov?.claims || []) {
        if (!ids.has(String(cl.id).toLowerCase())) continue;
        for (const cell of cl.cells || []) {
          if (!out.has(key)) out.set(key, fresh());
          const f = out.get(key);
          const g = of.get(cell.lane);
          if (g) f.groups.add(g);
          f.lanes.add(cell.lane);
          const tm = cards.get(key)?.times?.[cell.turn];
          if (Number.isFinite(tm)) f.times.push(tm);
        }
      }
    }
    const sid = (key) => (key === FOCAL ? focalSessionId : String(key).toLowerCase());
    const listed = investigation.claims.filter((cl) => ids.has(String(cl.id).toLowerCase()));
    for (const key of cards.keys()) {
      if (out.has(key)) continue;
      const evs = investigation.inTraces?.get(sid(key)) || [];
      const self = `session:${sid(key)}`;
      const held = listed.filter((cl) => cl.evidence.some((k) => evs.some((e) => e.key === k) || String(k).toLowerCase() === self));
      if (!held.length) continue;
      const times = evs.filter((e) => held.some((cl) => cl.evidence.includes(e.key)) && Number.isFinite(e.at)).map((e) => e.at);
      out.set(key, { groups: new Set(), lanes: new Set(), times });
    }
    return out.size ? out : null;
  }, [focusOn, listing, momentNodes, investigation, evidence, overlays, cards, focalSessionId]);
  // A card given a height shows the rows that fit it: its densest groups open
  // while they fit, the last of them part way, or, with less room than its
  // groups folded, its densest groups' rows and one row for the rest. Focused
  // on the evidence (or a walkthrough's moments), a card it rests on shows
  // the rows it rests on in their groups first and, made taller, the rows
  // nearest them as they fit; one it does not rest on fits its rows as any
  // card does. One asked for all its groups shows them as the focus lays them
  // out.
  const fittedRows = useMemo(() => {
    const out = new Map();
    for (const c of cards.values()) {
      const sz = sizeOf(c.key);
      if (!sz?.h || !c.model) continue;
      const cited = focusLanes?.get(c.key);
      if (cited && allGroups.has(c.key)) continue;
      const shape = new Map(c.model.lanes.map((l) => [l.id, { id: l.id, label: l.label, domain: l.domain, cells: NO_CELLS }]));
      const heightOf = (opts) => layoutRows(cardRows({ model: c.model, lanesById: shape, openDirs: EMPTY, showAll: EMPTY, ...opts })).height;
      const budget = sz.h - CARD_HEAD - ((sz.m && Math.abs(sz.m - 1) > 0.01) || sz.off ? CARD_RULER_H : 0) - 2;
      if (cited?.lanes.size) {
        out.set(c.key, fitFocusToHeight({ model: c.model, cited, budget, laneDensity: (l) => l.cells?.size || 0, heightOf }));
        continue;
      }
      const density = (g) => (g.lanes || []).reduce((a, l) => a + (l.cells?.size || 0), 0);
      out.set(c.key, fitRowsToHeight({ model: c.model, budget, density, heightOf }));
    }
    return out;
  }, [cards, sizeOf, focusLanes, allGroups]);
  const rowsOpts = useCallback((key) => {
    if (roll >= 1) return ROLLED_ROWS;
    const fit = fittedRows.get(key);
    if (fit) return { openGroups: fit.openGroups, openDirs: EMPTY, showAll: EMPTY, only: fit.only, caps: fit.caps || null, keep: fit.keep || null };
    // Focused on the evidence: a card it rests on shows the groups holding
    // the rows it rests on, opened to those rows, and one row for the rest
    // (one held only by an event in it, its conversation's group); a card it
    // does not rest on folds all its groups into one row. A group opened by
    // hand shows too. Asked for the rest, a card shows all its groups, those
    // it rests on opened to the rows it rests on.
    if (focusLanes) {
      const cited = focusLanes.get(key);
      const opened = openGroups.get(key) || EMPTY;
      return {
        openGroups: new Set([...opened, ...(cited?.groups || [])]),
        openDirs: openDirs.get(key) || EMPTY,
        showAll: showAll.get(key) || EMPTY,
        only: allGroups.has(key) ? null : new Set([...(cited?.groups?.size ? cited.groups : cited ? [CONVERSATION] : []), ...opened, ...(keptGroups.get(key) || [])]),
        keep: cited?.lanes.size ? cited.lanes : null,
      };
    }
    // A preview's host may keep the anchor's card to some of its groups.
    if (key === FOCAL && preview?.only) return { openGroups: openGroups.get(key) || EMPTY, openDirs: openDirs.get(key) || EMPTY, showAll: showAll.get(key) || EMPTY, only: preview.only };
    return {
      openGroups: openGroups.get(key) || EMPTY,
      openDirs: openDirs.get(key) || EMPTY,
      showAll: showAll.get(key) || EMPTY,
      only: key === FOCAL || allGroups.has(key) ? null : keptGroups.has(key)
        ? new Set([...(joinedGroups.get(key) || [CONVERSATION]), ...keptGroups.get(key)])
        : joinedGroups.get(key) || new Set([CONVERSATION]),
    };
  }, [roll, focusLanes, fittedRows, openGroups, openDirs, showAll, allGroups, joinedGroups, keptGroups, preview?.only]);
  const sizing = useMemo(() => {
    const out = new Map();
    for (const c of cards.values()) {
      if (!c.model || !c.times) continue;
      const shape = new Map(c.model.lanes.map((l) => [l.id, { id: l.id, label: l.label, domain: l.domain, cells: NO_CELLS }]));
      const rows = cardRows({ model: c.model, lanesById: shape, ...rowsOpts(c.key) });
      const sz = moving?.key === c.key && moving.off != null ? { ...(sizeOf(c.key) || {}), off: moving.off } : sizeOf(c.key);
      const ruler = (sz?.m && Math.abs(sz.m - 1) > 0.01) || sz?.off ? CARD_RULER_H : 0;
      // A card the reader gave a height is that height, between its fewest
      // rows and all of them; its rows fit inside it.
      const fit = CARD_HEAD + ruler + layoutRows(rows).height + 2;
      let h = fit;
      if (sz?.h && !roll && fittedRows.has(c.key)) {
        const lim = cardHeightLimits(c.model, ruler);
        h = Math.max(lim.min, Math.min(lim.max, sz.h));
        // The rows a focused card rests on show whatever height it was given.
        if (fittedRows.get(c.key).focus) h = Math.max(h, fit);
      }
      out.set(c.key, { h });
    }
    return out;
  }, [cards, rowsOpts, sizeOf, moving?.key, moving?.off, roll, fittedRows]);
  const planeW = Math.max(200, size.w - MACH_W);
  // What the view holds of the board, in the board's own units, under the camera.
  const viewW = planeW / cam;
  // How much of the board's right side a sheet over it covers (the add
  // sheet): the board can slide out from under it, a card brought into view
  // comes into the part left uncovered, and the corner's buttons sit beside it.
  const coverR = Math.max(0, Math.round(Number(boardBar?.cover) || 0));
  const planeH = Math.max(100, size.h - RULER_H);
  const viewH = planeH / cam;
  const axisU = clock?.U ?? CLOCK_U;
  // The zoom that shows the whole clock: across the plane after its left pad
  // (and, on the board, its right pad).
  // A still picture is laid out as wide as the room it is drawn into
  // (`still` until that is measured), narrower or wider by `stillK`.
  const layoutW = still ? Math.max(200, (size.w ? planeW : still - MACH_W) / (stillK || 1)) : planeW;
  const fitK = Math.max(0.05, (layoutW - PAD_L - BOARD_PAD_R) / axisU);
  // What the board holds, in the clock's units, as the reader left it: a card
  // moved or sized along the board covers its own stretch, not its trace's,
  // so the clock can run on past what is drawn. The board scrolls over what
  // it holds; it opens on (and zooms out no further than) its traces at their
  // own scale, where the reader moved them, so a card sized smaller stays
  // smaller and one sized larger runs on past the canvas. The cards still
  // pack at the whole clock's fit, so a card carried or sized never re-packs
  // the board.
  const boardSpan = useMemo(() => {
    if (!clock) return null;
    let u0 = Infinity;
    let u1 = -Infinity;
    let f0 = Infinity;
    let f1 = -Infinity;
    const of = new Map();
    const keys = [FOCAL, ...shown.map((r) => r.id).filter((key) => cards.get(key)), ...machineCards.keys()];
    for (const key of keys) {
      const c = cards.get(key) || machineCards.get(key);
      if (!c || !Number.isFinite(c.firstMs)) continue;
      const sz = sizeOf(key);
      const a = clock.at(c.firstMs);
      const b = clock.at(Number.isFinite(c.lastMs) ? c.lastMs : c.firstMs);
      const span = cardSpanU(a, b, { m: sz?.m || 1, off: sz?.off || 0, pinEnd: !!sz?.m && sz.pin === "end" });
      of.set(key, span);
      u0 = Math.min(u0, span[0]);
      u1 = Math.max(u1, span[1]);
      const own = cardSpanU(a, b, { off: sz?.off || 0 });
      f0 = Math.min(f0, own[0]);
      f1 = Math.max(f1, own[1]);
    }
    // The session graph's stubs are on it as well, where each ran.
    for (const r of sessionGraph ? relatedRows : []) {
      const a = clock.at(r.firstMs);
      const b = clock.at(Number.isFinite(r.lastMs) ? r.lastMs : r.firstMs);
      u0 = Math.min(u0, a);
      u1 = Math.max(u1, b);
      f0 = Math.min(f0, a);
      f1 = Math.max(f1, b);
    }
    return Number.isFinite(u0) ? { u0, u1, of, fit: [f0, f1] } : null;
  }, [clock, shown, cards, machineCards, sizeOf, sessionGraph, relatedRows]);
  const spanU0 = boardSpan?.u0 ?? null;
  const spanU1 = boardSpan?.u1 ?? null;
  const fitU0 = boardSpan?.fit[0] ?? null;
  const fitU1 = boardSpan?.fit[1] ?? null;
  const spanFitK = fitU0 != null
    ? Math.max(fitK, Math.min(K_MAX, (layoutW - PAD_L - BOARD_PAD_R) / Math.max(MIN_WINDOW_U, fitU1 - fitU0)))
    : fitK;
  const kk = k ?? spanFitK;
  const laidOut = useMemo(() => {
    const t = (key) => {
      const v = cards.get(key)?.firstMs;
      return Number.isFinite(v) ? v : Infinity;
    };
    const keys = [FOCAL, ...shown.map((r) => r.id).filter((key) => cards.get(key))].sort((a, b) => t(a) - t(b));
    // The board: every trace a card on the one axis, at the time it ran and
    // as wide as it ran, its lane names on a rail just before it. Where the
    // cards sit down the board is settled with everything in view: a card,
    // its machine's name over it and the lines its list keeps room for rise
    // to the first space they fit there, the anchor first. Zoomed in, each
    // keeps that place and only grows along the axis, and the gaps between
    // them only widen, so zooming never reshuffles the board.
    // The reader's loose notes, and what waits for a card of its own: a
    // search, a machine or a workspace inside with no card on the clock (still
    // reading, nothing of it in the range, or all of it laid on).
    const notes = new Map((looseNotes || []).map((n) => [`note:${n.id}`, { n, h: noteHeight(n.text) }]));
    const waiting = new Map();
    const stub = (thing, id, label, said) => {
      waiting.set(`${thing}:${id}`, { key: `${thing}:${id}`, kind: "thingStub", thing, id, label, said, w: NOTE_W, h: THING_STUB_H });
    };
    for (const q of padSearches || []) {
      if (machineCards.has(`graph:search:${q.id}`) || drops.has(`graph:search:${q.id}`)) continue;
      stub("search", q.id, q.name || "A search", q.status === "reading" ? "Reading the sessions it matches..." : q.sessions?.length ? "Every session it matches is on the workspace" : "No session matches it in this range");
    }
    for (const m of padMachines || []) {
      if (machineCards.has(`graph:machine:${m.id}`) || drops.has(`graph:machine:${m.id}`)) continue;
      stub("machine", m.id, m.name || "Machine not known", m.status === "reading" ? "Reading its sessions..." : m.sessions?.length ? "Every session it ran in this range is on the workspace" : "No session ran on it in this range");
    }
    for (const w of padNested || []) {
      if (machineCards.has(`graph:workspace:${w.id}`) || drops.has(`graph:workspace:${w.id}`)) continue;
      stub("workspace", w.id, w.name || "Untitled workspace", w.note ? w.note.split("\n")[0] : w.sub);
    }
    // Stacked By time or rolled up, the notes and what waits run in a band
    // along the board's top, the cards under it. Otherwise each note stands
    // where the reader put it (one just written takes a place in view as it
    // first shows, and keeps it) and what waits takes the first place it fits
    // along the top, the cards packing around both: nothing holds the cards
    // under a line across the board.
    const banded = stacked || !!roll;
    const noteItems = [];
    let nx = PAD_L;
    if (banded) {
      for (const [key, { n, h: nh }] of notes) {
        noteItems.push({ key, kind: "note", id: n.id, text: n.text, x: nx, y: MARGIN_Y, w: NOTE_W, h: nh });
        nx += NOTE_W + PACK_GAP_X;
      }
      for (const it of waiting.values()) {
        noteItems.push({ ...it, x: nx, y: MARGIN_Y });
        nx += NOTE_W + PACK_GAP_X;
      }
    }
    const top0 = MARGIN_Y + (noteItems.length ? Math.max(...noteItems.map((it) => it.h)) + PACK_GAP_Y : 0);
    const notesRight = noteItems.reduce((m, it) => Math.max(m, it.x + it.w), 0);
    if (!clock) {
      // Nothing on the board has a span yet: each note where it was put (one
      // with no place yet along the top after the rest), what waits along the
      // top after them, and what was dropped on it where it was let go while
      // it is read.
      const free = [];
      if (!banded) {
        let fx = PAD_L;
        for (const [key, { n, h: nh }] of notes) {
          if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) continue;
          free.push({ key, kind: "note", id: n.id, text: n.text, x: n.x, y: Math.max(MARGIN_Y, n.y), w: NOTE_W, h: nh, handAt: n.y, placed: true });
        }
        for (const it of free) if (it.y < MARGIN_Y + NOTE_MIN_H + PACK_GAP_Y) fx = Math.max(fx, it.x + NOTE_W + PACK_GAP_X);
        for (const [key, { n, h: nh }] of notes) {
          if (Number.isFinite(n.x) && Number.isFinite(n.y)) continue;
          free.push({ key, kind: "note", id: n.id, text: n.text, x: fx, y: MARGIN_Y, w: NOTE_W, h: nh, handAt: null, placed: false });
          fx += NOTE_W + PACK_GAP_X;
        }
        for (const it of waiting.values()) {
          free.push({ ...it, x: fx, y: MARGIN_Y });
          fx += NOTE_W + PACK_GAP_X;
        }
      }
      const landings = [...drops].filter(([key]) => !cards.get(key)?.times && !machineCards.has(key)).map(([key, d]) => {
        const top = Math.max(top0, Number.isFinite(d.top) ? d.top : d.by - DROP_GRAB_Y);
        return { key, kind: "landing", x: d.bx - DROP_GRAB_X, y: top, w: LANDING_W, h: LANDING_H, top, title: d.title || cards.get(key)?.titleText || "", failed: cards.get(key)?.status === "error", status: d.status || null, note: d.note || null };
      });
      const items = [...noteItems, ...free, ...landings];
      const bottom = items.reduce((m, it) => Math.max(m, it.y + it.h), 0);
      return { items, h: items.length ? bottom + MARGIN_Y : 0, w: Math.max(notesRight, ...items.map((it) => it.x + it.w)) + BOARD_PAD_R, x0: Math.min(0, ...items.map((it) => it.x)), top0 };
    }
    const XF = (tm) => PAD_L + clock.at(tm) * fitK;
    const XK = (tm) => PAD_L + clock.at(tm) * kk;
    const endK = PAD_L + axisU * kk;
    // Dropped on the board from outside: where it was let go (along the
    // board, by the moment the view holds still as the clock changes under
    // it, and how far from that moment's place).
    const dropAt = (key) => {
      const d = moving?.key === key ? null : drops.get(key);
      if (!d) return null;
      return Number.isFinite(d.t) ? { ...d, bx: XK(d.t) + d.dx } : d;
    };
    // A card's place along the board (at the fit, where the board packs, and
    // at this zoom): its stretch of the axis, sized and moved as the reader
    // left it, or as it is being carried.
    const xg = new Map();
    const xGeoOf = (key) => {
      if (xg.has(key)) return xg.get(key);
      const c = cards.get(key) || machineCards.get(key);
      const sz = moving?.key === key && moving.off != null ? { ...(sizeOf(key) || {}), off: moving.off } : sizeOf(key);
      const m = sz?.m || (machineCards.has(key) ? Math.min(1, GROUP_BODY_W / Math.max(CELL * 2, XF(c.lastMs) - XF(c.firstMs) + 6)) : 1);
      // A card the reader sized sideways is as wide as they made it, its
      // head and list no wider, and holds the edge they did not take hold
      // of where it was: `off` is how far, in the clock's units, that edge
      // sits from its trace's own start (or, sized from the left, end). A
      // card moved along the board keeps an `off` of its own, sized or not.
      const sizedW = !!sz?.m;
      const pinEnd = sizedW && sz.pin === "end";
      const trueWF = Math.max(CELL * 2, XF(c.lastMs) - XF(c.firstMs) + 6);
      const bodyWF = Math.max(CELL * 2, m * trueWF);
      const trueW = Math.max(CELL * 2, XK(c.lastMs) - XK(c.firstMs) + 6);
      const bodyW = Math.max(CELL * 2, m * trueW);
      // Dropped on the board, it runs from where it was let go, its head
      // under the pointer (back on its own time when let go close to it).
      const dropped = dropAt(key);
      const onTimeX = (pinEnd ? XK(c.lastMs) + 3 - bodyW : XK(c.firstMs) - 3) - BOARD_RAIL;
      const droppedOff = dropped ? (dropped.bx - DROP_GRAB_X - onTimeX) / kk : 0;
      // Laid on from a cell, it lands on its own time, moved along the board
      // as the card it came from is.
      const off = dropped ? (dropped.from ? dropped.srcOff || 0 : Math.abs(droppedOff * kk) < MOVE_SNAP_PX ? 0 : droppedOff) : sz?.off || 0;
      const xFAt = (o) => (pinEnd ? XF(c.lastMs) + 3 + o * fitK - bodyWF : XF(c.firstMs) - 3 + o * fitK);
      // Carried, a card keeps its place among the rest where it was until it
      // is let go, so nothing closes up behind it.
      const bodyXF = xFAt(moving?.key === key ? sizeOf(key)?.off || 0 : off);
      const bodyX = pinEnd ? XK(c.lastMs) + 3 + off * kk - bodyW : XK(c.firstMs) - 3 + off * kk;
      const g = { sz, m, sizedW, pinEnd, off, trueWF, bodyWF, bodyXF, cardWF: BOARD_RAIL + bodyWF, xF: bodyXF - BOARD_RAIL, trueW, bodyW, bodyX, x: bodyX - BOARD_RAIL, cardW: BOARD_RAIL + bodyW };
      xg.set(key, g);
      return g;
    };
    // A card's place in a shape whose head and list are `colW` wide
    // (fillColumn).
    const xs = new Map();
    const shapeGeo = (key, colW) => {
      const g = xGeoOf(key);
      if (g.sizedW || !(colW > g.cardWF)) return g;
      const id = `${key}|${colW}`;
      if (!xs.has(id)) xs.set(id, fillColumn(g, colW));
      return xs.get(id);
    };
    // The shape each card took, for what hangs from it.
    const took = new Map();
    // Where an attachment's moment falls across its card, at the fit or at
    // this zoom: the middle of the cell its turn falls in, as the card draws
    // it; none for one about the whole trace.
    const momentAt = (key, nd, fit, og = took.get(key) || xGeoOf(key)) => {
      const oc = cards.get(key);
      const tu = turnUs.get(key);
      const a = (nd.anchors || []).find((x) => x.key === key);
      if (!oc || !tu || !a || !Number.isFinite(oc.firstMs)) return null;
      const k = fit ? fitK : kk;
      const bodyW = fit ? og.bodyWF : og.bodyW;
      const u0 = clock.at(oc.firstMs) - 3 / k;
      const n = Math.max(1, Math.round(bodyW / CELL));
      const { bucket } = bucketAt(tu[a.turn], { u0, u1: u0 + (fit ? og.trueWF : og.trueW) / k }, n);
      return bucket == null ? null : (fit ? og.bodyXF : og.bodyX) + (bucket + 0.5) * (bodyW / n);
    };
    // A card's band as it stands at the fit, where the board packs (and so
    // at every zoom): its opened cards placed as the card will place them,
    // the band as deep as they stack, and as wide as the blocks set along it
    // run (at the fit, and at this zoom).
    const bandAtFit = (key, atts, minWF, og) => {
      const items = atts.map((nd) => ({ nd, ax: momentAt(key, nd, true, og) }));
      const pinOffsets = new Map(atts.map((nd) => [nd.id, sizeOf(`pin:${nd.id}`)?.dx || 0]));
      const common = { x0: og.bodyXF + 2, y: 0, isStanding, pinnedHeights: pinH, carriedOut: freePins, pinOffsets, openRank };
      const opened = hangAttachments(items, { ...common, x1: og.xF + minWF });
      const wF = Math.max(minWF, opened.reduce((m, p) => Math.max(m, p.x + p.w - og.xF), 0));
      const depth = hangAttachments(items, { ...common, x1: og.xF + wF }).reduce((m, p) => Math.max(m, p.y + p.h), 0);
      let wK = 0;
      for (const p of opened) {
        const dx = pinOffsets.get(p.nd.id);
        if (!dx) continue;
        const ax = momentAt(key, p.nd, false, og);
        wK = Math.max(wK, Math.max(og.bodyX + 2, (ax ?? og.bodyX + 16) - 14 + dx) + PIN_W - og.x);
      }
      return { depth, wF, wK };
    };
    // The pinned attachments the reader carried out of their band, a block
    // each after the card they hang from (a folded card's wait with it).
    const pinOf = new Map();
    const pinsAfter = new Map();
    for (const key of keys) {
      if (sizeOf(key)?.fold || roll) continue;
      const list = (nodesByCard.get(key) || []).filter((nd) => isStanding(nd) && freePins.has(nd.id));
      if (!list.length) continue;
      pinsAfter.set(key, list.map((nd) => `pin:${nd.id}`));
      for (const nd of list) pinOf.set(`pin:${nd.id}`, { owner: key, nd });
    }
    // The session graph's related sessions not open: a stub each, a
    // placeholder at its time.
    const stubOf = new Map(sessionGraph ? relatedRows.map((r) => [r.id, r]) : []);
    const fin = (v) => (Number.isFinite(v) ? v : Infinity);
    const startOf = (key) => fin(cards.get(key)?.firstMs ?? machineCards.get(key)?.firstMs ?? stubOf.get(key)?.firstMs);
    const taken = [];
    // Where each card placed so far ends (its band with it), for what was
    // laid on from it to land under it.
    const placedFoot = new Map();
    // The highest place at or below `floor` a footprint fits, under
    // everything placed that it would touch; `pastFree`, but for what the
    // reader let go over others (a place the board keeps is not pushed down
    // by a card put on top of it).
    const fitFrom = (floor, x0, fw, fh, gapY = PACK_GAP_Y, pastFree = false) => {
      let y = floor;
      for (;;) {
        const hit = taken.find((r) => !(pastFree && r.free) && x0 < r.x1 + PACK_GAP_X && r.x0 < x0 + fw + PACK_GAP_X && y < r.y1 + gapY && r.y0 < y + fh + gapY);
        if (!hit) break;
        y = hit.y1 + gapY;
      }
      return y;
    };
    // Rolled to heads, the heads sit closer: there is less to tell apart.
    const gapY = roll >= 2 ? Math.round(PACK_GAP_Y / 2) : PACK_GAP_Y;
    const riseTo = (x0, fw, fh) => fitFrom(top0, x0, fw, fh, gapY);
    // Stacked, each card takes the next row down, under the one before; the
    // stubs after a card share the rows under it where they fit, and the next
    // card goes under them.
    let cursor = top0;
    let floor = top0;
    let runBottom = null;
    const topFor = stacked ? () => cursor : riseTo;
    // The order the reader set (while a card is carried, the order it would
    // land in), then the rest in time order.
    const focalSid = String(focalSessionId || "").toLowerCase();
    const listed = orderSaved.map((sid) => (sid === focalSid ? FOCAL : sid));
    // Where down the board the reader put a card (carried there and let go,
    // or dropped there from outside), or a note: it keeps that place, and only
    // what comes down onto it from above (a card over it grown taller, or one
    // put over it) pushes it on down. A card no one put anywhere keeps the
    // place it first took (settledTops), so what comes in or grows after it
    // never moves it but by that same push. The rest packs around what has
    // its place. Rolled up, the cards keep the order they were put in, closed
    // up.
    const hands = new Map();
    const handTop = (key) => {
      if (stacked) return null;
      if (hands.has(key)) return hands.get(key);
      let v;
      if (notes.has(key)) v = roll ? null : notes.get(key).n.y;
      else {
        v = sizesSaved?.[key === FOCAL ? focalSessionId : key]?.top;
        const d = dropAt(key);
        if (d?.from) v = null;
        else if (d) v = roll ? orderTopAt(lastFeet.current, d.by, key, (k) => sizesSaved?.[k === FOCAL ? focalSessionId : k]?.top) : d.top;
        else if (!Number.isFinite(v) && !roll) v = settledTops.current.get(key);
      }
      const out = Number.isFinite(v) ? v : null;
      hands.set(key, out);
      return out;
    };
    // Put where the reader let it go: there, over whatever lies there, and
    // nothing the board placed pushes it on down. A note the reader put
    // somewhere is.
    const handFree = (key) => {
      if (stacked || roll || handTop(key) == null) return false;
      if (notes.has(key)) return true;
      return !dropAt(key) && sizesSaved?.[key === FOCAL ? focalSessionId : key]?.free === true;
    };
    // A note along the board (at the fit, where the board packs, and at this
    // zoom): at the moment it was put at, and as far past either end of the
    // clock as it was put there; else where it was put before the board had
    // a clock; null for one put nowhere yet.
    const noteX = (n) => {
      if (Number.isFinite(n.t)) {
        const u = clock.at(n.t);
        const dx = Number(n.dx) || 0;
        return { xF: PAD_L + u * fitK + dx, x: PAD_L + u * kk + dx };
      }
      if (Number.isFinite(n.x)) return { xF: PAD_L + ((n.x - PAD_L) / kk) * fitK, x: n.x };
      return null;
    };
    // Along the top, at the fit, for a note put nowhere yet and what waits.
    let slotF = PAD_L;
    const nextSlot = () => {
      const at = slotF;
      slotF += NOTE_W + PACK_GAP_X;
      return at;
    };
    const rank = new Map(listed.map((key, i) => [key, i]));
    // A pinned block the reader has not placed goes after its own card.
    const rankOf = (key) => rank.get(key) ?? (pinOf.has(key) && rank.has(pinOf.get(key).owner) ? rank.get(pinOf.get(key).owner) + 0.5 : 1e9);
    let packing;
    if (stacked) {
      // Stacked: one card to a row, in the order the reader set and otherwise
      // in time, the anchor among the rest, so reading down is reading along
      // time. A card the reader's order does not hold yet, and each stub,
      // comes after the card that began last before it.
      const lastBefore = (seq, k) => {
        let at = -1;
        for (let i = 0; i < seq.length; i++) {
          if (startOf(seq[i]) < startOf(k) && (at < 0 || startOf(seq[i]) >= startOf(seq[at]))) at = i;
        }
        return at;
      };
      const byStart = [...keys, ...machineCards.keys()].sort((a, b) => startOf(a) - startOf(b));
      const seq = byStart.filter((k) => rank.has(k)).sort((a, b) => rank.get(a) - rank.get(b));
      for (const k of byStart.filter((x) => !rank.has(x))) {
        let i = lastBefore(seq, k) + 1;
        while (i < seq.length && !rank.has(seq[i]) && startOf(seq[i]) <= startOf(k)) i++;
        seq.splice(i, 0, k);
      }
      const runs = new Map();
      for (const sk of [...stubOf.keys()].sort((a, b) => startOf(a) - startOf(b))) {
        const at = lastBefore(seq, sk);
        runs.set(at, [...(runs.get(at) || []), sk]);
      }
      packing = [...(runs.get(-1) || [])];
      seq.forEach((k, i) => packing.push(k, ...(pinsAfter.get(k) || []), ...(runs.get(i) || [])));
    } else {
      // Packed: the notes and what waits along the top, then the anchor and
      // the rest in time order, each with its pinned blocks after it, then
      // the cards for what the chat found outside them and the stubs, each
      // rising to the first place it fits.
      const order = [
        ...[...keys].sort((a, b) => (b === FOCAL) - (a === FOCAL) || t(a) - t(b)).flatMap((k) => [k, ...(pinsAfter.get(k) || [])]),
        ...machineCards.keys(),
      ];
      packing = [
        ...(banded ? [] : [...notes.keys(), ...waiting.keys()]),
        ...[...order].sort((a, b) => rankOf(a) - rankOf(b)), ...[...stubOf.keys()].sort((a, b) => startOf(a) - startOf(b)),
      ];
      // What was dropped on the board holds its place while it is read,
      // whether or not it has a card yet.
      for (const k of drops.keys()) if (!packing.includes(k)) packing.push(k);
      // What the reader put in place first, top down, so the rest fits
      // around it.
      const placed = packing.filter((k) => handTop(k) != null).sort((a, b) => handTop(a) - handTop(b));
      packing = [...placed, ...packing.filter((k) => handTop(k) == null)];
    }
    // What was laid on from a card is placed right after that card (and the
    // blocks pinned under it), to land under it.
    for (const [k, d] of drops) {
      if (!d.from) continue;
      const at = packing.indexOf(k);
      if (at >= 0) packing.splice(at, 1);
      let i = packing.indexOf(d.from.key);
      if (i < 0) {
        packing.push(k);
        continue;
      }
      i += 1;
      while (i < packing.length && pinOf.get(packing[i])?.owner === d.from.key) i++;
      packing.splice(i, 0, k);
    }
    const items = [];
    let h = 0;
    let wMax = -Infinity;
    let wMin = Infinity;
    // The stubs placed since the last card, their rows set at their
    // narrowest. Closing the run widens each into the room before the next on
    // its row, up to what its words want, and puts the next card under them.
    let run = [];
    const closeRun = () => {
      const rows = new Map();
      for (const r of run) rows.set(r.it.y, [...(rows.get(r.it.y) || []), r]);
      for (const row of rows.values()) {
        row.sort((a, b) => a.xF - b.xF);
        row.forEach((r, i) => {
          const room = i + 1 < row.length ? row[i + 1].xF - PACK_GAP_X - r.xF : Infinity;
          r.it.w = Math.max(r.it.x1 - r.it.x, Math.min(STUB_MIN_W, Math.max(STUB_NARROW_W, room)));
          wMax = Math.max(wMax, r.it.x + r.it.w);
        });
      }
      run = [];
      if (runBottom == null) return;
      cursor = Math.max(cursor, runBottom + PACK_GAP_Y);
      runBottom = null;
    };
    // A card still reading its record takes the depth the read ones beside
    // the anchor have (their middle: the anchor shows all its groups, they
    // their conversation and what their wires land on), else the least such a
    // card shows, its conversation and a row for the rest, so it moves little
    // when its record arrives. It keeps the depth it first took while it
    // reads: the others coming in do not change it under the reader.
    const readHs = [...cards.values()].filter((c) => c.model && c.key !== FOCAL).map((c) => sizing.get(c.key)?.h).filter(Number.isFinite).sort((a, b) => a - b);
    const loadingNow = readHs.length ? readHs[Math.floor(readHs.length / 2)] : CARD_HEAD + 2 * COMPACT_H + 2;
    const loadingH = (key) => {
      if (!loadingHs.current.has(key)) loadingHs.current.set(key, loadingNow);
      return loadingHs.current.get(key);
    };
    for (const key of packing) {
      // A note: where it was put, along the board and down it (the highest
      // place there it fits, under what was put over it); one put nowhere yet
      // along the top, after the one before. What waits for a card goes along
      // the top too, as high as it fits.
      if (notes.has(key) || waiting.has(key)) {
        const nt = notes.get(key);
        const at = nt ? noteX(nt.n) : null;
        const xF = at ? at.xF : nextSlot();
        const x = at ? at.x : PAD_L + ((xF - PAD_L) / fitK) * kk;
        const nh = nt ? nt.h : THING_STUB_H;
        const hand = nt ? handTop(key) : null;
        const free = nt ? handFree(key) : false;
        const top = hand != null ? (free ? Math.max(MARGIN_Y, hand) : fitFrom(Math.max(MARGIN_Y, hand), xF, NOTE_W, nh, gapY, true)) : riseTo(xF, NOTE_W, nh);
        const foot = { x0: xF, x1: xF + NOTE_W, y0: top, y1: top + nh };
        taken.push({ ...foot, free });
        items.push(nt
          ? { key, kind: "note", id: nt.n.id, text: nt.n.text, x, y: top, w: NOTE_W, h: nh, top, foot, handAt: hand, placed: !!at && hand != null }
          : { ...waiting.get(key), x, y: top, top, foot });
        wMax = Math.max(wMax, x + NOTE_W);
        wMin = Math.min(wMin, x);
        h = Math.max(h, top + nh + MARGIN_Y);
        continue;
      }
      // A pinned block: at its moment on its card's own stretch of the
      // board (or where the reader carried it along), the highest place it
      // fits, tied back to its moment and cells.
      if (pinOf.has(key)) {
        const { owner, nd } = pinOf.get(key);
        const oc = cards.get(owner);
        if (!oc || !Number.isFinite(oc.firstMs) || !Number.isFinite(oc.lastMs)) continue;
        const og = took.get(owner) || xGeoOf(owner);
        const a = (nd.anchors || []).find((x) => x.key === owner);
        const at = a && Number.isFinite(oc.times?.[a.turn]) ? oc.times[a.turn] : oc.firstMs;
        const psz = moving?.key === key && moving.off != null ? { ...(sizeOf(key) || {}), off: moving.off } : sizeOf(key);
        const poff = psz?.off || 0;
        // Carried, it keeps its place among the rest until let go.
        const pxFAt = (o) => og.bodyXF + ((XF(at) - XF(oc.firstMs) + 3) / og.trueWF) * og.bodyWF - 14 + o * fitK;
        const pxF = pxFAt(moving?.key === key ? sizeOf(key)?.off || 0 : poff);
        const px = og.bodyX + ((XK(at) - XK(oc.firstMs) + 3) / og.trueW) * og.bodyW - 14 + poff * kk;
        const ph = pinH.get(nd.id) ?? PIN_EST;
        if (stacked) closeRun();
        const hand = roll ? null : handTop(key);
        const pinFree = handFree(key);
        const top = hand != null ? (pinFree ? Math.max(top0, hand) : fitFrom(Math.max(top0, hand), pxF, PIN_W, ph, gapY, true)) : topFor(pxF, PIN_W, ph);
        if (stacked) { cursor = top + ph + PACK_GAP_Y; floor = cursor; }
        const carried = moving?.key === key;
        const drawnY = carried ? moving.top : top;
        taken.push({ x0: pxF, x1: pxF + PIN_W, y0: top, y1: top + ph, free: pinFree });
        wMax = Math.max(wMax, px + PIN_W);
        wMin = Math.min(wMin, px);
        items.push({ key, kind: "pin", owner, nd, x: px, y: drawnY, w: PIN_W, h: ph, top, settleTop: null, moved: poff !== 0, foot: { x0: pxF, x1: pxF + PIN_W, y0: top, y1: top + ph }, handAt: handTop(key), off: poff });
        h = Math.max(h, Math.max(top, drawnY) + ph + MARGIN_Y);
        continue;
      }
      // A related session not open (the session graph's): a stub at its
      // time, one line high, its words starting where it began and its time
      // along its foot. Stacked, the stubs after a card share the rows under
      // it, each as narrow as it can be until the rows are set and then as
      // wide as the room before the next on its row lets its words be.
      // Packed, it takes the room its words want where there is room, else a
      // narrower one, keeping its place rather than going under its
      // neighbours.
      const sb = stubOf.get(key);
      if (sb) {
        const lastMs = Number.isFinite(sb.lastMs) ? sb.lastMs : sb.firstMs;
        const x0 = XK(sb.firstMs) - 3;
        const x1 = Math.max(x0 + CELL * 2, XK(lastMs) + 3);
        const bx = x0 - CARD_TEXT_X;
        const xF = XF(sb.firstMs) - 3 - CARD_TEXT_X;
        const spanF = XF(lastMs) + 3 - xF;
        const shapes = (stacked ? [STUB_NARROW_W] : [STUB_MIN_W, STUB_NARROW_W]).map((min) => {
          const wF = Math.max(spanF, min);
          return { min, wF, top: stacked ? fitFrom(floor, xF, wF, STUB_H, STUB_GAP) : riseTo(xF, wF, STUB_H) };
        });
        const pick = shapes.reduce((p, q) => (q.top < p.top ? q : p));
        const top = pick.top;
        const it = { key, kind: "stub", x: bx, y: top, w: Math.max(x1 - bx, pick.min), h: STUB_H, x0, x1 };
        taken.push({ x0: xF, x1: xF + pick.wF, y0: top, y1: top + STUB_H });
        items.push(it);
        wMax = Math.max(wMax, bx + it.w);
        wMin = Math.min(wMin, bx);
        h = Math.max(h, top + STUB_H + MARGIN_Y);
        if (stacked) {
          run.push({ it, xF });
          runBottom = Math.max(runBottom ?? -Infinity, top + STUB_H);
        }
        continue;
      }
      const c = cards.get(key) || machineCards.get(key);
      // Dropped on the board, and what it brings still being read: the place
      // it landed is held for it (whatever lies there makes room) until its
      // card comes and grows out of it.
      const dl = (!c || !Number.isFinite(c.firstMs) || !Number.isFinite(c.lastMs)) ? dropAt(key) : null;
      if (dl) {
        const lx = dl.bx - DROP_GRAB_X;
        const lxF = PAD_L + ((lx - PAD_L) / kk) * fitK;
        const hand = roll ? null : handTop(key);
        const under = dl.from ? placedFoot.get(dl.from.key) : null;
        const top = under != null ? fitFrom(Math.max(top0, under), lxF, LANDING_W, LANDING_H, gapY)
          : hand != null ? fitFrom(Math.max(top0, hand), lxF, LANDING_W, LANDING_H, gapY) : topFor(lxF, LANDING_W, LANDING_H);
        taken.push({ x0: lxF, x1: lxF + LANDING_W, y0: top, y1: top + LANDING_H });
        placedFoot.set(key, top + LANDING_H);
        if (stacked) { cursor = Math.max(cursor, top + LANDING_H + gapY); floor = cursor; }
        items.push({ key, kind: "landing", x: lx, y: top, w: LANDING_W, h: LANDING_H, top, foot: { x0: lxF, x1: lxF + LANDING_W, y0: top, y1: top + LANDING_H }, title: dl.title || c?.titleText || c?.label || "", failed: c?.status === "error", status: dl.status || null, note: dl.note || null });
        wMax = Math.max(wMax, lx + LANDING_W);
        wMin = Math.min(wMin, lx);
        h = Math.max(h, top + LANDING_H + MARGIN_Y);
        continue;
      }
      if (!c || !Number.isFinite(c.firstMs) || !Number.isFinite(c.lastMs)) continue;
      if (stacked) closeRun();
      const rolled = roll >= 2 && !sizeOf(key)?.fold;
      const folded = !!sizeOf(key)?.fold || roll >= 2;
      const listH = c.graph ? CARD_HEAD + c.rows.length * GRAPH_ROW_H + 4 : 0;
      const ch = folded ? CARD_HEAD : c.graph ? Math.max(listH, c.workspace ? sizeOf(key)?.h || 0 : 0) : sizing.get(key)?.h || (c.model ? CARD_HEAD + 30 : loadingH(key));
      const hand = roll ? null : handTop(key);
      const free = handFree(key);
      // Laid on from a card and still settling: under that card.
      const cameDrop = dropAt(key)?.from || null;
      const under = cameDrop ? placedFoot.get(cameDrop.key) ?? null : null;
      // Its place, with everything in view. A short card's head and list are
      // wider than it, for their words; where that width would push it under
      // its neighbours, it takes a narrower head and list and keeps its place.
      // A card being carried sits where it is carried to along the board.
      const atts = c.graph || folded || roll ? [] : attachBand(key);
      // Its place under the wider head and list, and under the narrower. A
      // trace's card is as wide as one with a list whether or not anything
      // hangs from it yet: what hangs from a card comes with its record and
      // after it, and a card that grew wider as it did could push the one
      // beside it down.
      const wideG = shapeGeo(key, c.graph ? HEAD_MIN_W : SHELF_MIN_W);
      const narrowG = shapeGeo(key, HEAD_NARROW_W);
      const { sizedW } = wideG;
      // The band's depth, the same at every zoom: the opened cards, stacked
      // as they stack at the fit (zooming in only spreads them apart).
      const attachCount = folded || roll ? (nodesByCard.get(key) || []).length : 0;
      // Its band, as it hangs under the wider head or the narrower one.
      const wideWF = sizedW ? wideG.cardWF : Math.max(wideG.cardWF, SHELF_MIN_W);
      const narrowWF = sizedW ? narrowG.cardWF : Math.max(narrowG.cardWF, HEAD_NARROW_W);
      const noBand = { depth: 0, wF: 0, wK: 0 };
      const bandWide = atts.length ? bandAtFit(key, atts, wideWF, wideG) : noBand;
      const bandNarrow = !atts.length ? noBand : narrowWF === wideWF && narrowG === wideG ? bandWide : bandAtFit(key, atts, narrowWF, narrowG);
      const shapes = [
        { g: wideG, headW: sizedW ? wideG.cardWF : Math.max(wideG.cardWF, HEAD_MIN_W), shelfW: atts.length ? bandWide.wF : 0, listW: atts.length ? wideWF : 0, band: bandWide },
        { g: narrowG, headW: sizedW ? narrowG.cardWF : Math.max(narrowG.cardWF, HEAD_NARROW_W), shelfW: atts.length ? bandNarrow.wF : 0, listW: atts.length ? narrowWF : 0, band: bandNarrow },
      ].map((sh) => {
        const shelfH = sh.band.depth;
        const shelfTop = shelfH ? HANG_GAP : 0;
        const fh = ch + shelfTop + shelfH;
        const fw = Math.max(sh.g.cardWF, sh.headW, sh.shelfW);
        const ax = sh.g.xF;
        const fhSlot = moving?.key === key && Number.isFinite(moving.slotH) ? moving.slotH : fh;
        return {
          ...sh, fw, fh, shelfH, shelfTop, ax,
          top: under != null ? fitFrom(Math.max(top0, under), ax, fw, fhSlot, gapY)
            : hand != null ? (free ? Math.max(top0, hand) : fitFrom(Math.max(top0, hand), ax, fw, fhSlot, gapY, true)) : topFor(ax, fw, fhSlot),
        };
      });
      // Rolled to heads, a head takes the wider shape: its words are all it
      // shows. Otherwise the shape it first took (settledShapes).
      const shapeKept = settledShapes.current.get(key);
      const chosen = roll >= 2 ? shapes[0] : shapeKept != null ? shapes[shapeKept] : shapes[1].top >= shapes[0].top ? shapes[0] : shapes[1];
      const drawnUp = sizeDraft?.key === key && sizeDraft.top != null;
      // While a card is sized the cards above it hold still, so nothing
      // moves under the pointer; the ones at or below its top make room as
      // it grows and close up as it shrinks, so it never lies over them.
      const sizedTop = sizeDraft?.key ? frozenTops?.get(sizeDraft.key) : null;
      const holds = frozenTops?.has(key) && (key === sizeDraft?.key || sizedTop == null || frozenTops.get(key) < sizedTop);
      const held = drawnUp ? sizeDraft.top : stacked || hand != null ? null : holds ? frozenTops.get(key) : null;
      const pick = held != null ? { ...chosen, top: held } : chosen;
      const { fh, shelfH, shelfTop } = pick;
      const { m, pinEnd, off, trueW, bodyW, bodyX, x, cardW, stretch = 1 } = pick.g;
      took.set(key, pick.g);
      const bandH = shelfH;
      // Sized from its top, or carried, a card follows the pointer over its
      // neighbours; let go, it stays there.
      const carried = moving?.key === key;
      const drawnY = carried ? moving.top : pick.top;
      // Carried, its place keeps the depth it had as the carry began (off
      // its time it grows a ruler of its own), so nothing moves under it.
      const slotH = carried && Number.isFinite(moving.slotH) ? moving.slotH : fh;
      taken.push({ x0: pick.ax, x1: pick.ax + pick.fw, y0: pick.top, y1: pick.top + slotH, free });
      placedFoot.set(key, pick.top + slotH);
      if (stacked) { cursor = pick.top + slotH + gapY; floor = cursor; }
      // At this zoom: the same place down the board, its stretch of the axis
      // along it. The head and the list start where the card does.
      const headW = sizedW ? cardW : Math.max(cardW, pick.headW);
      const shelfW = atts.length ? (sizedW ? cardW : Math.max(cardW, pick.listW)) : 0;
      const ax = x;
      // The card and its list are one column: as wide as the wider of them.
      const colW = Math.max(cardW, headW, shelfW);
      // Its band runs on past the column as far as the blocks set along it do.
      const bandW = atts.length ? Math.max(colW, pick.band.wK, pick.shelfW) : colW;
      wMax = Math.max(wMax, ax + bandW);
      wMin = Math.min(wMin, ax);
      items.push({ key, kind: "card", shape: chosen === shapes[1] ? 1 : 0, x, headX: ax, headW: colW, y: drawnY, w: cardW, h: ch, bodyX, bodyW, trueW, u0: (XK(c.firstMs) - 3 - PAD_L) / kk, scale: m, stretch, moved: off !== 0, pin: pinEnd ? "end" : "start", top: pick.top, settleTop: null, folded, rolled: rolled || (roll === 1 && !folded), attachCount, foot: { x0: pick.ax, x1: pick.ax + pick.fw, y0: pick.top, y1: pick.top + slotH }, handAt: cameDrop ? (handTop(cameDrop.key) != null ? handTop(cameDrop.key) + 0.5 : pick.top) : handTop(key), off });
      if (shelfH) items.push({ key: `nodes:${key}`, kind: "node", owner: key, x: ax, y: drawnY + ch + shelfTop, w: bandW, h: shelfH, nodes: atts, bandH });
      h = Math.max(h, Math.max(pick.top, drawnY) + fh + MARGIN_Y);
    }
    // Carried, a card lands where it is let go, over whatever is there
    // (Tidy lays the board out again, none over another); above the board's
    // top, the board's top comes up to it (putDown).
    if (moving?.key && !roll) {
      const it = items.find((x) => x.key === moving.key && x.foot);
      if (it) it.settleTop = Math.round(moving.top);
    }
    // Across, it runs from its first card's edge to its last's, by one pad
    // (room to take a right edge), wherever on the clock they sit; down,
    // only to its lowest card. With nothing on it, it is the clock.
    if (stacked) closeRun();
    if (!Number.isFinite(wMin)) { wMin = 0; wMax = endK; }
    if (noteItems.length) {
      items.unshift(...noteItems);
      h = Math.max(h, top0 + MARGIN_Y);
      wMax = Math.max(wMax, notesRight);
      wMin = Math.min(wMin, PAD_L);
    }
    return { items, h, w: wMax + BOARD_PAD_R, x0: wMin, top0 };
  }, [shown, cards, sizing, clock, fitK, kk, axisU, attachBand, nodesByCard, isStanding, openRank, turnUs, pinH, freePins, sizeOf, frozenTops, sizeDraft, moving, orderSaved, focalSessionId, machineCards, sessionGraph, relatedRows, stacked, roll, looseNotes, padSearches, padNested, drops, sizesSaved, MARGIN_Y]);
  // Focused on the evidence, the board lays its cards out again for it for as
  // long as the focus lasts (nothing of it kept): the cards holding it in a
  // column in the order it happened, each cut to the stretch of time it rests
  // on and drawn across the board (what it ran before and after said in its
  // head, its own ruler over it), the cards holding none under them; what
  // hangs from a card goes with it.
  const layout = useMemo(() => {
    if (!focusLanes || !clock || !laidOut.items.length) return laidOut;
    const x = PAD_L;
    const bodyW = Math.max(240, planeW - BOARD_RAIL - 2 * FOCUS_EDGE);
    const first = (key) => {
      const f = focusLanes.get(key);
      return f?.times.length ? Math.min(...f.times) : cards.get(key)?.firstMs ?? Infinity;
    };
    const cardsIn = laidOut.items.filter((it) => it.kind === "card");
    const order = [...cardsIn].sort((a, b) => (focusLanes.has(b.key) - focusLanes.has(a.key)) || first(a.key) - first(b.key));
    const bands = new Map(laidOut.items.filter((it) => it.kind === "node" && it.owner).map((it) => [it.owner, it]));
    const out = laidOut.items.filter((it) => it.kind !== "card" && !(it.kind === "node" && it.owner));
    let y = laidOut.top0 ?? MARGIN_Y;
    let right = 0;
    for (const it of order) {
      const f = focusLanes.get(it.key);
      const c = cards.get(it.key);
      let next = { ...it, x, headX: x, y, top: y, settleTop: null, handAt: null, bodyX: x + BOARD_RAIL, focus: true };
      if (f?.times.length && c && Number.isFinite(c.firstMs) && Number.isFinite(c.lastMs)) {
        const lo = Math.min(...f.times);
        const hi = Math.max(...f.times);
        const spare = Math.max(30_000, (hi - lo) * 0.12) + Math.max(0, FOCUS_SPAN_MS - (hi - lo)) / 2;
        const t0 = Math.max(c.firstMs, lo - spare);
        const t1 = Math.min(c.lastMs, hi + spare);
        const u0 = clock.at(t0);
        const u1 = clock.at(t1);
        if (u1 > u0) {
          const trueW = (u1 - u0) * kk;
          next = { ...next, w: BOARD_RAIL + bodyW, headW: Math.max(it.headW || 0, BOARD_RAIL + bodyW), bodyW, trueW, u0, scale: bodyW / trueW, moved: false, h: it.h + CARD_RULER_H, crop: { before: t0 - c.firstMs, after: c.lastMs - t1 } };
        }
      }
      const band = bands.get(it.key);
      const grew = next.h - it.h;
      const movedBand = band ? { ...band, x: band.x + (x - it.x), y: band.y + (y - it.y) + grew } : null;
      const slot = Math.max(next.h, movedBand ? movedBand.y + movedBand.h - y : 0);
      next.foot = { x0: x, x1: x + Math.max(next.w, next.headW || 0), y0: y, y1: y + slot };
      out.push(next);
      if (movedBand) out.push(movedBand);
      right = Math.max(right, x + Math.max(next.w, next.headW || 0), movedBand ? movedBand.x + movedBand.w : 0);
      y += slot + PACK_GAP_Y;
    }
    const bottom = out.reduce((m, it) => Math.max(m, it.y + it.h), y);
    return { ...laidOut, items: out, h: bottom + MARGIN_Y, w: Math.max(laidOut.w, right + BOARD_PAD_R), x0: Math.min(laidOut.x0, x) };
  }, [laidOut, focusLanes, clock, kk, planeW, cards, MARGIN_Y]);

  // A card keeps the place it first took on the board (settledTops), still
  // reading its record or read: it grows or shrinks there as its record
  // comes in, pushing on down only what lies under it. One taken off forgets
  // it, so laid on again it finds a place afresh.
  useLayoutEffect(() => {
    if (stacked || roll || still || !size.w) return;
    const on = new Set();
    for (const it of layout.items) {
      if (it.kind !== "card") continue;
      on.add(it.key);
      if (moving?.key === it.key) continue;
      if (!settledShapes.current.has(it.key)) settledShapes.current.set(it.key, it.shape || 0);
      if (it.handAt != null || settledTops.current.has(it.key)) continue;
      settledTops.current.set(it.key, Math.round(it.top));
    }
    for (const kept of [settledTops.current, settledShapes.current]) {
      for (const key of [...kept.keys()]) if (!on.has(key) && !drops.has(key)) kept.delete(key);
    }
    for (const key of [...loadingHs.current.keys()]) if (!on.has(key) || cards.get(key)?.model) loadingHs.current.delete(key);
  }, [layout]); // eslint-disable-line react-hooks/exhaustive-deps
  // Once every record on the board is in, each card no one put anywhere is
  // kept where it stands then, with the places the reader gave: the board
  // opens again as it was left, and nothing moves on from where the reader
  // last saw it.
  useEffect(() => {
    if (reading || stacked || roll || still || moving || resizing) return;
    const sidOf = (key) => (key === FOCAL ? focalSessionId : key);
    const fresh = layout.items.filter((it) => it.kind === "card" && settledTops.current.has(it.key) && !Number.isFinite(sizesSaved?.[sidOf(it.key)]?.top));
    if (!fresh.length) return;
    const next = { ...(sizesSaved || {}) };
    for (const it of fresh) {
      const top = Math.round(it.top);
      settledTops.current.set(it.key, top);
      const entry = sizeEntry(next[sidOf(it.key)], { ...(next[sidOf(it.key)] || {}), top });
      if (entry) next[sidOf(it.key)] = entry;
    }
    if (onSizes) onSizes(next);
    else setOwnSizes(next);
  }, [reading, layout]); // eslint-disable-line react-hooks/exhaustive-deps
  // A note with no place on the board yet takes one as it first shows, and
  // the board keeps it there: one just written, the first place it fits at
  // the top left of the view, so it is written where the reader is looking;
  // one kept from before notes had a place down the board, where it shows.
  useLayoutEffect(() => {
    if (!onLooseNote || stacked || roll || still || !size.w) return;
    const moves = [];
    for (const it of layout.items) {
      if (it.kind !== "note" || it.placed) continue;
      const n = (looseNotes || []).find((x) => x.id === it.id);
      if (!n) continue;
      let { x, y } = it;
      if (!Number.isFinite(n.x) && !Number.isFinite(n.t)) {
        const feet = [...feetAsDrawn(layout.items, new Set([it.key])).values()];
        x = view.x + NOTE_IN_VIEW / cam;
        y = Math.max(MARGIN_Y, view.y + NOTE_IN_VIEW / cam);
        for (;;) {
          const hit = feet.find((f) => x < f.x1 + PACK_GAP_X && f.x0 < x + NOTE_W + PACK_GAP_X && y < f.y1 + PACK_GAP_Y && f.y0 < y + it.h + PACK_GAP_Y);
          if (!hit) break;
          y = hit.y1 + PACK_GAP_Y;
        }
      }
      moves.push({ id: it.id, ...notePlace(x, y) });
    }
    if (moves.length) onLooseNote(moves);
  }, [layout]); // eslint-disable-line react-hooks/exhaustive-deps

  // The layout as drawn: across a change, the new one with each card (and
  // what hangs off it) set back toward where it stood, along the board and
  // down it, by what is left of the glide, so nothing jumps and the eye can
  // follow each thing to where it goes. Where it stood is on the board, not
  // the screen: the view moved meanwhile moves it with everything else.
  const drawnLayout = useMemo(() => {
    const from = glideFrom.current;
    if (!from || glideT >= 1) return layout;
    const left = (1 - glideT) ** 3;
    const dOf = new Map();
    for (const it of layout.items) {
      const was = from.get(it.key);
      // The card in hand is where the hand has it, whatever glide was under way.
      if (was == null || it.key === moving?.key) continue;
      dOf.set(it.key, {
        dy: (was.y - it.y) * left,
        dx: Number.isFinite(was.x) ? (was.x - it.x) * left : 0,
      });
    }
    return {
      ...layout,
      items: layout.items.map((it) => {
        const d = dOf.get(it.key) ?? (it.owner ? dOf.get(it.owner) : null);
        // A card that shows less shrinks to it, its frame closing up over
        // the glide rather than at once; one that shows more opens out to
        // it, its rows coming into view as its frame does.
        const was = it.kind === "card" ? from.get(it.key) : null;
        const h = was ? it.h + (was.h - it.h) * left : it.h;
        const opening = !!was && was.h < it.h;
        const dx = d?.dx || 0;
        const dy = d?.dy || 0;
        if (!dx && !dy && h === it.h) return it;
        return {
          ...it, y: it.y + dy, h,
          ...(dx ? { x: it.x + dx, ...(it.headX != null ? { headX: it.headX + dx } : {}), ...(it.bodyX != null ? { bodyX: it.bodyX + dx } : {}) } : {}),
          ...(opening ? { opening } : {}),
        };
      }),
    };
  }, [layout, glideT, moving?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where everything sits at this zoom.
  const hoverKey = hover?.key || null;
  // In focus: the session selected, else the one pointed at (its card, or its
  // row on a machine's card).
  const focusKey = sel?.key && sel.key !== FOCAL ? sel.key : hoverKey && hoverKey !== FOCAL ? hoverKey : null;
  // Placement is the zoom's business and nothing else's: hovering or moving
  // the cursor restyles the wires but never rebuilds a card.
  const placed = useMemo(() => {
    if (!clock) return null;
    const X = (t) => PAD_L + clock.at(t) * kk;
    const cardGeo = new Map();
    const place = (c, it) => {
      // Its stretch of the board's axis, at the board's one scale, after its
      // rail.
      const tu = turnUs.get(c.key);
      if (!c.model || !tu) return null;
      const rail = BOARD_RAIL;
      const y = it.y;
      // Its own stretch of the clock across its body: at the board's scale,
      // or at the scale the reader sized it to, its own ruler saying so.
      const scaled = Math.abs((it.scale || 1) - 1) > 0.01 || !!it.moved;
      const geoOf = { headX: it.headX, headW: it.headW, scaled, scale: it.scale || 1, stretch: it.stretch || 1, moved: !!it.moved, focus: !!it.focus, crop: it.crop || null };
      const bodyW = it.bodyW;
      const u0 = it.u0 ?? (it.bodyX - PAD_L) / kk;
      const win = { u0, u1: u0 + (it.trueW || bodyW) / kk };
      const x = it.x;
      const head = scaled && !it.folded ? CARD_HEAD + CARD_RULER_H : CARD_HEAD;
      const own = { clock, kx: bodyW / Math.max(1e-9, win.u1 - win.u0), ...geoOf };
      const n = Math.max(1, Math.round(bodyW / CELL));
      // Its scene (its rows, their cells, what is ringed and pinned on them)
      // is the card's own: where the card stands down the board has no part
      // in it. Built again only when something it is built from changed,
      // not on every frame of a glide or a carry.
      const opts = rowsOpts(c.key);
      const selIn = sel?.key === c.key && sel.laneId ? `${sel.laneId}:${sel.turn}` : "";
      const pinsIn = c.focal && focalPins ? focalPins : findingPinsOf.get(c.key) || null;
      const litIn = lit && (lit.key ?? FOCAL) === c.key ? litCells : null;
      const sig = [c.model, tu, win.u0, win.u1, n, bodyW, opts.openGroups, opts.openDirs, opts.showAll, opts.only, opts.keep, opts.caps, selIn, pinsIn, litIn];
      const kept = sceneCache.current.get(c.key);
      let lanes;
      let scene;
      let pinIds;
      if (kept && kept.sig.length === sig.length && kept.sig.every((v, i) => v === sig[i])) ({ lanes, scene, pinIds } = kept);
      else {
        lanes = bucketLanes(c.model.lanes, tu, win, n);
        const rows = cardRows({ model: c.model, lanesById: lanes, ...opts });
        const rowOf = (laneId) => rows.find((r) => (r.type === "lane" && r.lane.id === laneId) || (r.type === "head" && r.laneIds?.includes(laneId)));
        let selHere = null;
        if (sel?.key === c.key && sel.laneId) {
          const { bucket } = bucketAt(tu[sel.turn], win, n);
          const row = rowOf(sel.laneId);
          if (bucket != null && row) selHere = { laneId: row.type === "lane" ? row.lane.id : row.id, turn: bucket };
        }
        // The findings' marks (or the host's pins, on a preview's card) on
        // their cells at their buckets: one a row and bucket (the scene places
        // a folded group's cells by its lanes' ids). A mark of the board's own
        // counts every finding there, at the worst one's tone, with the
        // findings it stands for; a host's pins, which may pin one finding on
        // several lanes, keep the first.
        const findingPins = [];
        pinIds = new Map();
        for (const p of pinsIn || []) {
          const { bucket } = bucketAt(tu[p.turn], win, n);
          const row = rowOf(p.laneId);
          if (bucket == null || !row) continue;
          const had = findingPins.find((q) => q.row === row && q.turn === bucket);
          if (had) {
            if (p.ring) had.ring = true;
            if (!p.ids) continue;
            const ids = [...new Set([...(pinIds.get(`${had.lane}:${bucket}`) || []), ...p.ids])];
            pinIds.set(`${had.lane}:${bucket}`, ids);
            had.count = ids.length;
            if ((TONE_RANK[p.tone] ?? 9) < (TONE_RANK[had.tone] ?? 9)) had.tone = p.tone;
            continue;
          }
          findingPins.push({ row, lane: p.laneId, turn: bucket, tone: p.tone, count: p.count, title: p.title, ring: !!p.ring });
          if (p.ids) pinIds.set(`${p.laneId}:${bucket}`, [...p.ids]);
        }
        // The cells carrying the lit hand-off's strings, ringed at their buckets.
        const joinRings = [];
        if (lit && (lit.key ?? FOCAL) === c.key) {
          for (const m of litCells) {
            const { bucket } = bucketAt(tu[m.turn], win, n);
            const row = rowOf(m.lane);
            if (bucket == null || !row || joinRings.some((q) => q.row === row && q.turn === bucket)) continue;
            joinRings.push({ row, lane: m.lane, turn: bucket });
          }
        }
        scene = buildLaneScene({ rows, cellW: bodyW / n, width: bodyW, turnCount: n, railW: rail, sel: selHere, canSplice: false, findingPins, joinRings });
        sceneCache.current.set(c.key, { sig, lanes, scene, pinIds });
      }
      // Given a height, the card is that height, its rows inside it.
      const full = Math.max(head + scene.height + 2, it.h || 0);
      const cg = {
        key: c.key, x, y, w: rail + bodyW, rail, h: it.folded ? head : it.opening ? Math.max(head, Math.min(it.h, full)) : full, bodyY: y + head, scene, lanes, win, n, cellW: bodyW / n, tu, ...own,
        folded: !!it.folded, rolled: !!it.rolled, attachCount: it.attachCount || 0, findingPinIds: pinIds || null,
      };
      // Nothing about it changed (another card carried, a glide elsewhere):
      // the card as it was placed, so what is drawn from it holds still.
      const was = cardCache.current.get(c.key);
      if (was && sameCardGeo(was, cg)) return was;
      cardCache.current.set(c.key, cg);
      return cg;
    };
    // Where a moment falls on a board card.
    const cardX = (cg, t) => cg.x + (cg.rail || 0) + (cg.clock.at(t) - cg.win.u0) * cg.kx;
    // A machine card: a row per stretch of the chat's finds, its marks on the
    // board's axis, those within a few pixels of each other merged.
    const placeGraph = (gc, it) => {
      const bodyY = it.y + CARD_HEAD;
      // Its own stretch of the clock: the board's axis, or the scale the
      // reader sized it to, as a trace's card has.
      const trueW = Math.max(1e-9, it.trueW || 1);
      const at = (t) => it.bodyX + ((X(t) - X(gc.firstMs) + 3) / trueW) * it.bodyW;
      const rows = gc.rows.map((r, i) => ({
        ...r, y: it.folded ? bodyY - 3 - GRAPH_ROW_H / 2 : bodyY + i * GRAPH_ROW_H,
        spanX: r.span ? { a: at(r.span.from), b: at(r.span.to) } : null,
      }));
      const marks = [];
      for (const r of rows) {
        let last = null;
        for (const e of r.events) {
          const mx = at(e.at);
          if (last && last.kind === e.kind && mx - last.x < 4) {
            last.events.push(e);
            last.isNew = last.isNew || e.isNew;
            continue;
          }
          last = { x: mx, y: r.y + GRAPH_ROW_H / 2, kind: e.kind, events: [e], isNew: e.isNew, row: r };
          marks.push(last);
        }
      }
      for (const m of marks) m.claims = (investigation?.claims || []).filter((cl) => m.events.some((e) => cl.evidence.includes(e.key))).map((cl) => cl.id);
      const u0 = it.u0 ?? (it.bodyX - PAD_L) / kk;
      const win = { u0, u1: u0 + trueW / kk };
      const listH = CARD_HEAD + rows.length * GRAPH_ROW_H + 4;
      const h = it.folded ? CARD_HEAD : gc.workspace ? Math.max(it.opening ? CARD_HEAD : listH, it.h || 0) : it.opening ? Math.max(CARD_HEAD, Math.min(it.h, listH)) : listH;
      // Made taller than its list, a workspace inside shows its own board in
      // place of the list, under its head across the card's whole frame.
      const fx0 = Math.min(it.x, it.headX ?? it.x);
      const fx1 = Math.max(it.x + it.w, (it.headX ?? it.x) + (it.headW || 0));
      const room = h - listH - 4;
      const inset = gc.workspace && !it.folded && !it.rolled && room >= NEST_INSET_MIN_H ? { x: fx0 + 1, y: bodyY, w: fx1 - fx0 - 2, h: h - CARD_HEAD - 2 } : null;
      return {
        key: gc.key, graph: true, x: it.x, y: it.y, w: it.w, h, rail: BOARD_RAIL, bodyY, folded: !!it.folded, inset,
        headX: it.headX, headW: it.headW, rows, marks, label: gc.label, people: gc.people, isNew: gc.isNew, firstMs: gc.firstMs, lastMs: gc.lastMs, machine: !!gc.machine, search: !!gc.search, workspace: !!gc.workspace, sub: gc.sub || "", more: gc.more || 0, members: gc.members || 0, facts: gc.facts || null,
        clock, win, kx: it.bodyW / Math.max(1e-9, win.u1 - win.u0), scaled: Math.abs((it.scale || 1) - 1) > 0.01 || !!it.moved, stretch: it.stretch || 1,
      };
    };
    // A cell on a card; on a folded one, its moment along the head's foot.
    const cellAt = (cg, lane, turn) => {
      if (!cg) return null;
      const { bucket } = bucketAt(cg.tu[turn], cg.win, cg.n);
      if (bucket == null) return null;
      const x = cg.x + (cg.rail || 0) + (bucket + 0.5) * cg.cellW;
      if (cg.folded) return { x, y: cg.y + cg.h - 3, w: cg.cellW, folded: true };
      const cy = cg.scene.centers.get(lane);
      if (cy == null) return null;
      return { x, y: cg.bodyY + cy, w: cg.cellW };
    };
    const loading = [];
    const nodeSlots = [];
    const pinSlots = [];
    const stubs = new Map();
    for (const it of drawnLayout.items) {
      if (it.kind === "node") { nodeSlots.push(it); continue; }
      if (it.kind === "pin") { pinSlots.push(it); continue; }
      if (it.kind === "stub") { stubs.set(it.key, { key: it.key, x: it.x, y: it.y, w: it.w, h: it.h, x0: it.x0, x1: it.x1, barY: it.y + it.h - 1, ...stubInfo.get(it.key) }); continue; }
      const c = cards.get(it.key) || machineCards.get(it.key);
      if (!c || it.kind !== "card") continue;
      const cg = c.graph ? placeGraph(c, it) : place(c, it);
      if (cg) {
        cardGeo.set(it.key, cg);
        continue;
      }
      // Still reading its record: where it will be, at the depth it will
      // likely take, its head drawn over rows that pulse until it arrives.
      if (!c.graph) loading.push({ key: it.key, x: it.x, y: it.y, w: it.w, h: it.h, headX: it.headX, headW: it.headW, status: c.status });
    }
    const focalCard = cardGeo.get(FOCAL) || null;
    // The annotation nodes, each under its card at the place of the first cell
    // it is about, with every cell it points at.
    const nodes = [];
    const shelves = [];
    // Each card's attachments where they hang (or, compared, in rows): the
    // moment each is about on the card's own clock, none for one about the
    // whole trace.
    const momentOf = (cg, nd) => {
      const a = (nd.anchors || []).find((x) => x.key === cg.key);
      if (a && cg.win && cg.tu) {
        const { bucket } = bucketAt(cg.tu[a.turn], cg.win, cg.n);
        if (bucket != null) return cg.x + (cg.rail || 0) + (bucket + 0.5) * cg.cellW;
      }
      return cg.rail ? null : cg.x + CARD_GLYPH_X;
    };
    const ptsOf = (nd) => nd.anchors.map((a) => cellAt(cardGeo.get(a.key), a.lane, a.turn)).filter(Boolean);
    for (const it of nodeSlots) {
      const cg = cardGeo.get(it.owner);
      if (!cg) continue;
      // A band whose card, slot and opened cards stand as they did (and the
      // cards they point into too) is the band as it was laid out: its nodes
      // the same objects, so their cards are not drawn again for nothing.
      const reach = [...new Set(it.nodes.flatMap((nd) => (nd.anchors || []).map((a) => a.key)))].map((k) => cardGeo.get(k));
      const sig = [cg, it.x, it.y, it.w, it.h, pinH, freePins, openRank, isStanding, sizeOf, ...it.nodes, ...reach];
      const hung = bandCache.current.get(it.owner);
      if (hung && hung.sig.length === sig.length && hung.sig.every((v, i) => v === sig[i])) {
        nodes.push(...hung.out);
        continue;
      }
      const start = nodes.length;
      const foot = cg.y + cg.h;
      const items = it.nodes.map((nd) => ({ nd, ax: momentOf(cg, nd) }));
      const pinOffsets = new Map(it.nodes.filter((nd) => isStanding(nd) && !freePins.has(nd.id)).map((nd) => [nd.id, sizeOf(`pin:${nd.id}`)?.dx || 0]));
      const placed = hangAttachments(items, { x0: cg.x + (cg.rail || 0) + 2, x1: it.x + it.w, y: it.y, isStanding, pinnedHeights: pinH, carriedOut: freePins, pinOffsets, openRank });
      for (const p of placed) {
        nodes.push({ ...p.nd, x: p.x, y: p.y, w: p.w, h: p.h, ax: p.ax ?? null, foot, level: p.level, standing: true, pinKey: `pin:${p.nd.id}`, pts: ptsOf(p.nd) });
      }
      bandCache.current.set(it.owner, { sig, out: nodes.slice(start) });
    }
    // The chat's notes over their cells, as pins (NotePin): one whose turn
    // falls outside what its card shows stands at the card's edge on its
    // lane, its cell not ringed.
    const pins = [];
    for (const [key, list] of nodesByCard) {
      const cg = cardGeo.get(key);
      if (!cg || cg.graph || !cg.win || !cg.tu) continue;
      for (const nd of list) {
        const a = nd.kind === "note" ? nd.anchors[0] : null;
        if (!a) continue;
        let pt = cellAt(cg, a.lane, a.turn);
        if (!pt) {
          const { side } = bucketAt(cg.tu[a.turn], cg.win, cg.n);
          const cy = cg.folded ? 0 : cg.scene.centers.get(a.lane);
          if (!side || cy == null) continue;
          const x = cg.x + (cg.rail || 0) + ((side === "before" ? 0 : cg.n - 1) + 0.5) * cg.cellW;
          pt = cg.folded ? { x, y: cg.y + cg.h - 3, w: cg.cellW, folded: true, edge: side } : { x, y: cg.bodyY + cy, w: cg.cellW, edge: side };
        }
        pins.push({ ...nd, x: pt.x, y: pt.y, folded: !!pt.folded, standing: isStanding(nd), pts: pt.edge || nd.earlier ? [] : [pt] });
      }
    }
    for (const it of pinSlots) {
      const cg = cardGeo.get(it.owner);
      if (!cg) continue;
      nodes.push({
        ...it.nd, level: "stand", standing: true, pinKey: it.key, moved: it.moved, free: true,
        x: it.x, y: it.y, w: it.w, h: it.h, ax: momentOf(cg, it.nd) ?? cg.x + (cg.rail || 0) + CARD_GLYPH_X, foot: cg.y + cg.h,
        railOf: null, rail: null, flow: null, pts: ptsOf(it.nd),
      });
    }
    // Where each of the chat's claims rests on the board: the cells of the
    // traces it cites, and the marks and rows of what it cites on the machine
    // cards.
    const claimPoints = new Map();
    const claimRests = new Map();
    if (investigation) {
      const add = (id, p) => {
        if (!claimPoints.has(id)) claimPoints.set(id, []);
        claimPoints.get(id).push(p);
      };
      // One line per thing on the board: what it names of the same trace or
      // machine runs on along that line.
      const rest = (id, r) => {
        if (!claimRests.has(id)) claimRests.set(id, []);
        const list = claimRests.get(id);
        const same = list.find((x) => x.on === r.on && !Number.isFinite(x.at) && !Number.isFinite(r.at));
        if (same) { if (!same.what.includes(r.what)) same.what = `${same.what}, ${r.what}`; } else list.push({ ...r });
      };
      for (const [key, ov] of overlays) {
        const cg = cardGeo.get(key);
        if (!cg || cg.graph) continue;
        for (const cl of ov?.claims || []) {
          if (!investigation.claimIds.has(cl.id)) continue;
          for (const cell of (cl.cells || []).slice(0, 8)) {
            const p = cellAt(cg, cell.lane, cell.turn);
            if (p) add(cl.id, { x: p.x, y: p.y, cell: true, key, lane: cell.lane, turn: cell.turn });
          }
          const c = cards.get(key);
          const lanes = [...new Set((cl.cells || []).map((cell) => c?.model?.laneById?.get(cell.lane)?.label).filter(Boolean))];
          const at = Math.min(...(cl.cells || []).map((cell) => c?.times?.[cell.turn]).filter(Number.isFinite));
          if (lanes.length) rest(cl.id, { on: c?.titleText || "a trace", what: lanes.slice(0, 3).join(", "), at });
        }
      }
      // A trace on the board: the chat's events inside it along the foot of
      // its head at their moments on its own clock, merged a few pixels
      // apart; a claim naming the trace itself, or the machine it ran on,
      // rests on the whole card.
      const sidOf = (key) => (key === FOCAL ? focalSessionId : String(key).toLowerCase());
      for (const cg of cardGeo.values()) {
        if (cg.graph || !cg.win || !cg.kx) continue;
        const c = cards.get(cg.key);
        const evs = investigation.inTraces?.get(sidOf(cg.key)) || [];
        const marks = [];
        let last = null;
        for (const e of evs) {
          const u = clock.at(e.at);
          if (u < cg.win.u0 || u > cg.win.u1) continue;
          const mx = cardX(cg, e.at);
          if (last && last.kind === e.kind && mx - last.x < 4) {
            last.events.push(e);
            last.isNew = last.isNew || e.isNew;
            continue;
          }
          last = { x: mx, y: cg.bodyY - 1, kind: e.kind, events: [e], isNew: e.isNew, onCard: true };
          marks.push(last);
        }
        for (const m of marks) {
          m.claims = investigation.claims.filter((cl) => m.events.some((e) => cl.evidence.includes(e.key))).map((cl) => cl.id);
          for (const id of m.claims) {
            add(id, { x: m.x, y: m.y, key: cg.key });
            rest(id, { on: c?.titleText || "a trace", what: `${m.events[0].kind} ${clip(String(m.events[0].label || ""), 60)}`, at: m.events[0].at });
          }
        }
        cg.evidence = marks;
        const head = { x: (cg.headX ?? cg.x) + CARD_GLYPH_X, y: cg.y + 13, key: cg.key };
        for (const cl of investigation.claims) {
          if (cl.evidence.includes(`session:${sidOf(cg.key)}`) || cl.evidence.some((k) => String(k).toLowerCase() === `session:${sidOf(cg.key)}`)) {
            add(cl.id, head);
            rest(cl.id, { on: c?.titleText || "a trace", what: "the session itself" });
          }
          const ep = c?.endpointId && c.endpointId !== "unknown" ? `endpoint:${c.endpointId}` : null;
          if (ep && cl.evidence.some((k) => String(k).toLowerCase() === ep.toLowerCase())) {
            add(cl.id, head);
            rest(cl.id, { on: c?.titleText || "a trace", what: `the machine it ran on${c.machine ? `, ${c.machine}` : ""}` });
          }
        }
      }
      for (const cg of cardGeo.values()) {
        if (!cg.graph) continue;
        for (const m of cg.marks) {
          for (const id of m.claims) {
            add(id, { x: m.x, y: m.y, key: cg.key });
            rest(id, { on: cg.label, what: `${m.events[0].kind} ${clip(String(m.events[0].label || ""), 70)}`, at: m.events[0].at });
          }
        }
        for (const r of cg.rows) {
          if (!r.span) continue;
          for (const cl of investigation.claims) {
            if (!cl.evidence.includes(r.key)) continue;
            add(cl.id, { x: X(r.span.from), y: r.y + GRAPH_ROW_H / 2, key: cg.key });
            rest(cl.id, { on: cg.label, what: [r.label, r.detail].filter(Boolean).join(" "), at: r.span.from });
          }
        }
      }
      // A trace on the board whose record is still being read, or could not
      // be read: a claim resting on it, on what the chat found inside it, or
      // on the machine it ran on rests on this workspace all the same, at its
      // card's head.
      for (const lc of loading) {
        const own = `session:${sidOf(lc.key)}`;
        const c = cards.get(lc.key);
        const ep = c?.endpointId && c.endpointId !== "unknown" ? `endpoint:${c.endpointId}`.toLowerCase() : null;
        const onIt = (k) => {
          const key = String(k).toLowerCase();
          return key === own || key === ep || String(investigation.events.get(k)?.row || "").toLowerCase() === own;
        };
        for (const cl of investigation.claims) {
          if (!cl.evidence.some(onIt)) continue;
          add(cl.id, { x: (lc.headX ?? lc.x) + CARD_GLYPH_X, y: lc.y + 13, key: lc.key });
          rest(cl.id, { on: c?.titleText || "a trace", what: lc.status === "error" ? "its record could not be read" : "its record is still being read" });
        }
      }
    }
    for (const cache of [sceneCache.current, cardCache.current, bandCache.current]) {
      for (const key of [...cache.keys()]) if (!cards.has(key)) cache.delete(key);
    }
    return { cards: cardGeo, loading, focalCard, X, cellAt, nodes, pins, shelves, claimPoints, claimRests, stubs };
  }, [clock, kk, turnUs, cards, machineCards, drawnLayout, rowsOpts, sel, investigation, focalSessionId, isStanding, openRank, planeW, pinH, freePins, sizeOf, stubInfo, focalPins, findingPinsOf, lit, litCells, nodesByCard]);
  const geo = useMemo(() => {
    if (!placed) return null;
    const { cards: cardGeo, focalCard, cellAt, stubs, X } = placed;
    // The wires, at this zoom: to the sessions on the board, and to the
    // session graph's stubs, from their records where read, else the census's
    // samples.
    const drawn = [];
    const dots = new Map();
    const onBoard = new Set(shown.map((r) => r.id));
    const readOff = allWires.filter((w) => !onBoard.has(w.key));
    const readKeys = new Set(readOff.map((w) => w.key));
    for (const wd of [...wires, ...readOff, ...rowWires.filter((w) => !readKeys.has(w.key))]) {
      const fromCard = wd.chain ? cardGeo.get(wd.from) : focalCard;
      const A = cellAt(fromCard, wd.a.lane, wd.a.turn);
      if (!A) continue;
      let B = null;
      let other = cardGeo.get(wd.key);
      if (other) B = cellAt(other, wd.b.lane, wd.b.turn);
      const emph = focusKey === wd.key || (!!wd.chain && focusKey === wd.from);
      if (!B) {
        // A stub shows where its hand-off lands, a dot along its foot; the
        // wire itself waits for the reader to point at it.
        const sb = stubs.get(wd.key);
        if (!sb) continue;
        other = null;
        B = { x: Math.max(sb.x0, Math.min(sb.x1, X(wd.b.t))), y: sb.barY };
        if (!emph) {
          if (wd.primary) dots.set(wd.key, B.x);
          continue;
        }
      }
      if (focusKey && !emph && !wd.primary) continue;
      const future = cursor != null && Math.max(wd.a.t, wd.b.t) > cursor;
      // A shared file's line is as strong as the file is specific to them.
      const alpha = (focusKey ? (emph ? 0.95 : 0.1) : 0.34) * (future ? 0.3 : 1) * (wd.shared ? wd.spec : 1);
      if (alpha < VISIBLE_ALPHA) continue;
      const [e0, e1] = wd.a.t <= wd.b.t ? [wd.a.t, wd.b.t] : [wd.b.t, wd.a.t];
      // From one session to the next, what it is that joins them and how
      // long after; from the anchor, when at each end.
      const label = !emph ? null
        : wd.chain ? `${clip(String(wd.text || ""), 40)}${DOT}${latencyText(e1 - e0).slice(1)} later`
        : `${sameDay(e0, e1) ? `${clockText(e0)}${ARROW}${clockText(e1)}` : `${whenText(e0)}${ARROW}${whenText(e1)}`}${DOT}${latencyText(e1 - e0)}`;
      const edge = B.y < fromCard.y ? fromCard.y : fromCard.y + fromCard.h;
      const apart = other && (other.x >= fromCard.x + fromCard.w || other.x + other.w <= fromCard.x);
      const route = apart ? wireRoute({ x: A.x, y: A.y, card: fromCard }, { x: B.x, y: B.y, card: other }) : dropRoute(A, B, edge);
      drawn.push({ ...wd, aKey: wd.chain ? wd.from : FOCAL, bKey: other ? wd.key : null, A, B, route, alpha, dashed: !wd.exact, label, lw: emph ? wd.w + 0.5 : wd.w });
    }
    // A machine's, a search's or a workspace's sessions on the board as
    // cards, each wired from its row to its card: to the cell that holds what
    // the search looks for, where the session's record has it, else to the
    // card's near edge at the row's time.
    for (const cg of cardGeo.values()) {
      if (!cg.graph || cg.folded) continue;
      // A workspace inside, its picture drawn in its card: a session's card
      // there is where its wire starts, out through the card's side.
      const foot = headHold(holdCam, cg.h).foot;
      const under = cg.inset ? Math.max(0, cg.y + foot - cg.inset.y) : 0;
      const drop = cg.inset ? 0 : Math.max(0, foot - CARD_HEAD);
      const shownH = cg.inset ? cg.inset.h - under : 0;
      const pic = shownH >= NEST_INSET_SHOWN ? pictures.get(cg.key) : null;
      for (const r of cg.rows) {
        if (!r.member || !r.spanX || r.y + drop + GRAPH_ROW_H > cg.y + cg.h) continue;
        const other = cardGeo.get(r.card);
        if (!other) continue;
        const inner = pic?.find((x) => x.key === String(r.key).slice("session:".length) && x.y < shownH) || null;
        const right = other.x + other.w / 2 >= cg.x + cg.w / 2;
        const A = inner
          ? { x: cg.inset.x + inner.x + (right ? inner.w : 0), y: cg.inset.y + under + inner.y + Math.min(10, inner.h / 2) }
          : cg.inset ? { x: right ? cg.x + cg.w : cg.x, y: cg.y + CARD_HEAD / 2 }
            : { x: Math.max(r.spanX.a, cg.x + cg.rail + 4), y: r.y + drop + GRAPH_ROW_H / 2 };
        const held = memberCells.get(`${cg.key}|${r.card}`);
        let B = held ? cellAt(other, held.lane, held.turn) : null;
        const above = other.y + other.h / 2 < cg.y;
        const apart = other.x >= cg.x + cg.w || other.x + other.w <= cg.x;
        if (!B && inner) B = { x: other.x >= cg.x + cg.w ? other.x : other.x + other.w, y: other.y + Math.min(other.h / 2, 12) };
        if (!B) B = { x: Math.max(other.x + (other.rail || 0) + 4, Math.min(other.x + other.w - 4, A.x)), y: above ? other.y + other.h : other.y };
        const emph = focusKey === r.card || focusKey === cg.key;
        const future = cursor != null && r.span.from > cursor;
        const route = apart || inner || cg.inset ? wireRoute({ x: A.x, y: A.y, card: cg }, { x: B.x, y: B.y, card: other }) : dropRoute(A, B, above ? cg.y : cg.y + cg.h);
        drawn.push({
          key: r.card, from: cg.key, aKey: cg.key, bKey: r.card, member: true, via: r.via || null, row: r.key, a: { t: r.span.from }, b: { t: r.span.from }, A, B, route, lead: !!inner,
          alpha: (focusKey ? (emph ? 0.9 : 0.08) : 0.32) * (future ? 0.3 : 1), dashed: !held, label: null, drop: emph, lw: emph ? 1.6 : 1.1, exact: !!held, primary: true,
        });
      }
    }
    // Laid on from a cell of a card: from that cell to its card, or to the
    // place held for it while it is found and read.
    const held = new Map((drawnLayout?.items || []).filter((it) => it.kind === "landing").map((it) => [it.key, it]));
    const came = [...cameFrom];
    for (const [key, d] of drops) if (d.from && !cameFrom.has(key)) came.push([key, d.from]);
    for (const [key, from] of came) {
      const fromCard = cardGeo.get(from.key);
      const A = fromCard ? cellAt(fromCard, from.lane, from.turn) : null;
      const landed = cardGeo.get(key);
      const it = landed ? null : held.get(key);
      const other = landed || (it ? { x: it.x, y: it.y, w: it.w, h: it.h, rail: 0 } : null);
      if (!A || !other) continue;
      const above = other.y + other.h / 2 < fromCard.y;
      const apart = other.x >= fromCard.x + fromCard.w || other.x + other.w <= fromCard.x;
      const B = { x: Math.max(other.x + (other.rail || 0) + 4, Math.min(other.x + other.w - 4, A.x)), y: above ? other.y + other.h : other.y };
      const emph = focusKey === key || focusKey === from.key;
      const route = apart ? wireRoute({ x: A.x, y: A.y, card: fromCard }, { x: B.x, y: B.y, card: other }) : dropRoute(A, B, above ? fromCard.y : fromCard.y + fromCard.h);
      drawn.push({
        key, from: from.key, aKey: from.key, bKey: landed ? key : null, came: true, a: {}, b: {}, A, B, route, label: null, primary: true,
        alpha: landed ? (focusKey ? (emph ? 0.9 : 0.08) : 0.34) : 0.7, dashed: !landed, lw: emph ? 1.6 : 1.2,
      });
    }
    return { ...placed, wires: drawn, dots };
  }, [placed, wires, allWires, shown, rowWires, focusKey, cursor, memberCells, pictures, holdCam, cameFrom, drops, drawnLayout]);
  // Where what the list holds rests on the board, and what it says of each:
  // a claim's points and rests as the board placed them, a moment's cell.
  const evidencePoints = useMemo(() => {
    if (listing !== "moments") return geo?.claimPoints || NO_RESTS;
    const out = new Map();
    for (const nd of momentNodes) {
      const pts = [];
      for (const a of nd.anchors) {
        const p = geo?.cellAt?.(geo.cards.get(a.key), a.lane, a.turn);
        if (p) pts.push({ x: p.x, y: p.y, cell: true, key: a.key, lane: a.lane, turn: a.turn });
      }
      if (pts.length) out.set(nd.id, pts);
    }
    return out;
  }, [listing, momentNodes, geo]);
  const evidenceRests = useMemo(() => {
    if (listing !== "moments") return geo?.claimRests || NO_RESTS;
    const out = new Map();
    for (const nd of momentNodes) {
      const a = nd.anchors[0];
      const c = cards.get(a.key);
      const at = nd.earlier ? null : c?.times?.[a.turn];
      out.set(nd.id, [{ on: c?.titleText || "a trace", what: nd.laneLabel || "", at: Number.isFinite(at) ? at : null }]);
    }
    return out;
  }, [listing, momentNodes, geo, cards]);

  // --- the surface ---
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const liftRef = useRef(null);
  const connRef = useRef(null);
  const hitsRef = useRef(null);
  const touched = useRef(false);
  const dragRef = useRef(null);

  // A preview takes its column's width once the column rests: laid out at
  // every step of a resize, it laid its whole trace out again each frame, and
  // the trace page's column is dragged while it is read. Its first width
  // lands at once; meanwhile it keeps the card it drew.
  const sizeNow = useRef(size);
  sizeNow.current = size;
  // A still picture sits in its card on a board that may be zoomed: its own
  // box, before that board's scale, is the room it lays itself into.
  const boxOf = (el) => (still ? { width: el.offsetWidth, height: el.offsetHeight } : el.getBoundingClientRect());
  const restTimer = useRef(0);
  const takeSize = useCallback((w, h) => {
    const apply = () => setSize((cur) => (cur.w === w && cur.h === h ? cur : { w, h }));
    window.clearTimeout(restTimer.current);
    if (!previewOn || !sizeNow.current.w) apply();
    else restTimer.current = window.setTimeout(apply, PREVIEW_REST_MS);
  }, [previewOn]);
  useEffect(() => () => window.clearTimeout(restTimer.current), []);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = boxOf(el);
      takeSize(Math.floor(r.width), Math.floor(r.height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [takeSize]);
  // And after every render: the surface settles its height as the records and
  // the dock arrive, and an observer in a hidden pane never reports it. Not
  // while a card is carried or sized, which never sizes the surface: a read
  // at every step made the page lay itself out again at every step.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || movingRef.current || rzRef.current) return;
    const r = boxOf(el);
    const w = Math.floor(r.width);
    const h = Math.floor(r.height);
    if (w !== size.w || h !== size.h) takeSize(w, h);
  });

  // A still picture shows all of its board, from its top-left corner, and
  // fills the room it has. Its cards, one to a row, show whole while the room
  // holds them at a size easy to read, else in brief, then by their heads
  // alone (what each did, where, its moments). Whole and shorter than the
  // room, they take the room left as heights of their own, each opening more
  // of its groups as a card made taller does. Then it is drawn as large as
  // the room's height allows, and laid out as wide as the room is at that
  // size. Given another room, or as each card's record first comes in, it
  // is sized again; a record read again (a page more of it) does not start
  // it over. It takes a few steps at most (STILL_STEPS), then holds.
  useLayoutEffect(() => {
    if (!still || !size.w || !size.h) return;
    const room = `${Math.round(planeW)}x${Math.round(planeH)}`;
    let fresh = room !== stillKey.current;
    for (const [key, c] of cards) {
      if (!c.model || stillSeen.current.has(key)) continue;
      stillSeen.current.add(key);
      fresh = true;
    }
    // Starting over is part of the same pass, so no step of the last sizing
    // lands over it.
    if (fresh) {
      stillKey.current = room;
      stillTries.current = 0;
      stillGrown.current = false;
      stillSteps.current = 0;
      if (roll || stillK != null || Object.keys(ownSizes).length) {
        setRoll(0);
        setStillK(null);
        setOwnSizes({});
        return;
      }
    }
    const x0 = (layout.x0 || 0) - BOARD_EDGE_L;
    const w = Math.max(1, (layout.w || 0) - x0);
    const h = Math.max(1, layout.h || 0);
    const stepping = stillSteps.current < STILL_STEPS;
    if (stepping) stillSteps.current += 1;
    if (stillK == null && stepping) {
      const k = Math.min(STILL_K_MAX, planeH / h);
      if (k < STILL_ROLL_K && roll < 2) {
        setRoll(roll + 1);
        return;
      }
      if (!roll && planeH - h > CELL && !stillGrown.current) {
        stillGrown.current = true;
        // The room left, shared by how much taller each card can be.
        const grow = [];
        for (const it of layout.items) {
          const c = it.kind === "card" ? cards.get(it.key) : null;
          const now = c?.model ? sizing.get(it.key)?.h : null;
          if (!Number.isFinite(now)) continue;
          const more = cardHeightLimits(c.model, 0).max - now;
          if (more > 0) grow.push({ key: it.key, now, more });
        }
        const all = grow.reduce((a, g) => a + g.more, 0);
        if (all > 0) {
          const f = Math.min(1, (planeH - h) / all);
          setOwnSizes(Object.fromEntries(grow.map((g) => [g.key === FOCAL ? focalSessionId : g.key, { h: Math.floor(g.now + f * g.more) }])));
          return;
        }
      }
      setStillK(k);
      return;
    }
    // A short session's card is as wide as its head at least, and one late
    // on the clock runs on past where the clock ends: laid out narrower
    // again (a few times at most), the cards close up under the room's width.
    const byW = planeW / w;
    const byH = Math.min(STILL_K_MAX, planeH / h);
    if (stillK != null && stepping && byW < byH * 0.97 && stillTries.current < 3) {
      stillTries.current += 1;
      setStillK(stillK * (byH / byW));
      return;
    }
    const fit = Math.max(0.05, Math.min(byW, byH));
    setCam((cur) => (Math.abs(cur - fit) < 1e-6 ? cur : fit));
    setViewState((cur) => (cur.x === x0 && cur.y === 0 ? cur : { x: x0, y: 0 }));
    if (!stillShown && [...cards.values()].every((c) => c.model || c.status === "error")) setStillShown(true);
  }, [still, size.w, size.h, planeW, planeH, layout.x0, layout.w, layout.h, layout.items, roll, stillK, ownSizes, cards, sizing, focalSessionId, stillShown]);
  useEffect(() => {
    if (!still || stillShown) return undefined;
    const t = setTimeout(() => setStillShown(true), STILL_SHOW_MS);
    return () => clearTimeout(t);
  }, [still, stillShown]);
  // A still picture says where it drew each of its cards, in its own box, for
  // the board it is drawn on to wire to them and point at them.
  const pictureRef = useRef(onPicture);
  pictureRef.current = onPicture;
  const picture = useMemo(() => (still ? layout.items.filter((it) => it.kind === "card").map((it) => {
    const x0 = Math.min(it.x, it.headX ?? it.x);
    const x1 = Math.max(it.x + it.w, (it.headX ?? it.x) + (it.headW ?? 0));
    return { key: it.key, title: cards.get(it.key)?.titleText || "", x: MACH_W + (x0 - view.x) * cam, y: (it.y - view.y) * cam, w: (x1 - x0) * cam, h: it.h * cam };
  }) : null), [still, layout.items, cards, view.x, view.y, cam]);
  useEffect(() => { if (still) pictureRef.current?.(picture); }, [still, picture]);
  useEffect(() => (still ? () => pictureRef.current?.(null) : undefined), [still]);
  // A board that holds one thing alone (a trace in a workspace of its own):
  // nothing else on it or still coming to it, only what hangs from its card.
  const lone = useMemo(() => {
    if (cards.size + machineCards.size !== 1 || relatedRows.length) return false;
    const card = layout.items.find((it) => it.kind === "card");
    return !!card && layout.items.every((it) => it === card || it.owner === card.key);
  }, [cards, machineCards, relatedRows, layout.items]);
  // A scroll held to the content: past a margin the plane stops, so nothing
  // scrolls off into empty canvas.
  const clampView = useCallback((v, atK = kk, atCam = cam) => {
    const vw = planeW / atCam;
    const vh = planeH / atCam;
    // Across the board it scrolls over what is on it and no further: from its
    // first card's edge to its last one's, keeping the margin a card at the
    // clock's start has; narrower than the view, it sits at the view's left
    // (one card alone, in its middle). At another zoom than the board was laid
    // out at, what it holds is reckoned from its stretch of the clock.
    const ext = atK === kk || spanU0 == null
      ? { x0: layout.x0 || 0, x1: layout.w || 0 }
      : { x0: BOARD_EDGE_L + spanU0 * atK, x1: PAD_L + spanU1 * atK + 3 + BOARD_PAD_R };
    const lo = ext.x0 - BOARD_EDGE_L;
    const hi = ext.x1 - vw + coverR / atCam;
    // Once the reader has moved the board, one that fits the view stays where
    // they have it, wholly in view: it is never put back in the middle.
    const held = touched.current;
    const x = resizing || moving ? v.x : hi >= lo ? Math.max(lo, Math.min(hi, v.x)) : held ? Math.max(hi, Math.min(lo, v.x)) : lone ? (lo + hi) / 2 : lo;
    // Down the board it fits what is on it: taller than the view, it scrolls
    // to its top and foot and no further; shorter, it sits at the view's top
    // (one card alone, in its middle) until the reader moves it. A card in
    // hand takes it where the pointer does; let go, it glides back.
    const fits = boardFits(layout.h, vh);
    const y = resizing || moving ? v.y
      : fits ? (held ? Math.max((layout.h || 0) - vh, Math.min(0, v.y)) : lone ? -(vh - (layout.h || 0)) / 2 : 0)
        : Math.max(0, Math.min(boardFootY(layout.h, vh), v.y));
    return { x, y };
  }, [kk, cam, planeW, planeH, layout.h, layout.w, layout.x0, resizing, moving, coverR, spanU0, spanU1, lone]);
  // What the board does to the view on its own (keeping what is on screen in
  // place as cards change, or as the clock does) only keeps some of the board
  // in view: a view the reader left goes on showing what they left it on, even
  // where the board has since grown or shrunk past it.
  const looseView = useCallback((v, atCam = cam) => {
    if (!touched.current || resizing || moving) return clampView(v, kk, atCam);
    const vw = planeW / atCam;
    const vh = planeH / atCam;
    const keep = 80 / atCam;
    return {
      x: Math.max((layout.x0 || 0) - vw + keep, Math.min((layout.w || 0) - keep, v.x)),
      y: Math.max(-(vh - keep), Math.min((layout.h || 0) - keep, v.y)),
    };
  }, [cam, clampView, kk, planeW, planeH, layout.x0, layout.w, layout.h, resizing, moving]);
  const shiftView = useCallback((dx, dy) => setViewState((cur) => {
    const next = looseView({ x: cur.x + dx, y: cur.y + dy });
    return cur.x === next.x && cur.y === next.y ? cur : next;
  }), [looseView]);
  const setView = useCallback((v) => setViewState((cur) => {
    const next = clampView(v);
    return cur.x === next.x && cur.y === next.y ? cur : next;
  }), [clampView]);
  // The reader's own moves (the board dragged, the wheel, the map) go
  // anywhere some of the board stays in view, as the camera's do: held to its
  // edges, a board smaller than the view would hardly move at all.
  const panTo = useCallback((v) => setViewState((cur) => {
    const next = looseView(v);
    return cur.x === next.x && cur.y === next.y ? cur : next;
  }), [looseView]);
  // Moves the view by an amount from wherever it stands: wheel events arrive
  // faster than the board draws, and each adds to the one before.
  const panBy = useCallback((dx, dy) => setViewState((cur) => {
    const next = looseView({ x: cur.x + dx, y: cur.y + dy });
    return cur.x === next.x && cur.y === next.y ? cur : next;
  }), [looseView]);
  // Rolls the board up (or back) to `d` zoom steps past the whole clock.
  // Across a change in what the cards show, the card under the pointer (or
  // the nearest down the board) stays where it was on screen.
  const rollHold = useRef(null);
  const exitBy = useRef(0);
  // Into a workspace inside this one, from where its card is on screen (the
  // part of it in view).
  // `focus`: one of its cards (a session's) to come in on.
  const enterInto = (id, from = null, focus = null) => {
    if (!onEnter) return;
    let rect = from;
    const it = rect ? null : layout.items.find((x) => x.key === `graph:workspace:${id}`);
    const box = canvasRef.current?.getBoundingClientRect();
    if (it && box) {
      const left = Math.max(box.left + MACH_W, box.left + MACH_W + (it.x - view.x) * cam);
      const top = Math.max(box.top + RULER_H, box.top + RULER_H + (it.y - view.y) * cam);
      const right = Math.min(box.right, box.left + MACH_W + (it.x + it.w - view.x) * cam);
      const bottom = Math.min(box.bottom, box.top + RULER_H + (it.y + it.h - view.y) * cam);
      if (right > left && bottom > top) rect = { left, top, width: right - left, height: bottom - top };
    }
    onEnter(id, rect, focus);
  };
  // Zooming in over a workspace inside this one pushes into it, going in
  // once it has pushed as far as ENTER_AT; let go part way, it settles back.
  const enterBy = useRef({ id: null, by: 0 });
  const enterSettle = useRef(0);
  const [entering, setEntering] = useState(null);
  const dropPush = () => {
    clearTimeout(enterSettle.current);
    if (!enterBy.current.id) return;
    enterBy.current = { id: null, by: 0 };
    setEntering(null);
  };
  const pushInto = (id, amount, from) => {
    const by = (enterBy.current.id === id ? enterBy.current.by : 0) + amount;
    clearTimeout(enterSettle.current);
    if (by >= ENTER_AT) {
      enterBy.current = { id: null, by: 0 };
      setEntering(null);
      enterInto(id, from);
      return;
    }
    enterBy.current = { id, by };
    setEntering({ key: `graph:workspace:${id}`, id, p: by / ENTER_AT });
    enterSettle.current = setTimeout(dropPush, 700);
  };
  const pushNow = useRef(null);
  pushNow.current = { pushInto, dropPush };
  useEffect(() => () => clearTimeout(enterSettle.current), []);
  // A card pointed out (the workspace just come back out of, a machine asked
  // for that is here already): brought into view once the board has its
  // size, its frame lit a moment.
  const [pointing, setPointing] = useState(null);
  const [litKey, setLitKey] = useState(null);
  const litTimer = useRef(0);
  useEffect(() => () => clearTimeout(litTimer.current), []);
  useEffect(() => {
    if (flash?.key) setPointing({ key: flash.key });
  }, [flash?.key, flash?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pointing || size.w <= MACH_W) return;
    const it = layout.items.find((x) => x.key === pointing.key || `graph:${x.key}` === pointing.key);
    if (!it) return;
    setPointing(null);
    const seen = it.x < view.x + viewW - 24 && it.x + it.w > view.x + 24 && it.y < view.y + viewH - 24 && it.y + Math.min(it.h, 60) > view.y + 8;
    if (!seen) {
      touched.current = true;
      setView({ x: it.x - 40, y: it.y - viewH / 3 });
    }
    setLitKey(pointing.key);
    clearTimeout(litTimer.current);
    litTimer.current = setTimeout(() => setLitKey(null), 1400);
  }, [pointing, size.w, layout, view, viewW, viewH, setView]);
  // Sets what the cards show, gliding everything there from where it stands.
  const rollGlide = useCallback((level) => {
    if (level === roll) return;
    const from = new Map();
    for (const it of layout.items) if (it.kind === "card" || it.kind === "stub" || it.kind === "pin") from.set(it.key, { x: it.x, y: it.y, h: it.h });
    glideFrom.current = from;
    setGlideT(0);
    setGlideRun((n) => n + 1);
    setRoll(level);
  }, [roll, layout.items, view.y, view.x]);
  const rollTo = useCallback((d, sy = null) => {
    const next = Math.max(0, Math.min(ROLL_MAX, d));
    rollBy.current = next;
    const level = rollOf(next);
    if (level === roll) return;
    const py = Number.isFinite(sy) && sy >= RULER_H ? sy : RULER_H + planeH / 2;
    const by = py - RULER_H + view.y;
    let pick = null;
    for (const it of layout.items) {
      if (it.kind !== "card") continue;
      const gap = by < it.y ? it.y - by : by > it.y + it.h ? by - it.y - it.h : 0;
      if (!pick || gap < pick.gap) pick = { it, gap };
    }
    rollHold.current = pick ? { key: pick.it.key, screenY: RULER_H + pick.it.y - view.y } : null;
    rollGlide(level);
  }, [roll, planeH, view.y, layout.items, rollGlide]);
  // The glide's clock: a quarter second, easing out.
  useEffect(() => {
    if (glideT >= 1) return undefined;
    const t0 = performance.now() - glideT * GLIDE_MS;
    let raf = 0;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / GLIDE_MS);
      setGlideT(t);
      if (t < 1) raf = requestAnimationFrame(step);
      else glideFrom.current = null;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [glideT >= 1, glideRun]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const hold = rollHold.current;
    if (!hold) return;
    rollHold.current = null;
    const it = layout.items.find((x) => x.kind === "card" && x.key === hold.key);
    if (it) setViewState((cur) => clampView({ x: cur.x, y: RULER_H + it.y - hold.screenY }));
  }, [layout]); // eslint-disable-line react-hooks/exhaustive-deps
  // Zoom the clock (the strip's zoom: the board's own time scale) about a
  // place along the board as the view draws it, the moment there staying put.
  const zoomTo = useCallback((nextK, sx = MACH_W + viewW / 2, sy = null) => {
    touched.current = true;
    const target = Math.max(spanFitK, Math.min(K_MAX, nextK));
    if (Math.abs(target - kk) < kk * 1e-6) return;
    const u = (view.x + (sx - MACH_W) - PAD_L) / kk;
    const x = PAD_L + u * target - (sx - MACH_W);
    // Zoomed all the way out, the board follows the canvas's width again.
    setK(target <= spanFitK * 1.0001 ? null : target);
    setViewState((cur) => clampView({ x, y: cur.y }, target));
  }, [spanFitK, kk, view.x, viewW, clampView]);
  // How far the camera has to pull back for the whole board to be in view.
  const camFit = useMemo(() => {
    const bw = Math.max(1, (layout.w || 0) - Math.min(0, layout.x0 || 0));
    const bh = Math.max(1, (layout.h || 0) + 2 * CONTROLS_CLEAR);
    return Math.min(planeW / bw, planeH / bh);
  }, [layout.w, layout.x0, layout.h, planeW, planeH]);
  // Zoom: the whole board scaled about a point on screen, what is under it
  // staying under it. Zoomed out past the whole board, going on out goes back
  // out of this workspace into the one it was entered from.
  const camNow = useRef({ cam, view });
  camNow.current = { cam, view };
  const zoomCam = useCallback((next, sx = MACH_W + planeW / 2, sy = RULER_H + planeH / 2) => {
    touched.current = true;
    const { cam: c0, view: v0 } = camNow.current;
    const floor = Math.max(CAM_MIN, Math.min(1, camFit) * 0.8);
    if (next < c0 && c0 <= floor * 1.001) {
      if (onExit) {
        exitBy.current += Math.log(c0 / Math.max(1e-6, next));
        if (exitBy.current >= EXIT_AT) {
          exitBy.current = 0;
          onExit();
        }
      }
      return;
    }
    if (next > c0) exitBy.current = 0;
    const c = Math.max(floor, Math.min(CAM_MAX, next));
    if (Math.abs(c - c0) < 1e-5) return;
    const bx = v0.x + (sx - MACH_W) / c0;
    const by = v0.y + (sy - RULER_H) / c0;
    const nextView = looseView({ x: bx - (sx - MACH_W) / c, y: by - (sy - RULER_H) / c }, c);
    camNow.current = { cam: c, view: nextView };
    setCam(c);
    setViewState(nextView);
  }, [camFit, looseView, onExit, planeW, planeH, RULER_H]);
  // Zoomed to a card: as big as fits the view, in its middle.
  const zoomToCard = useCallback((key) => {
    const it = layout.items.find((x) => x.key === key && (x.kind === "card" || x.kind === "landing"));
    if (!it) return false;
    const x0 = Math.min(it.x, it.headX ?? it.x);
    const x1 = Math.max(it.x + it.w, (it.headX ?? it.x) + (it.headW || 0));
    const band = layout.items.find((x) => x.kind === "node" && x.owner === key);
    const y1 = Math.max(it.y + it.h, band ? band.y + band.h : 0);
    const c = Math.max(CAM_MIN, Math.min(CAM_MAX, Math.min((planeW * 0.9) / Math.max(1, x1 - x0), (planeH * 0.9) / Math.max(1, y1 - it.y))));
    touched.current = true;
    holdPick.current = key;
    const nextView = looseView({ x: (x0 + x1) / 2 - planeW / (2 * c), y: (it.y + y1) / 2 - planeH / (2 * c) }, c);
    camNow.current = { cam: c, view: nextView };
    setCam(c);
    setViewState(nextView);
    return true;
  }, [layout.items, planeW, planeH, looseView]);
  const fitAll = useCallback(() => {
    touched.current = true;
    setK(null);
    setViewState({ x: clampView({ x: -Infinity, y: 0 }, spanFitK).x, y: 0 });
  }, [layout.h, planeH, clampView, spanFitK]);
  // The board's view of a stretch of the clock: zoomed so the stretch spans
  // the canvas after its pads, scrolled to its start.
  const showSpan = useCallback(({ u0, u1 }, holdAt = null) => {
    if (!clock) return;
    const nextK = Math.max(spanFitK, Math.min(K_MAX, (planeW - PAD_L - BOARD_PAD_R) / Math.max(MIN_WINDOW_U, u1 - u0)));
    touched.current = true;
    setK(nextK <= spanFitK * 1.0001 ? null : nextK);
    setViewState((cur) => clampView({ x: u0 * nextK, y: cur.y }, nextK));
    // The view is set by this clock: should the clock change as it goes in
    // (a stub opened as it is shown), the moment `holdAt` (else the one at its
    // left edge) keeps its place on screen.
    const t = Number.isFinite(holdAt) ? holdAt : clock.inv(Math.max(0, Math.min(clock.U, u0)));
    xHold.current = { t, x: PAD_L + clock.at(t) * nextK, clock, kk: nextK <= spanFitK * 1.0001 ? spanFitK : nextK };
  }, [clock, spanFitK, planeW, clampView]);
  // The view of one stretch of time: a trace's, or any.
  const fitSpan = useCallback((t0, t1) => {
    if (!clock || !Number.isFinite(t0) || !Number.isFinite(t1)) return;
    showSpan(windowOver(clock, Math.min(t0, t1), Math.max(t0, t1)));
  }, [clock, showSpan]);
  // A lens over a stretch, opened to a share of the plane at the zoom it is
  // drawn at. The zoom freezes first, so opening a lens never re-fits the rest.
  const addLens = useCallback((t0, t1, id = null) => {
    if (!clock || !Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return;
    touched.current = true;
    if (k == null) setK(kk);
    const px = Math.max(1, (clock.at(t1) - clock.at(t0)) * kk);
    const zoom = Math.max(1.5, Math.min(96, (planeW * LENS_SHARE) / px));
    setLenses((cur) => [...cur.filter((l) => l.id !== id && !(l.t0 >= t0 && l.t1 <= t1)), { id: id || `lens:${Math.round(t0)}`, t0, t1, zoom }]);
  }, [clock, k, kk, planeW]);
  const zoomLens = useCallback((id, factor) => {
    touched.current = true;
    if (k == null) setK(kk);
    setLenses((cur) => cur.map((l) => (l.id === id ? { ...l, zoom: Math.max(1.25, Math.min(96, l.zoom * factor)) } : l)).filter((l) => l.zoom > 1.26 || factor > 1));
  }, [k, kk]);
  const dropLens = useCallback((id) => setLenses((cur) => cur.filter((l) => l.id !== id)), []);
  // Everything: the clock's own scale back to the fit, its lenses gone, and
  // the camera pulled back until the whole board is in view (never past its
  // own size), from its top-left corner. The board lays itself out again
  // first.
  const fitRoll = useRef(false);
  const [fitAsk, setFitAsk] = useState(0);
  const showEverything = useCallback(() => {
    setEvidenceFocus(false);
    preFocusCam.current = null;
    setLenses([]);
    setHeldTimes((cur) => (cur.length ? [] : cur));
    setKeptGroups((cur) => (cur.size ? new Map() : cur));
    if (joinsHeld.current.size) {
      joinsHeld.current = new Map();
      setJoinsEpoch((n) => n + 1);
    }
    fitAll();
    rollBy.current = 0;
    setRoll(0);
    fitRoll.current = true;
    setFitAsk((n) => n + 1);
  }, [fitAll]);
  useEffect(() => {
    if (!fitRoll.current) return;
    fitRoll.current = false;
    const c = Math.max(CAM_MIN, Math.min(1, camFit * 0.96));
    const nextView = clampView({ x: (layout.x0 || 0) - BOARD_EDGE_L, y: 0 }, kk, c);
    camNow.current = { cam: c, view: nextView };
    setCam(c);
    setViewState(nextView);
  }, [layout.h, layout.w, planeH, planeW, fitAsk]); // eslint-disable-line react-hooks/exhaustive-deps
  // A card's size, kept: a missing side goes back to the board's.
  const saveSize = useCallback((key, fields) => {
    const sid = key === FOCAL ? focalSessionId : key;
    const next = { ...(sizesSaved || {}) };
    const entry = sizeEntry(sizesSaved?.[sid], fields);
    if (entry) next[sid] = entry;
    else delete next[sid];
    if (onSizes) onSizes(next);
    else setOwnSizes(next);
  }, [sizesSaved, onSizes, focalSessionId]);
  // Put down where the reader let it go: every card on the board keeps the
  // place it has (one with no place of its own takes the one it has now), and
  // `key` (when there is one) takes `fields`: its place down the board
  // (`top`, or rolled up, `mid`, where its middle was let go among the
  // others'), its time offset. Off the stack By time made, the board is the
  // reader's to arrange from then on. `liftBy`: how far above the board's top
  // `note` was let go (putNote), `key` then null.
  const putDown = (key, { mid = null, ...given } = {}, { liftBy = 0, note = null } = {}) => {
    const next = { ...(sizesSaved || {}) };
    const sidOf = (k) => (k === FOCAL ? focalSessionId : k);
    // The notes keep their own places, and what waits for a card has none.
    const tops = new Map([...(roll ? unrolledTops.current : new Map(layout.items.filter((x) => x.foot).map((x) => [x.key, x.foot.y0])))].filter(([k]) => !OWN_PLACES.test(k)));
    // Let go above the board's top, the top comes up to it: everything else
    // (the notes too) moves down the board by as much and the view with it,
    // so nothing moves on screen and what was let go stays where it was let
    // go.
    const lift = liftBy || (key != null && Number.isFinite(given.top) && given.top < MARGIN_Y ? Math.round(MARGIN_Y - given.top) : 0);
    const fields = lift && key != null ? { ...given, top: MARGIN_Y } : given;
    for (const [k, t] of tops) {
      const sid = sidOf(k);
      if (k === key || !Number.isFinite(t)) continue;
      if (Number.isFinite(next[sid]?.top)) {
        if (lift) next[sid] = { ...next[sid], top: next[sid].top + lift };
        continue;
      }
      next[sid] = { ...(next[sid] || {}), top: Math.max(0, Math.round(t)) + lift };
    }
    if (lift) {
      setViewState((cur) => ({ x: cur.x, y: cur.y + lift }));
      // Where things were drawn moves with the board, so nothing glides.
      for (const p of lastDrawn.current?.pos?.values() || []) p.y += lift;
      for (const p of glideFrom.current?.values() || []) p.y += lift;
      for (const [k, t] of settledTops.current) settledTops.current.set(k, t + lift);
      const moved = (looseNotes || []).filter((n) => Number.isFinite(n.y) && n.id !== note).map((n) => ({ id: n.id, y: n.y + lift }));
      if (moved.length) onLooseNote?.(moved);
    }
    if (key != null) {
      // Put somewhere new, it takes the shape that fits there.
      settledShapes.current.delete(key);
      const sid = sidOf(key);
      const own = { ...(next[sid] || {}), ...fields };
      if (mid != null) {
        const at = orderTopAt(lastFeet.current, mid, key, (k) => next[sidOf(k)]?.top);
        if (at != null) own.top = at;
      }
      const entry = sizeEntry(next[sid], own);
      if (entry) next[sid] = entry;
      else delete next[sid];
    }
    if (onSizes) onSizes(next);
    else setOwnSizes(next);
    if (stacked) setStacked(false);
  };
  // A note's place, drawn at (x, y): along the board, the moment under its
  // left edge and how far past either end of the clock it is, in the board's
  // units (so it stays by the cards beside it as the board's time scale
  // changes), and where it is, for a board with no clock; down the board, its
  // top.
  const notePlace = (x, y) => {
    const out = { x: Math.round(x), y: Math.max(MARGIN_Y, Math.round(y)) };
    if (!clock) return out;
    const t = clock.inv((x - PAD_L) / kk);
    return { ...out, t, dx: Math.round(x - (PAD_L + clock.at(t) * kk)) };
  };
  // A note let go at (x, y), as drawn: there, over whatever is there, as a
  // card is; let go above the board's top, the top comes up to it,
  // everything else moving down with the view (putDown).
  const putNote = (id, x, y) => {
    const key = `note:${id}`;
    const it = layout.items.find((x2) => x2.key === key);
    if (!it || !onLooseNote) return;
    const top = Math.round(y);
    const lift = top < MARGIN_Y ? Math.round(MARGIN_Y - top) : 0;
    lastDrawn.current?.pos?.set(key, { x, y, h: it.h });
    onLooseNote(id, notePlace(x, top + lift));
    if (lift || stacked) putDown(null, {}, { liftBy: lift, note: id });
  };
  const putNoteRef = useRef(putNote);
  putNoteRef.current = putNote;
  const onPutNote = useCallback((id, x, y) => putNoteRef.current(id, x, y), []);
  // A card the reader sized, or sized back to its own time or rows: every
  // card keeps the place it has, so nothing rises into the room it left or
  // moves on past where it was drawn; stacked By time, each keeps its row.
  const keepPlaces = (key, fields) => {
    const it = layout.items.find((x) => x.kind === "card" && x.key === key);
    if (stacked || roll || !it) saveSize(key, fields);
    else putDown(key, { ...fields, top: it.top });
  };
  // Dropped on this board from outside, what a drop brings lands where it was
  // let go, and everything already on the board keeps its place.
  const dropRef = useRef(null);
  dropRef.current = { putDown, focalSid: String(focalSessionId || "").toLowerCase(), k, kk, view, layout, cam };
  useEffect(() => {
    if (!workspaceId) return undefined;
    return onBoardDrop(workspaceId, ({ keys, clientX, clientY, title = "" }) => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r || clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) return;
      const { putDown: put, focalSid, k: kNow, kk: kkNow, view: v, layout: lay, cam: c } = dropRef.current;
      const cx = clientX - r.left;
      const cy = clientY - r.top;
      const ks = keys.map((x) => (x === focalSid ? FOCAL : x));
      const bx = (cx - MACH_W) / c + v.x;
      const by = (cy - RULER_H) / c + v.y;
      // Down the board it lands as a card carried there would, among the
      // rest as they are drawn now: settled once, as it is let go.
      const { top } = dropLanding(lay.items, new Set(ks), bx, by);
      // Along the board, by the moment the view holds still as the clock
      // changes under it, and how far from that moment's place.
      const h = xHold.current;
      const d = { bx, t: h ? h.t : null, dx: h ? bx - h.x : 0, by, top, title, at: Date.now() };
      put(null);
      // The board stays as the reader has it: no fitting it again to show
      // what arrives, which would move everything.
      touched.current = true;
      if (kNow == null) setK(kkNow);
      setDrops((cur) => {
        const next = new Map(cur);
        for (const x of ks) next.set(x, d);
        return next;
      });
    });
  }, [workspaceId, RULER_H]);
  useEffect(() => {
    if (!workspaceId) return undefined;
    return onBoardDragOver(workspaceId, (d) => {
      const r = d ? wrapRef.current?.getBoundingClientRect() : null;
      if (!d || !r || d.clientX < r.left || d.clientX > r.right || d.clientY < r.top || d.clientY > r.bottom) {
        setDragOver(null);
        return;
      }
      const { focalSid, view: v, cam: c } = dropRef.current;
      const keys = d.keys.map((x) => (x === focalSid ? FOCAL : x));
      const bx = (d.clientX - r.left - MACH_W) / c + v.x;
      const by = (d.clientY - r.top - RULER_H) / c + v.y;
      setDragOver((cur) => (cur && cur.bx === bx && cur.by === by && cur.title === d.title ? cur : { keys, title: d.title, bx, by }));
    });
  }, [workspaceId, RULER_H]);
  // Where what is carried over the board would land, as a card's outline:
  // the place a drop there gives it, as wide and deep as its card (one
  // already on the board) or as a card still reading its record.
  const dropGhost = useMemo(() => {
    if (!dragOver) return null;
    const skip = new Set(dragOver.keys);
    const on = layout.items.find((it) => it.kind === "card" && skip.has(it.key));
    const { x, top } = dropLanding(layout.items, skip, dragOver.bx, dragOver.by);
    const title = dragOver.title || (on ? cards.get(on.key)?.titleText || machineCards.get(on.key)?.label || "" : "");
    return { x, y: Math.max(MARGIN_Y, top), w: on ? Math.max(on.w, on.headW || 0) : LANDING_W, h: on ? on.h : LANDING_H, title, moving: !!on };
  }, [dragOver, layout, cards, machineCards, MARGIN_Y]);
  // Sessions on the workspace not on the board yet (their spans still being
  // read, or not readable): while nothing is on the board they stand in a
  // stack in its middle, each card gliding out of its place there into its
  // own as it comes in.
  const pending = useMemo(() => [...cards.values()].filter((c) => !c.focal && !Number.isFinite(c.firstMs) && !drops.has(c.key)), [cards, drops]);
  const placeWaiting = useCallback((key, el) => {
    const cv = wrapRef.current?.getBoundingClientRect();
    if (!cv || drops.has(key)) return;
    const r = el.getBoundingClientRect();
    // Where it stands on the board, under the view (and camera) it is seen in.
    const { cam: c, view: v } = camNow.current;
    dropFrom.current.set(key, { x: v.x + (r.left - cv.left - MACH_W) / c, y: v.y + (r.top - cv.top - RULER_H) / c, h: r.height / c });
  }, [drops, RULER_H]);
  // Once what a drop brought is on the board, and every record on it is in
  // (until then the clock can still change under it, and the card follows
  // the one the view holds still), where it landed is its own to keep. A drop
  // whose card never comes is let go of after a while.
  useLayoutEffect(() => {
    if (!drops.size) return;
    const now = Date.now();
    const landed = [];
    const gone = [];
    for (const [key, d] of drops) {
      const it = layout.items.find((x) => x.key === key && x.kind === "card");
      const settled = !reading && (!cards.get(key)?.times || admitted.has(key));
      if (it && Number.isFinite(it.handAt) && (settled || now - d.at > DROP_KEEP_MS)) landed.push([key, it]);
      else if (!it && d.status !== "finding" && now - d.at > DROP_KEEP_MS) gone.push(key);
    }
    if (!landed.length && !gone.length) return;
    if (landed.length) {
      const next = { ...(sizesSaved || {}) };
      for (const [key, it] of landed) {
        const sid = key === FOCAL ? focalSessionId : key;
        const entry = sizeEntry(next[sid], { ...(next[sid] || {}), top: it.handAt, off: it.off });
        if (entry) next[sid] = entry;
      }
      if (onSizes) onSizes(next);
      else setOwnSizes(next);
    }
    for (const key of gone) dropFrom.current.delete(key);
    setDrops((cur) => {
      const next = new Map(cur);
      for (const [key] of landed) next.delete(key);
      for (const key of gone) next.delete(key);
      return next;
    });
  }, [layout]); // eslint-disable-line react-hooks/exhaustive-deps
  // A session laid on from a cell of a card lands where it can be seen to
  // come from: right under that card (and what hangs from it), on its own
  // time when it is known (else the cell's moment), its place held at once
  // and wired to the cell, the board moving as little as brings it in. Its
  // card grows out of that place when its record is in. `key`: the session,
  // or a stand-in while it is still being found. False when the board has no
  // clock to place it by yet.
  const dropsRef = useRef(drops);
  dropsRef.current = drops;
  const holdLanding = (key, from, { title = "", startMs = null, status = null } = {}) => {
    const card = layout.items.find((x) => x.kind === "card" && x.key === from.key);
    const t = Number.isFinite(startMs) ? startMs : cards.get(from.key)?.times?.[from.turn];
    if (!card || !clock || !Number.isFinite(t)) return false;
    const band = layout.items.find((x) => x.kind === "node" && x.owner === from.key);
    const foot = Math.max(card.y + card.h, band ? band.y + band.h : 0);
    // Moved along the board with the card it comes from (`srcOff`, in the
    // clock's units), so it stays under it; what lies under that card makes
    // room for it.
    const srcOff = sizeOf(from.key)?.off || 0;
    const dx = srcOff * kk + DROP_GRAB_X - 3 - BOARD_RAIL;
    const bx = PAD_L + clock.at(t) * kk + dx;
    const top = foot + PACK_GAP_Y;
    const x = bx - DROP_GRAB_X;
    touched.current = true;
    setDrops((cur) => new Map(cur).set(key, { bx, t, dx, by: top + DROP_GRAB_Y, top, title, at: Date.now(), from, status, srcOff }));
    const pad = 40 / cam;
    const fit = (at, len, at0, span) => {
      const shown = Math.min(len, span - 2 * pad);
      if (at - at0 < pad) return at - pad;
      if (at + shown - at0 > span - pad) return at + shown - (span - pad);
      return at0;
    };
    setView({ x: fit(x, LANDING_W, view.x, viewW - coverR / cam), y: fit(top, LANDING_H, view.y, viewH) });
    return true;
  };
  // Found, the session takes the place held for it, and keeps where it came
  // from.
  const handLanding = (held, id) => {
    const key = id === focalSessionId ? FOCAL : id;
    const d = dropsRef.current.get(held);
    setDrops((cur) => {
      const was = cur.get(held);
      if (!was) return cur;
      const next = new Map(cur);
      next.delete(held);
      if (!cards.get(key)?.times) next.set(key, { ...was, status: null, at: Date.now() });
      return next;
    });
    if (d?.from) setCameFrom((cur) => new Map(cur).set(key, d.from));
  };
  const missLanding = (held, note) => setDrops((cur) => {
    const d = cur.get(held);
    return d ? new Map(cur).set(held, { ...d, status: "missed", note, at: Date.now() }) : cur;
  });
  const letGoLanding = (key) => setDrops((cur) => {
    if (!cur.has(key)) return cur;
    const next = new Map(cur);
    next.delete(key);
    return next;
  });
  // A hand-off's strings, lit on its card as soon as its record is in.
  const arrivals = useRef(new Map());
  const lightArrivals = () => {
    for (const [key, a] of arrivals.current) {
      const c = cards.get(key);
      const recordTurns = key === FOCAL ? turns : c?.read?.turns;
      if (!c?.model || !recordTurns?.length) continue;
      arrivals.current.delete(key);
      if (litOn(handoff.get(), key === FOCAL ? null : key) !== a.follow) handoff.toggle(key === FOCAL ? null : key, a.follow, a.cellsOf(recordTurns, c.model), a.cellsOf);
    }
  };
  useEffect(lightArrivals, [cards, turns, handoff]); // eslint-disable-line react-hooks/exhaustive-deps
  // Lays a session on from a cell of a card (`from`: { key, lane, turn }),
  // landing it as above; one already on the board is brought into view
  // instead. `follow` and `cellsOf`: the hand-off it came by, lit on its card.
  const landFrom = (from, { sid, title = "", startMs = null, follow = null, cellsOf = null } = {}) => {
    const id = String(sid || "").toLowerCase();
    if (!id) return;
    const key = id === focalSessionId ? FOCAL : id;
    if (follow && cellsOf) arrivals.current.set(key, { follow, cellsOf });
    if (cards.has(key)) {
      requestWorkspaceFocus(id);
      lightArrivals();
      return;
    }
    holdLanding(key, from, { title, startMs });
    setCameFrom((cur) => new Map(cur).set(key, from));
    onLayer?.([{ id, title }]);
  };
  const landRef = useRef(landFrom);
  landRef.current = landFrom;
  useEffect(() => landing?.attach((req) => landRef.current({ ...req.from, key: req.from?.key ?? FOCAL }, req)), [landing]);
  // Sizing a card by one of its handles: the edge follows the pointer (its
  // time scale sideways, its height down, between its fewest rows and all of
  // them), the rest of the board holds still until it is let go, and a chip
  // by the pointer says what the card has come to.
  const startResize = (e, cg, edge) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const c = cards.get(cg.key) || machineCards.get(cg.key);
    if (!c || (!c.model && !c.graph) || !cg.win || !clock || (cg.focus && /[lr]/.test(edge))) return;
    const cur = sizeOf(cg.key) || {};
    const it = layout.items.find((x) => x.kind === "card" && x.key === cg.key);
    const rail = cg.rail || 0;
    // Sideways, the frame's own width is where a drag starts from, so the
    // edge moves the moment it is drawn and the other edge stays put.
    const frameR = Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w));
    rzRef.current = {
      key: cg.key, edge, x: e.clientX, y: e.clientY, px: e.clientX, py: e.clientY, vx: view.x, vy: view.y, kk, cam,
      bodyW0: Math.max(CARD_MIN_BODY, frameR - cg.x - rail), h0: cg.h, top0: it?.top ?? cg.y,
      trueW: Math.max(1, (cg.win.u1 - cg.win.u0) * kk), m0: cur.m ?? null, pin0: cur.pin ?? null, off0: cur.off ?? null, hSet: cur.h ?? null,
      offStart: (cg.x + rail - (PAD_L + clock.at(c.firstMs) * kk - 3)) / kk,
      offEnd: (frameR - (PAD_L + clock.at(c.lastMs) * kk + 3)) / kk,
      lim: c.graph ? (c.workspace ? { min: CARD_HEAD + c.rows.length * GRAPH_ROW_H + 4, max: NEST_MAX_H } : { min: cg.h, max: cg.h }) : cardHeightLimits(c.model, cg.scaled ? CARD_RULER_H : 0),
      ceil: roomOver(layout.items, it),
    };
    // Taken hold of, the board stays where the reader leaves it, at the zoom
    // it is at (letting go never re-fits it to what the card has come to).
    touched.current = true;
    if (k == null) setK(kk);
    setFrozenTops(new Map(layout.items.filter((x) => x.kind === "card").map((x) => [x.key, x.top])));
    setResizing({ key: cg.key, edge });
    setTip(null);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  // What the card has come to with the pointer here and the board scrolled
  // here: the pointer's travel on the board, the scroll included, so an edge
  // carried past the canvas's side stays under the pointer.
  const draftOf = (r, vx, vy) => {
    const dx = (r.px - r.x) / (r.cam || 1) + (vx - r.vx);
    const dy = (r.py - r.y) / (r.cam || 1) + (vy - r.vy);
    const next = { key: r.key, m: r.m0, pin: r.pin0, off: r.off0, h: r.hSet, top: null };
    const fit = (w) => Math.max(SCALE_MIN, Math.min(SCALE_MAX, w / r.trueW));
    let m = null;
    if (r.edge.includes("r")) {
      m = fit(Math.max(CARD_MIN_BODY, r.bodyW0 + dx));
      Object.assign(next, { m, pin: "start", off: r.offStart });
    } else if (r.edge.includes("l")) {
      // It may run on back before the clock's start: the board widens to its
      // left for it, as it does for a card carried there.
      m = fit(Math.max(CARD_MIN_BODY, r.bodyW0 - dx));
      Object.assign(next, { m, pin: "end", off: r.offEnd });
    }
    // Close to its own time it snaps back to it, where it began and ended.
    if (m != null && Math.abs(m - 1) < SCALE_SNAP) {
      // At its own scale the edge it holds is where it starts; near its own
      // time, it goes back to it.
      const held = next.off || 0;
      Object.assign(next, { m: null, pin: null, off: Math.abs(held * r.kk) < MOVE_SNAP_PX ? null : held });
    }
    if (r.edge.includes("b")) next.h = Math.max(r.lim.min, Math.min(r.lim.max, r.h0 + dy));
    else if (r.edge.includes("t")) {
      // Up to whatever is over it along the board (which holds still), else
      // to the board's top and on into the room the view shows over it (a
      // board that fits sits lower in the view); let go, it stays there.
      const room = Number.isFinite(r.ceil) ? r.top0 - r.ceil : r.top0 + Math.max(0, -vy);
      next.h = Math.max(r.lim.min, Math.min(r.lim.max, r.h0 + Math.max(0, room), r.h0 - dy));
      next.top = r.top0 - (next.h - r.h0);
    }
    return next;
  };
  const sameDraft = (a, b) => !!a && !!b && a.key === b.key && a.m === b.m && a.pin === b.pin && a.off === b.off && a.h === b.h && a.top === b.top;
  const moveResize = (e) => {
    const r = rzRef.current;
    if (!r) return;
    r.px = e.clientX;
    r.py = e.clientY;
    const next = draftOf(r, view.x, view.y);
    setSizeDraft((cur) => (sameDraft(cur, next) ? cur : next));
    const wr = wrapRef.current?.getBoundingClientRect();
    if (wr) setResizeChip({ x: e.clientX - wr.left, y: e.clientY - wr.top });
  };
  const endResize = () => {
    const r = rzRef.current;
    rzRef.current = null;
    if (r && sizeDraft?.key === r.key) {
      // Let go, every card keeps the place it has; sized from its top, the
      // card stays where its top was let go.
      if (sizeDraft.top != null) putDown(r.key, { ...sizeDraft, top: Math.max(0, sizeDraft.top) });
      else keepPlaces(r.key, { ...sizeDraft, top: undefined });
    }
    setSizeDraft(null);
    setFrozenTops(null);
    setResizing(null);
    setResizeChip(null);
  };
  const resetSize = (key, edge) => {
    const cur = sizeOf(key) || {};
    const sideways = edge.includes("r") || edge.includes("l");
    const down = edge.includes("b") || edge.includes("t");
    keepPlaces(key, sideways ? { m: null, pin: null, off: null, h: down ? null : cur.h } : { ...cur, h: down ? null : cur.h });
  };
  const viewNow = useRef(view);
  viewNow.current = view;
  // What the board holds can shrink under the scroll (a card narrowed,
  // shortened or moved back, a list folded): once nothing is in hand, a view
  // left showing almost none of it glides back to it (looseView). Until the
  // reader has moved the board the view is the opening fit's to set, at once,
  // and nothing glides.
  // The glide is timed from its first frame: a frame's stamp can fall before
  // the moment the glide was asked for (and, in a test's window, on another
  // clock altogether).
  // Where it starts and ends are read at its first frame, after whatever the
  // board's own effects did to the view in the same update.
  const settleRaf = useRef(0);
  useEffect(() => {
    if (resizing || moving || !touched.current) return undefined;
    let from = null;
    let to = null;
    let t0 = null;
    const step = (now) => {
      if (t0 == null) {
        from = viewNow.current;
        to = looseView(from);
        if (Math.abs(to.x - from.x) < 0.5 && Math.abs(to.y - from.y) < 0.5) return;
        t0 = now;
      }
      const f = Math.min(1, (now - t0) / 240);
      const e = 1 - (1 - f) ** 3;
      setViewState({ x: from.x + (to.x - from.x) * e, y: from.y + (to.y - from.y) * e });
      if (f < 1) settleRaf.current = requestAnimationFrame(step);
    };
    settleRaf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(settleRaf.current);
  }, [looseView, resizing, moving]);
  const saveOrder = useCallback((next) => {
    if (onOrder) onOrder(next);
    else setOwnOrder(next);
  }, [onOrder]);
  // By time: every card one to a row in the order it began, each back on the
  // board's time scale (a card sized or moved along the board loses its own
  // clock), from the opening view. An action, not a mode: moving or sizing a
  // card afterwards is the reader's, and By time puts it all back again.
  // Heights, folds and placed blocks are not time and stay.
  const arrangeByTime = () => {
    touched.current = false;
    letShapeGo();
    settledTops.current = new Map();
    settledShapes.current = new Map();
    if (orderSaved.length) saveOrder([]);
    const next = {};
    for (const [sid, sz] of Object.entries(sizesSaved || {})) {
      const kept = {
        ...(Number.isFinite(sz?.h) ? { h: sz.h } : {}),
        ...(sz?.fold ? { fold: true } : {}),
        ...(Number.isFinite(sz?.dx) ? { dx: sz.dx } : {}),
      };
      if (Object.keys(kept).length) next[sid] = kept;
    }
    if (onSizes) onSizes(next);
    else setOwnSizes(next);
    setStacked(true);
    setArranged((n) => n + 1);
  };
  // Tidy: the board laid out again as it opens, each card rising to the
  // first place it fits, none over another, in the reader's order. A card
  // keeps its own time scale and offset, its rows and its fold; only where
  // the reader put it down the board goes. The notes stay where they are.
  const tidyUp = () => {
    letShapeGo();
    settledTops.current = new Map();
    settledShapes.current = new Map();
    const next = {};
    for (const [sid, sz] of Object.entries(sizesSaved || {})) {
      const { top: _top, free: _free, z: _z, ...kept } = sz || {};
      if (Object.keys(kept).length) next[sid] = kept;
    }
    if (onSizes) onSizes(next);
    else setOwnSizes(next);
    setStacked(false);
  };
  // The carried card with its top here, the pointer at `py`.
  const carryTo = (m, top, py) => ({ ...m, top, py });
  // Carried on to `next`. A card is drawn there at the next frame on the
  // canvas above the board, what hangs from it moved with it (drawCarry):
  // laying the whole board out again at every step made a carry lag. The
  // board lays it again when how it is drawn changes (back on its own time,
  // or off it) and when the pointer rests. A block out of its band is laid
  // at every step, its place in the band shown as it goes.
  const movingLaid = useRef(null);
  movingLaid.current = moving;
  const carryFrame = useRef(0);
  const carryRest = useRef(0);
  const drawCarryRef = useRef(null);
  const carryOn = useRef(null);
  carryOn.current = (next) => {
    movingRef.current = next;
    const at = movingLaid.current;
    if (next.pin || !at || at.key !== next.key || (next.off === 0) !== (at.off === 0)) {
      setMoving(next);
      return;
    }
    if (!carryFrame.current) {
      carryFrame.current = requestAnimationFrame(() => {
        carryFrame.current = 0;
        drawCarryRef.current?.();
      });
    }
    window.clearTimeout(carryRest.current);
    carryRest.current = window.setTimeout(() => {
      const m = movingRef.current;
      if (m && !m.pin) setMoving(m);
    }, CARRY_REST_MS);
  };
  const carryDone = () => {
    cancelAnimationFrame(carryFrame.current);
    carryFrame.current = 0;
    window.clearTimeout(carryRest.current);
  };
  useEffect(() => () => {
    cancelAnimationFrame(carryFrame.current);
    window.clearTimeout(carryRest.current);
  }, []);
  // A pinned block carried over its own card's band (or just past its end):
  // where it would go back in place, or null. Along the band it goes where
  // its left edge is let go, on its moment when let go close to it, or right
  // beside an opened card when let go close to that, and it may run on past
  // the band's end. Down it, it takes its turn among the opened cards over its
  // width by where its middle is (before the first one whose middle is lower),
  // and the band lays them out in that order.
  const dockAt = (m, clientX, clientY) => {
    if (!m?.pin) return null;
    const cv = canvasRef.current;
    const cg = geo?.cards?.get(m.pin.owner);
    const card = layout.items.find((x) => x.kind === "card" && x.key === m.pin.owner);
    if (!cv || !cg || !card || card.folded) return null;
    const r = cv.getBoundingClientRect();
    const bx = (clientX - r.left - MACH_W) / cam + viewNow.current.x;
    const by = (clientY - r.top - RULER_H) / cam + viewNow.current.y;
    const band = layout.items.find((x) => x.kind === "node" && x.owner === m.pin.owner);
    const foot = card.y + card.h;
    const bandTop = band ? band.y : foot + HANG_GAP;
    const bandEnd = band ? band.x + band.w : card.x + Math.max(card.w, card.headW || 0);
    const bottom = Math.max(band ? band.y + band.h : bandTop, bandTop + Math.min(m.h, 120)) + 36;
    const left = bx - m.grabDX;
    if (bx < card.x || left > bandEnd + CHIP_GAP + 18 || by < foot || by > bottom) return null;
    const x0 = cg.x + (cg.rail || 0) + 2;
    const home = m.pin.ax != null ? m.pin.ax - 14 : x0;
    const snapped = Math.abs(left - home) < MOVE_SNAP_PX;
    const rank = (id) => openRank.get(id) ?? -1;
    const others = (geo?.nodes || [])
      .filter((n) => n.key === m.pin.owner && n.level === "stand" && !n.free && n.id !== m.pin.id)
      .sort((a, b) => rank(a.id) - rank(b.id) || a.x - b.x);
    const beside = others.flatMap((o) => [o.x + PIN_W + CHIP_GAP, o.x - PIN_W - CHIP_GAP])
      .filter((sx) => sx >= x0 && Math.abs(left - sx) < 2 * MOVE_SNAP_PX)
      .sort((a, b) => Math.abs(left - a) - Math.abs(left - b))[0];
    const x = snapped ? Math.max(x0, Math.min(bandEnd - PIN_W, home)) : Math.max(x0, beside ?? left);
    const over = (a, b) => a.x < b.x + PIN_W + CHIP_GAP && b.x < a.x + PIN_W + CHIP_GAP;
    const self = { id: m.pin.id, x, h: m.h };
    const mid = (m.top ?? bandTop) + m.h / 2;
    const at = others.findIndex((o) => over(o, self) && o.y + o.h / 2 > mid);
    const order = at < 0 ? [...others, self] : [...others.slice(0, at), self, ...others.slice(at)];
    const placed = [];
    let y = bandTop;
    for (const o of order) {
      const top = placed.reduce((acc, p) => (over(o, p) ? Math.max(acc, p.bottom + CHIP_GAP) : acc), 0);
      placed.push({ x: o.x, bottom: top + o.h });
      if (o === self) y = bandTop + top;
    }
    return { x, y, dx: snapped ? 0 : x - home, before: at < 0 ? null : others[at].id };
  };
  const dockRef = useRef(dockAt);
  dockRef.current = dockAt;
  // Where along the board a carried card has come to, as its time offset:
  // the pointer's travel, the scroll included (the board grows to the left
  // for it); back on its own time when let go close to it.
  const offAt = (m, clientX, vx) => {
    if (!m.free) return null;
    const off = Math.max(m.offMin, m.off0 + ((clientX - m.cx0) / (m.cam || 1) + (vx - m.vx0)) / m.kk);
    return Math.abs(off * m.kk) < MOVE_SNAP_PX ? 0 : off;
  };
  // Carried to the canvas's top or foot, the board scrolls on under the card.
  useEffect(() => {
    if (!moving?.key) return undefined;
    let raf = 0;
    const EDGE = 36;
    // The surface's place on the page, held while the card is carried.
    let wr = wrapRef.current?.getBoundingClientRect();
    const onResize = () => { wr = wrapRef.current?.getBoundingClientRect(); };
    window.addEventListener("resize", onResize);
    const tick = () => {
      const m = movingRef.current;
      if (m && wr && m.py != null) {
        const py = m.py - wr.top;
        const px = (m.cx ?? m.cx0) - wr.left;
        const speed = (past) => Math.min(12, 0.5 + past * 0.3);
        const sy = py > wr.height - EDGE ? speed(py - (wr.height - EDGE)) : py < RULER_H + EDGE ? -speed(RULER_H + EDGE - py) : 0;
        const sx = !m.free ? 0 : px > wr.width - EDGE ? speed(px - (wr.width - EDGE)) : px < MACH_W + EDGE ? -speed(MACH_W + EDGE - px) : 0;
        const v = viewNow.current;
        const mc = m.cam || 1;
        if (sx || sy) setView({ x: v.x + sx / mc, y: v.y + sy / mc });
        const top = (py - RULER_H) / mc + v.y - m.grab;
        const off = offAt(m, m.cx ?? m.cx0, v.x);
        const dock = dockRef.current({ ...m, top }, m.cx ?? m.cx0, m.py);
        if (top !== m.top || off !== m.off || (dock?.x ?? null) !== (m.dock?.x ?? null) || (dock?.y ?? null) !== (m.dock?.y ?? null)) {
          const next = { ...carryTo(m, top, m.py), off, dock };
          // Scrolling, the board is laid again anyway.
          if (sx || sy) {
            movingRef.current = next;
            setMoving(next);
          } else carryOn.current(next);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [moving?.key, setView, RULER_H]);
  const draftNow = useRef(null);
  draftNow.current = { draftOf, sameDraft };
  useEffect(() => {
    if (!resizing) return undefined;
    let raf = 0;
    const EDGE = 36;
    const EDGE_Y = 12;
    const tick = () => {
      const r = rzRef.current;
      const wr = wrapRef.current?.getBoundingClientRect();
      if (r && wr) {
        const px = r.px - wr.left;
        const py = r.py - wr.top;
        // Held at either side of the canvas the edge moves toward, the board
        // scrolls that way: out past the far side to widen, back past the
        // near one to narrow again. Only once the pointer has gone that way
        // (a card's own edge can sit by the canvas's side), and down the
        // board only past the plane's top (over the ruler) or at its foot: a
        // card at the board's top has its top edge right under the ruler,
        // where a press alone would run it on up the board.
        const speed = (past) => Math.min(12, 0.5 + past * 0.3);
        const sideways = /[lr]/.test(r.edge);
        const down = /[tb]/.test(r.edge);
        const gone = { x: r.px - r.x, y: r.py - r.y };
        let sx = 0;
        let sy = 0;
        if (sideways && px > wr.width - EDGE && gone.x > 4) sx = speed(px - (wr.width - EDGE));
        else if (sideways && px < MACH_W + EDGE && gone.x < -4) sx = -speed(MACH_W + EDGE - px);
        if (down && py > wr.height - EDGE_Y && gone.y > 4) sy = speed(py - (wr.height - EDGE_Y));
        else if (down && py < RULER_H && gone.y < -4) sy = -speed(RULER_H - py);
        const v = viewNow.current;
        const { draftOf: d, sameDraft: same } = draftNow.current;
        let next = d(r, v.x, v.y);
        if (sx || sy) {
          // Only while the edge still goes that way: at its fewest rows or all
          // of them, its narrowest or widest, the board stays where it is.
          const to = { x: v.x + sx / (r.cam || 1), y: v.y + sy / (r.cam || 1) };
          const there = d(r, to.x, to.y);
          if (!same(there, next)) {
            setView(to);
            next = there;
          }
        }
        setSizeDraft((cur) => (same(cur, next) ? cur : next));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [resizing, setView, RULER_H]);
  // The buttons on a card's head.
  const toggleGroups = (key) => {
    setKeptGroups((cur) => { if (!cur.has(key)) return cur; const next = new Map(cur); next.delete(key); return next; });
    const sz = sizeOf(key);
    if (sz?.h) saveSize(key, { ...sz, h: null });
    if (key === FOCAL) {
      for (const setter of [setOpenGroups, setOpenDirs, setShowAll]) setter((cur) => { const n = new Map(cur); n.delete(FOCAL); return n; });
    } else toggleSet(setAllGroups, key);
  };
  // The trace that anchors the board next when the anchor is taken off: the one
  // the review found worst, then the one with most findings, most wires into
  // the anchor, most turns.
  const nextAnchor = () => {
    const rank = (key) => [
      flags.get(key) ? SEVERITY_RANK[flags.get(key).severity] ?? 5 : 9,
      -(findingReports.get(key)?.incidents?.length || 0),
      -wires.filter((w) => w.key === key).length,
      -(cards.get(key)?.times?.length || 0),
    ];
    const keys = [...cards.keys()].filter((k) => k !== FOCAL && cards.get(k)?.r?.added);
    keys.sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
      return 0;
    });
    return keys[0] || null;
  };
  // A machine, a search or a note the workspace holds comes off it; Undo
  // puts it back where it stood, and bringing it on again does too.
  const takeThing = (kind, id, title) => {
    const was = onThingOff?.(kind, id);
    if (was) recordRemoval({ kind: "thing", owner: boardId, what: kind, was, title });
  };
  const holdShape = (key) => {
    if (k == null) setK(kk);
    const c = cards.get(key) || machineCards.get(key);
    const pts = c ? (machineCards.has(key) ? [c.firstMs, c.lastMs] : clockPointsOf(c, admitted)) : [];
    if (pts.length) setHeldTimes((cur) => [...cur, ...pts.filter(Number.isFinite)]);
    setKeptGroups((cur) => {
      const next = new Map(cur);
      for (const [other, groups] of joinedGroups) {
        if (other !== key) next.set(other, new Set([...(next.get(other) || []), ...groups]));
      }
      return next;
    });
  };
  const letShapeGo = () => {
    setHeldTimes((cur) => (cur.length ? [] : cur));
    setKeptGroups((cur) => (cur.size ? new Map() : cur));
    if (joinsHeld.current.size) {
      joinsHeld.current = new Map();
      setJoinsEpoch((n) => n + 1);
    }
  };
  const takeOff = (key) => {
    holdShape(key);
    const own = /^graph:(machine|search|workspace):(.+)$/.exec(String(key));
    if (own && onThingOff) {
      if (detail?.key === key) setDetail(null);
      if (sel?.key === key) setSel(null);
      takeThing(own[1], own[2], machineCards.get(key)?.label || (own[1] === "search" ? "the search" : own[1] === "workspace" ? "the workspace" : "the machine"));
      return;
    }
    // A machine card from the chat's graph leaves the board; the chat's graph
    // keeps it, and Undo (or Clear) brings it back.
    if (String(key).startsWith("graph:")) {
      recordRemoval({ kind: "graph", owner: boardId, key, title: machineCards.get(key)?.label || "the machine" });
      if (detail?.key === key) setDetail(null);
      if (sel?.key === key) setSel(null);
      setHiddenNodes((cur) => new Set(cur).add(key));
      return;
    }
    // One open in the session graph alone goes back to its stub there; the
    // workspace never held it.
    if (peeks.has(key)) {
      const byHand = hereNow.open.has(key);
      recordRemoval({ kind: "here", owner: boardId, sid: key, byHand, title: cards.get(key)?.titleText || `session ${String(key).slice(0, 8)}` });
      if (detail?.key === key) setDetail(null);
      if (sel?.key === key) setSel(null);
      setHere(() => {
        const open = new Set(hereNow.open);
        const closed = new Set(hereNow.closed);
        open.delete(key);
        closed.add(key);
        return { sid: focalSessionId, open, closed };
      });
      return;
    }
    const sid = key === FOCAL ? focalSessionId : key;
    const c = cards.get(key);
    const next = key === FOCAL ? nextAnchor() : null;
    recordRemoval({
      owner: boardId, sid, title: c?.titleText || `session ${String(sid).slice(0, 8)}`, wasAnchor: key === FOCAL,
      nextTitle: next ? cards.get(next)?.titleText || `session ${String(next).slice(0, 8)}` : null,
      readings: readings.filter((r) => r.session === sid),
      notes: (notes || []).filter((n) => n.session === sid),
      size: sizesSaved?.[sid] || null,
      orderAt: orderSaved.indexOf(String(sid).toLowerCase()),
    });
    if (detail?.key === key) setDetail(null);
    if (heldId && geo?.nodes?.find((n) => n.id === heldId)?.key === key) setHeldId(null);
    if (sel?.key === key) setSel(null);
    if (next && onAnchor) onAnchor(next);
    onUnlayer?.(sid);
  };
  // The last thing taken off this board (another board's is its own).
  const lastRemoval = useLastRemoval();
  const removal = lastRemoval?.owner === boardId ? lastRemoval : null;
  const putBack = () => {
    const u = removal;
    if (!u) return;
    clearRemoval();
    if (u.kind === "thing") {
      onThingBack?.(u.what, u.was);
      return;
    }
    if (u.kind === "graph") {
      setHiddenNodes((cur) => { const next = new Set(cur); next.delete(u.key); return next; });
      return;
    }
    if (u.kind === "here") {
      setHere(() => {
        const open = new Set(hereNow.open);
        const closed = new Set(hereNow.closed);
        if (u.byHand) open.add(u.sid);
        closed.delete(u.sid);
        return { sid: focalSessionId, open, closed };
      });
      return;
    }
    onLayer?.([u.sid]);
    if (u.wasAnchor && onAnchor) onAnchor(u.sid);
    if (u.readings.length) setReadings((cur) => [...cur, ...u.readings.filter((r) => !cur.some((x) => x.id === r.id))]);
    if (u.notes.length && onNotes) onNotes([...(notes || []), ...u.notes.filter((n) => !(notes || []).some((x) => x.id === n.id))]);
    if (u.size && onSizes) onSizes({ ...(sizesSaved || {}), [u.sid]: u.size });
    if (u.orderAt >= 0) {
      const next = orderSaved.filter((x) => x !== String(u.sid).toLowerCase());
      next.splice(Math.min(u.orderAt, next.length), 0, u.sid);
      saveOrder(next);
    }
  };
  // Keys, anywhere on the page but in a field: F shows everything, + and -
  // zoom, Esc lets go of what is open.
  const heldOpenRef = useRef(false);
  heldOpenRef.current = !!(sel || detail || evidencePick);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""))) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!boardKeys(wrapRef.current, e)) return;
      if (e.key === "Escape") {
        // A card being carried goes back where it was.
        if (movingRef.current) {
          movingRef.current = null;
          carryDone();
          setMoving(null);
          if (dragRef.current) dragRef.current.dead = true;
          e.preventDefault();
          return;
        }
        if (heldOpenRef.current) e.preventDefault();
        setSel(null);
        setDetail(null);
        setEvidencePick(null);
        return;
      }
      if (e.key === "f" || e.key === "F") { e.preventDefault(); showEverything(); }
      else if (e.key === "=" || e.key === "+") { e.preventDefault(); zoomCam(camNow.current.cam * 1.4); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomCam(camNow.current.cam / 1.4); }
      else if (e.key === "0") { e.preventDefault(); zoomCam(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showEverything, zoomCam]);
  // The stretch of the clock on screen, as the strip frames it: where the
  // board stands, in the clock's units (before its start or past its end
  // where a card was carried there).
  const visWin = useMemo(() => ({
    u0: view.x / kk,
    u1: (view.x + viewW - PAD_L - BOARD_PAD_R) / kk,
  }), [view.x, kk, viewW]);
  // What the strip spans: what the board holds, as its cards are drawn at this
  // zoom (the reach the board scrolls over) and as the reader left them, so
  // the window always lies over the board and each trace's mark sits where
  // its card does, wherever on the clock that is.
  const stripDom = useMemo(() => {
    if (!clock) return null;
    const a = ((layout.x0 || 0) - BOARD_EDGE_L) / kk;
    const b = ((layout.w || 0) - BOARD_PAD_R - PAD_L - 3) / kk;
    // And its cards' first and last moments where the board has them: a card
    // sized along the board draws them inside its pads, nearer its edges.
    let f0 = Infinity;
    let f1 = -Infinity;
    for (const c of geo?.cards?.values() || []) {
      if (!c.win) continue;
      const cc = cardClock(c, { k: kk });
      f0 = Math.min(f0, (cc.xOf(c.win.u0 + 3 / kk) - PAD_L) / kk);
      f1 = Math.max(f1, (cc.xOf(c.win.u1 - 3 / kk) - PAD_L) / kk);
    }
    const u0 = Math.min(a, spanU0 ?? a, f0);
    return { u0, u1: Math.max(b, spanU1 ?? b, f1, u0 + MIN_WINDOW_U) };
  }, [clock, layout.x0, layout.w, kk, spanU0, spanU1, geo]);

  // The sidebar lists what the canvas knows: its cards' hues and titles, and
  // the sessions related to the anchor, strongest first. One board tells the
  // page about itself at a time (`publish`): the overlay's while it is up.
  const publishing = publish && !previewOn;
  // What it tells the page, said as text, so the page hears of it only when
  // it changes: a page that draws the board again whenever it hears, handing
  // it fresh lists, would otherwise be told again at every render, and draw
  // it again, without end. What hangs from each trace (`things`) is by the
  // board's own ids, tucked away or not, for a chat to tuck away or bring
  // back (the reader's readings stay the reader's), at the turn the board
  // prints, in its lane.
  const canvasSaid = useMemo(() => {
    if (!publishing) return null;
    return JSON.stringify({
      anchor: focalSessionId,
      status: joinStatus,
      cards: [...cards.values()].filter((c) => c.focal || c.r?.added).map((c) => ({
        id: c.focal ? focalSessionId : c.key, hue: c.hue || null, title: c.titleText || "", status: c.status || "done", machine: c.machine || null,
        turns: c.times?.length ?? null, firstTurn: c.turnBase || 0,
        // The lanes that did the most, by name, for the chat to point at.
        lanes: (c.model?.lanes || []).filter((l) => l.turns?.length).sort((a, b) => b.turns.length - a.turns.length).slice(0, 24).map((l) => l.label || l.id),
      })),
      related: related.filter((r) => !r.added).slice(0, 80).map((r) => ({
        id: r.id, evidence: r.evidence, shared: r.shared, endpointId: r.endpointId, agent: r.agent, model: r.model,
        firstMs: r.firstMs, lastMs: r.lastMs, inGraph: !!r.inGraph, kgOnly: !!r.kgOnly, same: !!r.same,
      })),
      things: [...nodesByCard].flatMap(([key, list]) => list.filter((nd) => nd.kind !== "reading").map((nd) => boardThingOf(nd, cards.get(key), key === FOCAL ? focalSessionId : key, isStanding(nd)))),
    });
  }, [publishing, focalSessionId, joinStatus, cards, related, nodesByCard, isStanding]);
  useEffect(() => {
    if (canvasSaid) publishWorkspaceCanvas(JSON.parse(canvasSaid));
  }, [canvasSaid]);
  useEffect(() => (publishing ? () => publishWorkspaceCanvas(null) : undefined), [publishing]);
  // A trace picked in the sidebar is brought into view once its card is
  // placed, and selected; requests made before this canvas mounted are not.
  const focusReq = useWorkspaceFocus();
  const focusSeen = useRef(focusReq.n);
  // A canvas opened on one of its traces (a claim's) brings it in once placed.
  // A focus of `<session>#<message>` (a case Investigate handed over) names a
  // moment too: once the card's record holds that message, its cell is
  // picked as a click would pick it, so the board opens on the evidence.
  const [pendingFocus, setPendingFocus] = useState(() => (initialFocus ? String(initialFocus).split("#")[0].toLowerCase() : null));
  const [pendingMoment, setPendingMoment] = useState(() => {
    const [session, message] = String(initialFocus || "").split("#");
    return session && message ? { session: session.toLowerCase(), message: message.toLowerCase() } : null;
  });
  useEffect(() => {
    if (preview || still || focusReq.n === focusSeen.current) return;
    focusSeen.current = focusReq.n;
    if (focusReq.id) setPendingFocus(focusReq.id);
  }, [focusReq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingFocus || !geo) return;
    const key = pendingFocus === focalSessionId ? FOCAL : pendingFocus;
    // One still reading its record is brought in where it will be.
    const lc = (geo.loading || []).find((l) => l.key === key);
    const cg = geo.cards.get(key)
      || (lc ? { x: Math.min(lc.x, lc.headX ?? lc.x), w: Math.max(lc.w, lc.headW ?? lc.w), y: lc.y, h: lc.h } : null);
    if (!cg) return;
    // As little movement as brings it in: none when it is already in view.
    const pad = 40 / cam;
    const fit = (at, len, from, span) => {
      const shown = Math.min(len, span - 2 * pad);
      if (at - from < pad) return at - pad;
      if (at + shown - from > span - pad) return at + shown - (span - pad);
      return from;
    };
    touched.current = true;
    holdPick.current = key;
    setView({ x: fit(cg.x, cg.w, view.x, viewW - coverR / cam), y: fit(cg.y, cg.h, view.y, viewH) });
    setSel(key === FOCAL ? null : { key });
    setPendingFocus(null);
  }, [pendingFocus, geo, focalSessionId, setView, view.x, view.y, viewW, viewH, coverR, cam]);
  useEffect(() => {
    if (!pendingMoment || pendingFocus || !geo) return;
    const key = pendingMoment.session === focalSessionId ? FOCAL : pendingMoment.session;
    const c = cards.get(key);
    const recs = key === FOCAL ? turns : c?.read?.turns;
    // Until the card's record is read there is no turn to pick.
    if (!c?.model || !recs?.length) return;
    setPendingMoment(null);
    const at = recs.findIndex((t) => String(t?.messageId || "").toLowerCase() === pendingMoment.message);
    if (at < 0) return;
    const lanes = c.model.lanes || [];
    const lane = lanes.find((l) => l.cells?.has(at) && isConversationDomain(l.domain)) || lanes.find((l) => l.cells?.has(at));
    if (!lane) return;
    let bucket = null;
    let inBucket = [at];
    for (const [b, ts] of geo.cards.get(key)?.lanes?.get(lane.id)?.bucketTurns || []) {
      if (ts.includes(at)) { bucket = b; inBucket = ts; break; }
    }
    const pick = { laneId: lane.id, turn: at, turns: inBucket };
    setSel({ key, ...pick });
    const click = ++cellClicks.current;
    if (onCellPick) onCellPick(cellPickOf(key, { ...pick, click }));
    else if (bucket != null) setDetail({ kind: "cell", key, ...pick, bucket, click });
  }, [pendingMoment, pendingFocus, geo, cards, turns, focalSessionId, onCellPick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each card's "earlier" (earlierOf), kept while its record stands, so the
  // card's handles are not drawn again on every pointer move.
  const earliers = useMemo(() => {
    const out = new Map();
    const add = (key, rec) => {
      const e = earlierOf(rec);
      if (e) out.set(key, e);
    };
    if (onFocalEarlier) add(FOCAL, focalRecord);
    for (const [id, r] of recordReads) if (r?.status === "done") add(id, r.record);
    return out;
  }, [focalRecord, onFocalEarlier, recordReads]);
  // A page read in front of a card's record moves its turns along: what is
  // selected on it, and read beside it, stays on its message. A message the
  // card no longer holds (its record read again from its newest page, another
  // anchor) is let go.
  const turnsOf = useRef(new Map());
  useLayoutEffect(() => {
    const moved = new Map();
    const seen = (key, list) => {
      const prev = turnsOf.current.get(key);
      turnsOf.current.set(key, list);
      if (prev?.length && list?.length && prev !== list) moved.set(key, [prev, list]);
    };
    seen(FOCAL, turns);
    for (const [id, r] of recordReads) if (r?.status === "done") seen(id, r.turns);
    if (!moved.size) return;
    // A turn's index on the card now, or null when its message is gone.
    const follow = (key, i) => {
      const [prev, list] = moved.get(key);
      const id = prev[i]?.messageId;
      if (id == null || list[i]?.messageId === id) return i;
      const j = list.findIndex((t) => t?.messageId === id);
      return j >= 0 ? j : null;
    };
    setSel((cur) => {
      if (cur?.turn == null || !moved.has(cur.key)) return cur;
      const t = follow(cur.key, cur.turn);
      return t === cur.turn ? cur : t == null ? { key: cur.key } : { ...cur, turn: t };
    });
    setDetail((cur) => {
      if (cur?.kind !== "cell" || !moved.has(cur.key)) return cur;
      const t = follow(cur.key, cur.turn);
      if (t === cur.turn) return cur;
      return t == null ? null : { ...cur, turn: t, turns: (cur.turns || []).map((x) => follow(cur.key, x)).filter((x) => x != null) };
    });
  }, [turns, recordReads]);
  // A cell picked on a card, as a dock reads it, the same for every card (the
  // anchor's too): the card's trace (its record, its machine, its span), the
  // chat's claims the board placed on it, what keeping it under its card
  // would keep, and the click that opened it.
  const cellPickOf = (key, cell) => {
    const c = cards.get(key);
    const focal = key === FOCAL;
    const session = focal ? focalSessionId : key;
    const kept = readingOfCell(c, session, cell);
    const { id } = kept;
    const helper = (nodesByCard.get(key) || []).find((nd) => nd.kind === "agent" && nd.laneId === cell.laneId) || null;
    return {
      key: focal ? null : key, laneId: cell.laneId, turn: cell.turn, click: cell.click ?? null, tab: cell.tab ?? null, session, title: c?.titleText || "", base: c?.turnBase || 0,
      record: {
        model: c?.model || null, turns: focal ? turns : c?.read?.turns || null, endpointId: c?.endpointId || null,
        shellItems: focal ? null : c?.read?.shellItems || null, firstMs: c?.firstMs ?? null, lastMs: c?.lastMs ?? null,
      },
      overlay: overlays.has(key) ? overlays.get(key) : undefined,
      keep: readingsRef.current.some((r) => r.id === id) ? null : () => setReadings((cur) => (cur.some((r) => r.id === id) ? cur : [...cur, kept].slice(-MAX_READINGS))),
      // On an agent's own lane, its session laid on the board from the dock.
      lay: helper ? () => attachActsNow.current.lay(helper) : null,
      light: handoff,
      land: (req) => landRef.current({ key, lane: cell.laneId, turn: cell.turn }, req),
      // The dock reading it stepped along its lane or to another turn: the
      // card lights where it is now, on the cell's own lane where that holds
      // the turn, else the conversation's, else any lane's.
      follow: (at) => setSel((cur) => {
        if (at?.turn == null) return cur;
        const lanes = c?.model?.lanes || [];
        const own = lanes.find((l) => l.id === at.laneId) || null;
        const lane = own?.cells?.has(at.turn) ? own
          : lanes.find((l) => l.cells?.has(at.turn) && isConversationDomain(l.domain)) || lanes.find((l) => l.cells?.has(at.turn)) || own;
        if (!lane || (cur?.key === key && cur.laneId === lane.id && cur.turn === at.turn)) return cur;
        return { key, laneId: lane.id, turn: at.turn };
      }),
    };
  };
  // The column the cell read beside its card is in now: its turn's, which
  // moves when a page comes in front of the card's record.
  const bucketOfDetail = (cg) => (cg?.tu ? bucketAt(cg.tu[detail.turn], cg.win, cg.n).bucket : null) ?? detail.bucket;
  // What is open beside its card: the annotation (or a reading of the cell),
  // and the place beside the card it sits, right of it where there is room,
  // else left, else over it.
  const detailView = useMemo(() => {
    if (!detail || !geo) return null;
    let node = null;
    let cg = null;
    let ax;
    let ay;
    let keep = null;
    let follow = null;
    if (detail.kind === "cell") {
      cg = geo.cards.get(detail.key) || null;
      const cy = cg?.scene?.centers?.get(detail.laneId);
      if (!cg || cy == null) return null;
      ax = cg.x + (cg.rail || 0) + (bucketOfDetail(cg) + 0.5) * cg.cellW;
      ay = cg.bodyY + cy;
      const kept = readingOfCell(cards.get(detail.key), detail.key === FOCAL ? focalSessionId : detail.key, detail);
      node = { id: kept.id, kind: "reading", key: detail.key, anchors: [{ key: detail.key, lane: detail.laneId, turn: detail.turn }], turns: detail.turns };
      if (!readings.some((r) => r.id === kept.id)) keep = kept;
      if (onBranch) follow = followOf(cards.get(detail.key)?.model?.laneById?.get(detail.laneId));
    }
    const sxp = (x) => MACH_W + (x - view.x) * cam;
    const syp = (y) => RULER_H + (y - view.y) * cam;
    if (!node) return null;
    const left = cg ? Math.min(cg.x, cg.headX ?? cg.x) : ax;
    const right = cg ? Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w)) : ax;
    let px;
    if (sxp(right) + 14 + DETAIL_W <= size.w - 8) px = sxp(right) + 14;
    else if (sxp(left) - 14 - DETAIL_W >= MACH_W + 8) px = sxp(left) - 14 - DETAIL_W;
    // No room beside the card: next to the cell, on the side with room, so
    // the reading stays tied to it by a short line.
    else if (sxp(ax) + 28 + DETAIL_W <= size.w - 8) px = sxp(ax) + 28;
    else if (sxp(ax) - 28 - DETAIL_W >= MACH_W + 8) px = sxp(ax) - 28 - DETAIL_W;
    else px = sxp(ax) < size.w / 2 ? size.w - DETAIL_W - 12 : MACH_W + 12;
    // In the canvas's lower half it grows up from the cell's level, in the
    // upper half down from it, so it sits beside the cell at any height.
    const low = syp(ay) > RULER_H + (size.h - RULER_H) / 2;
    const pb = low ? Math.max(8, size.h - Math.min(size.h - 8, syp(ay) + 28)) : null;
    const h = Math.max(160, Math.min(DETAIL_H, low ? size.h - pb - RULER_H - 8 : size.h - RULER_H - 16));
    const py = low ? null : Math.max(RULER_H + 8, Math.min(size.h - h - 8, syp(ay) - 28));
    return { node, keep, follow, px, py, pb, w: DETAIL_W, h, ax: sxp(ax), ay: syp(ay), line: true };
  }, [detail, geo, view.x, view.y, cam, size.w, size.h, focalSessionId, readings, cards, RULER_H, onBranch]);
  const detailCard = detail?.kind === "cell" ? cards.get(detail.key) : null;
  const detailPick = useMemo(
    () => (cellDock && detail?.kind === "cell" && detailCard ? cellPickOf(detail.key, detail) : null),
    [cellDock, detail, detailCard?.model, detailCard?.read, overlays, handoff], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const detailLine = detailView?.line ? { ax: detailView.ax, ay: detailView.ay, bx: detailView.px < detailView.ax ? detailView.px + detailView.w : detailView.px, by: detailView.py != null ? detailView.py + 22 : detailView.ay } : null;
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && boardKeys(wrapRef.current, e)) setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);
  useEffect(() => {
    if (!heldId) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && boardKeys(wrapRef.current, e)) setHeldId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [heldId]);
  // The rest of the call behind the cell in hand, hovered or else picked (as
  // the Lanes plane's focus is), ringed on its card.
  const callRings = useMemo(() => {
    if (!geo?.cards) return null;
    let cell = hover?.cell || null;
    if (!cell && sel?.laneId) {
      const cg = geo.cards.get(sel.key);
      const { bucket } = cg ? bucketAt(cg.tu?.[sel.turn], cg.win, cg.n) : { bucket: null };
      cell = bucket != null ? { key: sel.key, laneId: sel.laneId, turn: bucket } : null;
    }
    const cg = cell ? geo.cards.get(cell.key) : null;
    if (!cg?.scene || cg.folded) return null;
    const rings = cardCallCells(cg, cards.get(cell.key)?.model, cell);
    return rings.length ? { key: cell.key, rings } : null;
  }, [geo, hover?.cell, sel, cards]);
  // The moments in sessions inside the workspaces this one holds, as pins on
  // those workspaces' cards, where the wires from their rows start: on the
  // session's card in the picture of what is inside, else at the start of
  // its row (its record is not read here, so where in it each one is is not
  // known), else along the foot of the card's head.
  const nestedPins = useMemo(() => {
    const out = [];
    for (const [key, list] of nestedMoments) {
      const cg = geo?.cards?.get(key);
      if (!cg) continue;
      const foot = headHold(holdCam, cg.h).foot;
      const under = cg.inset ? Math.max(0, cg.y + foot - cg.inset.y) : 0;
      const drop = cg.inset ? 0 : Math.max(0, foot - CARD_HEAD);
      const shownH = cg.inset ? cg.inset.h - under : 0;
      const pic = shownH >= NEST_INSET_SHOWN ? pictures.get(key) : null;
      const x0 = cg.x + (cg.rail || 0) + 4;
      for (const nd of list) {
        const inner = pic?.find((x) => x.key === nd.inside.session && x.y < shownH) || null;
        const r = !cg.inset && nd.inside.row ? cg.rows.find((x) => x.key === nd.inside.row && x.spanX) : null;
        const listed = r && (cg.folded || r.y + drop + GRAPH_ROW_H <= cg.y + cg.h);
        const at = inner ? { x: cg.inset.x + inner.x + CARD_GLYPH_X, y: cg.inset.y + under + inner.y + Math.min(10, inner.h / 2) }
          : listed ? { x: Math.max(r.spanX.a, x0), y: cg.folded ? cg.y + cg.h - 3 : r.y + drop + GRAPH_ROW_H / 2 }
            : { x: x0 + CARD_GLYPH_X, y: cg.folded ? cg.y + cg.h - 3 : cg.y + foot };
        out.push({ ...nd, x: at.x, y: at.y, folded: !!cg.folded, standing: false, pts: [] });
      }
    }
    return out;
  }, [geo, nestedMoments, pictures, holdCam]);
  const allPins = useMemo(() => (nestedPins.length ? [...(geo?.pins || []), ...nestedPins] : geo?.pins || NO_PINS), [geo, nestedPins]);
  // The pins as they stand at this zoom, each where its point touches its
  // cell's ring in the board's units scaled to the screen's: pins on a card
  // whose heads would cover each other stand as one. Zoomed far out, a
  // card's head held over its top rows hides their pins, as it hides their
  // rings.
  // A walkthrough's moments never share a pin with other notes: pins of one
  // kind whose heads would cover each other stand as one, and where the
  // other kind's stand at the same place too, theirs stands above on a
  // longer needle, the moments nearest the cell (`lift`).
  const pinGroups = useMemo(() => {
    const out = [];
    const close = (g, sx, sy) => Math.abs(g.sx - sx) < PIN_HEAD + PIN_GAP && Math.abs(g.sy - sy) < PIN_HEAD + PIN_GAP;
    for (const p of allPins) {
      const cg = geo.cards.get(p.key);
      if (cg && !p.folded && cam < HOLD_CAM && p.y < cg.y + headHold(cam, cg.h).foot) continue;
      const sx = p.x * cam;
      const sy = (p.y - RING_HALF) * cam;
      const kind = p.n != null ? "moment" : "note";
      const near = out.find((g) => g.key === p.key && g.kind === kind && close(g, sx, sy));
      if (near) near.members.push(p);
      else out.push({ id: p.id, key: p.key, kind, sx, sy, lift: 0, members: [p] });
    }
    for (const g of out) {
      g.members.sort((a, b) => (a.n ?? Infinity) - (b.n ?? Infinity));
      if (g.members.length > 1) g.id = `pins:${g.members[0].id}`;
      if (g.kind === "note" && out.some((m) => m.kind === "moment" && m.key === g.key && close(m, g.sx, g.sy))) g.lift = 1;
    }
    return out;
  }, [geo, allPins, cam]);
  const noteAsk = useWorkspaceNoteAsk();
  const askedPoint = publishing ? noteAsk.point : null;
  const hoverNode = useMemo(() => {
    if (hoverNodeDom) return hoverNodeDom;
    if (askedPoint) return askedPoint;
    const c = hover?.cell;
    const cg = c ? geo?.cards?.get(c.key) : null;
    if (!cg || !(geo?.nodes?.length || pinGroups.length)) return null;
    const cy = cg.scene.centers.get(c.laneId);
    if (cy == null) return null;
    const x = cg.x + (cg.rail || 0) + (c.turn + 0.5) * cg.cellW;
    const y = cg.bodyY + cy;
    const on = (pts) => pts?.some((pt) => Math.abs(pt.x - x) < 1 && Math.abs(pt.y - y) < 1);
    return geo.nodes.find((nd) => on(nd.pts))?.id || pinGroups.find((g) => g.members.some((p) => on(p.pts)))?.id || null;
  }, [hoverNodeDom, askedPoint, hover, geo, pinGroups]);
  // The notes whose pins the pointer is on, for a list of them beside the
  // board to light: a stack's every member, or the one picked out of it.
  useEffect(() => {
    if (!publishing) return;
    const stack = hoverNodeDom ? pinGroups.find((g) => g.id === hoverNodeDom) : null;
    const ids = stack ? stack.members.map((m) => m.id) : hoverNodeDom && allPins.some((p) => p.id === hoverNodeDom) ? [hoverNodeDom] : null;
    publishWorkspaceNotesPointed(ids);
  }, [hoverNodeDom, pinGroups, allPins, publishing]);
  useEffect(() => (publishing ? () => publishWorkspaceNotesPointed(null) : undefined), [publishing]);
  // Where each card stands among the cards laid over each other (by card
  // key): the last carried on top; one never carried, under them in its
  // place on the board.
  const zOf = useMemo(() => {
    const out = new Map();
    const focalSid = String(focalSessionId || "").toLowerCase();
    for (const [sid, e] of Object.entries(sizesSaved || {})) if (Number.isInteger(e?.z)) out.set(String(sid).toLowerCase() === focalSid ? FOCAL : sid, e.z);
    return out;
  }, [sizesSaved, focalSessionId]);
  // The claim the evidence along the board's foot points at, else the one
  // picked there: ringed on the board, and its badge on the strip.
  const evidenceIds = useMemo(() => {
    const id = evidencePoint?.id || evidencePick?.id || null;
    return id ? new Set([id]) : null;
  }, [evidencePoint?.id, evidencePick?.id]);
  // The board's close, over the right end of its top row (BoardClose).
  const closing = !!boardBar?.onClose;
  const st = useMemo(() => ({
    // A card carried, the rest fade back at once, as under one rested on.
    geo, layout, cards, clock, k: kk, cam, sel, view, roll, canExit: !!onExit, litKey, entering, zOf, dimKey: moving?.key || dimKey,
    hoverKey, focusKey, hoverCell: hover?.cell || null, hoverLens: hover?.lens ?? null, hoverGrow: hover?.grow ? { key: hover.key, row: hover.grow } : null, lensDraft, lensable: !previewOn,
    focalFirst, focalLast, get pointerX() { return pointerRef.current.x; }, get pointerY() { return pointerRef.current.y; }, preview: previewOn, headRoom: preview?.headRoom || 0, still: !!still, previewHot, rulerH: RULER_H, closeRoom: closing ? CLOSE_ROOM : 0, win: visWin, stripDom, cursor, hoverStrip: hover?.strip || null, hoverCont: hover?.cont || null, hoverReset: hover?.reset || null, resizingKey: resizing?.key || null, movingKey: moving?.key || null, toolbarKey, hoverNode, hoverNodeRow: hoverNodeDom, flags, tags,
    investigation, evidenceIds, evidencePoints, evidenceFrom: evidencePoint?.from || (evidencePick ? evidenceOpenAt : null), claimFocus: hover?.claim ?? null, claimRing: String(hoverNode || "").startsWith("claim:") ? String(hoverNode).slice(6) : null,
    detailLine, detailId: heldId, heldId, callRings, pictures, pictureCanvas, pinGroups,
  }), [pictures, pictureCanvas, callRings, pinGroups, heldId, hoverNode, hoverNodeDom, flags, tags, visWin, stripDom, previewHot, resizing, moving?.key, toolbarKey, investigation, evidenceIds, evidencePoints, evidencePoint?.from, evidencePick, evidenceOpenAt, detailLine?.ax, detailLine?.ay, detailLine?.bx, detailLine?.by, detail, geo, layout, cards, clock, kk, cam, sel, view, roll, onExit, litKey, entering, zOf, dimKey, hoverKey, focusKey, hover, lensDraft, focalFirst, focalLast, previewOn, RULER_H, closing, cursor]);
  const stRef = useRef(st);
  stRef.current = st;
  // Every change in where the cards sit (a record or a head read, wires
  // opening a card's groups, By time, a card folded, laid on or let go)
  // glides there from where each was last drawn, as a roll does, so the
  // board never jumps and the eye can follow each thing to where it goes.
  // What the reader is carrying or sizing, and a zoom, follow at once. A
  // change arriving mid-glide glides on from where things are drawn then.
  const lastDrawn = useRef(null);
  const takeUp = useRef(workspaceId ? HANDOFFS.get(workspaceId) || null : null);
  useLayoutEffect(() => {
    const left = takeUp.current;
    if (!left || !size.w || !layout.items.some((it) => it.kind === "card")) return;
    takeUp.current = null;
    const box = canvasRef.current?.getBoundingClientRect();
    if (left.board === boardId || performance.now() - left.at > HANDOFF_MS || !box) return;
    const keyOf = (sid) => (sid === String(focalSessionId || "").toLowerCase() ? FOCAL : sid);
    const from = new Map();
    for (const [sid, p] of left.pos) from.set(keyOf(sid), { x: view.x + (p.cx - box.left - MACH_W) / cam, y: view.y + (p.cy - box.top - RULER_H) / cam, h: p.h / cam });
    glideFrom.current = from;
    setGlideT(0);
    setGlideRun((n) => n + 1);
  }, [layout, size.w]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const was = lastDrawn.current;
    // A card dropped on the board comes from under the pointer.
    const fromDrop = new Map();
    for (const it of layout.items) {
      const p = dropFrom.current.get(it.key);
      if (!p) continue;
      dropFrom.current.delete(it.key);
      if (!was?.pos.has(it.key)) fromDrop.set(it.key, p);
    }
    // A still picture is drawn where things are, with no glide to get there:
    // it is sized in steps, and its cards would wander between them.
    if (!dataGlides || still || !was || was.layout === layout || was.kk !== kk || was.cam !== cam || resizing || moving || (glideFrom.current && glideT === 0)) return;
    // Only somewhere new to go starts a glide: laid out again with each thing
    // going where it was going already (as mid-glide), the glide under way
    // goes on. Measured from where things are drawn, every frame of a glide
    // would look like a move and start it again from where it began.
    const aim = new Map(was.layout.items.filter((it) => GLIDES.has(it.kind)).map((it) => [it.key, it]));
    for (const [key, p] of fromDrop) aim.set(key, p);
    let moved = false;
    for (const it of layout.items) {
      const p = aim.get(it.key);
      if (p && (Math.abs(p.y - it.y) >= 1 || Math.abs(p.x - it.x) >= 1 || Math.abs(p.h - it.h) >= 1)) { moved = true; break; }
    }
    if (!moved) return;
    const from = fromDrop.size ? new Map([...was.pos, ...fromDrop]) : was.pos;
    glideFrom.current = from;
    setGlideT(0);
    setGlideRun((n) => n + 1);
  }, [layout]); // eslint-disable-line react-hooks/exhaustive-deps
  // When cards change depth (a record read, attachments arriving, one folded
  // or pinned) on a board taller than the view, what is on screen stays where
  // it was: the card under the pointer, else the topmost in view, keeps its
  // place on the screen. A board that fits the view keeps to its top
  // instead. Cards laid on or taken off, and a card being sized or carried
  // (and the update that lets it go), move the board as they will.
  const anchorRef = useRef(null);
  useLayoutEffect(() => {
    if (!layout?.items || still) return;
    const tops = new Map(layout.items.filter((it) => it.kind === "card").map((it) => [it.key, it]));
    const keys = [...tops.keys()].sort().join(",");
    const busy = !!(resizing || moving);
    const prev = anchorRef.current;
    if (prev && !prev.busy && !busy && prev.keys === keys && tops.has(prev.key) && !boardFits(layout.h, viewH)) {
      const dy = tops.get(prev.key).y - prev.y;
      if (Math.abs(dy) >= 1) shiftView(0, dy);
    }
    let key = hoverKey && tops.has(hoverKey) ? hoverKey : null;
    if (!key) {
      let best = null;
      for (const it of tops.values()) {
        if (it.y + it.h < view.y) continue;
        if (!best || it.y < best.y) best = it;
      }
      key = best?.key || null;
    }
    anchorRef.current = key ? { key, y: tops.get(key).y, keys, busy } : { key: null, keys, busy };
    // Only the layout moves the anchor; the view and pointer are read as they stand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);
  // When the clock changes under the cards (a session laid on that ran
  // before the rest, a record read), a moment keeps its place on screen, so
  // what was in view stays where it was: where something dropped on the
  // board was let go, else the start of the card the reader last asked to
  // see, else of the card nearest the middle of the view.
  useLayoutEffect(() => {
    const prev = xHold.current;
    if (prev && clock && prev.clock !== clock && prev.kk === kk && touched.current && !resizing && !moving) {
      const x = PAD_L + clock.at(prev.t) * kk;
      if (Math.abs(x - prev.x) >= 1) {
        cancelAnimationFrame(settleRaf.current);
        shiftView(x - prev.x, 0);
      }
    }
    const startOf = (key) => (cards.get(key) || machineCards.get(key) || relatedRows.find((r) => r.id === key))?.firstMs;
    const dropped = [...drops.values()].find((d) => Number.isFinite(d.t));
    const picked = holdPick.current != null ? startOf(holdPick.current) : null;
    const near = holdCard(layout.items, view.x + viewW / 2, drops);
    const t = dropped ? dropped.t : Number.isFinite(picked) ? picked : near ? startOf(near.key) : null;
    xHold.current = clock && Number.isFinite(t) ? { t, x: PAD_L + clock.at(t) * kk, clock, kk } : null;
    // Only the layout moves the view here; the view is read as it stands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  // How far the pointer has carried the card in hand since the board last
  // laid it, in the board's units: null with none in hand (or with a block,
  // which the board lays at every step).
  const carryShift = useCallback(() => {
    const m = movingRef.current;
    const at = movingLaid.current;
    if (!m || m.pin || !at || at.key !== m.key) return null;
    return { key: m.key, dx: ((m.off ?? 0) - (at.off ?? 0)) * (stRef.current.k || 1), dy: m.top - at.top };
  }, []);
  // What hangs from the card in hand on the page, moved with it: its chips
  // and blocks (one carried out of its band only along the board), its
  // notes' pins, and the lines from the reader's notes to them. Each goes
  // back to where the board lays it once the card is let go.
  const carriedEls = useRef([]);
  const linesMoved = useRef(new Set());
  const noteLinesNow = useRef([]);
  const moveCarried = useCallback((shift) => {
    const root = wrapRef.current;
    if (!root || (!shift && !carriedEls.current.length && !linesMoved.current.size)) return;
    const now = [];
    if (shift) {
      const cam = stRef.current.cam || 1;
      for (const el of root.querySelectorAll("[data-chips-of]")) {
        if (el.getAttribute("data-chips-of") !== shift.key) continue;
        el.style.transform = `translate(${shift.dx}px, ${el.hasAttribute("data-chips-free") ? 0 : shift.dy}px)`;
        now.push(el);
      }
      for (const el of root.querySelectorAll("[data-carried-pins]")) {
        if (el.getAttribute("data-carried-pins") !== shift.key) continue;
        el.style.transform = `translate(${shift.dx * cam}px, ${shift.dy * cam}px)`;
        now.push(el);
      }
    }
    for (const el of carriedEls.current) if (!now.includes(el)) el.style.transform = "";
    carriedEls.current = now;
    const lines = new Map(noteLinesNow.current.map((ln) => [ln.id, ln]));
    const moved = new Set();
    for (const el of root.querySelectorAll("[data-note-line]")) {
      const ln = lines.get(el.getAttribute("data-note-line"));
      if (!ln) continue;
      const on = !!shift && ln.key === shift.key;
      if (!on && !linesMoved.current.has(ln.id)) continue;
      const tx = on ? ln.tx + shift.dx : ln.tx;
      const ty = on ? ln.ty + shift.dy : ln.ty;
      el.setAttribute("x1", on ? Math.max(ln.box.x, Math.min(ln.box.x + ln.box.w, tx)) : ln.fx);
      el.setAttribute("y1", on ? Math.max(ln.box.y, Math.min(ln.box.y + ln.box.h, ty)) : ln.fy);
      el.setAttribute("x2", tx);
      el.setAttribute("y2", ty);
      if (on) moved.add(ln.id);
    }
    linesMoved.current = moved;
  }, []);
  const drawNow = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv || !size.w || !size.h) return;
    const dpr = window.devicePixelRatio || 1;
    if (cv.width !== Math.round(size.w * dpr) || cv.height !== Math.round(size.h * dpr)) {
      cv.width = Math.round(size.w * dpr);
      cv.height = Math.round(size.h * dpr);
      cv.style.width = `${size.w}px`;
      cv.style.height = `${size.h}px`;
    }
    // No 2D context (canvas switched off, or a test's DOM): sized, undrawn.
    const ctx2d = cv.getContext("2d");
    if (!ctx2d) return;
    hitsRef.current = drawFocus(ctx2d, stRef.current, theme, { W: size.w, H: size.h, dpr, view: stRef.current.view });
    const lift = liftRef.current;
    if (lift) {
      if (lift.width !== Math.round(size.w * dpr) || lift.height !== Math.round(size.h * dpr)) {
        lift.width = Math.round(size.w * dpr);
        lift.height = Math.round(size.h * dpr);
        lift.style.width = `${size.w}px`;
        lift.style.height = `${size.h}px`;
      }
      drawLift(lift.getContext("2d"), stRef.current, theme, { W: size.w, H: size.h, dpr, view: stRef.current.view, shift: carryShift() });
    }
    const conn = connRef.current;
    if (conn) {
      if (conn.width !== Math.round(size.w * dpr) || conn.height !== Math.round(size.h * dpr)) {
        conn.width = Math.round(size.w * dpr);
        conn.height = Math.round(size.h * dpr);
        conn.style.width = `${size.w}px`;
        conn.style.height = `${size.h}px`;
      }
      drawConnectors(conn.getContext("2d"), stRef.current, theme, { W: size.w, H: size.h, dpr, view: stRef.current.view });
    }
  }, [size, theme, carryShift]);
  drawNowRef.current = drawNow;
  useLayoutEffect(() => { drawNow(); }, [drawNow, st]);
  // The card in hand where the pointer has carried it since the board last
  // laid it: drawn on the canvas above, and what hangs from it on the page
  // moved with it (moveCarried).
  const drawCarry = useCallback(() => {
    const lift = liftRef.current;
    const dpr = window.devicePixelRatio || 1;
    if (lift && size.w && size.h) {
      // Sized as the board last drew it; else the board draws it all again.
      if (lift.width !== Math.round(size.w * dpr) || lift.height !== Math.round(size.h * dpr)) {
        drawNow();
        moveCarried(carryShift());
        return;
      }
      const ctx2d = lift.getContext("2d");
      if (ctx2d) drawLift(ctx2d, stRef.current, theme, { W: size.w, H: size.h, dpr, view: stRef.current.view, shift: carryShift() });
    }
    moveCarried(carryShift());
  }, [size, theme, drawNow, carryShift, moveCarried]);
  drawCarryRef.current = drawCarry;
  // Laid again (or anything else drawn again) while it is carried on, what
  // hangs from it is moved from where the board laid it now.
  useLayoutEffect(() => { moveCarried(carryShift()); });
  useLayoutEffect(() => {
    const pos = new Map();
    for (const it of drawnLayout.items) if (GLIDES.has(it.kind)) pos.set(it.key, { x: it.x, y: it.y, h: it.h });
    lastDrawn.current = { pos, layout, kk, cam };
    lastFeet.current = new Map(layout.items.filter((x) => x.foot).map((x) => [x.key, x.foot]));
    if (!roll && !moving && !resizing) unrolledTops.current = new Map([...lastFeet.current].map(([key, f]) => [key, f.y0]));
    // Where each card stands on the screen, for a hand-off from this board,
    // read off the page as it lies; while a card is carried or sized, as it
    // lies once it is let go (a read at every step made the page lay itself
    // out again at every step).
    if (workspaceId && size.w && !movingRef.current && !rzRef.current) {
      const box = canvasRef.current?.getBoundingClientRect();
      if (box) {
        const at = new Map();
        for (const [key, p] of pos) at.set(key === FOCAL ? String(focalSessionId || "").toLowerCase() : String(key).toLowerCase(), { cx: box.left + MACH_W + (p.x - view.x) * cam, cy: box.top + RULER_H + (p.y - view.y) * cam, h: p.h * cam });
        HANDOFFS.set(workspaceId, { board: boardId, at: performance.now(), pos: at });
      }
    }
  });

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;
    WORKBENCHES.set(cv, {
      get state() { return stRef.current; },
      get hits() { return hitsRef.current; },
      zoomTo, zoomCam, fitSpan, fitAll, setView: (v) => { touched.current = true; setView(v); },
    });
    return () => WORKBENCHES.delete(cv);
  }, [zoomTo, zoomCam, fitSpan, fitAll, setView]);

  // The wheel gesture under way, carried across the handler's re-binds.
  const wheelRail = useRef({ at: -Infinity, axis: null, ax: 0, ay: 0 });
  useEffect(() => {
    const cv = canvasRef.current;
    const surface = wrapRef.current;
    if (!cv || !surface) return undefined;
    // Over anything laid on the canvas (an attachment, a card's edges and
    // buttons, a panel) the wheel moves the board as it does over the canvas,
    // unless it is over something with scroll of its own left to take it.
    const ownScroll = (el, dx, dy) => {
      for (let n = el; n && n !== surface && n instanceof Element; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (dy && /(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1
          && ((dy < 0 && n.scrollTop > 0) || (dy > 0 && n.scrollTop + n.clientHeight < n.scrollHeight - 1))) return true;
        if (dx && /(auto|scroll)/.test(cs.overflowX) && n.scrollWidth > n.clientWidth + 1
          && ((dx < 0 && n.scrollLeft > 0) || (dx > 0 && n.scrollLeft + n.clientWidth < n.scrollWidth - 1))) return true;
      }
      return false;
    };
    const onWheel = (e) => {
      if (e.target !== cv && !e.ctrlKey && !e.metaKey && ownScroll(e.target, e.deltaX, e.deltaY)) return;
      e.preventDefault();
      const r = cv.getBoundingClientRect();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? planeH : 1;
      const zoomKey = e.ctrlKey || e.metaKey;
      // Over a lens along the strip's scale, the wheel zooms that stretch,
      // further or back.
      const over = hitFocus(hitsRef.current, stRef.current, e.clientX - r.left, e.clientY - r.top);
      if (over?.type === "lens" && Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
        zoomLens(over.id, Math.exp(-e.deltaY * unit * 0.004));
        return;
      }
      // Zooming in over a workspace inside this one pushes into it: its place
      // in the band (while it has no card) at once, its card once the card
      // fills the view (or the camera can come no closer).
      if (zoomKey && onEnter) {
        const stub = e.target instanceof Element ? e.target.closest("[data-workspace-stub]") : null;
        const inner = stub?.dataset.workspaceStub || /^graph:workspace:(.+)$/.exec(String(over?.key || ""))?.[1];
        const card = inner && !stub ? layout.items.find((x) => x.key === `graph:workspace:${inner}`) : null;
        const fills = !!card && (Math.max(card.w, card.headW || 0) * cam >= planeW * 0.85 || cam >= CAM_MAX * 0.999);
        if (inner && e.deltaY < 0 && (stub || fills)) {
          pushNow.current.pushInto(inner, -e.deltaY * unit * 0.0025, stub ? stub.getBoundingClientRect() : null);
          return;
        }
        pushNow.current.dropPush();
      }
      const move = zoomKey ? null : railWheel(wheelRail.current, e.timeStamp, (e.shiftKey ? e.deltaY : e.deltaX) * unit, (e.shiftKey ? 0 : e.deltaY) * unit);
      // With the zoom key held, or over the strip, the board zooms about the
      // moment under the pointer (on the strip, about that moment's place on
      // the board); otherwise it scrolls, both ways. Across the strip it
      // scrolls too.
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;
      if (!zoomKey && !move) return;
      // Over the board, the zoom key scales the whole board about the pointer.
      if (zoomKey && sy >= RULER_H) {
        zoomCam(camNow.current.cam * Math.exp(-e.deltaY * unit * 0.0025), sx, sy);
        return;
      }
      // Over the strip, the wheel zooms the clock about the moment under the
      // pointer.
      if (sy < RULER_H && (zoomKey || move.dy)) {
        let at = MACH_W + viewW / 2;
        const sp = hitsRef.current?.strip;
        if (sp) at = MACH_W + PAD_L + (sp.u0 + Math.max(0, Math.min(sp.U, ((sx - sp.x0) / sp.w) * sp.U))) * kk - view.x;
        zoomTo(kk * Math.exp(-(zoomKey ? e.deltaY * unit : move.dy) * 0.0025), at, sy);
        return;
      }
      touched.current = true;
      holdPick.current = null;
      panBy(move.dx / cam, move.dy / cam);
      setTip(null);
    };
    surface.addEventListener("wheel", onWheel, { passive: false });
    return () => surface.removeEventListener("wheel", onWheel);
  }, [zoomTo, zoomCam, zoomLens, kk, cam, panBy, view, viewW, viewH, planeW, RULER_H, layout.h, layout.items, onEnter]);

  // The canvas's place on the page, read as a press starts and held while it
  // drags (a read at every move after the page changed made it lay itself
  // out again at every move).
  const pressBox = useRef(null);
  const local = (e) => {
    const r = (dragRef.current || movingRef.current) && pressBox.current ? pressBox.current : canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const hitAt = (p) => hitFocus(hitsRef.current, stRef.current, p.x, p.y);
  const restKey = !moving && !resizing && !panning && hover?.key && (cards.has(hover.key) || machineCards.has(hover.key)) ? hover.key : null;
  useEffect(() => {
    setDimKey(null);
    if (!restKey) return undefined;
    const t = setTimeout(() => setDimKey(restKey), CARD_DIM_REST_MS);
    return () => clearTimeout(t);
  }, [restKey]);
  // A note's pin carried over the board: the line from the note follows the
  // pointer, and the cell under it is outlined as one pointed at is; let go
  // on a cell, the note is pinned there (one cell once).
  const cellUnder = (clientX, clientY) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return null;
    const p = { x: clientX - r.left, y: clientY - r.top };
    const hit = hitAt(p);
    return { bx: (p.x - MACH_W) / cam + view.x, by: (p.y - RULER_H) / cam + view.y, hit: hit?.type === "cell" ? hit : null };
  };
  const linkMove = (id, clientX, clientY) => {
    const at = cellUnder(clientX, clientY);
    if (!at) return;
    setLinkDraft({ id, x: at.bx, y: at.by });
    const next = at.hit ? { key: at.hit.key, cell: { key: at.hit.key, laneId: at.hit.laneId, laneIds: at.hit.laneIds, turn: at.hit.turn } } : null;
    setHover((cur) => (sameHover(next, cur) ? cur : next));
  };
  const linkEnd = (id, clientX, clientY, put) => {
    setLinkDraft(null);
    setHover(null);
    const hit = put ? cellUnder(clientX, clientY)?.hit : null;
    const pick = hit ? resolvePick(stRef.current.geo?.cards.get(hit.key), hit) : null;
    const note = (looseNotes || []).find((x) => x.id === id);
    if (!pick || !note || !onLooseNote) return;
    const session = String(hit.key === FOCAL ? focalSessionId : hit.key).toLowerCase();
    const turn = (cards.get(hit.key)?.turnBase || 0) + pick.turn;
    const links = note.links || [];
    if (links.some((l) => l.session === session && l.turn === turn && (l.lane || null) === (pick.laneId || null))) return;
    onLooseNote(id, { links: [...links, { id: newLinkId(), session, turn, lane: pick.laneId }] });
  };
  const linkActs = useRef(null);
  linkActs.current = { move: linkMove, end: linkEnd };
  const onNoteLink = useMemo(() => ({
    move: (...a) => linkActs.current.move(...a),
    end: (...a) => linkActs.current.end(...a),
  }), []);

  const toggleSet = (setter, key) => setter((cur) => {
    const n = new Set(cur);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    return n;
  });
  // A session graph stub opened in place: a card there, from its record.
  const openHere = (id) => setHere(() => {
    const open = new Set(hereNow.open).add(id);
    const closed = new Set(hereNow.closed);
    closed.delete(id);
    return { sid: focalSessionId, open, closed };
  });
  const toggleIn = (setter, key, id) => setter((cur) => {
    const n = new Set(cur.get(key) || []);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return new Map(cur).set(key, n);
  });
  const onClick = useCallback((hit, at = null) => {
    if (hit?.type !== "cardflag" && hit?.type !== "fpin") setTagList(null);
    if (!hit) { setSel(null); setDetail(null); return; }
    if (hit.type === "cardflag") {
      setTagList((cur) => (cur?.key === hit.key && !cur.ids ? null : { key: hit.key, ids: null, at }));
      return;
    }
    // A finding's mark opens it (and its message in the dock); a mark holding
    // several lists them to pick from.
    if (hit.type === "fpin") {
      const ids = stRef.current.geo?.cards?.get(hit.key)?.findingPinIds?.get(`${hit.laneId}:${hit.turn}`) || [];
      if (ids.length === 1) {
        setTagList(null);
        attachActsNow.current.open(ids[0]);
      } else if (ids.length) setTagList({ key: hit.key, ids, at });
      return;
    }
    if (hit.type === "ruler") return;
    if (hit.type === "claimbadge") {
      askClaimNow.current(hit.id);
      return;
    }
    if (hit.type === "claimrow") return;
    if (hit.type === "glay") {
      const id = String(hit.row?.key || "").slice("session:".length).toLowerCase();
      if (id && onLayer) onLayer([id]);
      return;
    }
    if (hit.type === "grow") {
      // On the board already: its card, brought into view.
      if (hit.row?.member) {
        setPointing({ key: hit.row.card });
        return;
      }
      // A session the chat found and the board does not hold: laid on.
      const id = hit.row?.kind === "session" ? String(hit.row.key).slice("session:".length).toLowerCase() : null;
      if (id && onLayer) onLayer([id]);
      return;
    }
    // A card in a workspace's picture: into that workspace, on it.
    if (hit.type === "insetcard") {
      handleActsNow.current?.enter(hit.key, hit.inner);
      return;
    }
    if (hit.type === "gmark" || hit.type === "gcard") return;
    if (hit.type === "reset") {
      const cur = sizeOf(hit.key) || {};
      keepPlaces(hit.key, { m: null, pin: null, off: null, h: cur.h });
      return;
    }
    if (hit.type === "cont") {
      // Scroll to the rest of the card that way.
      const cg = stRef.current.geo?.cards?.get(hit.key);
      if (!cg) return;
      touched.current = true;
      setView({ x: hit.side === "l" ? cg.x - 40 : cg.x + cg.w - viewW + 40, y: stRef.current.view.y });
      return;
    }
    if (hit.type === "stub") {
      setSel({ key: hit.key });
      openHere(hit.key);
      return;
    }
    if (hit.type === "cardhead" || hit.type === "wire") {
      setSel((cur) => (cur?.key === hit.key && !cur.laneId ? null : { key: hit.key }));
      return;
    }
    // An agent's row lays its session on the board, or brings it into view
    // once it is on.
    if (hit.type === "rail" && hit.rowKind !== "head") {
      const agent = agentOfLane(hit.key, hit.laneId);
      if (agent) attachActsNow.current.lay(agent);
      return;
    }
    if (hit.type === "rail" && hit.rowKind === "head") {
      // One click opens one thing, one level deep: a group its first level, a
      // directory its own entries, a "+k more" row the rest of its level.
      const id = String(hit.rowId || "");
      // A card the reader gave a height opens and folds by hand from the rows
      // it shows, and fits its rows again.
      const fit = fittedRows.get(hit.key);
      if (fit) {
        // One focused on what it rests on keeps to the focus's groups.
        setOpenGroups((cur) => new Map(cur).set(hit.key, new Set(fit.openGroups)));
        if (!fit.only && !fit.focus) setAllGroups((cur) => new Set(cur).add(hit.key));
        const sz = sizeOf(hit.key) || {};
        saveSize(hit.key, { ...sz, h: null });
      }
      if (id === "groups:more") toggleSet(setAllGroups, hit.key);
      else if (id.startsWith("more:")) toggleIn(setShowAll, hit.key, id.slice(5));
      else if (id.startsWith("dir:")) toggleIn(setOpenDirs, hit.key, id);
      else toggleIn(setOpenGroups, hit.key, id);
      return;
    }
    if (hit.type === "cell" || hit.type === "row" || hit.type === "gpin") {
      const pick = resolvePick(stRef.current.geo?.cards.get(hit.key), hit);
      if (!pick) { setSel({ key: hit.key }); return; }
      setSel({ key: hit.key, laneId: pick.laneId, turn: pick.turn });
      if (onCellPick) {
        // Read in the page's dock, as the board's own reading reads it.
        setDetail(null);
        onCellPick(cellPickOf(hit.key, { ...pick, click: ++cellClicks.current }));
        return;
      }
      // A picked cell's reading opens beside its card, in place of whatever
      // was open; "Keep" lists it under the card.
      const click = ++cellClicks.current;
      setDetail((cur) => {
        lastCellOpen.current = { at: performance.now(), before: cur };
        return { kind: "cell", key: hit.key, laneId: pick.laneId, turn: pick.turn, turns: pick.turns, bucket: hit.turn, click };
      });
    }
  }, [onCellPick, focalSessionId, setReadings, setHiddenNodes, cards, clock, planeW, planeH, setView, fittedRows, sizeOf, saveSize, onLayer, hereNow, overlays, turns, agentOfLane]); // eslint-disable-line react-hooks/exhaustive-deps

  const sameHover = (a, b) => (a?.key || null) === (b?.key || null)
    && (a?.cell?.laneId ?? null) === (b?.cell?.laneId ?? null) && (a?.cell?.turn ?? null) === (b?.cell?.turn ?? null)
    && (a?.wire ?? null) === (b?.wire ?? null)
    && (a?.lens ?? null) === (b?.lens ?? null) && !!a?.scale === !!b?.scale
    && (a?.label?.rowId ?? null) === (b?.label?.rowId ?? null)
    && (a?.strip ?? null) === (b?.strip ?? null) && (a?.cont ?? null) === (b?.cont ?? null)
    && (a?.reset ?? null) === (b?.reset ?? null) && !!a?.head === !!b?.head && (a?.claim ?? null) === (b?.claim ?? null)
    && (a?.grow ?? null) === (b?.grow ?? null) && (a?.inset ?? null) === (b?.inset ?? null);
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (heldId) setHeldId(null);
    pressBox.current = canvasRef.current.getBoundingClientRect();
    const p = { x: e.clientX - pressBox.current.left, y: e.clientY - pressBox.current.top };
    const hit = hitAt(p);
    dragRef.current = { x: p.x, y: p.y, vx: view.x, vy: view.y, moved: false, hit, lastX: p.x };
    dragRef.current.win0 = visWin;
    setPointer(null, null);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const p = local(e);
    const d = dragRef.current;
    if (d) {
      if (d.dead) return;
      if (!d.moved && Math.hypot(p.x - d.x, p.y - d.y) < 4) return;
      d.moved = true;
      if (tip) setTip(null);
      if (d.hit?.type === "strip" && d.win0 && clock) {
        // The window: an edge moves one end, the body slides it, the track
        // draws a new one from where the drag began.
        const sp = hitsRef.current?.strip;
        if (!sp) return;
        const uNow = sp.u0 + Math.max(0, Math.min(sp.U, ((p.x - sp.x0) / sp.w) * sp.U));
        const w0 = d.win0;
        const part = d.hit.part;
        if (part === "edge-l") showSpan({ u0: Math.min(uNow, w0.u1 - MIN_WINDOW_U), u1: w0.u1 });
        else if (part === "edge-r") showSpan({ u0: w0.u0, u1: Math.max(uNow, w0.u0 + MIN_WINDOW_U) });
        else if (part === "body") {
          // Sliding the frame scrolls the board.
          const span = w0.u1 - w0.u0;
          const u0 = Math.max(sp.u0, Math.min(sp.u0 + sp.U - span, w0.u0 + (uNow - d.hit.u)));
          touched.current = true;
          setView({ x: u0 * kk, y: view.y });
        } else if (Math.abs(uNow - d.hit.u) >= MIN_WINDOW_U) showSpan({ u0: Math.min(uNow, d.hit.u), u1: Math.max(uNow, d.hit.u) });
        return;
      }
      if (d.hit?.type === "scale" || (d.hit?.type === "lens" && !d.hit.close)) {
        // Drawing a lens: the stretch between where the drag began and here.
        setLensDraft({ x0: d.x, x1: Math.max(MACH_W, Math.min(size.w, p.x)) });
        return;
      }
      if (d.hit?.type === "ruler") return;
      // Taken by its head, a card is carried anywhere over the board, the
      // rest holding still under it: down the board, and along it off its own
      // time (its own ruler then says where it runs), back on its time when
      // let go close to it.
      if (d.hit?.type === "cardhead" || d.hit?.type === "pinhead") {
        let m = movingRef.current;
        if (!m) {
          let it = layout.items.find((x) => (x.kind === "card" || x.kind === "pin") && x.key === d.hit.key);
          // Pinned in place, it comes out of its band as a block as it is carried.
          if (!it && d.hit.type === "pinhead") {
            const nd = geo?.nodes?.find((n) => n.pinKey === d.hit.key && !n.free);
            if (!nd) return;
            setFreePins((cur) => new Set(cur).add(nd.id));
            it = { key: d.hit.key, y: nd.y, h: nd.h };
          }
          if (!it) return;
          touched.current = true;
          if (k == null) setK(kk);
          setDetail(null);
          setHoverNodeDom(null);
          const pinNode = d.hit.type === "pinhead" ? geo?.nodes?.find((n) => n.pinKey === d.hit.key) : null;
          // Out of its band, a block starts along the board from where it sat in it.
          const fromBand = !!pinNode && !pinNode.free;
          const off0 = fromBand && pinNode.ax != null ? (pinNode.x - (pinNode.ax - 14)) / kk : sizeOf(it.key)?.off || 0;
          m = {
            key: it.key, grab: (d.y - RULER_H) / cam + d.vy - it.y, h: it.h, slotH: it.foot ? it.foot.y1 - it.foot.y0 : null,
            free: true, cx0: e.clientX - (p.x - d.x), vx0: d.vx, kk, cam, off0,
            offMin: -Infinity,
            pin: pinNode ? { id: pinNode.id, owner: pinNode.key, ax: pinNode.ax } : null, fromBand,
            grabDX: pinNode ? (d.x - MACH_W) / cam + d.vx - pinNode.x : 0,
          };
        }
        const carried = carryTo(m, (p.y - RULER_H) / (m.cam || 1) + view.y - m.grab, e.clientY);
        carryOn.current({ ...carried, cx: e.clientX, off: offAt(m, e.clientX, view.x), dock: dockAt(carried, e.clientX, e.clientY) });
        return;
      }
      touched.current = true;
      holdPick.current = null;
      if (!panning) setPanning(true);
      // By what the pointer moved since its last move, not from where the
      // drag began: while records land the board shifts the view to hold
      // what is on screen in place, and a view set from the drag's start
      // undid each shift, the board jumping under a still pointer.
      const fromX = d.px ?? d.x;
      const fromY = d.py ?? d.y;
      d.px = p.x;
      d.py = p.y;
      panBy(-(p.x - fromX) / cam, -(p.y - fromY) / cam);
      return;
    }
    setPointer(p.y >= RULER_H ? MACH_W + (p.x - MACH_W) / cam : p.x, p.y >= RULER_H ? RULER_H + (p.y - RULER_H) / cam : p.y);
    const hit = hitAt(p);
    const next = hit ? {
      key: hit.key || null,
      wire: hit.type === "wire" ? hit.index : null,
      cell: hit.type === "cell" ? { key: hit.key, laneId: hit.laneId, laneIds: hit.laneIds, turn: hit.turn } : null,
      lens: hit.type === "lens" ? hit.id : null,
      scale: hit.type === "scale",
      label: hit.type === "rail" ? { key: hit.key, rowId: hit.rowId } : null,
      strip: hit.type === "strip" ? hit.part : null,
      reset: hit.type === "reset" ? hit.key : null,
      cont: hit.type === "cont" ? `${hit.key}:${hit.side}` : null,
      head: hit.type === "cardhead",
      claim: hit.type === "claimbadge" ? hit.id : null,
      grow: hit.type === "grow" || hit.type === "glay" ? hit.row?.key ?? null : null,
      inset: hit.type === "insetcard" ? hit.inner : null,
    } : null;
    if (!sameHover(next, hover)) setHover(next);
    // A tip shows at once and follows the pointer; the board renders again
    // only when what it is about changes.
    const tipHit = hit && hit.type !== "ruler" && hit.type !== "scale" ? hit : null;
    tipAt.set(e.clientX, e.clientY);
    if (tipKeyOf(tipHit) !== tipKeyOf(tip?.hit)) setTip(tipHit ? { hit: tipHit } : null);
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (panning) setPanning(false);
    const m = movingRef.current;
    if (m) {
      // Let go, the card stays where it was carried to, down the board and
      // along it, over whatever is there, and the rest where they are.
      movingRef.current = null;
      carryDone();
      // Let go over its own card's band, a pinned block goes back in place
      // there: that far from its moment, and in its turn among the opened
      // cards over its width.
      if (m.pin && m.dock) {
        saveSize(m.key, { ...(sizeOf(m.key) || {}), dx: m.dock.dx });
        const { id } = m.pin;
        const before = m.dock.before;
        setStandOver((cur) => {
          const rest = [...cur].filter(([k]) => k !== id);
          const i = before == null ? -1 : rest.findIndex(([k]) => k === before);
          rest.splice(i < 0 ? (before == null ? rest.length : 0) : i, 0, [id, true]);
          return new Map(rest);
        });
        setFreePins((cur) => { if (!cur.has(m.pin.id)) return cur; const next = new Set(cur); next.delete(m.pin.id); return next; });
        setMoving(null);
        return;
      }
      // Where the pointer has it, which may be on past where the board last
      // laid it.
      const it = layout.items.find((x) => x.key === m.key && x.foot);
      const off = m.free && m.off != null && (m.off !== m.off0 || m.fromBand) ? m.off : undefined;
      const zTop = 1 + Math.max(0, ...Object.values(sizesSaved || {}).map((e) => (Number.isInteger(e?.z) ? e.z : 0)));
      putDown(m.key, {
        ...(roll ? { mid: m.top + m.h / 2 } : it && Number.isFinite(m.top) ? { top: Math.round(m.top), free: true, z: zTop } : {}),
        ...(off !== undefined ? { off } : {}),
      });
      setMoving(null);
      return;
    }
    if (!d) return;
    if (d.moved) {
      // Let go along the scale, the stretch dragged out becomes a lens.
      const sc = hitsRef.current?.scale;
      if (lensDraft && sc && clock) {
        const a = Math.min(lensDraft.x0, lensDraft.x1);
        const b = Math.max(lensDraft.x0, lensDraft.x1);
        const on = (x) => clock.inv(Math.max(0, Math.min(clock.U, scaleU(sc, stRef.current, x))));
        if (b - a >= 6) addLens(on(a), on(b));
      }
      setLensDraft(null);
      return;
    }
    if (d.hit?.type === "pinhead") { unpin(String(d.hit.key).slice(4)); return; }
    if (d.hit?.type === "lens") { if (d.hit.close) dropLens(d.hit.id); return; }
    if (d.hit?.type === "scale") return;
    if (d.hit?.type === "overview") { setPendingFocus(d.hit.key === FOCAL ? focalSessionId : d.hit.key); return; }
    if (d.hit?.type === "strip" && clock) {
      // A trace's span puts the window over it; a click on the track moves
      // the window there; a click in the window puts the cursor there.
      const c = d.hit.key ? cards.get(d.hit.key) : null;
      const drawn = d.hit.key ? boardSpan?.of?.get(d.hit.key) : null;
      if (drawn) {
        const w = Math.max(MIN_WINDOW_U, drawn[1] - drawn[0]);
        showSpan({ u0: drawn[0] - w * 0.04, u1: drawn[1] + w * 0.04 });
      } else if (c && Number.isFinite(c.firstMs)) showSpan(windowOver(clock, c.firstMs, c.lastMs));
      else if (d.hit.part === "track" && visWin) {
        touched.current = true;
        setView({ x: (d.hit.u - (visWin.u1 - visWin.u0) / 2) * kk, y: view.y });
      } else if (d.hit.part === "body") {
        // The moment the board draws there, on the card there.
        const at = timeAt(stRef.current, d.hit.u);
        if (Number.isFinite(at)) setCursor(at);
      }
      return;
    }
    if (d.hit?.type === "ruler") return;
    onClick(d.hit, { x: d.x, y: d.y });
  };
  // Double-click: a lens comes off; on the strip the cursor lifts; on a
  // session the board zooms to its span, unrolled and the card where it was
  // (the reading a first click opened giving way); anywhere else everything
  // comes back.
  const onDoubleClick = (e) => {
    if (performance.now() - lastCellOpen.current.at < 600) setDetail(lastCellOpen.current.before);
    const hit = hitAt(local(e));
    if (hit?.type === "lens") { dropLens(hit.id); return; }
    if (hit?.type === "strip" || hit?.type === "scale") { setCursor(null); return; }
    const inner = /^graph:workspace:(.+)$/.exec(String(hit?.key || ""));
    if (inner && onEnter) { enterInto(inner[1], null, hit.type === "insetcard" ? hit.inner : null); return; }
    const c = hit?.key ? cards.get(hit.key) || (hit.type === "stub" ? relatedRows.find((r) => r.id === hit.key) : null) : null;
    if (c && clock && Number.isFinite(c.firstMs)) {
      holdPick.current = hit.key;
      // A stub opens in place first; the camera goes to it once it is a card.
      if (hit.type === "stub") {
        openHere(hit.key);
        showSpan(windowOver(clock, c.firstMs, Number.isFinite(c.lastMs) ? c.lastMs : c.firstMs), c.firstMs);
      } else zoomToCard(hit.key);
    } else showEverything();
  };

  // Clear: the anchor alone (every other session and every machine the chat
  // found off, those whose sessions were on the board as traces included, and
  // in the session graph what it opened), the whole clock across the canvas
  // again with no stretch zoomed, and the view the board's own until
  // the reader moves it, so what is left comes to its middle. The view goes
  // back to the board's start at once: the board's settle glides from the view
  // as it stands, and one left scrolled along a zoomed board would stop short
  // of the anchor's start. What comes off is the host's to say (`boardBar`):
  // in the workspace home's panes, everything, and the pane starts again.
  const clearBoard = () => {
    touched.current = false;
    letShapeGo();
    settledTops.current = new Map();
    settledShapes.current = new Map();
    setK(null);
    rollBy.current = 0;
    setRoll(0);
    setLenses([]);
    setViewState((cur) => ({ x: 0, y: cur.y }));
    if (peeks.size) setHere({ sid: focalSessionId, open: new Set(), closed: new Set([...hereNow.closed, ...peeks]) });
    boardBar?.onClear?.();
  };

  // Until the reader moves the canvas, it opens at the top of the whole clock
  // and follows the records in; after that, nothing moves under them. A
  // stacked board runs down in time, so the trace can sit well down it: there
  // the opening view brings the trace's card into sight.
  const focalTop = layout.items.find((it) => it.key === FOCAL && it.kind === "card")?.y ?? 0;
  useEffect(() => {
    if (touched.current || still) return;
    // The board opens at its top-left corner (one card alone, in the view's
    // middle); stacked and taller than the view, at its anchor's card.
    const at = stacked && !boardFits(layout.h, planeH) ? Math.max(0, Math.min(boardFootY(layout.h, planeH), focalTop - 40)) : 0;
    const { x, y } = clampView({ x: -Infinity, y: at });
    setViewState((cur) => (cur.x === x && cur.y === y ? cur : { x, y }));
  }, [layout.h, size.w, planeH, clampView, focalTop, stacked, arranged]); // eslint-disable-line react-hooks/exhaustive-deps

  const tipBody = useCallback((hit) => {
    if (!hit) return null;
    if (hit.type === "cardflag") return <div className="text-[12px] leading-[1.4] text-[color:var(--muted)]">Click to list what is about this trace, to open</div>;
    if (hit.type === "fpin") {
      const ids = geo?.cards?.get(hit.key)?.findingPinIds?.get(`${hit.laneId}:${hit.turn}`) || [];
      const list = (nodesByCard.get(hit.key) || []).filter((nd) => ids.includes(nd.id));
      if (!list.length) return null;
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{clip(list[0].title || "A finding", 120)}</div>
          {list.length > 1 ? <div className="text-[color:var(--muted)]">{`and ${plural(list.length - 1, "more finding")} here`}</div> : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{list.length > 1 ? "Click to pick one to open" : "Click to open it, with its message in the dock"}</div>
        </div>
      );
    }
    if (hit.type === "lens") {
      const l = clock?.lenses?.find((x) => x.id === hit.id);
      if (!l) return null;
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{`Zoomed ${scaleText(l.zoom)}`}</div>
          <div className="text-[color:var(--muted)]">{rangeText(l.t0, l.t1)}</div>
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{hit.close ? "Click to take this zoom off." : "Scroll here to zoom it further or back; double-click to take it off. The rest of the clock keeps its scale."}</div>
        </div>
      );
    }
    if (hit.type === "wire") {
      const w = hitsRef.current?.wires?.[hit.index];
      if (!w) return null;
      const other = cards.get(w.key);
      if (w.came) {
        return (
          <div className="text-[12px] leading-[1.4]">
            <div className="font-medium">{clip(other?.titleText || drops.get(w.key)?.title || "A session", 120)}</div>
            <div className="text-[color:var(--muted)]">{`Laid on from ${clip(cards.get(w.from)?.titleText || "a session", 80)}`}</div>
          </div>
        );
      }
      const text = needles[w.needle]?.needle;
      const [e0, e1] = w.a.t <= w.b.t ? [w.a.t, w.b.t] : [w.b.t, w.a.t];
      if (w.member) {
        const g = machineCards.get(w.from);
        return (
          <div className="text-[12px] leading-[1.4]">
            <div className="font-medium">{clip(other?.titleText || (w.key === FOCAL ? "This session" : "A session"), 120)}</div>
            <div className="text-[color:var(--muted)]">{g?.search ? (w.via === "content" ? `Its record holds what ${g.label} looks for` : `Matches ${g.label}`) : g?.workspace ? `In ${g.label} too` : `Ran on ${g?.label || "this machine"}`}</div>
            <div className="mt-[3px] text-[color:var(--muted-soft)]">{w.exact ? "The line lands on the cell that holds what the search looks for." : "Its row on the card the line comes from is when it ran there."}</div>
          </div>
        );
      }
      if (w.shared) {
        return (
          <div className="text-[12px] leading-[1.4]">
            <div className="font-medium">{`${clip(cards.get(w.from)?.titleText || "a session", 60)}${ARROW}${clip(other?.titleText || "a session", 60)}`}</div>
            <div className="text-[color:var(--muted)]">{`${sameDay(e0, e1) ? `${clockText(e0)}${ARROW}${clockText(e1)}` : `${whenText(e0)}${ARROW}${whenText(e1)}`}${DOT}${latencyText(e1 - e0).slice(1)} later`}</div>
            <div className="mt-[3px] break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(w.text || ""), 160)}</div>
            <div className="mt-[3px] text-[color:var(--muted-soft)]">{`Both touched this file${w.wrote ? ", and at least one of them wrote to it" : ""}; the line runs from the last time in one to the first in the next. ${w.df.toLocaleString("en-US")} of the ${w.total.toLocaleString("en-US")} sessions ${w.onMachines ? `${[...new Set([cards.get(w.from)?.machine, other?.machine].filter(Boolean))].join(" and ") || "their machines"} ran in the range` : "in the range"} touched it; the fewer, the stronger the line.`}</div>
          </div>
        );
      }
      if (w.chain) {
        return (
          <div className="text-[12px] leading-[1.4]">
            <div className="font-medium">{`${clip(cards.get(w.from)?.titleText || "a session", 60)}${ARROW}${clip(other?.titleText || "a session", 60)}`}</div>
            <div className="text-[color:var(--muted)]">{`${sameDay(e0, e1) ? `${clockText(e0)}${ARROW}${clockText(e1)}` : `${whenText(e0)}${ARROW}${whenText(e1)}`}${DOT}${latencyText(e1 - e0).slice(1)} later`}</div>
            {text ? <div className="mt-[3px] break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(text).replace(/\s+/g, " "), 160)}</div> : null}
            <div className="mt-[3px] text-[color:var(--muted-soft)]">{`What this workspace searches for, from the last place it came up in one session to the first in the next.${w.exact ? "" : " Placed at the turn it was said in where no cell holds it."}`}</div>
          </div>
        );
      }
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{clip(other?.titleText || "related session", 120)}</div>
          <div className="text-[color:var(--muted)]">{`${w.a.t <= w.b.t ? "here first" : "there first"}${DOT}${sameDay(e0, e1) ? `${clockText(e0)}${ARROW}${clockText(e1)}` : `${whenText(e0)}${ARROW}${whenText(e1)}`}${DOT}${latencyText(e1 - e0)}`}</div>
          {text ? <div className="mt-[3px] break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(text).replace(/\s+/g, " "), 160)}</div> : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{w.exact ? "The string sits on a cell in both sessions." : "Placed at the turn it was said in; no cell of that session holds it."} Click to follow this session.</div>
        </div>
      );
    }
    if (hit.type === "claimbadge") {
      const cl = investigation?.claims.find((x) => x.id === hit.id);
      if (!cl) return null;
      const rests = geo?.claimRests?.get(cl.id) || [];
      return (
        <div className="grid gap-[2px] text-[12px] leading-[1.4]">
          <div className="font-medium">{`Claim ${cl.n}${DOT}${cl.status === "supported" ? "supported" : "not yet supported"}${cl.isNew ? `${DOT}new at this step` : ""}`}</div>
          <div className="text-[color:var(--ink)]">{clip(String(cl.statement || ""), 280)}</div>
          <div className="mt-[3px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Rests on</div>
          {rests.slice(0, 4).map((r, i) => (
            <div key={i} className="min-w-0 truncate text-[color:var(--muted)]">
              <span className="text-[color:var(--ink)]">{clip(String(r.on || ""), 56)}</span>
              {`${DOT}${r.what}${Number.isFinite(r.at) ? `${DOT}${hhmm(r.at)}` : ""}`}
            </div>
          ))}
          {rests.length > 4 ? <div className="text-[color:var(--muted-soft)]">{`and ${rests.length - 4} more`}</div> : null}
          {!rests.length ? <div className="text-[color:var(--muted)]">{`${cl.evidence.length} thing${cl.evidence.length === 1 ? "" : "s"} the chat found, none of them on the board`}</div> : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">Click to go to where it rests, and read it in the evidence along the board's foot.</div>
        </div>
      );
    }
    if (hit.type === "claimrow") {
      const badge = (supported, n) => (
        <span className={`grid h-[12px] w-[14px] shrink-0 place-items-center border border-[color:var(--graph)] text-[9.5px] font-semibold leading-none ${supported ? "bg-[color:var(--graph)] text-[color:var(--bg)]" : "text-[color:var(--graph)]"}`}>{n}</span>
      );
      return (
        <div className="grid gap-[4px] text-[12px] leading-[1.4]">
          <div className="font-medium">The chat&apos;s claims, each at the moment it rests on</div>
          <div className="flex items-center gap-[8px] text-[color:var(--muted)]">{badge(true, 4)}supported: the graph bears it out</div>
          <div className="flex items-center gap-[8px] text-[color:var(--muted)]">{badge(false, 8)}not yet supported</div>
          <div className="flex items-center gap-[8px] text-[color:var(--muted)]"><span className="h-[2px] w-[14px] shrink-0 bg-[color:var(--graph)]" />new at the step shown</div>
          <div className="text-[color:var(--muted-soft)]">Numbered as the chat numbers them. Hover one to see what it rests on, click to hold it; one resting on nothing dated sits by this row&apos;s name.</div>
        </div>
      );
    }
    if (hit.type === "gmark") {
      const m = hit.mark;
      const first = m.events[0];
      const kind = findWords(first.kind, m.events.length);
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{`${m.events.length === 1 ? FIND_WORDS[first.kind]?.[0] || first.kind : kind}${DOT}${whenText(first.at)}`}</div>
          {m.claims?.length ? (
            <div className="text-[color:var(--muted)]">{`Evidence for claim ${(investigation?.claims || []).filter((cl) => m.claims.includes(cl.id)).map((cl) => cl.n).join(", ")}`}</div>
          ) : null}
          {m.events.slice(0, 3).map((e) => <div key={e.key} className="break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(e.label || ""), 180)}</div>)}
          {m.events.length > 3 ? <div className="text-[color:var(--muted)]">{`${m.events.length - 3} more`}</div> : null}
          {first.reached?.length ? <div className="text-[color:var(--muted)]">{`reached ${first.reached.slice(0, 3).join(", ")}`}</div> : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{m.onCard ? "Found by the chat inside this trace; drawn at its moment." : m.row?.kind === "session" ? `In ${m.row.label || "a session"} ${m.row.detail || ""}, which the board does not hold.` : `On ${m.row?.label || "a machine"}, in no session the chat knows.`}{m.claims?.length ? ` Claim ${m.claims.map((id) => investigation?.claims.find((x) => x.id === id)?.n).filter(Boolean).join(", ")} rests on it.` : ""}</div>
        </div>
      );
    }
    if (hit.type === "insetcard") {
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{clip(hit.title || "A session", 160)}</div>
          <div className="text-[color:var(--muted)]">{`In ${machineCards.get(hit.key)?.label || "the workspace inside"}`}</div>
          <div className="mt-[3px] text-[color:var(--muted-soft)]">Click to go in to it.</div>
        </div>
      );
    }
    if (hit.type === "glay") return <div className="text-[12px] leading-[1.4]">Lay it on the workspace as a trace</div>;
    if (hit.type === "grow") {
      const r = hit.row;
      // A row of a machine, a search or a workspace inside: a session there.
      const own = /^graph:(machine|search|workspace):/.test(String(hit.key));
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{[r.label, r.detail].filter(Boolean).join(" ")}</div>
          <div className="text-[color:var(--muted)]">{own ? (r.span ? `Ran ${whenText(r.span.from)} to ${whenText(r.span.to)}` : "When it ran is not known")
            : [r.kind === "session" && r.machine ? `On ${r.machine}` : null, `${foundWords(r.events) || "nothing dated"} the chat found ${r.kind === "session" ? "in it" : "here, in no session"}${r.span ? `, seen ${whenText(r.span.from)} to ${whenText(r.span.to)}` : ""}`].filter(Boolean).join(DOT)}</div>
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{r.member ? "On the workspace as a card; the line runs to it. Click to bring it into view."
            : r.kind === "session" ? "Click to lay it on the workspace as a trace." : "Work on this machine outside any session the chat has found."}</div>
        </div>
      );
    }
    if (hit.type === "cardhead" && String(hit.key).startsWith("graph:")) {
      const kind = /^graph:(machine|search|workspace):/.exec(String(hit.key))?.[1];
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{kind === "workspace" ? "A workspace inside this one: its sessions, a row each at the time it ran; those on this workspace too are wired to their cards"
            : kind === "search" ? "The sessions a search matches, a row each at the time it ran; those on the workspace are wired to their cards"
            : kind === "machine" ? "The machine's sessions, a row each at the time it ran; those on the workspace are wired to their cards"
            : "What the chat found that the workspace does not hold: the sessions it looked into, and work on a machine in no session, a row each"}</div>
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{kind === "workspace" ? "Zoom in on it or double-click it to go in; drag its head to move it up or down the board."
            : kind ? "Click a row to lay that session on, or to bring its card into view; drag its head to move it up or down the board." : "Click a session's row to lay it on; drag its head to move it up or down the board."}</div>
        </div>
      );
    }
    if (hit.type === "stub") {
      const r = relatedRows.find((x) => x.id === hit.key);
      const ln = geo?.stubs?.get(hit.key);
      if (!r || !ln) return null;
      const hand = (geo?.wires || []).find((w) => w.key === r.id && w.primary)
        || [...allWires, ...rowWires].find((w) => w.key === r.id && w.primary);
      const text = hand ? needles[hand.needle]?.needle : null;
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{clip(ln.label || "related session", 160)}</div>
          <div className="text-[color:var(--muted)]">{[[r.agent, r.model].filter(Boolean).join(DOT), ln.machine ? ln.machine + (ln.sameMachine ? " (same machine)" : "") : null].filter(Boolean).join(DOT)}</div>
          <div className="text-[color:var(--muted)]">{`${whenText(r.firstMs)}${Number.isFinite(focalFirst) ? `, ${relText(r.firstMs - focalFirst)} this session` : ""}`}</div>
          <div className="text-[color:var(--muted)]">{`${r.direction}${DOT}${r.shared} shared strings${DOT}evidence ${r.evidence.toFixed(2).replace(/\.?0+$/, "")}`}</div>
          {text ? (
            <div className="mt-[4px] text-[color:var(--muted)]">
              {`Closest hand-off${DOT}${latencyText(Math.abs(hand.b.t - hand.a.t))}`}
              <div className="break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(text).replace(/\s+/g, " "), 140)}</div>
            </div>
          ) : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">Not loaded yet. Click to load its lanes here; double-click to load it and fit its span.</div>
        </div>
      );
    }
    const c = hit.key ? cards.get(hit.key) : null;
    if (!c) return null;
    if (hit.type === "cardhead") {
      if (c.focal) {
        return (
          <div className="text-[12px] leading-[1.4]">
            <div className="font-medium">{clip(c.titleText, 160)}</div>
            <div className="text-[color:var(--muted)]">{c.subText}</div>
            {c.sessionId ? <div className="font-mono text-[11px] text-[color:var(--muted-soft)]">{c.sessionId}</div> : null}
            {wires.length ? <div className="text-[color:var(--muted)]">{`${plural(wires.length, "wire")} to ${plural(new Set(wires.map((w) => w.key)).size, "other trace")}`}</div> : null}
            <div className="mt-[3px] text-[color:var(--muted-soft)]">Drag its head to move it.</div>
          </div>
        );
      }
      const r = c.r;
      const on = wires.filter((w) => w.key === c.key);
      const how = r.kgOnly ? "Found by the chat" : r.added && !r.evidence ? null : `${r.direction}${DOT}${plural(r.shared, "shared string")}`;
      const ep = c.read?.meta?.endpointId || (r.endpointId === "added" ? null : r.endpointId);
      const ran = [c.who, ep ? machineName(ep) + (r.same ? " (same machine)" : "") : null].filter(Boolean).join(DOT);
      const read = c.status === "done"
        ? [on.length ? plural(on.length, "wire") : null, c.read?.byOrder ? "Most turn times are estimated" : null, c.read?.codeMode ? (c.read.codeMode.unwrapped ? "Code cells unwrapped" : "Code cells as written") : null].filter(Boolean).join(DOT)
        : c.status === "error" ? `Its record could not be read (${c.error})` : c.status === "unread" ? "Not read yet: click to read it." : "Reading...";
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{clip(c.titleText, 160)}</div>
          {ran ? <div className="text-[color:var(--muted)]">{ran}</div> : null}
          <div className="text-[color:var(--muted)]">{`${whenText(c.firstMs)}${Number.isFinite(focalFirst) && Number.isFinite(c.firstMs) ? `, ${relText(c.firstMs - focalFirst)} ${sessionGraph ? "this session" : "the anchor"}` : ""}`}</div>
          {how ? <div className="text-[color:var(--muted)]">{how}</div> : null}
          {read ? <div className="mt-[3px] text-[color:var(--muted-soft)]">{read}</div> : null}
          <div className="mt-[3px] text-[color:var(--muted-soft)]">{peeks.has(c.key) ? "Opened here only: lay it on to keep it." : "Drag its head to move it."}</div>
        </div>
      );
    }
    if (hit.type === "gpin") return <div className="text-[12px]">{hit.label || "claim"}</div>;
    if (hit.type === "rail") {
      const id = String(hit.rowId || "");
      const agent = hit.rowKind !== "head" ? agentOfLane(hit.key, hit.laneId) : null;
      const mounted = agent ? agentMounts.get(agent.id) : null;
      const what = id === "groups:more" ? "Show this trace's other groups."
        : id.startsWith("more:") ? "Show the rest of this level."
          : agent ? (mounted?.id && cards.has(mounted.id) ? "Click to bring its session into view." : mounted?.status === "finding" ? "Finding its session." : "Click to lay its session on the board.")
            : hit.rowKind !== "head" ? null
              : hit.closed ? (id.startsWith("dir:") ? "Open this directory, one level." : "Open this group, one level.") : "Fold it back.";
      return <div className="text-[12px]">{`${hit.label || ""}${hit.count != null ? `${DOT}${hit.count} lanes` : ""}`}{what ? <div className="text-[color:var(--muted-soft)]">{what}</div> : null}</div>;
    }
    if (hit.type === "cell" || hit.type === "row") {
      const cg = geo?.cards.get(c.key);
      if (!cg) return null;
      const pick = resolvePick(cg, hit);
      const lane = pick ? cg.lanes.get(pick.laneId) : null;
      const b0 = clock.inv(cg.win.u0 + (hit.turn / cg.n) * (cg.win.u1 - cg.win.u0));
      const b1 = clock.inv(cg.win.u0 + ((hit.turn + 1) / cg.n) * (cg.win.u1 - cg.win.u0));
      const folded = (hit.laneIds?.length || 0) > 1;
      const row = cg.scene.rows.find((r) => (r.type === "head" ? r.id : r.lane.id) === hit.laneId);
      const { lines, more } = hit.type === "cell" ? bucketLines(cg, hit) : { lines: [], more: 0 };
      const ids = new Set(hit.laneIds?.length ? hit.laneIds : [hit.laneId]);
      const landing = (geo?.wires || []).filter((w) => [w.a, w.b].some((end) => end.key === c.key && ids.has(end.lane)
        && bucketAt(cg.tu[end.turn], cg.win, cg.n).bucket === hit.turn));
      return (
        <div className="text-[12px] leading-[1.4]">
          <div className="font-medium">{folded ? `${row?.label || "group"}${DOT}${hit.laneIds.length} lanes` : lane?.label || row?.label || "nothing here"}</div>
          <div className="text-[color:var(--muted)]">{`${clip(c.titleText, 80)}${DOT}${rangeText(b0, b1)}`}</div>
          {pick ? <div className="text-[color:var(--muted)]">{`turn ${(c.turnBase || 0) + pick.turn}${pick.turns.length > 1 ? ` and ${pick.turns.length - 1} more in this stretch` : ""}`}</div> : null}
          {lines.length ? (
            <div className="mt-[4px] flex flex-col gap-[1px] font-mono text-[11px] text-[color:var(--ink)]">
              {lines.map((l, i) => <div key={i} className="break-words">{l}</div>)}
              {more ? <div className="text-[color:var(--muted-soft)]">{`+${more} more`}</div> : null}
            </div>
          ) : null}
          {landing.slice(0, 3).map((w, i) => {
            const text = needles[w.needle]?.needle;
            const other = cards.get(w.key === c.key ? FOCAL : w.key);
            return (
              <div key={`w${i}`} className="mt-[4px] text-[color:var(--muted)]">
                {`Shares with ${clip(other?.titleText || "the other session", 60)}${DOT}${latencyText(Math.abs(w.b.t - w.a.t))}`}
                {text ? <div className="break-words font-mono text-[11px] text-[color:var(--ink)]">{clip(String(text).replace(/\s+/g, " "), 140)}</div> : null}
              </div>
            );
          })}
          {pick ? <div className="mt-[3px] text-[color:var(--muted-soft)]">{onCellPick ? "Click to read it in the dock." : "Click to read it beside the card."}</div> : null}
        </div>
      );
    }
    return null;
  }, [cards, wires, allWires, rowWires, relatedRows, geo, needles, machineName, focalFirst, clock, investigation, onCellPick, sessionGraph, peeks, nodesByCard, agentOfLane, agentMounts]);

  // The cell whose reading is open says it there, not in a tip as well.
  const tipOpen = tip?.hit?.type === "cell" && detail?.kind === "cell" && tip.hit.key === detail.key && tip.hit.turn === bucketOfDetail(geo?.cards.get(detail.key));
  const tipLabel = tip && !tipOpen ? tipBody(tip.hit) : null;

  const zoomPct = Math.round(cam * 100);
  // What the bar's cells do, through the board as it stands at the press
  // (barNow, set as it renders), so the bar is drawn again only when what it
  // shows changes.
  const barNow = useRef(null);
  const barActs = useMemo(() => Object.fromEntries(BAR_ACTS.map((name) => [name, (...args) => barNow.current?.[name]?.(...args)])), []);
  const emptyButton = "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
  // What lies on the board in the page's own elements rides the camera as the
  // canvas does: scaled about the plane's corner, the view's corner at it.
  const boardXf = `translate(${MACH_W}px, 0px) scale(${cam}) translate(${-view.x}px, ${-view.y}px)`;
  // The board small in its corner, while it runs past the view.
  const mini = useMemo(() => {
    if (previewOn || !layout.items.some((it) => it.kind === "card" || it.kind === "landing")) return null;
    const things = layout.items.filter((it) => it.kind === "card" || it.kind === "landing" || it.kind === "note" || it.kind === "pin").map((it) => {
      const x0 = Math.min(it.x, it.headX ?? it.x);
      const x1 = Math.max(it.x + it.w, (it.headX ?? it.x) + (it.headW || 0));
      const band = it.kind === "card" ? layout.items.find((n) => n.kind === "node" && n.owner === it.key) : null;
      return { key: it.key, x: x0, y: it.y, w: x1 - x0, h: Math.max(it.y + it.h, band ? band.y + band.h : 0) - it.y };
    });
    const bx0 = Math.min(...things.map((t) => t.x));
    const bx1 = Math.max(...things.map((t) => t.x + t.w));
    const by1 = Math.max(...things.map((t) => t.y + t.h));
    if (bx1 - bx0 <= viewW + 1 && by1 <= viewH + 1) return null;
    return { things, extent: { x0: Math.min(bx0, view.x), y0: Math.min(0, view.y), x1: Math.max(bx1, view.x + viewW), y1: Math.max(by1, view.y + viewH) } };
  }, [previewOn, layout.items, view, viewW, viewH]);
  const onMiniMove = useCallback((v) => { touched.current = true; holdPick.current = null; panTo(v); }, [panTo]);

  // What a card's handles do, behind functions that stay the same across
  // renders, so the handles are drawn again only when what they show changes.
  const handleActsNow = useRef(null);
  handleActsNow.current = {
    earlier: (key) => (key === FOCAL ? onFocalEarlier?.() : readEarlier(key)),
    startResize, moveResize, endResize, resetSize, toggleGroups, takeOff,
    // Into a workspace inside this one, on one of its cards (`focus`).
    enter: (key, focus = null) => {
      const inner = /^graph:workspace:(.+)$/.exec(String(key));
      if (inner) enterInto(inner[1], null, focus);
    },
    // Out from a session to its machine: a card of the machine's sessions.
    // The machine's card is pointed out, kept here just now or already.
    branch: (key) => {
      const c = cards.get(key);
      const ep = c?.endpointId;
      if (!onBranch || !ep || ep === "unknown" || ep === "added") return;
      const got = padMachines?.some((m) => m.id === ep) ? { key: `graph:machine:${ep}` } : onBranch({ kind: "machine", id: ep, name: c.machine || "" });
      if (got?.key) setPointing({ key: got.key });
    },
    fold: (key) => {
      const sz = sizeOf(key) || {};
      saveSize(key, { ...sz, fold: !sz.fold });
    },
    fit: (key) => {
      const c = cards.get(key) || machineCards.get(key);
      if (c && clock && Number.isFinite(c.firstMs)) zoomToCard(key);
    },
    anchor: (key) => { if (key !== FOCAL && onAnchor) onAnchor(key); },
    toolbarEnter: (key) => setToolbarKey(key),
    toolbarLeave: (key) => setToolbarKey((k) => (k === key ? null : k)),
  };
  // An agent the anchor started, laid on as its own session: found the way the
  // trace page finds a sub-agent's conversation (the content join from its
  // spawn), then laid on the board and brought into view.
  const agentThreads = useMemo(() => (focalSessionId ? buildLanesThreads({
    sessionId: focalSessionId, currentTraceId: trace?.traceId || trace?.id || null, toolExecutions: trace?.toolExecutions || null,
    spawnPrompts: null, onOpenTrace: () => {}, queryFn: query, endpointId: trace?.endpointId || null,
  }) : null), [focalSessionId, trace?.traceId, trace?.id, trace?.toolExecutions, trace?.endpointId]);
  // Any other card's helpers are found the same way, from its session.
  const cardThreads = useRef(new Map());
  const threadsFor = (key) => {
    if (key === FOCAL) return agentThreads;
    if (!cardThreads.current.has(key)) {
      const ep = cards.get(key)?.endpointId;
      cardThreads.current.set(key, buildLanesThreads({
        sessionId: key, currentTraceId: key, toolExecutions: null, spawnPrompts: null, onOpenTrace: () => {}, queryFn: query,
        endpointId: ep && ep !== "unknown" && ep !== "added" ? ep : null,
      }));
    }
    return cardThreads.current.get(key);
  };
  const setMount = useCallback((id, state) => setAgentMounts((cur) => new Map(cur).set(id, state)), []);
  // Where an agent's session stands: being found, found and reading its
  // record, on the canvas, or not found.
  const mountStateOf = (nd) => {
    if (nd?.kind !== "agent") return null;
    const m = agentMounts.get(nd.id);
    if (!m) return null;
    if (m.status === "finding" || m.status === "missed") return m.status;
    const c = m.id ? cards.get(m.id) : null;
    if (!c) return null;
    return c.status === "loading" || c.status === "unread" ? "reading" : "on";
  };
  // Its card's place is held from the click, under the card that spawned it
  // at the spawn and wired to the spawn's cell, while its session is found;
  // found, the session takes that place.
  const mountAgent = (nd) => {
    const card = cards.get(nd.key);
    const lane = card?.model?.laneById?.get(nd.laneId);
    const threads = threadsFor(nd.key);
    if (!threads?.loadLaneTurns || !lane) {
      setMount(nd.id, { status: "missed", note: "Its session cannot be searched for from here." });
      return;
    }
    const recordTurns = nd.key === FOCAL ? turns : card?.read?.turns;
    const t = lane.turns?.[0];
    const spawnMs = Number.isFinite(card?.times?.[t]) ? card.times[t] : null;
    const held = `land:${nd.id}`;
    holdLanding(held, { key: nd.key, lane: nd.laneId, turn: t }, { title: nd.label || "", startMs: spawnMs, status: "finding" });
    setMount(nd.id, { status: "finding", label: nd.label || "" });
    const missed = (note) => {
      setMount(nd.id, { status: "missed", note });
      missLanding(held, note);
    };
    Promise.resolve(threads.loadLaneTurns(lane, {
      anchorMs: spawnMs,
      anchorSpan: spawnWindowFromTurns(recordTurns, lane),
      sessionFromMs: Number.isFinite(card?.firstMs) ? card.firstMs : null,
    })).then((sub) => {
      const id = sub?.turns && sub.traceId ? String(sub.traceId).toLowerCase() : null;
      if (!id) {
        missed(threadMissText(sub?.miss, { retry: "Try again once the board has settled." }));
        return;
      }
      setMount(nd.id, { status: "found", id });
      handLanding(held, id);
      onLayer?.([{ id, title: nd.label || "" }]);
    }, () => missed("Its session could not be read."));
  };
  const pullOut = (id, on) => {
    setStandOver((cur) => {
      const next = new Map(cur);
      next.delete(id);
      return next.set(id, on);
    });
    setHeldId(null);
    setHoverNodeDom(null);
  };
  const unpin = (id) => {
    pullOut(id, false);
    setFreePins((cur) => { if (!cur.has(id)) return cur; const next = new Set(cur); next.delete(id); return next; });
  };
  // An agent's session laid on in one go, from its tag's list or the dock;
  // once it is on the board, brought into view.
  const layAgent = (nd) => {
    const m = agentMounts.get(nd.id);
    if (m?.status === "finding") return;
    if (m?.id && cards.has(m.id)) requestWorkspaceFocus(m.id);
    else mountAgent(nd);
  };
  // What an agent's lay-on says it does: lay it on, or show it once it is on.
  const layWords = (nd) => {
    const m = agentMounts.get(nd.id);
    return m?.status === "finding" ? "Finding it" : m?.id && cards.has(m.id) ? "Show it" : "Lay on";
  };
  // An attachment by its id, from what the board lists under its cards.
  const attachmentOf = (id) => {
    for (const list of nodesByCard.values()) {
      const nd = list.find((n) => n.id === id);
      if (nd) return nd;
    }
    return null;
  };
  // A cell picked for what is about it, as a click on it picks it: read in
  // the page's dock, opened on `tab` there (the message itself, "turn"), or
  // beside its card where the board has no dock.
  const pickAnchor = (key, a, tab = null) => {
    if (!a) return;
    let turns = [a.turn];
    let bucket = null;
    for (const [b, ts] of geo?.cards?.get(key)?.lanes?.get(a.lane)?.bucketTurns || []) {
      if (ts.includes(a.turn)) { bucket = b; turns = ts; break; }
    }
    const pick = { laneId: a.lane, turn: a.turn, turns };
    setSel({ key, ...pick });
    const click = ++cellClicks.current;
    if (onCellPick) {
      setDetail(null);
      onCellPick(cellPickOf(key, { ...pick, click, tab }));
    } else if (bucket != null) setDetail({ kind: "cell", key, ...pick, bucket, click });
  };
  // An attachment opened: its card stands under its card, and the message it
  // is about opens in the dock (`pick` false leaves the dock as it is, as a
  // chat's opening does).
  const openAttachment = (id, { pick = true } = {}) => {
    const nd = attachmentOf(id);
    if (!nd) return;
    pullOut(id, true);
    if (pick) pickAnchor(nd.key, (nd.anchors || []).find((a) => a.key === nd.key), "turn");
  };
  // Its one control: a kept reading comes off (its cell can be kept again),
  // the reader's own pin is unpinned from its cell, and anything else closes,
  // back to its card's tag, its mark on its cell, or its pin.
  const takeOffAttachment = (id) => {
    if (heldId === id) setHeldId(null);
    if (hoverNodeDom === id) setHoverNodeDom(null);
    const mine = readerPinOf(id);
    if (mine) {
      const note = (looseNotes || []).find((x) => x.id === mine.note);
      if (note) onLooseNote?.(note.id, { links: (note.links || []).filter((l) => l.id !== mine.link) });
      return;
    }
    if (attachmentOf(id)?.kind === "reading") setReadings((cur) => cur.filter((r) => r.id !== id));
    else unpin(id);
  };
  // A pinned block taken by its head: carried as a card is, the pointer held
  // by the canvas so the carry runs through its own handlers.
  const grabPin = (e, nd) => {
    if (e.button !== 0 || !nd.pinKey) return;
    const cv = canvasRef.current;
    if (!cv) return;
    e.preventDefault();
    const r = cv.getBoundingClientRect();
    const p = { x: e.clientX - r.left, y: e.clientY - r.top };
    dragRef.current = { x: p.x, y: p.y, vx: view.x, vy: view.y, moved: false, hit: { type: "pinhead", key: nd.pinKey }, lastX: p.x };
    cv.setPointerCapture?.(e.pointerId);
  };
  // A drag ends wherever its button comes up. The canvas (or a handle) hears
  // the release only while it holds the pointer, and that hold can be lost: a
  // handle drawn again, the window losing focus, a release outside the window.
  // The window hears the rest, and a move with no button down means a release
  // went unheard; either way the drag ends where it got to (a press that
  // never moved does nothing), and the scroll at the canvas's edge stops.
  const strayEndNow = useRef(null);
  strayEndNow.current = () => {
    if (rzRef.current) endResize();
    const d = dragRef.current;
    if (movingRef.current || d?.moved) onPointerUp({ clientX: NaN, clientY: NaN });
    dragRef.current = null;
    if (panning) setPanning(false);
  };
  useEffect(() => {
    const busy = () => !!(rzRef.current || dragRef.current || movingRef.current);
    const up = () => { if (busy()) strayEndNow.current(); };
    const move = (e) => { if (e.buttons === 0 && busy()) strayEndNow.current(); };
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("blur", up);
    window.addEventListener("pointermove", move);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("blur", up);
      window.removeEventListener("pointermove", move);
    };
  }, []);
  // A thing on the board as a chat is handed it ("Add to chat"): a note or
  // a walkthrough's moment, a finding, a claim, a sub-agent, a cell's
  // reading kept on the board; where it is (its trace, message, turn and
  // lane), and what it says.
  const chatItemOf = (nd) => {
    if (!nd) return null;
    const card = cards.get(nd.key);
    const session = String(nd.key === FOCAL ? focalSessionId || "" : nd.key).toLowerCase();
    const recs = nd.key === FOCAL ? turns : card?.read?.turns;
    const a = nd.anchors?.[0] || null;
    // Its own message where it names one (a note's, a finding's, where a
    // sub-agent was started), not the charted cell it stands on beside it.
    const turn = Number.isInteger(nd.turn) ? nd.turn : Number.isInteger(a?.turn) ? a.turn : null;
    const placed = turn != null && !nd.earlier;
    const src = nd.kind === "note" ? (notes || []).find((n) => n.id === nd.id) || null : null;
    const lane = a?.lane != null ? card?.model?.laneById?.get(a.lane) || null : null;
    const where = {
      session: session || null,
      sessionTitle: card?.titleText || null,
      messageId: src?.messageId || (placed ? recs?.[turn]?.messageId : null) || null,
      turnLabel: placed ? (card?.turnBase || 0) + turn : null,
      laneLabel: lane?.label || nd.laneLabel || null,
    };
    const dot = (parts) => parts.filter(Boolean).join(" \u00b7 ");
    if (nd.kind === "note") {
      const moment = !!walkthroughKey && nd.chat?.key === walkthroughKey && Number.isInteger(nd.n);
      const first = String(nd.text || "").split(/(?<=[.!?])\s/)[0];
      // What a moment covers goes with it: the claims as the board numbers
      // them now, and the case's notes in their words.
      const covers = moment ? (nd.covers || []).map((c) => (c.kind === "claim"
        ? `Claim ${claimsById.get(String(c.id).toLowerCase())?.n ?? c.n ?? "?"}`
        : `the case's note "${c.text || ""}"`)) : [];
      return { ...where, kind: moment ? "moment" : "note", key: `note:${nd.id}`, label: moment ? `Moment ${nd.n}` : first, text: covers.length ? dot([nd.text, `covers ${covers.join(", ")}`]) : nd.text, ref: nd.id };
    }
    if (nd.kind === "finding") return { ...where, kind: "finding", key: `finding:${nd.id}`, label: nd.title, text: dot([nd.severity, nd.rationale, ...(nd.quotes || [])]), ref: nd.id };
    if (nd.kind === "claim") return { ...where, kind: "claim", key: `claim:${String(nd.id).toLowerCase()}`, label: Number.isInteger(nd.n) ? `Claim ${nd.n}` : "Claim", text: dot([nd.statement, nd.status]), ref: String(nd.id).replace(/^claim:/, "") };
    if (nd.kind === "agent") {
      const own = agentMounts.get(nd.id)?.id || null;
      return { ...where, kind: "agent", key: `agent:${nd.id}`, label: nd.label || nd.agentType || "Sub-agent", text: dot([nd.agentType, nd.prompt, own ? `its own session ${own}` : ""]), ref: nd.laneId || nd.id };
    }
    if (nd.kind === "reading") return { ...where, kind: "cell", key: `reading:${nd.id}`, label: where.turnLabel != null ? `T${where.turnLabel}${where.laneLabel ? ` ${where.laneLabel}` : ""}` : "A message", text: "" };
    return null;
  };
  const attachActsNow = useRef(null);
  attachActsNow.current = {
    open: openAttachment, close: takeOffAttachment, shut: unpin, grab: grabPin, mount: mountAgent, lay: layAgent, show: (id) => requestWorkspaceFocus(id),
    cell: (nd, a) => pickAnchor(nd.key, a, "turn"),
    chat: (nd) => { const item = chatItemOf(nd); if (item) addToChat(item); },
  };
  // What a chat opens or closes on the board in front (workspace_open): its
  // cards open as the reader's do, leaving the dock where it is.
  const openAsk = useWorkspaceOpenAsk();
  const openAskSeen = useRef(openAsk.n);
  useEffect(() => {
    if (openAsk.n === openAskSeen.current) return;
    openAskSeen.current = openAsk.n;
    if (!publishing) return;
    for (const id of openAsk.ids) {
      if (openAsk.open) attachActsNow.current.open(id, { pick: false });
      else attachActsNow.current.shut(id);
    }
  }, [openAsk, publishing]);
  // The chat's claims listed along the board's foot: the ones its latest
  // answer cites, in the answer's order, where that answer rests on this
  // board's graph (or the answer a ref was clicked in); else all of them. The
  // claims this board can show go out for the chat to link refs to them only.
  useEffect(() => {
    if (!publishing) return undefined;
    publishBoardClaims(investigation?.claimIds || null);
    return () => publishBoardClaims(null);
  }, [publishing, investigation?.claimIds]);
  // A claim picked: from its card along the foot, its badge on the strip, or
  // a ref to it in the chat.
  const picks = useRef(0);
  const refocus = () => setEvidenceFocus(true);
  const unfocus = () => {
    const back = preFocusCam.current;
    preFocusCam.current = null;
    if (back) glideTo(back);
  };
  const pickClaimNow = useRef(null);
  pickClaimNow.current = (id) => {
    if (!evidenceOn) refocus();
    picks.current += 1;
    setEvidenceOn(true);
    setEvidencePick({ id: String(id).toLowerCase(), n: picks.current });
  };
  // A claim asked for (its badge on the board, a ref to it in the chat's
  // answer, or a moment that covers it) opens in the claims' list, in the
  // answer's words: the chat's evidence is the chat's, whatever walkthrough
  // stands beside it. The moments that cover it are named on its card. A
  // moment asked for from a claim's card opens in the moments' list.
  // The list is the claims the answer it was asked from cites (`text`), or
  // all of them where it was asked with no answer behind it (a badge, a
  // moment's card), so it always holds the claim asked for.
  const askClaimNow = useRef(null);
  askClaimNow.current = (id, { text = "" } = {}) => {
    setAskedAnswer(text);
    setClaimsListed(true);
    pickClaimNow.current(String(id).toLowerCase());
  };
  const askClaimItself = useCallback((id) => askClaimNow.current(id), []);
  const askMoment = useCallback((id) => {
    setClaimsListed(false);
    pickClaimNow.current(id);
  }, []);
  const evidenceAsk = useClaimEvidenceAsk();
  const evidenceAskSeen = useRef(evidenceAsk.n);
  useEffect(() => {
    if (evidenceAsk.n === evidenceAskSeen.current) return;
    if (!publishing) {
      evidenceAskSeen.current = evidenceAsk.n;
      return;
    }
    // Asked while its graph is still read in: kept until it is.
    if (!investigation) return;
    evidenceAskSeen.current = evidenceAsk.n;
    const id = evidenceAsk.ids.find((x) => investigation.claimIds.has(x));
    if (!id) return;
    askClaimNow.current(id, { text: evidenceAsk.text });
  }, [evidenceAsk, publishing, investigation]);
  const evidenceStrip = useRef(null);
  const pointEvidence = useCallback((id, el) => {
    if (!id || !el) {
      setEvidencePoint(null);
      return;
    }
    const box = evidenceStrip.current?.parentElement?.getBoundingClientRect?.();
    const r = el.getBoundingClientRect();
    const from = box ? { x: r.left + r.width / 2 - box.left, y: r.top - box.top } : null;
    setEvidencePoint((cur) => (cur?.id === id && cur.from?.x === from?.x && cur.from?.y === from?.y ? cur : { id, from }));
  }, []);
  const pickEvidence = useCallback((id) => pickClaimNow.current(id), []);
  // The claim or moment open in the list, handed to the chat in front: its
  // node on the board where it has one (so where it rests goes with it), else
  // what the list says of it.
  const chatEvidenceNow = useRef(null);
  chatEvidenceNow.current = (e) => {
    const nodes = [...nodesByCard.values()].flat();
    const nd = listing === "moments"
      ? momentNodes.find((m) => m.id === e.id)
      : nodes.find((m) => m.kind === "claim" && String(m.id).toLowerCase() === `claim:${e.id}`);
    const item = nd ? chatItemOf(nd) : { kind: listing === "moments" ? "moment" : "claim", key: `${listing}:${e.id}`, label: Number.isInteger(e.n) ? `${listing === "moments" ? "Moment" : "Claim"} ${e.n}` : "", text: "", ref: e.id };
    if (!item) return;
    const said = [e.words !== e.statement ? e.words : "", e.statement || (listing === "moments" ? e.words : "")].filter(Boolean).join(" \u00b7 ");
    addToChat({ ...item, text: [said, item.text].filter(Boolean).join(" \u00b7 ") });
  };
  const chatEvidence = useCallback((e) => chatEvidenceNow.current(e), []);
  // A moment pointed at beside the board (its line in the walkthrough's story
  // in the chat, or a chat's list of its pins) points from its card in the
  // list, the board faded back around it as under the pointer.
  const outsidePoint = useMemo(() => {
    if (!evidenceShown || listing !== "moments") return null;
    return askedPoint && evidence.some((e) => e.id === askedPoint) ? askedPoint : null;
  }, [evidenceShown, listing, askedPoint, evidence]);
  // A moment pointed at in the list lights its line in the walkthrough's
  // story, as its pin does, and lets it go when the pointer leaves or the
  // list turns back to the claims.
  const momentLit = useRef(false);
  useEffect(() => {
    const ids = listing === "moments" && evidencePoint?.id ? [evidencePoint.id] : null;
    if (!ids && !momentLit.current) return;
    momentLit.current = !!ids;
    if (publishing) publishWorkspaceNotesPointed(ids);
  }, [evidencePoint?.id, listing]); // eslint-disable-line react-hooks/exhaustive-deps
  const reportOpenAt = useCallback((p) => setEvidenceOpenAt((cur) => (cur?.x === p?.x && cur?.y === p?.y ? cur : p)), []);
  const closeEvidence = useCallback(() => {
    setEvidencePick(null);
    setEvidencePoint(null);
  }, []);
  // A cell the pointer is on that a listed claim rests on lights its card.
  const claimsAtCell = useMemo(() => {
    const out = new Map();
    for (const [id, pts] of evidencePoints) {
      for (const q of pts) {
        if (!q.cell || q.lane == null) continue;
        const k = `${q.key}|${q.lane}|${q.turn}`;
        if (!out.has(k)) out.set(k, []);
        out.get(k).push(id);
      }
    }
    return out;
  }, [evidencePoints]);
  const cellPointed = hover?.cell ? claimsAtCell.get(`${hover.cell.key}|${hover.cell.laneId}|${hover.cell.turn}`)?.[0] || null : null;
  useEffect(() => {
    if (evidenceShown) return;
    setEvidencePoint(null);
    setEvidencePick(null);
  }, [evidenceShown]);
  // The camera glides from wherever it is to where it is going; the reader
  // moving the board meanwhile takes it back.
  const glide = useRef(null);
  useEffect(() => () => { if (glide.current) cancelAnimationFrame(glide.current.raf); }, []);
  const glideTo = useCallback((to) => {
    if (glide.current) cancelAnimationFrame(glide.current.raf);
    if (!dataGlides) {
      glide.current = null;
      camNow.current = { cam: to.cam, view: to.view };
      setCam(to.cam);
      setViewState(to.view);
      return;
    }
    const { cam: c0, view: v0 } = camNow.current;
    const mid = (v, c) => ({ x: v.x + planeW / (2 * c), y: v.y + planeH / (2 * c) });
    const a = mid(v0, c0);
    const b = mid(to.view, to.cam);
    const t0 = performance.now();
    const g = { raf: 0, last: null };
    glide.current = g;
    touched.current = true;
    const frame = (now) => {
      if (glide.current !== g) return;
      const was = g.last;
      const cur = camNow.current;
      if (was && (Math.abs(cur.cam - was.cam) > 1e-6 || Math.abs(cur.view.x - was.view.x) > 0.5 || Math.abs(cur.view.y - was.view.y) > 0.5)) {
        glide.current = null;
        return;
      }
      const u = Math.min(1, (now - t0) / PICK_GLIDE_MS);
      const e = u < 0.5 ? 4 * u * u * u : 1 - ((2 - 2 * u) ** 3) / 2;
      const c = c0 * (to.cam / c0) ** e;
      const v = u >= 1 ? to.view : { x: a.x + (b.x - a.x) * e - planeW / (2 * c), y: a.y + (b.y - a.y) * e - planeH / (2 * c) };
      camNow.current = { cam: c, view: v };
      g.last = camNow.current;
      setCam(c);
      setViewState(v);
      if (u < 1) g.raf = requestAnimationFrame(frame);
      else glide.current = null;
    };
    g.raf = requestAnimationFrame(frame);
  }, [planeW, planeH]);
  // Focused on the evidence, the camera comes once to the column of cards,
  // when the board has read them: at the board's own size, at its top.
  const focusFitDue = useRef(false);
  useEffect(() => { focusFitDue.current = !!focusOn; }, [focusOn, listing]);
  useEffect(() => {
    if (!focusFitDue.current || reading || !focusLanes?.size) return;
    // Only once the board has its own size to lay the column out in.
    if (size.w < 320 || planeH - (evidenceStrip.current?.offsetHeight || EVIDENCE_H) < 120) return;
    const top = layout.items.filter((it) => it.kind === "card" && it.focus);
    if (!top.length) return;
    focusFitDue.current = false;
    const y0 = Math.min(...top.map((it) => it.y));
    if (!preFocusCam.current) preFocusCam.current = { cam: camNow.current.cam, view: { ...camNow.current.view } };
    glideTo({ cam: 1, view: looseView({ x: PAD_L - FOCUS_EDGE, y: y0 - FOCUS_EDGE }, 1) });
  }, [layout, reading, focusLanes, size.w, size.h]); // eslint-disable-line react-hooks/exhaustive-deps
  // A claim picked comes into view where nothing it rests on is in sight
  // above the strip, the camera keeping its zoom; the message it rests on is
  // read in the dock. A moment standing in for a message its card has not
  // read in yet reads nothing there: its page is read in instead (below).
  const pickedEarlier = listing === "moments" && !!evidencePick && !!momentNodes.find((nd) => nd.id === evidencePick.id)?.earlier;
  useEffect(() => {
    if (!evidencePick) return;
    const pts = stRef.current.evidencePoints?.get(evidencePick.id) || [];
    if (!pts.length) return;
    const { cam: c, view: v } = camNow.current;
    const footH = planeH - (evidenceStrip.current?.offsetHeight || EVIDENCE_H);
    const seen = (q) => {
      const sx = (q.x - v.x) * c;
      const sy = (q.y - v.y) * c;
      return sx >= 0 && sx <= planeW && sy >= 0 && sy <= footH;
    };
    if (!pts.some(seen)) glideTo({ cam: c, view: looseView({ x: pts[0].x - planeW / (2 * c), y: pts[0].y - footH / (2 * c) }, c) });
    const cell = pts.find((q) => q.cell && q.lane != null && Number.isInteger(q.turn));
    if (cell && !pickedEarlier) pickAnchor(cell.key, { lane: cell.lane, turn: cell.turn }, "turn");
  }, [evidencePick]); // eslint-disable-line react-hooks/exhaustive-deps
  // A list opened from a tag stands where it was clicked: the board moving
  // under it closes it.
  useEffect(() => { setTagList(null); }, [view.x, view.y, cam]);
  const attachActs = useMemo(() => ({
    open: (id) => attachActsNow.current.open(id),
    close: (id) => attachActsNow.current.close(id),
    grab: (e, nd) => attachActsNow.current.grab(e, nd),
    mount: (nd) => attachActsNow.current.mount(nd),
    show: (id) => attachActsNow.current.show(id),
    cell: (nd, a) => attachActsNow.current.cell(nd, a),
    chat: (nd) => attachActsNow.current.chat(nd),
  }), []);
  // The attachments on the board, made again only when what they show
  // changes: scrolling down or up moves the layer they sit in and nothing else.
  // The cards in the order they stand over each other, and for each the
  // footprints of those over it: what hangs from a card (its chips, its
  // pins) shows only where none of them lies. The card in hand is drawn over
  // them all where the pointer has it, on the canvas above the board, which
  // hides what lies under it there; its place here would be where the board
  // last laid it.
  const covers = useMemo(() => (geo?.cards ? coversOf(stackOrder([...geo.cards.values()].filter((c) => c.key !== moving?.key), { zOf, front: moving?.key || dimKey, sizing: resizing?.key || null })) : null), [geo, zOf, dimKey, moving?.key, resizing?.key]);
  const chipLayer = useMemo(() => (geo?.nodes?.length ? chipsByCard(geo.nodes.map((nd) => {
    const mount = nd.kind === "agent" ? agentMounts.get(nd.id) || null : null;
    return (
      <AttachmentCard
        key={nd.id} node={nd} card={cards.get(nd.key)} turns={nd.key === FOCAL ? turns : cards.get(nd.key)?.read?.turns}
        x={nd.x} y={nd.y} w={nd.w} lifted={moving?.key === nd.key || (!!nd.pinKey && moving?.key === nd.pinKey)}
        pinned onClose={attachActs.close} onHeight={onPinHeight} onGrab={attachActs.grab} onCell={attachActs.cell}
        mount={mount} mountState={mountStateOf(nd)} onMount={nd.kind === "agent" ? attachActs.mount : null} onShowSession={attachActs.show}
        onChat={attachActs.chat}
      />
    );
  }), geo.nodes, { covers, front: moving?.key || dimKey }) : null),
  [geo, cards, turns, moving?.key, attachActs, onPinHeight, agentMounts, covers, dimKey]);
  // The notes' pins, made again only when what they show changes: panning
  // moves the layer they stand in.
  const pinActsNow = useRef(null);
  pinActsNow.current = (g) => {
    if (g.members.every((m) => m.earlier)) handleActsNow.current.earlier(g.key);
    // One in a session inside a workspace this one holds has no card here
    // to open under: a click keeps what it says open by its pin.
    else if (g.members.length > 1 || g.members[0].inside) setHeldId((cur) => (cur === g.id ? null : g.id));
    else if (g.members[0].standing) unpin(g.members[0].id);
    else openAttachment(g.members[0].id);
  };
  const onPinPick = useCallback((g) => pinActsNow.current(g), []);
  // A note opened from beside the board: its pin brought into view, as little
  // as that takes, and its note opened under its card, as a click on the pin
  // opens it. One whose pin is not placed yet waits for it; one on a page the
  // card has not read has that page read in, a page at a time, until it is.
  // Asked while this is the board in front, from outside it; each ask is
  // opened once, the newest in place of any waiting.
  const [openWanted, setOpenWanted] = useState(null);
  const wantSeq = useRef(0);
  // A moment picked in the list over the board that stands in for a message
  // not read in yet opens as its line in the walkthrough's story does.
  useEffect(() => {
    if (!pickedEarlier) return;
    wantSeq.current += 1;
    setOpenWanted({ id: evidencePick.id, n: wantSeq.current });
  }, [evidencePick, pickedEarlier]);
  const askOpenSeen = useRef(noteAsk.open.n);
  useEffect(() => {
    if (noteAsk.open.n === askOpenSeen.current) return;
    askOpenSeen.current = noteAsk.open.n;
    if (!publishing || !noteAsk.open.id) return;
    wantSeq.current += 1;
    setOpenWanted({ id: noteAsk.open.id, n: wantSeq.current });
  }, [noteAsk.open, publishing]);
  const openSeen = useRef(0);
  const earlierAsked = useRef(null);
  useEffect(() => {
    if (!openWanted || openWanted.n === openSeen.current) return;
    const p = allPins.find((x) => x.id === openWanted.id);
    if (!p) return;
    if (p.earlier) {
      const more = earliers.get(p.key);
      if (!more) { openSeen.current = openWanted.n; return; }
      const held = p.key === FOCAL ? turns.length : cards.get(p.key)?.read?.turns?.length || 0;
      const asked = `${openWanted.n}:${held}`;
      if (more.busy || earlierAsked.current === asked) return;
      earlierAsked.current = asked;
      handleActsNow.current.earlier(p.key);
      return;
    }
    openSeen.current = openWanted.n;
    const pad = 80 / cam;
    const fit = (at, from, span) => (at - from < pad ? at - pad : at - from > span - pad ? at - (span - pad) : from);
    touched.current = true;
    setView({ x: fit(p.x, view.x, viewW - coverR / cam), y: fit(p.y, view.y, viewH) });
    if (p.inside) setHeldId(pinGroups.find((g) => g.members.some((m) => m.id === p.id))?.id ?? p.id);
    else if (!p.standing) pullOut(p.id, true);
  }, [openWanted, allPins, pinGroups, earliers, cam, view.x, view.y, viewW, viewH, coverR]); // eslint-disable-line react-hooks/exhaustive-deps
  // A message asked for from outside the board in front (a finding opened
  // from the welcome page's lists, at its message): once its card holds it
  // (read in a page at a time, as a note on an earlier page is), its cell, or
  // the nearest charted one, is picked and read in the dock, and brought into
  // view as little as that takes. One its card cannot reach is let go.
  const messageAsk = useWorkspaceMessageAsk();
  const messageEarlierAsked = useRef(null);
  useEffect(() => {
    if (!publishing || !messageAskOpen(messageAsk)) return;
    const focal = String(focalSessionId || "").toLowerCase();
    const key = messageAsk.session === focal ? FOCAL : cards.has(messageAsk.session) ? messageAsk.session : null;
    const c = key ? cards.get(key) : null;
    if (!c?.model) return;
    const recs = key === FOCAL ? turns : c.read?.turns;
    const turn = (recs || []).findIndex((t) => String(t?.messageId || "").toLowerCase() === messageAsk.messageId);
    if (turn < 0) {
      const more = earliers.get(key);
      if (!more || more.failed) { settleWorkspaceMessage(messageAsk.n); return; }
      const asked = `${messageAsk.n}:${recs?.length || 0}`;
      if (more.busy || messageEarlierAsked.current === asked) return;
      messageEarlierAsked.current = asked;
      handleActsNow.current.earlier(key);
      return;
    }
    const lanes = c.model.lanes || [];
    let lane = null;
    let at = turn;
    for (let d = 0; d <= 4 && !lane; d++) {
      for (const t of d ? [turn - d, turn + d] : [turn]) {
        const hit = lanes.find((l) => l.cells?.has(t) && isConversationDomain(l.domain)) || lanes.find((l) => l.cells?.has(t));
        if (hit) { lane = hit; at = t; break; }
      }
    }
    if (!lane) { settleWorkspaceMessage(messageAsk.n); return; }
    const cg = geo?.cards?.get(key);
    if (!cg) return;
    settleWorkspaceMessage(messageAsk.n);
    pickAnchor(key, { lane: lane.id, turn: at }, "turn");
    const p = geo.cellAt?.(cg, lane.id, at);
    if (!p) return;
    const pad = 80 / cam;
    const fit = (to, from, span) => (to - from < pad ? to - pad : to - from > span - pad ? to - (span - pad) : from);
    touched.current = true;
    setView({ x: fit(p.x, view.x, viewW - coverR / cam), y: fit(p.y, view.y, viewH) });
  }, [messageAsk, publishing, cards, turns, earliers, geo, focalSessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Each of the reader's pins tied to its note: a line from the note's edge
  // nearest the pin to the pin's needle, in the board's units; lit while the
  // note or the pin is pointed at.
  const noteLines = useMemo(() => {
    const boxes = new Map(drawnLayout.items.filter((it) => it.kind === "note").map((it) => [it.id, it]));
    const out = [];
    for (const p of geo?.pins || []) {
      const mine = readerPinOf(p.id);
      const box = mine ? boxes.get(mine.note) : null;
      if (!box) continue;
      const tx = p.x;
      const ty = p.y - RING_HALF;
      out.push({ id: p.id, note: mine.note, key: p.key, box: { x: box.x, y: box.y, w: box.w, h: box.h }, fx: Math.max(box.x, Math.min(box.x + box.w, tx)), fy: Math.max(box.y, Math.min(box.y + box.h, ty)), tx, ty });
    }
    return out;
  }, [geo, drawnLayout.items]);
  noteLinesNow.current = noteLines;
  const draftLine = useMemo(() => {
    const box = linkDraft ? drawnLayout.items.find((it) => it.kind === "note" && it.id === linkDraft.id) : null;
    if (!box) return null;
    return { fx: Math.max(box.x, Math.min(box.x + box.w, linkDraft.x)), fy: Math.max(box.y, Math.min(box.y + box.h, linkDraft.y)), tx: linkDraft.x, ty: linkDraft.y };
  }, [linkDraft, drawnLayout.items]);
  const pinnedCount = useMemo(() => new Map((looseNotes || []).map((n) => [n.id, n.links?.length || 0])), [looseNotes]);
  const pinFront = moving?.key || dimKey;
  // The pins on the card in hand stand over it, carried with it (moveCarried);
  // the rest under the canvas it is drawn on, so it hides those it lies over.
  const carriedPins = moving && !moving.pin && geo?.cards?.has(moving.key) ? moving.key : null;
  const pinLayer = useMemo(() => {
    const pin = (g) => (
      <NotePin key={g.id} group={g} hot={hoverNode === g.id || g.members.some((m) => m.id === hoverNode)} held={heldId === g.id} faded={!!pinFront && g.key !== pinFront}
        covered={inRects(covers?.get(g.key), g.members[0].x, g.members[0].y)} onHover={setHoverNodeDom} onPick={onPinPick} />
    );
    return {
      under: pinGroups.filter((g) => g.key !== carriedPins).map(pin),
      carried: carriedPins ? pinGroups.filter((g) => g.key === carriedPins).map(pin) : [],
    };
  }, [pinGroups, hoverNode, heldId, onPinPick, pinFront, covers, carriedPins]);
  // What a pin says, open by it: over its head, or under its cell's ring.
  const pinPeekAt = (g) => {
    const ax = MACH_W + g.sx - view.x * cam;
    const ay = g.sy - view.y * cam;
    const top = ay - pinNeedle(g) - PIN_HEAD;
    const bottom = ay + 2 * RING_HALF * cam;
    const layerH = size.h - RULER_H;
    return {
      left: Math.max(MACH_W + 4, Math.min(size.w - GROWN_W - 4, ax - GROWN_W / 2)),
      top, bottom, layerH,
      below: Math.min(GROWN_H, layerH - (bottom + PEEK_GAP) - 8 - CONTROLS_CLEAR),
      above: Math.min(GROWN_H, top - PEEK_GAP - 8),
    };
  };
  const peekPin = pinGroups.find((g) => g.id === heldId) || pinGroups.find((g) => g.id === hoverNodeDom) || null;
  // A tag's list (or a mark's), under where it was clicked.
  const tagListAt = (t) => {
    const layerH = size.h - RULER_H;
    const top = (t.at?.y ?? RULER_H) - RULER_H - 10;
    const bottom = top + 20;
    return {
      left: Math.max(MACH_W + 4, Math.min(size.w - GROWN_W - 4, (t.at?.x ?? MACH_W) - 16)),
      top, bottom, layerH,
      below: Math.min(GROWN_H, layerH - (bottom + PEEK_GAP) - 8 - CONTROLS_CLEAR),
      above: Math.min(GROWN_H, top - PEEK_GAP - 8),
    };
  };
  const tagMembers = tagList ? (nodesByCard.get(tagList.key) || []).filter((nd) => TAG_KINDS.has(nd.kind) && (!tagList.ids || tagList.ids.includes(nd.id))) : [];
  // Agents whose sessions are being found, for the board's status line.
  const finding = [...agentMounts.values()].filter((m) => m.status === "finding");
  const settling = reading || focalWork || joinStatus === "loading" || finding.length > 0 || choosing || !!boardBar?.status;
  const handleActs = useMemo(() => Object.fromEntries(
    ["startResize", "moveResize", "endResize", "resetSize", "toggleGroups", "takeOff", "fold", "fit", "anchor", "branch", "enter", "toolbarEnter", "toolbarLeave", "earlier"]
      .map((name) => [name, (...args) => handleActsNow.current[name](...args)]),
  ), []);

  // A preview's host that draws over its card is told where the card's pins
  // and moments sit, in the preview's own pixels: each host pin (by its
  // title), the x of any turn, and the cell under a point.
  const onAnchors = preview?.onAnchors || null;
  useEffect(() => {
    if (!onAnchors) return;
    const cg = placed?.cards?.get(FOCAL);
    if (!cg || cg.folded || !cg.scene) { onAnchors(null); return; }
    const ox = -view.x;
    const oy = -view.y;
    const pins = (cg.scene.index?.pins || []).filter((p) => p.kind === "fpin").map((p) => ({
      x: cg.x + (p.x0 + p.x1) / 2 + ox, y: cg.bodyY + (p.y0 + p.y1) / 2 + oy, title: p.title || "", tone: p.tone,
    }));
    const xOfTurn = (turn) => {
      const { bucket, side } = bucketAt(cg.tu?.[turn], cg.win, cg.n);
      if (bucket == null) return side === "before" ? cg.x + cg.rail + ox : side === "after" ? cg.x + cg.w + ox : null;
      return cg.x + cg.rail + (bucket + 0.5) * cg.cellW + ox;
    };
    // The cell under a point: its lane, the turns its column stands for, and
    // its middle, or null off every cell.
    const cellAt = (px, py) => {
      const x = px - ox - cg.x;
      const y = py - oy - cg.bodyY;
      const cell = (cg.scene.index?.cells || []).find((c) => x >= c.x0 - 1 && x <= c.x1 + 1 && y >= c.y0 && y <= c.y1);
      if (!cell) return null;
      const col = Math.max(cell.first, Math.min(cell.last, Math.floor((x - cg.rail) / cg.cellW)));
      // The column under the point, else the nearest of the cell's with turns.
      const cols = [col, ...Array.from({ length: cell.last - cell.first + 1 }, (_, i) => cell.first + i).sort((a, b) => Math.abs(a - col) - Math.abs(b - col))];
      for (const b of cols) {
        const got = resolvePick(cg, { laneId: cell.laneId, laneIds: cell.laneIds, turn: b });
        if (got) return { ...got, x: cg.x + cg.rail + (b + 0.5) * cg.cellW + ox, y: cg.bodyY + (cell.y0 + cell.y1) / 2 + oy };
      }
      return null;
    };
    onAnchors({ width: size.w || 0, pins, xOfTurn, cellAt, body: { left: cg.x + cg.rail + ox, right: cg.x + cg.w + ox, top: cg.bodyY + oy, bottom: cg.y + cg.h + oy } });
  }, [onAnchors, placed, view, size.w]);

  // A preview (the trace page's): the anchor's card on the board's ground as
  // the workspace draws it, as tall as it is (its frame's hairlines on top),
  // with one button over the whole of it that opens the workspace; the
  // board's own hands stand aside. In hand, the card fades toward the page's
  // ground (darker on a dark page, paler on a light one; the trace still
  // reads through it), outlined as the workspace's frame is while the page
  // around it is pointed at, and its head shows the detail header's
  // Workspace control at its end, as a board card shows its buttons there,
  // its name giving way to it.
  if (preview) {
    const fc = layout.items.find((it) => it.kind === "card" && it.key === FOCAL);
    const fcRight = fc ? Math.max(fc.x + fc.w, (fc.headX ?? fc.x) + (fc.headW ?? fc.w)) : 0;
    const dim = fc ? { left: Math.round(fc.x - view.x), top: Math.round(fc.y - view.y), width: Math.round(fcRight - fc.x), height: Math.round(fc.h) } : null;
    const cue = fc ? {
      top: Math.round(fc.y - view.y + 3),
      right: Math.round((size.w || 0) - (fcRight - view.x) + 6),
    } : null;
    return (
      <div className="relative min-w-0 overflow-hidden rounded-[2px] border border-[color:var(--line-soft)] hover:border-[color:var(--line-strong)]" style={{ height: Math.max(48, Math.ceil(layout.h || 0)) + 2 }} data-session-canvas="" data-workspace-preview="">
        {machines.filter((m) => m.id !== "unknown" && m.id !== "added").map((m) => <EndpointName key={m.id} id={m.id} onName={onName} />)}
        <div ref={wrapRef} className="absolute inset-0" data-session-surface="">
          <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 block" aria-hidden="true" />
        </div>
        {preview.onOpen ? <button
          type="button"
          onClick={preview.onOpen}
          onMouseEnter={() => setPreviewHot(true)}
          onMouseLeave={() => setPreviewHot(false)}
          onFocus={() => setPreviewHot(true)}
          onBlur={() => setPreviewHot(false)}
          aria-label="Open this session in a workspace of its own, over the page"
          className="absolute inset-0 cursor-pointer bg-transparent focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--green)]"
          data-workspace-preview-open=""
        /> : null}
        {previewHot && dim ? (
          <span aria-hidden="true" className="pointer-events-none absolute rounded-[2px] border border-[color:color-mix(in_srgb,var(--muted)_55%,transparent)] bg-[color:color-mix(in_srgb,var(--bg)_42%,transparent)] backdrop-saturate-[0.5]" style={dim} data-workspace-preview-dim="" />
        ) : null}
        {/* The control as the detail header draws it, its tint over the page's
            ground rather than over the dim. */}
        {previewHot && cue ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inline-flex h-[20px] items-center gap-[5px] border border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] px-[8px] text-[11px] leading-[1] tracking-[0.06em] uppercase text-[color:var(--ink)]"
            style={cue}
            data-workspace-preview-cue=""
          >
            Workspace
            <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 3h4.5v4.5M9 3 3.5 8.5" /></svg>
          </span>
        ) : null}
      </div>
    );
  }

  // A still picture: the board's cards drawn whole into the room it has, its
  // notes over them as written; nothing to take hold of.
  if (still) {
    return (
      <div className="relative h-full w-full min-h-0 min-w-0 overflow-hidden transition-opacity duration-150" style={{ opacity: stillShown ? 1 : 0 }} data-session-canvas="" data-board-still="">
        {machines.filter((m) => m.id !== "unknown" && m.id !== "added").map((m) => <EndpointName key={m.id} id={m.id} onName={onName} />)}
        <div ref={wrapRef} className="absolute inset-0" data-session-surface="">
          <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 block" aria-hidden="true" />
        </div>
        {drawnLayout.items.some((it) => it.kind === "note") ? (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" data-loose-notes="">
            <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
              {drawnLayout.items.filter((it) => it.kind === "note").map((it) => (
                <div key={it.key} className="absolute flex flex-col overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)]" style={{ left: it.x, top: it.y, width: it.w, height: it.h }} data-loose-note={it.id}>
                  <div className="flex h-[22px] flex-none items-center border-b border-[color:var(--line-soft)] px-[8px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--eyebrow)]">Note</div>
                  <div className="min-h-0 flex-1 whitespace-pre-wrap px-[8px] py-[6px] text-[12.5px] leading-[17px] text-[color:var(--ink)]">{it.text}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  barNow.current = {
    putBack,
    clearLit: () => { if (lit) handoff.toggle(lit.key, lit.follow); },
    follow: (e) => boardBar?.onFollow?.(e),
    walk: walkFromBar,
    toggleList: (as, shown) => {
      if (shown) {
        unfocus();
        setEvidenceOn(false);
        return;
      }
      if (!evidenceOn) refocus();
      setClaimsListed(as === "claims");
      setEvidencePick(null);
      setEvidenceOn(true);
    },
    clear: clearBoard,
    note: (e) => boardBar?.onNote?.(e),
    add: (e) => boardBar?.onAdd?.(e),
    tidy: tidyUp,
    byTime: arrangeByTime,
    everything: showEverything,
    layIt: () => { if (onLayer && sel?.key) onLayer([sel.key]); },
    zoomOut: () => zoomCam(cam / 1.4),
    zoomReset: () => zoomCam(1),
    zoomIn: () => zoomCam(cam * 1.4),
    close: (e) => onClose?.(e),
  };
  const barStatus = settling ? [
    boardBar?.status || null,
    reading ? (() => {
      const all = [...cards.values()];
      const left = all.filter((c) => c.status === "loading" || c.status === "unread").length;
      return `${left} of ${all.length} traces still reading; the board settles once they are in`;
    })() : null,
    finding.length === 1 ? `Finding the session of ${finding[0].label || "an agent"}` : finding.length ? `Finding the sessions of ${finding.length} agents` : null,
    focalWork ? `Reading the anchor's shell calls${focalWork.calls ? ` (${Math.min(focalWork.done, focalWork.calls)} of ${focalWork.calls})` : ""}` : null,
    joinStatus === "loading" ? "Finding what the traces share" : null,
    choosing && joinStatus !== "loading" ? (() => {
      const read = candidates.filter((r) => reads.get(r.id)?.status === "done" || reads.get(r.id)?.status === "error").length;
      return `Reading the strongest related sessions (${read} of ${candidates.length})`;
    })() : null,
  ].filter(Boolean).join(DOT) : "";

  return (
    // Its words are labels, not text to select: a card carried, a strip
    // dragged or a double-click over them would select them otherwise.
    <div className="flex min-h-0 min-w-0 flex-1 select-none flex-col" data-session-canvas="" onPointerDownCapture={letGoOfSelection}>
      {machines.filter((m) => m.id !== "unknown" && m.id !== "added").map((m) => <EndpointName key={m.id} id={m.id} onName={onName} />)}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[2px] border border-[color:var(--line)]">
        <div ref={wrapRef} className="relative min-h-0 min-w-0 flex-1" data-session-surface="" onPointerDownCapture={() => { touched.current = true; }}>
          <canvas
            ref={canvasRef}
            className="absolute inset-0 block focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--green)]"
            style={{ cursor: panning || moving ? "grabbing" : hover?.head ? "grab" : hover?.reset ? "pointer" : hover?.strip === "edge-l" || hover?.strip === "edge-r" || hover?.lens != null ? "ew-resize" : hover?.scale ? "crosshair" : hover?.strip ? "pointer" : hover?.cell || hover?.key ? "pointer" : "grab", touchAction: "none" }}
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={() => { setTip(null); setHover(null); setPointer(null, null); }}
            onDoubleClick={onDoubleClick}
            aria-label="Workspace"
          />
          {boardBar?.onClose ? <BoardClose onClose={boardBar.onClose} /> : null}
          {mini ? (
            <BoardMinimap
              items={mini.things}
              extent={mini.extent}
              view={view}
              viewW={viewW}
              viewH={viewH}
              focusKey={sel?.key || null}
              right={10 + coverR}
              onMove={onMiniMove}
            />
          ) : null}
          {!layout.items.some((it) => it.kind === "card") && pending.length ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[6] grid place-items-center overflow-hidden px-[16px]" style={{ top: RULER_H }} data-board-waiting="">
              {/* One column the stack's own width: a long title is cut to it, not
                  let widen the column past the middle of the board. */}
              <div className="grid w-full max-w-[420px] grid-cols-[minmax(0,1fr)] gap-[8px]">
                <div className="text-center text-[12.5px] text-[color:var(--muted)]">
                  {pending.some((c) => c.status !== "error")
                    ? `Reading ${pending.length} session${pending.length === 1 ? "" : "s"}; each takes its place on the board as it comes in`
                    : `${pending.length} session${pending.length === 1 ? "" : "s"} on this workspace could not be read`}
                </div>
                {pending.slice(0, 8).map((c) => <WaitingCard key={c.key} c={c} onPlace={placeWaiting} />)}
                {pending.length > 8 ? <div className="text-center text-[11.5px] text-[color:var(--muted)]">{`and ${pending.length - 8} more`}</div> : null}
              </div>
            </div>
          ) : null}
          {!trace && !layout.items.length && boardBar && !pending.length ? (
            // Nothing on it: what can go on it, and the ways to start (the
            // host's own, where it has them).
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[6] grid place-items-center px-[16px]" style={{ top: RULER_H, paddingRight: 16 + coverR }} data-board-empty="">
              {boardBar.empty ? <div className="pointer-events-auto grid w-full place-items-center">{boardBar.empty}</div> : (
                <div className="pointer-events-auto max-w-[380px] text-center text-[12.5px] leading-[1.5] text-[color:var(--muted)]">
                  <div className="text-[color:var(--ink)]">Nothing on this workspace yet</div>
                  <div className="mt-[4px]">Drop a session, a finding, a machine or another workspace on it, or start from a note of your own.</div>
                  <div className="mt-[10px] flex flex-wrap justify-center gap-[8px]">
                    {boardBar.onNote ? <button type="button" className={emptyButton} onClick={boardBar.onNote}>Write a note</button> : null}
                    {boardBar.onAdd ? <button type="button" className={emptyButton} onClick={boardBar.onAdd}>Find something to bring here</button> : null}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          {drawnLayout.items.some((it) => it.kind === "note" || it.kind === "thingStub") ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[6] overflow-hidden" style={{ top: RULER_H }} data-loose-notes="">
              <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                {noteLines.length || draftLine ? (
                  <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width="1" height="1" aria-hidden="true" data-note-lines="">
                    {noteLines.map((ln) => {
                      const lit = noteLit === ln.note || hoverNode === ln.id || !!pinGroups.find((g) => g.id === hoverNode)?.members.some((m) => m.id === ln.id);
                      return <line key={ln.id} x1={ln.fx} y1={ln.fy} x2={ln.tx} y2={ln.ty} stroke="var(--ink)" strokeOpacity={lit ? 0.85 : 0.35} strokeWidth={lit ? 1.5 : 1} vectorEffect="non-scaling-stroke" data-note-line={ln.id} />;
                    })}
                    {draftLine ? <line x1={draftLine.fx} y1={draftLine.fy} x2={draftLine.tx} y2={draftLine.ty} stroke="var(--ink)" strokeOpacity={0.7} strokeWidth={1.25} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" data-note-line-draft="" /> : null}
                  </svg>
                ) : null}
                {drawnLayout.items.map((it) => (it.kind === "note" ? <LooseNote key={it.key} it={it} cam={cam} pinned={pinnedCount.get(it.id) || 0} onChange={onLooseNote} onPut={onPutNote} onOff={onThingOff ? (id) => takeThing("note", id, "the note") : null} onLink={onLooseNote ? onNoteLink : null} onLit={setNoteLit} />
                  : it.kind === "thingStub" ? (
                    <ThingStub key={it.key} it={it} lit={litKey === `graph:${it.key}`} onEnter={onEnter ? enterInto : null} onOff={onThingOff ? (x) => takeThing(x.thing, x.id, x.label) : null} />
                  ) : null))}
              </div>
            </div>
          ) : null}
          {geo?.nodes?.length ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden" style={{ top: RULER_H }} data-canvas-nodes="">
              {/* Over the cards' own grips (its transform makes it one layer). */}
              <div className="absolute left-0 top-0 z-[5]" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                {/* A block being carried: where it lands if let go now. */}
                {moving?.dock ? (
                  <div className="pointer-events-none absolute rounded-[2px] border border-dashed border-[color:var(--line-strong)]" style={{ left: moving.dock.x, top: moving.dock.y, width: PIN_W, height: moving.h }} data-pin-settle="" />
                ) : moving ? (() => {
                  const it = layout.items.find((x) => x.kind === "pin" && x.key === moving.key);
                  return it?.settleTop != null && Math.abs(it.settleTop - it.y) >= 1 ? (
                    <div className="pointer-events-none absolute rounded-[2px] border border-dashed border-[color:var(--line-strong)]" style={{ left: it.x, top: it.settleTop, width: it.w, height: it.h }} data-pin-settle="" />
                  ) : null;
                })() : null}
                {chipLayer}
              </div>
            </div>
          ) : null}
          {evidenceShown ? (
            <EvidenceStrip onChat={chatEvidence} onClaim={askClaimItself} onMoment={askMoment} caption={listing === "moments" ? momentsCaption : ""} list={evidence} listed={listing === "moments"} noun={listing === "moments" ? "moment" : "claim"} rests={evidenceRests} picked={evidencePick?.id || null} pointed={evidencePoint?.id || cellPointed} outside={outsidePoint} onPoint={pointEvidence} onPick={pickEvidence} onClose={closeEvidence} onOpenAt={reportOpenAt} openMax={Math.max(180, Math.round((size.h - RULER_H) * 0.7))} stripRef={evidenceStrip} />
          ) : null}
          {tagMembers.length ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[8] overflow-hidden" style={{ top: RULER_H }} data-card-tag-list={tagList.key}>
              <PeekFrame at={tagListAt(tagList)}>
                <StackList
                  node={{ id: `tags:${tagList.key}`, key: tagList.key, members: tagMembers }}
                  card={cards.get(tagList.key)}
                  turns={tagList.key === FOCAL ? turns : cards.get(tagList.key)?.read?.turns}
                  style={PEEK_FILL}
                  held
                  head={tagWords(tagMembers)}
                  pick="open"
                  onRelease={() => setTagList(null)}
                  onMember={(mid) => { setTagList(null); openAttachment(mid); }}
                  onMemberHover={setHoverNodeDom}
                  act={(m) => (m.kind === "agent" ? { label: layWords(m), run: () => { setTagList(null); layAgent(m); } } : null)}
                  chat={attachActs.chat}
                />
              </PeekFrame>
            </div>
          ) : null}
          {/* Mounted only while something is tied, so the board holds one
              full-size bitmap the rest of the time. */}
          {st.claimFocus != null || detailLine || st.evidenceIds ? <canvas ref={connRef} className={`pointer-events-none absolute inset-0 ${st.evidenceFrom ? "z-[8]" : "z-[5]"} block`} aria-hidden="true" data-canvas-connectors="" /> : null}
          {drawnLayout.items.some((it) => it.kind === "landing") ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[6] overflow-hidden" style={{ top: RULER_H }} data-board-landings="">
              <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                {drawnLayout.items.filter((it) => it.kind === "landing").map((it) => (
                  <div
                    key={it.key}
                    className="absolute overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[10px] py-[8px] shadow-[var(--node-shadow)]"
                    style={{ left: it.x, top: it.y, width: it.w, height: it.h }}
                    data-board-landing={it.key}
                  >
                    <div className="flex items-start gap-[6px]">
                      <div className="min-w-0 flex-1 truncate text-[12.5px] text-[color:var(--ink)]">{it.title || "What you dropped here"}</div>
                      {it.status === "missed" ? (
                        <button type="button" onClick={() => letGoLanding(it.key)} aria-label="Let go" className="pointer-events-auto grid h-[16px] w-[16px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]" data-board-landing-off="">{closeIcon}</button>
                      ) : null}
                    </div>
                    <div className="mt-[2px] truncate text-[11px] text-[color:var(--muted)]">
                      {it.status === "missed" ? `Its session was not found${it.note ? `${DOT}${it.note}` : ""}`
                        : it.failed ? "Its record could not be read"
                          : it.status === "finding" ? "Finding its session; its card comes here"
                            : "Reading its record; its card comes here"}
                    </div>
                    {it.failed || it.status === "missed" ? null : (
                      <div className="mt-[8px] grid gap-[6px]" aria-hidden="true">
                        {[64, 42].map((w, i) => (
                          <span key={i} className="h-[8px] rounded-[2px] bg-[color:color-mix(in_srgb,var(--muted)_35%,transparent)] motion-safe:animate-pulse" style={{ width: `${w}%`, animationDelay: `${i * 160}ms` }} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {pinGroups.length ? (
            <div className={`pointer-events-none absolute inset-x-0 bottom-0 ${carriedPins ? "z-[5]" : "z-[7]"} overflow-hidden`} style={{ top: RULER_H }} data-board-pins="">
              <div className="absolute left-0 top-0" style={{ transform: `translate(${MACH_W - view.x * cam}px, ${-view.y * cam}px)` }}>
                {pinLayer.under}
              </div>
              {peekPin && !(peekPin.members.length === 1 && peekPin.members[0].standing) ? (() => {
                const card = cards.get(peekPin.key);
                const common = { card, turns: peekPin.key === FOCAL ? turns : card?.read?.turns, style: PEEK_FILL };
                // In a session inside a workspace this one holds: no card
                // here to open it under, so it is read here, held by a click.
                const inside = peekPin.members[0].inside || null;
                const held = heldId === peekPin.id;
                return (
                  <PeekFrame key={`pin:${peekPin.id}`} at={pinPeekAt(peekPin)}>
                    {peekPin.members.length === 1
                      ? <AttachmentCard {...common} node={peekPin.members[0]} hint={inside ? (held ? "Click to close it" : "Click to keep it open") : peekPin.members[0].earlier ? "Click to read its page in" : "Click to open it under its card"} />
                      : <StackList {...common} node={peekPin} head={stackWords(peekPin.members)} held={held} pick="open" foot={inside ? `In ${inside.workspace}` : null} onRelease={() => setHeldId(null)} onMember={inside ? null : (mid) => openAttachment(mid)} onMemberHover={setHoverNodeDom} chat={inside ? null : attachActs.chat} />}
                  </PeekFrame>
                );
              })() : null}
            </div>
          ) : null}
          {pinLayer.carried.length ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[7] overflow-hidden" style={{ top: RULER_H }} data-board-pins-carried="">
              <div className="absolute left-0 top-0" style={{ transform: `translate(${MACH_W - view.x * cam}px, ${-view.y * cam}px)` }}>
                <div className="absolute left-0 top-0" data-carried-pins={carriedPins}>{pinLayer.carried}</div>
              </div>
            </div>
          ) : null}
          {dropGhost ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[7] overflow-hidden" style={{ top: RULER_H }} data-drop-ghost-layer="">
              <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                <div
                  className="absolute overflow-hidden rounded-[2px] border border-dashed border-[color:var(--line-strong)] bg-[color:color-mix(in_srgb,var(--green-soft)_70%,transparent)] px-[10px] py-[8px]"
                  style={{ left: dropGhost.x, top: dropGhost.y, width: dropGhost.w, height: dropGhost.h }}
                  data-drop-ghost=""
                >
                  <div className="truncate text-[12.5px] text-[color:var(--ink)]">{dropGhost.title || "What you are carrying"}</div>
                  <div className="mt-[2px] text-[11px] text-[color:var(--muted)]">{dropGhost.moving ? "Moves here when let go" : "Lands here when let go"}</div>
                </div>
              </div>
            </div>
          ) : null}
          {nestInset && geo && [...geo.cards.values()].some((cg) => cg.inset || insetsSeen.current.has(cg.key)) ? (
            <div ref={insetsRef} className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] overflow-hidden" style={{ top: RULER_H }} data-canvas-insets="">
              <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                {[...geo.cards.values()].filter((cg) => cg.workspace && (cg.inset || insetsSeen.current.has(cg.key))).map((cg) => {
                  // A still picture of its own board, filling the room it has;
                  // only to look at. A head held over the top of the card
                  // takes the top of that room. Carried, the card draws it.
                  const Inset = nestInset;
                  const under = cg.inset ? Math.max(0, cg.y + headHold(cam, cg.h).foot - cg.inset.y) : 0;
                  const ih = cg.inset ? cg.inset.h - under : 0;
                  const shown = ih >= NEST_INSET_SHOWN;
                  if (shown) insetsSeen.current.set(cg.key, { x: cg.inset.x, y: cg.inset.y + under, w: cg.inset.w, h: ih });
                  const box = insetsSeen.current.get(cg.key);
                  if (!box) return null;
                  const lit = shown && hover?.inset && hover.key === cg.key ? (pictures.get(cg.key) || []).find((r) => r.key === hover.inset) : null;
                  return (
                    <div key={cg.key} inert className="absolute overflow-hidden [&_*]:pointer-events-none" style={{ left: box.x, top: box.y, width: box.w, height: box.h, visibility: shown && moving?.key !== cg.key ? undefined : "hidden" }} data-workspace-inset={cg.key}>
                      <InsetRoom w={box.w} h={box.h}>
                        {(room) => (
                          <>
                            <Inset workspaceId={String(cg.key).slice("graph:workspace:".length)} range={range} width={Math.round(room.w)} onPicture={pictureFor(cg.key)} />
                            {lit ? <div className="absolute rounded-[2px] border border-[color:var(--line-strong)]" style={{ left: lit.x - 1, top: lit.y - 1, width: lit.w + 2, height: lit.h + 2 }} data-workspace-inset-lit={lit.key} /> : null}
                          </>
                        )}
                      </InsetRoom>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {geo ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[4] overflow-hidden" style={{ top: RULER_H }} data-canvas-handles="">
              <div className="absolute left-0 top-0" style={{ transform: boardXf, transformOrigin: "0 0" }}>
                {focalWork && geo.cards.get(FOCAL) ? (() => {
                  const cg = geo.cards.get(FOCAL);
                  const w = Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w)) - Math.min(cg.x, cg.headX ?? cg.x);
                  const share = focalWork.calls ? Math.max(0.04, Math.min(1, focalWork.done / focalWork.calls)) : 0.3;
                  return (
                    <div className="pointer-events-none absolute h-[2px] overflow-hidden bg-[color:color-mix(in_srgb,var(--muted)_20%,transparent)]" style={{ left: Math.min(cg.x, cg.headX ?? cg.x) + 1, top: cg.y + headHold(cam, cg.h).foot - 1, width: Math.max(0, w - 2) }} data-card-working="" aria-hidden="true">
                      <div className="h-full bg-[color:var(--graph)] motion-safe:animate-pulse" style={{ width: `${Math.round(share * 100)}%` }} />
                    </div>
                  );
                })() : null}
                {(geo.loading || []).filter((lc) => lc.status !== "error").map((lc) => {
                  const foot = headHold(cam, lc.h).foot;
                  const room = lc.h - foot - 20;
                  return room < 8 ? null : (
                    <div
                      key={`loading:${lc.key}`}
                      className="pointer-events-none absolute grid content-start gap-[10px] overflow-hidden"
                      style={{ left: Math.max(lc.x, view.x) + 14, top: lc.y + foot + 12, width: Math.max(0, Math.min(lc.w - 28, viewW - 28, 720)), height: room }}
                      data-card-loading=""
                      aria-hidden="true"
                    >
                      {[58, 82, 40, 70, 52, 76, 46, 64].slice(0, Math.max(1, Math.floor(room / 18))).map((w, i) => (
                        <span key={i} className="h-[8px] rounded-[2px] bg-[color:color-mix(in_srgb,var(--muted)_35%,transparent)] motion-safe:animate-pulse" style={{ width: `${w}%`, animationDelay: `${i * 160}ms` }} />
                      ))}
                    </div>
                  );
                })}
                {[...geo.cards.values()].map((cg) => {
                  // In hand, a card is drawn on the canvas above the board,
                  // over where its handles would be.
                  if (moving?.key === cg.key) return null;
                  const right1 = Math.max(cg.x + cg.w, (cg.headX ?? cg.x) + (cg.headW ?? cg.w));
                  const hold = headHold(cam, cg.h).tools;
                  const tbW = cg.graph ? GRAPH_TOOLBAR_W : onAnchor ? CARD_TOOLBAR_W : CARD_TOOLBAR_W - CARD_TOOL;
                  const carried = resizing?.key === cg.key || moving?.key === cg.key;
                  const drawn = carried ? layout.items.find((x) => x.kind === "card" && x.key === cg.key) : null;
                  // Where it will settle, its list with it.
                  const settle = drawn?.settleTop != null && Math.abs(drawn.settleTop - drawn.y) >= 1
                    ? { top: drawn.settleTop, h: cg.h + (layout.items.find((x) => x.kind === "node" && x.owner === cg.key)?.h || 0) }
                    : null;
                  return (
                    <CardHandles
                      key={`h:${cg.key}`}
                      cg={cg}
                      inHand={hoverKey === cg.key || sel?.key === cg.key || resizing?.key === cg.key || toolbarKey === cg.key}
                      edge={resizing?.key === cg.key ? resizing.edge : null}
                      settle={settle}
                      isAnchor={cg.key === FOCAL}
                      canAnchor={!!onAnchor}
                      canBranch={!!onBranch}
                      canEnter={!!onEnter && String(cg.key).startsWith("graph:workspace:")}
                      folded={!!cg.folded && !cg.rolled}
                      graph={!!cg.graph}
                      own={/^graph:(machine|search|workspace):/.test(String(cg.key))}
                      here={peeks.has(cg.key)}
                      groupsLabel={cg.key === FOCAL ? "Fold every group" : allGroups.has(cg.key) ? "Only the rows its wires land on" : "Show all its groups"}
                      earlier={cg.graph || cg.folded ? null : earliers.get(cg.key) || null}
                      // The buttons keep to the canvas's right edge when the card runs past it.
                      tbX={Math.min(right1, view.x + viewW) - tbW - CARD_TOOLBAR_INSET * hold}
                      tbW={tbW}
                      hold={hold}
                      acts={handleActs}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}
          {moving ? <canvas ref={liftRef} className="pointer-events-none absolute inset-0 z-[6] block" aria-hidden="true" /> : null}
          {investigation?.steps?.length ? (
            <BoardSteps steps={investigation.steps} step={investigation.step} following={graphStepPick == null || graphStepPick >= investigation.steps.length - 1} onPick={setGraphStepPick} inset={closing ? CLOSE_ROOM : 0} />
          ) : null}
          {resizing && resizeChip ? (
            <div className="pointer-events-none absolute z-[7] whitespace-nowrap border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[7px] py-[2px] text-[11px] tabular-nums text-[color:var(--ink)] shadow-lg" style={{ left: Math.max(8, Math.min(resizeChip.x + 14, size.w - 150)), top: Math.max(RULER_H + 8, Math.min(resizeChip.y + 14, size.h - 30)) }}>
              {[
                /[lr]/.test(resizing.edge) ? (sizeDraft?.m ? `\u00d7${sizeDraft.m.toFixed(1).replace(/\.0$/, "")} its time` : "Its own time") : null,
                /[tb]/.test(resizing.edge) ? (() => {
                  const fit = fittedRows.get(resizing.key);
                  if (!fit) return null;
                  if (fit.focus) return `${fit.keep.size} row${fit.keep.size === 1 ? "" : "s"} shown`;
                  return fit.only ? `${fit.only.size} group${fit.only.size === 1 ? "" : "s"} shown` : `${fit.openGroups.size} group${fit.openGroups.size === 1 ? "" : "s"} open`;
                })() : null,
              ].filter(Boolean).join(DOT)}
            </div>
          ) : null}
          {detailView && detailPick ? (
            // A cell read beside its card in the dock a host reads cells in,
            // as it would read it there; following its file or command keeps
            // a search for it on the workspace.
            <div
              className="pointer-events-auto absolute z-10 flex flex-col overflow-hidden border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg"
              style={{ left: detailView.px, top: detailView.py ?? undefined, bottom: detailView.pb ?? undefined, width: detailView.w, height: detailView.h }}
              data-canvas-detail="cell"
              data-board-readable=""
            >
              <div className="flex h-[26px] shrink-0 items-center justify-end gap-[6px] border-b border-[color:var(--line-soft)] px-[6px] text-[11px]">
                {detailView.follow ? (
                  <HoverTooltip placement="top" label={`Keep a search for this ${detailView.follow.what} on the workspace: the other sessions that ${detailView.follow.whose}, to lay on and see wired to it`}>
                    <button
                      type="button"
                      onClick={() => {
                        const got = onBranch(detailView.follow);
                        setDetail(null);
                        if (got?.key) setPointing({ key: got.key });
                      }}
                      className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[8px] py-[1px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]"
                      data-detail-follow=""
                    >
                      {`Follow ${detailView.follow.what}`}
                    </button>
                  </HoverTooltip>
                ) : null}
                <button type="button" onClick={() => setDetail(null)} aria-label="Close" className="grid h-[18px] w-[18px] shrink-0 place-items-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">
                  {closeIcon}
                </button>
              </div>
              {(() => {
                const Dock = cellDock;
                return <Dock pick={detailPick} onClose={() => setDetail(null)} />;
              })()}
            </div>
          ) : detailView ? (
            <DetailPanel
              key={detailView.node.id}
              node={detailView.node}
              card={cards.get(detailView.node.key)}
              turns={detailView.node.key === FOCAL ? turns : cards.get(detailView.node.key)?.read?.turns}
              style={{ left: detailView.px, top: detailView.py ?? undefined, bottom: detailView.pb ?? undefined, width: detailView.w, maxHeight: detailView.h }}
              onClose={() => setDetail(null)}
              onKeep={detailView.keep ? () => {
                setReadings((cur) => (cur.some((r) => r.id === detailView.keep.id) ? cur : [...cur, detailView.keep].slice(-MAX_READINGS)));
                setDetail(null);
              } : null}
              onFollow={detailView.follow ? {
                what: detailView.follow.what,
                whose: detailView.follow.whose,
                go: () => {
                  const got = onBranch(detailView.follow);
                  setDetail(null);
                  if (got?.key) setPointing({ key: got.key });
                },
              } : null}
            />
          ) : null}
        </div>
        <BoardZoomPct.Provider value={zoomPct}>
          <BoardControls
            lead={boardBar?.lead || null}
            tail={boardBar?.tail || null}
            removal={removal}
            litTip={lit ? `${litCells.length ? (litCells.length === 1 ? "1 cell carries" : `${litCells.length} cells carry`) : "No cell on its card carries"} the followed content, on ${clip(cards.get(lit.key ?? FOCAL)?.titleText || "its card", 80)}` : ""}
            litText={lit ? (litCells.length ? (litCells.length === 1 ? "1 cell" : `${litCells.length} cells`) : "not on its card") : ""}
            settling={!!settling}
            status={barStatus}
            coverR={coverR}
            follow={boardBar?.canFollow ? !!boardBar.following : null}
            walk={boardWalk && publishing && !still && (boardWalk.status === "running" || !boardWalk.held) ? (boardWalk.status === "running" ? "running" : "ready") : null}
            moments={!previewOn && !still && momentList.length > 0}
            claims={!previewOn && !still && claimList.length > 0}
            listed={evidenceOn ? listing : null}
            clearLabel={boardBar ? boardBar.clearLabel || "Take every trace off this workspace" : null}
            canNote={!!boardBar?.onNote}
            adding={boardBar ? !!boardBar.adding : null}
            canLay={!!(onLayer && sel?.key && peeks.has(sel.key))}
            sessionGraph={!!sessionGraph}
            canClose={!!onClose}
            acts={barActs}
          />
        </BoardZoomPct.Provider>
      </div>
      {tipLabel ? <BoardTip store={tipAt} label={tipLabel} /> : null}
    </div>
  );
}
